import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"; import axios from "axios";
import {
  FiPlus,
  FiBarChart2,
  FiArrowDown,
  FiArrowUp,
  FiHash,
  FiX,
  FiDivide,
  FiTrendingUp,
  FiTarget,
  FiTrendingDown,
} from 'react-icons/fi';
import { IoIosTrendingUp, IoIosTrendingDown } from 'react-icons/io';
import { TbMathAvg, TbMathMax, TbMathMin } from 'react-icons/tb';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Tabs,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  Progress,
  Empty,
  notification,
  Table,
  Tooltip,
  Badge,
  Modal,
  Input,
  Pagination,
  Layout,
  message,
  Descriptions,
  Switch,
  Divider,
  List,
  Collapse,
} from "antd";
import {
  DatabaseOutlined,
  BarChartOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  NumberOutlined,
  RiseOutlined,
  FallOutlined,
  RightOutlined,
  EnvironmentOutlined,
  ApartmentOutlined,
  LeftOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  StarOutlined,
  SearchOutlined,
  FullscreenOutlined,
  PercentageOutlined,
  PieChartOutlined,
  FieldNumberOutlined,
  CalculatorOutlined,
  DashboardOutlined,
  LineChartOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
  WarningOutlined,
  TableOutlined,
  AreaChartOutlined,
  CloseOutlined,
  DotChartOutlined,
} from "@ant-design/icons";
import { UnorderedListOutlined, AppstoreOutlined } from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  ReferenceLine,
  Cell,
  ScatterChart,
  ZAxis,
  Scatter,
} from "recharts";
import Navbar from "./Navbar";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;
const { Content } = Layout;
const { Panel } = Collapse;

// ==================== FORM FIELD FILTER COMPONENT (WITH DEBUGGING) ====================
const FormFieldFilter = ({ selectedForm, formData, formFields, fieldLabels, referenceCache, onFilterChange }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [uniqueValues, setUniqueValues] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const isProcessingRef = useRef(false);
  const lastFieldRef = useRef(null);

  // Helper functions for resolving IDs
  const resolveDistrictName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.districts && referenceCache.districts[id]) {
      return referenceCache.districts[id].name;
    }
    return id;
  };

  const resolveDivisionName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.divisions && referenceCache.divisions[id]) {
      return referenceCache.divisions[id].name;
    }
    return id;
  };

  const resolveUlbName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.ulbs && referenceCache.ulbs[id]) {
      return referenceCache.ulbs[id].ulb_name || referenceCache.ulbs[id].name;
    }
    return id;
  };

  // Clean up row - remove duplicates and ID fields
  const cleanRow = (row) => {
    if (!row) return row;
    const cleaned = { ...row };

    // Remove ID fields
    delete cleaned.district_id;
    delete cleaned.division_id;
    delete cleaned.ulb_id;
    delete cleaned.user_id;
    delete cleaned.role_id;
    delete cleaned.population_id;

    // Remove workflow fields
    delete cleaned.workflow_action;
    delete cleaned.workflow_action_at;
    delete cleaned.workflow_action_by;
    delete cleaned.workflow_action_by_name;
    delete cleaned.workflow_action_by_role;
    delete cleaned.workflow_action_comments;
    delete cleaned.workflow_sequence;
    delete cleaned._workflow_status;
    delete cleaned.submission_workflow_id;
    delete cleaned.workflow_instance_id;
    delete cleaned.workflow_status;
    delete cleaned.workflow_stage;
    delete cleaned.workflow_updated_at;
    delete cleaned.status;
    delete cleaned.progress;

    // Remove _formatted and _numeric duplicates
    Object.keys(cleaned).forEach(key => {
      if (key.endsWith('_formatted') || key.endsWith('_numeric')) {
        const baseKey = key.replace(/_formatted$|_numeric$/, '');
        if (cleaned[baseKey] !== undefined) {
          delete cleaned[key];
        }
      }
    });

    // Remove duplicate location fields (keep only name versions)
    if (cleaned.division_name && cleaned.division) delete cleaned.division;
    if (cleaned.district_name && cleaned.district) delete cleaned.district;
    if (cleaned.ulb_name && cleaned.ulb) delete cleaned.ulb;

    return cleaned;
  };

  // UPDATED: Normalize value for comparison - handles numbers properly
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return value;

    // Convert numbers to string for consistent comparison
    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return String(value);
  };

  // UPDATED: Get unique values with proper numeric handling
  const getUniqueValues = (fieldName) => {
    if (!selectedForm || !formData || formData.length === 0) return [];
    if (isProcessingRef.current) return [];

    isProcessingRef.current = true;

    try {
      const valuesMap = new Map();

      formData.forEach(row => {
        let value = row[fieldName];

        // Resolve ID fields to names
        if (fieldName === 'district_id' || fieldName === 'district') {
          value = resolveDistrictName(value);
        } else if (fieldName === 'division_id' || fieldName === 'division') {
          value = resolveDivisionName(value);
        } else if (fieldName === 'ulb_id' || fieldName === 'ulb' || fieldName === 'ulb_name') {
          value = resolveUlbName(value);
        }

        if (value !== null && value !== undefined && value !== '') {
          // Use string representation as key for Map
          const stringKey = String(value);
          if (!valuesMap.has(stringKey)) {
            valuesMap.set(stringKey, String(value));
          }
        }
      });

      // Get unique values and sort intelligently (numbers first, then strings)
      const uniqueVals = Array.from(valuesMap.values());

      // Log for debugging
      console.log(`📊 Unique values for field "${fieldName}":`, uniqueVals.slice(0, 10));
      console.log(`📊 Total unique values: ${uniqueVals.length}`);

      // Sort: try numeric sort first, fallback to string sort
      return uniqueVals.sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        // Check if both are valid numbers and the string representation matches (no extra characters)
        if (!isNaN(numA) && !isNaN(numB) && String(numA) === a && String(numB) === b) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
    } catch (error) {
      console.error('Error getting unique values:', error);
      return [];
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleFieldSelect = (value) => {
    if (lastFieldRef.current === value) return;
    lastFieldRef.current = value;

    setSelectedField(value);
    setSelectedValue(null);
    setShowResults(false);
    setFilteredData([]);

    // Notify parent that filter is cleared when field changes
    if (onFilterChange) {
      onFilterChange(null, null, []);
    }

    if (value) {
      setLoadingValues(true);
      setTimeout(() => {
        const uniqueVals = getUniqueValues(value);
        setUniqueValues(uniqueVals);
        setLoadingValues(false);
      }, 10);
    } else {
      setUniqueValues([]);
      setLoadingValues(false);
    }
  };

  // UPDATED: Handle value selection with proper numeric comparison
  const handleValueSelect = (value) => {
    setSelectedValue(value);

    if (!value || !formData || !selectedField) {
      setFilteredData([]);
      setShowResults(false);
      if (onFilterChange) {
        onFilterChange(null, null, []);
      }
      return;
    }

    // Try to parse as number for comparison
    let numericSelectedValue = null;
    const parsedNum = parseFloat(value);
    if (!isNaN(parsedNum) && String(parsedNum) === String(value)) {
      numericSelectedValue = parsedNum;
    }

    const filtered = formData.filter(row => {
      let rowValue = row[selectedField];

      if (selectedField === 'district_id' || selectedField === 'district') {
        rowValue = resolveDistrictName(rowValue);
      } else if (selectedField === 'division_id' || selectedField === 'division') {
        rowValue = resolveDivisionName(rowValue);
      } else if (selectedField === 'ulb_id' || selectedField === 'ulb' || selectedField === 'ulb_name') {
        rowValue = resolveUlbName(rowValue);
      }

      if (rowValue === null || rowValue === undefined) return false;

      const rowValueStr = String(rowValue);
      const selectedValueStr = String(value);

      const rowNum = parseFloat(rowValueStr);
      if (!isNaN(rowNum) && numericSelectedValue !== null && rowNum === numericSelectedValue) {
        return true;
      }

      return rowValueStr.toLowerCase() === selectedValueStr.toLowerCase();
    });

    const cleanedData = filtered.map(row => cleanRow(row));
    setFilteredData(cleanedData);
    setShowResults(true);

    // FIX: Make sure to pass fieldDisplayName correctly
    if (onFilterChange) {
      const fieldDisplayName = getFieldDisplayName(selectedField);
      console.log("📢 Calling onFilterChange with:", {
        field: selectedField,
        value: value,
        recordCount: cleanedData.length,
        fieldDisplayName: fieldDisplayName
      });
      onFilterChange(selectedField, value, cleanedData, fieldDisplayName);
    }

    if (cleanedData.length === 0) {
      message.warning(`No records found matching "${value}"`);
    } else {
      message.success(`Found ${cleanedData.length} records matching "${value}"`);
    }
  };

  const handleClear = () => {
    lastFieldRef.current = null;
    setSelectedField(null);
    setUniqueValues([]);
    setSelectedValue(null);
    setFilteredData([]);
    setShowResults(false);

    // Notify parent that filter is cleared
    if (onFilterChange) {
      onFilterChange(null, null, []);
    }

    message.info('Quick filter cleared');
  };

  // UPDATED: Include numeric fields for filtering (not just text fields)
  const getTextFieldOptions = () => {
    if (!formFields || formFields.length === 0) return [];

    const excludeFields = [
      'id', '_id', 'created_at', 'updated_at', 'user_id', 'status',
      'division_id', 'district_id', 'ulb_id', 'population_id',
      'total_population', 'sc_population', 'st_population', 'area',
      'workflow_action', 'workflow_action_at', 'workflow_action_by',
      'workflow_action_by_name', 'workflow_action_by_role', 'workflow_action_comments',
      'workflow_sequence', '_workflow_status', 'submission_workflow_id',
      'workflow_instance_id', 'workflow_status', 'workflow_stage', 'workflow_updated_at',
      'progress', '_form_id', '_status', '_created_at', '_updated_at'
    ];

    return formFields.filter(field => {
      const fieldStr = String(field);
      const fieldLower = fieldStr.toLowerCase();

      // Exclude ID fields
      if (fieldLower.endsWith('_id') || fieldLower === 'id') return false;
      if (excludeFields.includes(fieldLower)) return false;

      // Include all non-excluded fields (both text and numeric)
      // This allows filtering by numeric fields like per_capita_water_supply_lpcd
      return true;
    });
  };

  const textFieldOptions = getTextFieldOptions();

  const getFieldDisplayName = (field) => {
    if (fieldLabels && fieldLabels[field]) {
      return fieldLabels[field].en || field;
    }
    return formatFieldName(field);
  };

  // ========== GET ALL COLUMNS WITH DEBUGGING ==========
  const getAllColumns = () => {
    console.log("🔍 [DEBUG] getAllColumns - Starting column extraction");

    if (!filteredData.length) {
      console.log("🔍 [DEBUG] getAllColumns - No filtered data, returning empty array");
      return [];
    }

    const sampleRow = filteredData[0];
    console.log("🔍 [DEBUG] getAllColumns - Sample row keys:", Object.keys(sampleRow));
    console.log("🔍 [DEBUG] getAllColumns - Total keys in sample row:", Object.keys(sampleRow).length);

    // Get all keys from the cleaned data
    let allKeys = Object.keys(sampleRow);
    console.log("🔍 [DEBUG] getAllColumns - Initial keys count:", allKeys.length);

    // Remove any remaining _formatted or _numeric fields
    const beforeFormatFilter = allKeys.length;
    allKeys = allKeys.filter(key => !key.endsWith('_formatted') && !key.endsWith('_numeric'));
    console.log(`🔍 [DEBUG] getAllColumns - Removed ${beforeFormatFilter - allKeys.length} _formatted/_numeric fields`);

    // Remove ID fields
    const beforeIdFilter = allKeys.length;
    allKeys = allKeys.filter(key => !key.endsWith('_id') && key !== 'id');
    console.log(`🔍 [DEBUG] getAllColumns - Removed ${beforeIdFilter - allKeys.length} ID fields`);

    // Remove workflow/internal fields
    const excludeList = [
      'workflow_action', 'workflow_action_at', 'workflow_action_by',
      'workflow_action_by_name', 'workflow_action_by_role', 'workflow_action_comments',
      'workflow_sequence', '_workflow_status', 'submission_workflow_id',
      'workflow_instance_id', 'workflow_status', 'workflow_stage', 'workflow_updated_at',
      'progress', 'status', '_id', '_form_id', '_status', '_created_at', '_updated_at',
      'created_at', 'updated_at'
    ];
    const beforeWorkflowFilter = allKeys.length;
    allKeys = allKeys.filter(key => !excludeList.includes(key) && !key.startsWith('_'));
    console.log(`🔍 [DEBUG] getAllColumns - Removed ${beforeWorkflowFilter - allKeys.length} workflow/internal fields`);

    // Remove duplicate location fields (prefer the name versions)
    let duplicateCount = 0;
    if (allKeys.includes('division_name') && allKeys.includes('division')) {
      allKeys = allKeys.filter(key => key !== 'division');
      duplicateCount++;
    }
    if (allKeys.includes('district_name') && allKeys.includes('district')) {
      allKeys = allKeys.filter(key => key !== 'district');
      duplicateCount++;
    }
    if (allKeys.includes('ulb_name') && allKeys.includes('ulb')) {
      allKeys = allKeys.filter(key => key !== 'ulb');
      duplicateCount++;
    }
    console.log(`🔍 [DEBUG] getAllColumns - Removed ${duplicateCount} duplicate location fields`);

    // Sort columns: location names first, then alphabetically
    const locationFields = ['division_name', 'district_name', 'ulb_name'];
    const locationCols = allKeys.filter(key => locationFields.includes(key));
    const otherCols = allKeys.filter(key => !locationFields.includes(key)).sort();

    const finalColumns = [...locationCols, ...otherCols];

    console.log("🔍 [DEBUG] getAllColumns - FINAL COLUMN COUNT:", finalColumns.length);
    console.log("🔍 [DEBUG] getAllColumns - Location columns:", locationCols);
    console.log("🔍 [DEBUG] getAllColumns - Other columns (first 10):", otherCols.slice(0, 10));
    console.log("🔍 [DEBUG] getAllColumns - All columns:", finalColumns);

    // Create table columns
    const tableColumns = finalColumns.map(key => ({
      title: (
        <Tooltip title={key}>
          <span>{getFieldDisplayName(key)}</span>
        </Tooltip>
      ),
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150,
      sorter: (a, b) => {
        const valA = a[key] || '';
        const valB = b[key] || '';
        return String(valA).localeCompare(String(valB));
      },
      render: (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') return value.toLocaleString();
        if (typeof value === 'string' && value.length > 50) {
          return <Tooltip title={value}>{value.substring(0, 47)}...</Tooltip>;
        }
        return value;
      }
    }));

    console.log("🔍 [DEBUG] getAllColumns - Table columns created:", tableColumns.length);

    return tableColumns;
  };

  // Debug effect to log when filteredData changes
  useEffect(() => {
    if (filteredData.length > 0) {
      console.log("📊 [DEBUG] filteredData updated - Records:", filteredData.length);
      const sampleRow = filteredData[0];
      console.log("📊 [DEBUG] Sample row keys after cleaning:", Object.keys(sampleRow));
      console.log("📊 [DEBUG] Total columns in cleaned data:", Object.keys(sampleRow).length);
    }
  }, [filteredData]);

  // Get active filter info for display
  const getActiveFilterInfo = () => {
    if (selectedField && selectedValue) {
      return {
        field: selectedField,
        fieldDisplayName: getFieldDisplayName(selectedField),
        value: selectedValue,
        recordCount: filteredData.length
      };
    }
    return null;
  };

  if (!selectedForm || textFieldOptions.length === 0) {
    return null;
  }

  const activeFilter = getActiveFilterInfo();

  // return (
  //   <Card
  //     title={
  //       <Space>
  //         <FilterOutlined style={{ color: "#1890ff" }} />
  //         <span style={{ fontWeight: 600 }}>Quick Filter by Field Value</span>
  //         <Tag color="blue" icon={<InfoCircleOutlined />}>
  //           Select Field → Choose Value → View Matching Rows
  //         </Tag>
  //       </Space>
  //     }
  //     style={{
  //       marginBottom: 16,
  //       borderRadius: "12px",
  //       boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  //       border: "1px solid #f0f0f0"
  //     }}
  //     bodyStyle={{ padding: "16px" }}
  //     size="small"
  //   >
  //     {/* Active Filter Banner */}
  //     {activeFilter && (
  //       <div style={{
  //         marginBottom: 16,
  //         padding: "8px 12px",
  //         background: "#e6f7ff",
  //         borderRadius: "8px",
  //         borderLeft: "3px solid #1890ff",
  //         display: "flex",
  //         justifyContent: "space-between",
  //         alignItems: "center",
  //         flexWrap: "wrap",
  //         gap: "8px"
  //       }}>
  //         <Space>
  //           <FilterOutlined style={{ color: "#1890ff" }} />
  //           <span>
  //             <strong>Active Filter:</strong> {activeFilter.fieldDisplayName} = "{activeFilter.value}"
  //           </span>
  //           <Tag color={activeFilter.recordCount > 0 ? "blue" : "red"}>
  //             {activeFilter.recordCount} matching records
  //           </Tag>
  //         </Space>
  //         <Button
  //           size="small"
  //           type="link"
  //           onClick={handleClear}
  //           icon={<DeleteOutlined />}
  //         >
  //           Clear Filter
  //         </Button>
  //       </div>
  //     )}

  //     <Row gutter={[16, 16]} align="middle">
  //       <Col xs={24} md={8}>
  //         <div style={{ marginBottom: 6 }}>
  //           <Text type="secondary" style={{ fontSize: "11px" }}>
  //             <DatabaseOutlined /> 1. Select Field
  //           </Text>
  //         </div>
  //         <Select
  //           placeholder="Choose a field to filter by..."
  //           value={selectedField}
  //           onChange={handleFieldSelect}
  //           style={{ width: "100%" }}
  //           showSearch
  //           allowClear
  //           size="middle"
  //           dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
  //           notFoundContent="No fields available"
  //         >
  //           {textFieldOptions.map(field => (
  //             <Option key={field} value={field}>
  //               <Space>
  //                 <span>📋</span>
  //                 <span>{getFieldDisplayName(field)}</span>
  //                 <span style={{ fontSize: "10px", color: "#8c8c8c" }}>
  //                   ({field})
  //                 </span>
  //               </Space>
  //             </Option>
  //           ))}
  //         </Select>
  //       </Col>

  //       {selectedField && (
  //         <Col xs={24} md={8}>
  //           <div style={{ marginBottom: 6 }}>
  //             <Text type="secondary" style={{ fontSize: "11px" }}>
  //               <SearchOutlined /> 2. Select Value
  //             </Text>
  //           </div>
  //           <Select
  //             placeholder={loadingValues ? "Loading unique values..." : "Choose a value to filter..."}
  //             value={selectedValue}
  //             onChange={handleValueSelect}
  //             style={{ width: "100%" }}
  //             showSearch
  //             allowClear
  //             loading={loadingValues}
  //             size="middle"
  //             dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
  //             notFoundContent={loadingValues ? <Spin size="small" /> : (uniqueValues.length === 0 ? "No values available" : "No matching values")}
  //           >
  //             {uniqueValues.map(value => (
  //               <Option key={value} value={value}>
  //                 <Tooltip title={value}>
  //                   {value.length > 60 ? value.substring(0, 57) + "..." : value}
  //                 </Tooltip>
  //               </Option>
  //             ))}
  //           </Select>
  //         </Col>
  //       )}

  //       {(selectedField || selectedValue || showResults) && (
  //         <Col xs={24} md={8}>
  //           <div style={{ marginBottom: 6 }}>&nbsp;</div>
  //           <Space style={{ width: "100%" }}>
  //             <Button
  //               icon={<DeleteOutlined />}
  //               onClick={handleClear}
  //               danger
  //               size="middle"
  //               style={{ flex: 1 }}
  //             >
  //               Clear All
  //             </Button>
  //             {showResults && filteredData.length > 0 && (
  //               <Button
  //                 type="primary"
  //                 icon={<EyeOutlined />}
  //                 onClick={() => {
  //                   const allColumns = getAllColumns();

  //                   console.log("🎯 [DEBUG] View All button clicked - Columns count:", allColumns.length);
  //                   console.log("🎯 [DEBUG] Column names:", allColumns.map(c => c.dataIndex));

  //                   Modal.info({
  //                     title: (
  //                       <Space>
  //                         <FilterOutlined />
  //                         <span>Records matching "{selectedValue}"</span>
  //                         <Tag color="blue">{filteredData.length} records</Tag>
  //                         <Tag color="green">{allColumns.length} columns</Tag>
  //                       </Space>
  //                     ),
  //                     width: "95%",
  //                     style: { top: 20 },
  //                     content: (
  //                       <Table
  //                         dataSource={filteredData}
  //                         columns={allColumns}
  //                         rowKey={(record, index) => `filtered_${index}`}
  //                         pagination={{
  //                           pageSize: 20,
  //                           showSizeChanger: true,
  //                           showTotal: (total, range) =>
  //                             `${range[0]}-${range[1]} of ${total} records`
  //                         }}
  //                         size="small"
  //                         scroll={{ x: "max-content", y: 500 }}
  //                       />
  //                     ),
  //                     okText: "Close",
  //                     icon: null,
  //                     maskClosable: true
  //                   });
  //                 }}
  //                 style={{ flex: 1 }}
  //               >
  //                 View All ({filteredData.length})
  //               </Button>
  //             )}
  //           </Space>
  //         </Col>
  //       )}
  //     </Row>

  //     {showResults && selectedValue && (filteredData.length > 0 ? (
  //       <div style={{ marginTop: 16 }}>
  //         <Divider style={{ margin: "8px 0" }} />
  //         <div style={{ marginBottom: 12 }}>
  //           <Space>
  //             <Badge
  //               count={filteredData.length}
  //               showZero
  //               style={{ backgroundColor: filteredData.length > 0 ? "#1890ff" : "#8c8c8c" }}
  //             />
  //             <Text strong>
  //               Records matching "{selectedValue}"
  //             </Text>
  //             <Tag color="geekblue" icon={<FilterOutlined />}>
  //               Field: {getFieldDisplayName(selectedField)}
  //             </Tag>
  //           </Space>
  //         </div>

  //         <Table
  //           dataSource={filteredData.slice(0, 5)}
  //           columns={getAllColumns().slice(0, 8)}
  //           rowKey={(record, index) => `preview_${index}`}
  //           pagination={false}
  //           size="small"
  //           scroll={{ x: "max-content" }}
  //           bordered
  //         />
  //         {filteredData.length > 5 && (
  //           <div style={{ textAlign: "center", marginTop: 12 }}>
  //             <Text type="secondary">+ {filteredData.length - 5} more records. Click "View All" to see all columns.</Text>
  //           </div>
  //         )}
  //       </div>
  //     ) : (
  //       // Show message when no records found
  //       <div style={{ marginTop: 16, textAlign: "center", padding: "20px" }}>
  //         <Alert
  //           message="No Matching Records"
  //           description={`No records found where ${getFieldDisplayName(selectedField)} equals "${selectedValue}".`}
  //           type="warning"
  //           showIcon
  //         />
  //       </div>
  //     ))}
  //   </Card>
  // );
};

const compactViewBackgroundStyles = `
  .compact-dashboard-container {
    position: relative;
    background-image: url('https://your-image-url.com/background.jpg'); /* Replace with your image URL */
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    min-height: 100%;
    border-radius: 8px;
  }

  .compact-dashboard-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.85); /* White overlay with 85% opacity for readability */
    border-radius: 8px;
    pointer-events: none;
  }

  .compact-dashboard-container > * {
    position: relative;
    z-index: 1;
  }
`;

const scrollbarStyles = `
  .invisible-scroll {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s ease;
  }

  .invisible-scroll:hover {
    scrollbar-color: #C1C1C1 #F1F1F1;
  }

  .invisible-scroll::-webkit-scrollbar {
    width: 6px;     /* Changed from 4px to 6px */
    height: 6px;    /* Changed from 4px to 6px */
    background: transparent;
  }

  .invisible-scroll::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
    transition: background 0.3s ease;
  }

  .invisible-scroll::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
    transition: background 0.3s ease;
  }

  .invisible-scroll:hover::-webkit-scrollbar-thumb {
    background: #C1C1C1;
  }

  .invisible-scroll:hover::-webkit-scrollbar-track {
    background: #F1F1F1;
  }
`;

const mobileResponsiveStyles = `
  /* Mobile Responsive Styles */
  @media (max-width: 768px) {
    /* Hero Section */
    .hero-section {
      min-height: 450px !important;
      margin-bottom: 30px !important;
    }

    .hero-content-container {
      padding: 20px 16px !important;
      min-height: 450px !important;
    }

    .compact-card-container {
      max-width: 100% !important;
      max-height: calc(450px - 40px) !important;
      width: 100% !important;
    }

    /* Compact Cards */
    .compact-card {
      padding: 10px 12px !important;
      margin-bottom: 8px !important;
    }

    .compact-card-icon {
      width: 30px !important;
    }

    .compact-card-icon img,
    .compact-card-icon span {
      width: 24px !important;
      height: 24px !important;
      font-size: 20px !important;
    }

    .compact-card-title {
      font-size: 14px !important;
    }

    /* Normal Cards Grid - Change from 3 columns to 1 column */
    .cards-grid {
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
    }

    .cards-grid > div {
      width: 100% !important;
    }

    /* Filter Section */
    .filter-section {
      padding: 12px !important;
    }

    .filter-row {
      flex-direction: column !important;
      gap: 10px !important;
    }

    .filter-row > div {
      width: 100% !important;
      min-width: 100% !important;
    }

    .filter-buttons {
      width: 100% !important;
      display: flex !important;
      gap: 10px !important;
    }

    .filter-buttons button {
      flex: 1 !important;
    }

    /* Data Analysis Section */
    .analysis-section {
      overflow-x: auto !important;
      padding-bottom: 8px !important;
    }

    .analysis-section .ant-select,
    .analysis-section .ant-input,
    .analysis-section button {
      min-width: auto !important;
    }

    /* Selected Form View */
    .selected-form-container {
      height: auto !important;
      min-height: calc(100vh - 200px) !important;
      margin-top: -10px !important;
    }

    .selected-form-split {
      flex-direction: column !important;
      gap: 12px !important;
    }

    .data-points-list {
      width: 100% !important;
      height: 300px !important;
    }

    .report-cards-container {
      width: 100% !important;
      height: auto !important;
    }

    /* Cards in selected form view */
    .data-point-card {
      flex-direction: column !important;
    }

    .data-point-card-left,
    .data-point-card-right {
      width: 100% !important;
      padding-right: 0 !important;
    }

    .data-point-card-right {
      margin-top: 16px !important;
    }

    /* Metrics grid in cards */
    .metrics-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 8px !important;
    }

    /* Tables */
    .ant-table {
      font-size: 12px !important;
    }

    .ant-table-cell {
      padding: 8px 4px !important;
    }

    /* Modals */
    .ant-modal {
      max-width: 95% !important;
      margin: 10px auto !important;
    }

    .ant-modal-body {
      padding: 16px !important;
      max-height: 70vh !important;
      overflow-y: auto !important;
    }

    /* Charts */
    .recharts-responsive-container {
      min-height: 250px !important;
    }

    .recharts-wrapper {
      font-size: 10px !important;
    }

    /* KPI Cards (Key Metrics) */
    .kpi-cards-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 10px !important;
    }

    /* Division Analysis Cards */
    .division-analysis-grid {
      flex-direction: column !important;
    }

    .division-card {
      width: 100% !important;
    }

    /* Tabs */
    .ant-tabs-tab {
      padding: 8px 12px !important;
      font-size: 12px !important;
    }

    /* Buttons */
    .ant-btn {
      font-size: 12px !important;
      height: 32px !important;
      padding: 0 12px !important;
    }

    /* Select dropdowns */
    .ant-select-selector {
      font-size: 12px !important;
    }

    /* Typography */
    h2, .ant-typography h2 {
      font-size: 18px !important;
    }

    h3 {
      font-size: 16px !important;
    }

    /* Statistic values */
    .ant-statistic-content {
      font-size: 18px !important;
    }

    /* Badges and Tags */
    .ant-tag {
      font-size: 10px !important;
      padding: 0 6px !important;
    }

    /* Search input */
    .ant-input-search {
      width: 100% !important;
    }

    /* State Total and Active Filters boxes */
    .state-total-box {
      padding: 6px 10px !important;
      width: auto !important;
      min-width: 100px !important;
    }

    .state-total-box .state-total-value {
      font-size: 16px !important;
    }
  }

  /* For medium screens (tablets) */
  @media (min-width: 769px) and (max-width: 1024px) {
    .cards-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 16px !important;
    }

    .filter-row {
      flex-wrap: wrap !important;
    }

    .filter-row > div {
      min-width: 200px !important;
    }

    .data-point-card-left {
      flex: 1.5 !important;
    }

    .data-point-card-right {
      flex: 1 !important;
    }
  }

  /* Small mobile devices */
  @media (max-width: 480px) {
    .hero-section {
      min-height: 400px !important;
    }

    .hero-content-container {
      padding: 16px !important;
      min-height: 400px !important;
    }

    .compact-card-title {
      font-size: 12px !important;
    }

    .metrics-grid {
      grid-template-columns: 1fr !important;
    }

    .kpi-cards-grid {
      grid-template-columns: 1fr !important;
    }

    .state-total-box {
      width: 100% !important;
      text-align: center !important;
    }

    .active-filters-container {
      flex-direction: column !important;
    }

    .active-filters-container > div {
      width: 100% !important;
    }
  }
`;

const getResponsiveStyles = (isMobile) => ({
  // Hero container
  heroContainer: {
    minHeight: isMobile ? "400px" : "600px",
    marginBottom: isMobile ? "30px" : "50px",
  },

  // Cards grid
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : (window.innerWidth <= 1024 ? "repeat(2, 1fr)" : "repeat(3, 1fr)"),
    gap: isMobile ? "16px" : "24px",
    width: "100%",
  },

  // Filter row
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: isMobile ? "10px" : "12px",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "stretch" : "flex-end",
  },

  // Filter item
  filterItem: {
    flex: isMobile ? "1" : "1 1 0",
    minWidth: isMobile ? "100%" : "150px",
  },

  // Selected form split layout
  selectedFormSplit: {
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "12px" : "16px",
  },

  // Data points list width
  dataPointsList: {
    width: isMobile ? "100%" : "320px",
    height: isMobile ? "300px" : "auto",
  },

  // KPI cards grid (Key Metrics)
  kpiCardsGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(200px, 1fr))",
    gap: isMobile ? "10px" : "12px",
  },

  // Metrics grid inside cards
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? (window.innerWidth <= 480 ? "1fr" : "repeat(2, 1fr)") : "repeat(3, 1fr)",
    gap: isMobile ? "8px" : "10px",
  },

  // Chart container
  chartContainer: {
    width: "100%",
    height: isMobile ? 250 : 350,
  },
});

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ;

const COLORS = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#faad14",
  "#a0d911",
  "#2f54eb",
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#faad14",
  "#a0d911",
  "#2f54eb",
];

const PIE_COLORS = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#faad14",
  "#a0d911",
  "#2f54eb",
  "#ff6b6b",
  "#4ecdc4",
  "#45b7d1",
  "#96ceb4",
  "#ffeaa7",
  "#dfe6e9",
  "#ffb6b9",
  "#fae3d9",
  "#bbded6",
  "#d4a5a5",
];

const CARD_START_COLORS = [
  '#1a6b8a', '#c0392b', '#4c1d95', '#14532d', '#9d174d', '#1e3a5f',
  '#92400e', '#1e293b', '#be185d', '#0e7490', '#3b0764', '#365314',
  '#7c2d12', '#1e1b4b', '#134e4a', '#6b21a8',
];

const CARD_END_COLORS = [
  '#0d9488', '#e67e22', '#7c3aed', '#16a34a', '#db2777', '#2563eb',
  '#d97706', '#475569', '#f97316', '#38bdf8', '#a21caf', '#65a30d',
  '#c2410c', '#4338ca', '#0d9488', '#9d174d',
];

// 7 solid shades for Data Point View cards - Dark to Light gradient
const DATAPOINT_GRADIENT_SHADES = [
  { start: '#008ae0', end: '#a0d0e2' },  // Unique - State Total
  { start: '#e000e4', end: '#d98eda' },  // Shade 2
  { start: '#b90000', end: '#f0c8c8' },  // Shade 3
  { start: '#02cb4f', end: '#afe1c3' },  // Shade 4
  { start: '#c25405', end: '#dbb8a2' },  // Shade 5
  { start: '#ce0059', end: '#dbb1c1' },  // Shade 6
  { start: '#29811d', end: '#8af586' },  // Shade 7
];

// Add this near your COLORS and PIE_COLORS constants
const COMPACT_CARD_STYLES = {
  cardStyle: {
    borderRadius: "6px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    background: "#fff",
    border: "none",
    overflow: "hidden",
    height: "auto",
    minHeight: "280px",
  },
  headerStyle: {
    padding: "10px 14px 0 14px",
    flexShrink: 0,
  },
  titleStyle: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "6px",
    cursor: "pointer",
    color: "#141414",
  },
  metricsSectionStyle: {
    marginBottom: "11px",
  },
  metricsGridStyle: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  metricCardStyle: {
    flex: "1",
    minWidth: "100px",
    padding: "6px",
    background: "#ffffff",
    borderRadius: "5px",
  },
  metricValueStyle: {
    fontSize: "12px",
    fontWeight: 600,
  },
  buttonStyle: {
    fontSize: "12px",
    height: "32px",
    borderRadius: "6px",
  },
  buttonContainerStyle: {
    padding: "10px 14px",
    borderTop: "1px solid #f0f0f0",
    background: "#fff",
    flexShrink: 0,
  },
};

const formatFieldName = (fieldName) => {
  if (!fieldName) return "Unknown Field";

  if (typeof fieldName === "object") {
    fieldName =
      fieldName.id || fieldName.label || fieldName.value || String(fieldName);
  }

  let name = String(fieldName);

  name = name
    .replace(/node_\d+_/g, "")
    .replace(/_\d+_/g, "_")
    .replace(/\./g, " - ")
    .replace(/:/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

  name = name.replace(/[_-]/g, " ");

  const fieldMappings = {
    division_id: "Division",
    division_name: "Division Name",
    district_id: "District",
    district_name: "District Name",
    ulb_id: "ULB",
    ulb_name: "ULB Name",
    population_id: "Population Reference",
    user_id: "User ID",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  if (fieldMappings[name]) {
    return fieldMappings[name];
  }

  if (
    name.toLowerCase().includes("population") ||
    name.toLowerCase().includes("count") ||
    name.toLowerCase().includes("area") ||
    name.toLowerCase().includes("capacity") ||
    name.toLowerCase().includes("amount") ||
    name.toLowerCase().includes("संख्या") ||
    name.toLowerCase().includes("क्षमता")
  ) {
    return name.replace(/\b\w/g, (char) => char.toUpperCase()) + " 📊";
  }

  name = name.replace(/\b\w/g, (char) => char.toUpperCase());

  return name.trim() || String(fieldName);
};

// Helper function to check if a field should be excluded from graphs
const shouldExcludeFromGraphs = (fieldName, displayName) => {
  if (!fieldName) return true;

  const fieldLower = fieldName.toLowerCase();
  const displayLower = (displayName || "").toLowerCase();

  // Exclude ID fields
  if (
    fieldLower.endsWith("_id") ||
    fieldLower.match(/\bid\b/) ||
    fieldLower.includes("division_id") ||
    fieldLower.includes("district_id") ||
    fieldLower.includes("ulb_id") ||
    fieldLower.includes("population_id") ||
    fieldLower.includes("user_id") ||
    fieldLower.includes("role_id")
  ) {
    return true;
  }

  // Exclude progress fields
  if (fieldLower.includes("progress") || displayLower.includes("progress")) {
    return true;
  }

  // Exclude location name fields (they're for grouping, not for value display)
  if (
    fieldLower.includes("division") ||
    fieldLower.includes("district") ||
    fieldLower.includes("ulb") ||
    fieldLower.includes("विभाग") ||
    fieldLower.includes("जिल्हा") ||
    fieldLower.includes("मनपा")
  ) {
    return true;
  }

  return false;
};

const startColors = [
  '#1a6b8a', // Ocean Drift
  '#c0392b', // Sunset Blaze
  '#4c1d95', // Violet Dusk
  '#14532d', // Forest Canopy
  '#9d174d', // Rose Gold
  '#1e3a5f', // Steel Blue
  '#92400e', // Amber Glow
  '#1e293b', // Midnight Slate
  '#be185d', // Coral Crush
  '#0e7490', // Arctic Teal
  '#3b0764', // Plum Noir
  '#365314', // Lime Zest
  '#7c2d12', // Copper Dust
  '#1e1b4b', // Indigo Night
  '#134e4a', // Sage Mist
  '#6b21a8', // Dusty Mauve
];

const endColors = [
  '#0d9488', // Ocean Drift end
  '#e67e22', // Sunset Blaze end
  '#7c3aed', // Violet Dusk end
  '#16a34a', // Forest Canopy end
  '#db2777', // Rose Gold end
  '#2563eb', // Steel Blue end
  '#d97706', // Amber Glow end
  '#475569', // Midnight Slate end
  '#f97316', // Coral Crush end
  '#38bdf8', // Arctic Teal end
  '#a21caf', // Plum Noir end
  '#65a30d', // Lime Zest end
  '#c2410c', // Copper Dust end
  '#4338ca', // Indigo Night end
  '#0d9488', // Sage Mist end
  '#9d174d', // Dusty Mauve end
];

const hoverColors = [
  '#2a8b9a', // Ocean Drift hover
  '#d0493b', // Sunset Blaze hover
  '#5c2da5', // Violet Dusk hover
  '#256342', // Forest Canopy hover
  '#ad2757', // Rose Gold hover
  '#2e4a6f', // Steel Blue hover
  '#a2571e', // Amber Glow hover
  '#2e394b', // Midnight Slate hover
  '#ce286d', // Coral Crush hover
  '#1e84a0', // Arctic Teal hover
  '#4b1874', // Plum Noir hover
  '#466324', // Lime Zest hover
  '#8c421c', // Copper Dust hover
  '#2e2b5b', // Indigo Night hover
  '#235e5a', // Sage Mist hover
  '#7b31b8', // Dusty Mauve hover
];
const FormKPIDashboard = () => {
  // Add this near your other state declarations (around line 100-150)
  const [jointChartType, setJointChartType] = useState("bar"); // For chart type toggle
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllCards, setShowAllCards] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});
  const [numericFields, setNumericFields] = useState([]);
  const [availableNumericColumns, setAvailableNumericColumns] = useState([]);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  // Add these near your other state declarations
  const [rankingType, setRankingType] = useState("top"); // 'top', 'bottom', 'all'
  const [rankingOrder, setRankingOrder] = useState("desc");
  const [showRankingOptions, setShowRankingOptions] = useState(false);
  const [rankingResults, setRankingResults] = useState(null);
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [rankingCount, setRankingCount] = useState(10); // ADD THIS
  const [showRankingCountInput, setShowRankingCountInput] = useState(false); // ADD THIS
  const [dashboardBgImages, setDashboardBgImages] = useState([]);
  const [currentBgImageIndex, setCurrentBgImageIndex] = useState(0);
  const [selectedFormColorIndex, setSelectedFormColorIndex] = useState(null);
  const [dashboardLayout, setDashboardLayout] = useState([]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const hexToRgb = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Handle 3-digit hex (e.g., #fff)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }

    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  // Add this with your other state declarations
  const [showOnlyMatchingUlbs, setShowOnlyMatchingUlbs] = useState(false);

  // Add near your other state declarations
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [locationModalData, setLocationModalData] = useState({
    title: '',
    locations: [],
    fieldName: '',
    threshold: null,
    operator: ''
  });
  const [locationModalLoading, setLocationModalLoading] = useState(false);

  const [selectedDataPointId, setSelectedDataPointId] = useState(null);

  // Graph Modal State
  const [graphModalVisible, setGraphModalVisible] = useState(false);
  const [graphModalData, setGraphModalData] = useState(null);
  const [graphModalLoading, setGraphModalLoading] = useState(false);

  const [filters, setFilters] = useState({
    district: null,
    district_id: null,
    division: null,
    division_id: null,
    ulb_name: null,
    ulb_id: null,
    ulb_type: null,
    ulb_class: null,
  });
  // Find this section in your code (around line 150-200)
  const [calculationResults, setCalculationResults] = useState([]);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [showAnalysisResults, setShowAnalysisResults] = useState(false);
  const [rawDataForAnalysis, setRawDataForAnalysis] = useState([]);
  const [analysisDataColumns, setAnalysisDataColumns] = useState([]);
  const [hierarchicalGraphData, setHierarchicalGraphData] = useState(null);
  const [selectedDivisionForGraph, setSelectedDivisionForGraph] = useState(null);
  const [selectedDistrictForGraph, setSelectedDistrictForGraph] = useState(null);
  const [hierarchicalStatistics, setHierarchicalStatistics] = useState({});
  const [selectedStatsField, setSelectedStatsField] = useState(null);


  // Add near your other state declarations (around line 100-150)
  const [selectedSubjectForm, setSelectedSubjectForm] = useState(null);
  const [filteredDataPoints, setFilteredDataPoints] = useState([]);
  const [yAxisSelectedFields, setYAxisSelectedFields] = useState([]); // Multi-select for Y-axis
  const [yAxisOptions, setYAxisOptions] = useState([]); // Available fields for Y-axis
  const [xAxisSelectedFields, setXAxisSelectedFields] = useState(['auto']); // Multi-select for X-axis (default to auto)
  const [xAxisOptions, setXAxisOptions] = useState([
    { value: 'auto', label: 'Auto (Based on Filters)', type: 'system' },
    { value: 'division', label: 'Division', type: 'system' },
    { value: 'district', label: 'District', type: 'system' },
    { value: 'ulb', label: 'ULB', type: 'system' },
  ]);
  const initialDisplayCount = 8;


  const selectedYAxisFields = yAxisSelectedFields
    .map(value => {
      const option = yAxisOptions.find(opt => opt.value === value);
      if (option) {
        return calculationResults.find(r => r.fieldId === option.fieldId);
      }
      return null;
    })
    .filter(Boolean);

  const selectedXAxisFields = xAxisSelectedFields
    .filter(value => value !== 'auto')
    .map(value => {
      const option = xAxisOptions.find(opt => opt.value === value);
      if (option && option.type === 'dataPoint') {
        return calculationResults.find(r => r.fieldId === option.fieldId);
      }
      return null;
    })
    .filter(Boolean);

  // Get selected X-axis fields data

  // Check if auto mode is selected
  const isAutoMode = xAxisSelectedFields.includes('auto');
  const [frontPageFilters, setFrontPageFilters] = useState({
    division: null,
    district: null,
    ulb_type: null,
    ulb_class: null,
    ulb_name: null,
  });

  // Determine which filters to use based on context
  const isInsideFormView = selectedForm !== null;
  const activeFiltersForGraph = isInsideFormView ? filters : frontPageFilters;

  // Check if we have an LTE/GTE operation with a threshold
  const lteGteOperation = calculationResults.find(
    result => result.operation === "lte" || result.operation === "gte"
  );
  const thresholdValue = lteGteOperation?.threshold;
  const thresholdOperator = lteGteOperation?.operation;
  const thresholdFieldName = lteGteOperation?.fieldName;

  // AFTER calculationResults is declared - Update X-axis and Y-axis option
  useEffect(() => {
    if (calculationResults && calculationResults.length > 0) {
      const dataPointOptions = calculationResults.map((result, index) => ({
        value: `field_${result.fieldId}`,
        label: result.fieldName,
        type: 'dataPoint',
        fieldId: result.fieldId,
        columnName: result.columnName,
        operation: result.operation,
      }));

      const systemOptions = [
        { value: 'auto', label: 'Auto (Based on Filters)', type: 'system' },
        { value: 'division', label: 'Division', type: 'system' },
        { value: 'district', label: 'District', type: 'system' },
        { value: 'ulb', label: 'ULB', type: 'system' },
      ];

      setXAxisOptions([...systemOptions, ...dataPointOptions]);
      setYAxisOptions(dataPointOptions);

      if (yAxisSelectedFields.length === 0 && dataPointOptions.length > 0) {
        const defaultSelected = dataPointOptions.slice(0, 5).map(opt => opt.value);
        setYAxisSelectedFields(defaultSelected);
      }

      if (xAxisSelectedFields.length === 0) {
        setXAxisSelectedFields(['auto']);
      }
    } else {
      setXAxisOptions([
        { value: 'auto', label: 'Auto (Based on Filters)', type: 'system' },
        { value: 'division', label: 'Division', type: 'system' },
        { value: 'district', label: 'District', type: 'system' },
        { value: 'ulb', label: 'ULB', type: 'system' },
      ]);
      setYAxisOptions([]);
      setYAxisSelectedFields([]);
      setXAxisSelectedFields(['auto']);
    }
  }, [calculationResults]);


  const [formColumnSearchTerm, setFormColumnSearchTerm] = useState("");
  const [searchingFormColumns, setSearchingFormColumns] = useState(false);
  const [filteredReportFields, setFilteredReportFields] = useState([]);

  // Add these near your other state declarations (around line 100-150)
  // Add this function near your other helper functions (around line where formatFieldName is defined)
  const getAvailableYAxisFields = () => {
    if (!calculationResults || calculationResults.length === 0) return [];

    return calculationResults.map((result) => ({
      fieldId: result.fieldId,
      fieldName: result.fieldName,
      columnName: result.columnName,
      operation: result.operation,
      result: result.result,
      details: result.details,
    }));
  };


  const initialGraphsLoadedRef = useRef(false);
  const processedFormIdsRef = useRef(new Set());

  // Add these near your other state declarations (around line 100-150)
  const [drillLevel, setDrillLevel] = useState('division'); // 'division', 'district', 'ulb'
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districtWiseData, setDistrictWiseData] = useState([]);
  const [ulbWiseData, setUlbWiseData] = useState([]);

  // Add this near your other state declarations
  const [preventReload, setPreventReload] = useState(false);

  // Add these near your other state declarations (around line 100-150)
  const [breakdownData, setBreakdownData] = useState(null);
  const [selectedBreakdownField, setSelectedBreakdownField] = useState(null);

  // Add these state variables near your other state declarations
  const [selectedGraphFieldId, setSelectedGraphFieldId] = useState(null);
  // Add this near your other state declarations (around line 100-150)
  const [originalFormData, setOriginalFormData] = useState([]);
  // Add this state variable

  const [graphLoadQueue, setGraphLoadQueue] = useState([]); // Queue for loading graphs
  const [graphLoadTimeouts, setGraphLoadTimeouts] = useState({});

  // State declarations (replace existing ones)

  const [loadedGraphs, setLoadedGraphs] = useState({});
  const [stableGraphData, setStableGraphData] = useState({});
  const [initialGraphsLoaded, setInitialGraphsLoaded] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [searchingByColumn, setSearchingByColumn] = useState(false);

  // Refs to prevent multiple loads
  const graphLoadingStarted = useRef(false);
  const graphLoadQueueRef = useRef([]);
  const isProcessingQueue = useRef(false);
  const lastLoadedFiltersRef = useRef(null);
  // Debug effect to monitor originalFormData
  // Helper function to get ULBs that match ALL threshold conditions
  // Add this new helper function to check if ANY operation has a threshold/filter
  const hasAnyFilterOperation = () => {
    return calculationResults.some(result =>
      result.operation === "lte" ||
      result.operation === "gte" ||
      result.operation === "min" ||
      result.operation === "max" ||
      result.operation === "percentage"
    );
  };

  // REPLACE the existing getUlbsMatchingAllConditions function with this enhanced version
  const getUlbsMatchingAllConditions = () => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return new Set();

    // Get ALL operations that can be used for filtering
    const filterOperations = calculationResults.filter(result =>
      result.operation === "lte" ||
      result.operation === "gte" ||
      result.operation === "min" ||
      result.operation === "max" ||
      result.operation === "percentage"
    );

    if (filterOperations.length === 0) return new Set();

    // For each ULB, check if it meets ALL operation conditions
    const ulbMatchCount = new Map();
    const totalConditions = filterOperations.length;

    // First, group data by ULB and calculate aggregates
    const ulbAggregates = new Map();

    rawDataForAnalysis.forEach((row) => {
      const ulbName = row.ulb_name || row.ulb;
      if (!ulbName) return;

      if (!ulbAggregates.has(ulbName)) {
        ulbAggregates.set(ulbName, {
          ulb: ulbName,
          district: row.district_name || row.district,
          division: row.division_name || row.division,
          values: {},
          counts: {}
        });
      }

      const ulbData = ulbAggregates.get(ulbName);

      // Collect values for each operation's column
      filterOperations.forEach(op => {
        const value = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
        if (!isNaN(value) && isFinite(value)) {
          if (!ulbData.values[op.fieldId]) {
            ulbData.values[op.fieldId] = 0;
            ulbData.counts[op.fieldId] = 0;
          }
          ulbData.values[op.fieldId] += value;
          ulbData.counts[op.fieldId]++;
        }
      });
    });

    // Now check each ULB against ALL operation conditions
    const matchingUlbs = new Set();

    for (const [ulbName, ulbData] of ulbAggregates.entries()) {
      let meetsAll = true;

      for (const op of filterOperations) {
        const totalValue = ulbData.values[op.fieldId] || 0;
        const count = ulbData.counts[op.fieldId] || 0;

        if (count === 0) {
          meetsAll = false;
          break;
        }

        let conditionValue = totalValue;

        // For percentage operation, use the percentage value
        if (op.operation === "percentage" && op.percentageSecondColumnName) {
          const secondTotal = ulbData.values[`${op.fieldId}_second`] || 0;
          if (secondTotal > 0) {
            conditionValue = (totalValue / secondTotal) * 100;
          } else {
            meetsAll = false;
            break;
          }
        }

        // Check the condition based on operation type
        if (op.operation === "lte") {
          const threshold = parseFloat(op.threshold);
          if (!(conditionValue <= threshold)) {
            meetsAll = false;
            break;
          }
        } else if (op.operation === "gte") {
          const threshold = parseFloat(op.threshold);
          if (!(conditionValue >= threshold)) {
            meetsAll = false;
            break;
          }
        } else if (op.operation === "min") {
          // For MIN operation, check if this ULB's total is the minimum
          let minValue = Infinity;
          for (const [otherUlb, otherData] of ulbAggregates.entries()) {
            const otherTotal = otherData.values[op.fieldId] || 0;
            if (otherTotal > 0 && otherTotal < minValue) {
              minValue = otherTotal;
            }
          }
          if (conditionValue !== minValue) {
            meetsAll = false;
            break;
          }
        } else if (op.operation === "max") {
          // For MAX operation, check if this ULB's total is the maximum
          let maxValue = -Infinity;
          for (const [otherUlb, otherData] of ulbAggregates.entries()) {
            const otherTotal = otherData.values[op.fieldId] || 0;
            if (otherTotal > maxValue) {
              maxValue = otherTotal;
            }
          }
          if (conditionValue !== maxValue) {
            meetsAll = false;
            break;
          }
        }
      }

      if (meetsAll) {
        matchingUlbs.add(ulbName);
      }
    }

    return matchingUlbs;
  };

  // REPLACE the existing renderMatchingUlbsList function with this enhanced version
  // REPLACE the existing renderMatchingUlbsList function with this enhanced version
  const renderMatchingUlbsList = () => {
    if (!showOnlyMatchingUlbs) return null;

    // Get ALL filter operations
    const filterOperations = calculationResults.filter(result =>
      result.operation === "lte" ||
      result.operation === "gte" ||
      result.operation === "min" ||
      result.operation === "max" ||
      result.operation === "percentage" ||
      result.operation === "ranking"
    );

    if (filterOperations.length === 0) return null;

    // Group data by ULB and calculate aggregates
    const ulbDataMap = new Map();

    rawDataForAnalysis.forEach(row => {
      const ulbName = row.ulb_name || row.ulb;
      if (!ulbName) return;

      if (!ulbDataMap.has(ulbName)) {
        ulbDataMap.set(ulbName, {
          ulb: ulbName,
          district: row.district_name || row.district || "N/A",
          division: row.division_name || row.division || "N/A",
          values: {},
          percentages: {},
          counts: {}
        });
      }

      const ulbData = ulbDataMap.get(ulbName);

      // Collect values for each operation's column
      filterOperations.forEach(op => {
        if (op.operation !== "percentage") {
          const value = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
          if (!isNaN(value) && isFinite(value)) {
            if (!ulbData.values[op.fieldId]) {
              ulbData.values[op.fieldId] = 0;
              ulbData.counts[op.fieldId] = 0;
            }
            ulbData.values[op.fieldId] += value;
            ulbData.counts[op.fieldId]++;
          }
        }
      });

      // Collect values for percentage operations
      filterOperations.forEach(op => {
        if (op.operation === "percentage" && op.percentageSecondColumnName) {
          const numerator = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
          const denominator = parseFloat(String(row[op.percentageSecondColumnName] || "").replace(/,/g, ""));

          if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
            if (!ulbData.percentages[op.fieldId]) {
              ulbData.percentages[op.fieldId] = { numerator: 0, denominator: 0 };
            }
            ulbData.percentages[op.fieldId].numerator += numerator;
            ulbData.percentages[op.fieldId].denominator += denominator;
          }
        }
      });
    });

    // Find percentage and ranking operations
    const percentageOp = filterOperations.find(op => op.operation === "percentage");
    const rankingOp = filterOperations.find(op => op.operation === "ranking");

    // Build ULB data with percentage (if exists)
    let ulbList = [];

    for (const [ulbName, ulbData] of ulbDataMap.entries()) {
      const ulbItem = {
        ulb: ulbName,
        district: ulbData.district,
        division: ulbData.division,
        values: ulbData.values,
        percentages: ulbData.percentages,
        counts: ulbData.counts
      };

      // Add percentage if percentage operation exists
      if (percentageOp && ulbData.percentages[percentageOp.fieldId]) {
        const percentData = ulbData.percentages[percentageOp.fieldId];
        if (percentData.denominator > 0) {
          ulbItem.percentage = (percentData.numerator / percentData.denominator) * 100;
          ulbItem.numerator = percentData.numerator;
          ulbItem.denominator = percentData.denominator;
        }
      }

      ulbList.push(ulbItem);
    }

    // Apply ranking filter based on ranking field (different from percentage)
    if (rankingOp) {
      // Sort by the ranking field value
      ulbList.sort((a, b) => {
        const valueA = a.values[rankingOp.fieldId] || 0;
        const valueB = b.values[rankingOp.fieldId] || 0;
        if (rankingOp.rankingType === "top") {
          return valueB - valueA;
        } else {
          return valueA - valueB;
        }
      });

      const rankingCount = rankingOp.rankingCount || 10;
      ulbList = ulbList.slice(0, rankingCount);
    }

    // Apply any additional threshold filters
    const lteGteOps = filterOperations.filter(op => op.operation === "lte" || op.operation === "gte");

    if (lteGteOps.length > 0) {
      ulbList = ulbList.filter(ulbItem => {
        let meetsAll = true;

        for (const op of lteGteOps) {
          let value = ulbItem.values[op.fieldId] || 0;

          // If this threshold applies to the percentage field, use percentage value
          if (percentageOp && op.fieldName === percentageOp.fieldName && ulbItem.percentage !== undefined) {
            value = ulbItem.percentage;
          }

          const threshold = parseFloat(op.threshold);
          if (op.operation === "lte") {
            if (!(value <= threshold)) meetsAll = false;
          } else if (op.operation === "gte") {
            if (!(value >= threshold)) meetsAll = false;
          }

          if (!meetsAll) break;
        }

        return meetsAll;
      });
    }

    if (ulbList.length === 0) {
      return (
        <Card style={{ marginBottom: 24, borderRadius: "12px" }}>
          <Empty
            description="No ULBs match all conditions"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    // Build columns dynamically
    const columns = [
      {
        title: "Rank",
        key: "rank",
        width: 70,
        render: (_, __, index) => index + 1,
      },
      {
        title: "Division",
        dataIndex: "division",
        key: "division",
        sorter: (a, b) => a.division.localeCompare(b.division),
      },
      {
        title: "District",
        dataIndex: "district",
        key: "district",
        sorter: (a, b) => a.district.localeCompare(b.district),
      },
      {
        title: "ULB Name",
        dataIndex: "ulb",
        key: "ulb",
        fixed: "left",
        sorter: (a, b) => a.ulb.localeCompare(b.ulb),
      },


    ];

    // Add percentage column if percentage operation exists
    if (percentageOp) {
      columns.push({
        title: (
          <Tooltip title={`${percentageOp.fieldName} (Percentage)`}>
            <span>
              {percentageOp.fieldName}
              <span style={{ color: "#eb2f96", marginLeft: 4 }}>(%)</span>
            </span>
          </Tooltip>
        ),
        dataIndex: "percentage",
        key: "percentage",
        align: "right",
        sorter: (a, b) => (a.percentage || 0) - (b.percentage || 0),
        render: (value) => value !== undefined ? `${value.toFixed(2)}%` : "-",
      });

      columns.push({
        title: `${percentageOp.fieldName} (Numerator)`,
        dataIndex: "numerator",
        key: "numerator",
        align: "right",
        render: (value) => value?.toLocaleString() || "-",
      });

      columns.push({
        title: percentageOp.percentageSecondFieldName || "Denominator",
        dataIndex: "denominator",
        key: "denominator",
        align: "right",
        render: (value) => value?.toLocaleString() || "-",
      });
    }

    // Add ranking field column
    if (rankingOp) {
      columns.push({
        title: `${rankingOp.fieldName} (for Ranking)`,
        dataIndex: ["values", rankingOp.fieldId],
        key: "ranking_value",
        align: "right",
        sorter: (a, b) => (a.values[rankingOp.fieldId] || 0) - (b.values[rankingOp.fieldId] || 0),
        render: (value) => value?.toLocaleString() || "0",
      });
    }

    const rankingText = rankingOp?.rankingType === "top"
      ? `Top ${rankingOp?.rankingCount || 10}`
      : rankingOp?.rankingType === "bottom"
        ? `Bottom ${rankingOp?.rankingCount || 10}`
        : "Ranking";

    const percentageText = percentageOp?.fieldName || "Percentage";

    return (
      <Card

        style={{
          marginBottom: 24,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}

      >

        <Table
          dataSource={ulbList}
          columns={columns}
          rowKey="ulb"
          pagination={{
            pageSize: 500,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ULBs`,
          }}
          size="middle"
          scroll={{ x: 800 }}
        />
      </Card>
    );
  };

  // Helper function to get condition text for display
  const getOperationConditionText = (op) => {
    // Check if this threshold applies to a percentage operation
    const relatedPercentageOp = calculationResults.find(p =>
      p.operation === "percentage" && p.fieldName === op.fieldName
    );

    switch (op.operation) {
      case 'lte':
        if (relatedPercentageOp) {
          return `% ≤ ${parseFloat(op.threshold).toLocaleString()}%`;
        }
        return `≤ ${parseFloat(op.threshold).toLocaleString()}`;
      case 'gte':
        if (relatedPercentageOp) {
          return `% ≥ ${parseFloat(op.threshold).toLocaleString()}%`;
        }
        return `≥ ${parseFloat(op.threshold).toLocaleString()}`;
      case 'min': return `= Minimum`;
      case 'max': return `= Maximum`;
      case 'percentage': return op.percentageSecondFieldName ? `% of ${op.percentageSecondFieldName}` : '%';
      case 'ranking':
        if (op.rankingType === "top") return `Top ${op.rankingCount || 10}`;
        if (op.rankingType === "bottom") return `Bottom ${op.rankingCount || 10}`;
        return `Ranking`;
      default: return '';
    }
  };

  // Update the useEffect that auto-enables the toggle to work with ANY operation
  // Auto-enable Advanced Analysis toggle when multiple filter operations exist
  useEffect(() => {
    // Check if we have ANY filter operations (lte, gte, min, max, percentage, ranking)
    const filterOperations = calculationResults.filter(result =>
      result.operation === "lte" ||
      result.operation === "gte" ||
      result.operation === "min" ||
      result.operation === "max" ||
      result.operation === "percentage" ||
      result.operation === "ranking"
    );

    const hasMultipleFilterOps = filterOperations.length >= 2;

    // Automatically enable the toggle when multiple conditions exist
    if (hasMultipleFilterOps && !showOnlyMatchingUlbs) {
      console.log("🔍 Multiple filter operations detected - Auto-enabling Advanced Analysis");
      setShowOnlyMatchingUlbs(true);
    }

    // Disable the toggle when conditions are removed
    if (!hasMultipleFilterOps && showOnlyMatchingUlbs) {
      console.log("🔍 Less than 2 filter operations - Auto-disabling Advanced Analysis");
      setShowOnlyMatchingUlbs(false);
    }
  }, [calculationResults]);
  // Single function to load a graph
  const loadSingleGraph = async (form, field, filters, forceReload = false) => {
    const graphKey = `${form.id}_${field.field_id || field.column_name}`;

    // Skip if already loaded and not forcing reload
    if (!forceReload && loadedGraphs[graphKey]) {
      return stableGraphData[graphKey];
    }

    // Set loading state
    setLoadingFormGraphs(prev => ({ ...prev, [graphKey]: true }));

    try {
      const graphData = await fetchImportantFieldGraph(form.id, field, filters);

      if (graphData) {
        setStableGraphData(prev => ({ ...prev, [graphKey]: graphData }));
        setFormGraphData(prev => ({ ...prev, [graphKey]: graphData }));
        setLoadedGraphs(prev => ({ ...prev, [graphKey]: true }));
        return graphData;
      }
    } catch (error) {
      console.error(`Error loading graph for form ${form.id}:`, error);
    } finally {
      setLoadingFormGraphs(prev => ({ ...prev, [graphKey]: false }));
    }

    return null;
  };

  // Process queue of graphs to load
  const processGraphQueue = async () => {
    if (isProcessingQueue.current || graphLoadQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueue.current = true;

    while (graphLoadQueueRef.current.length > 0) {
      const { form, field, filters, resolve } = graphLoadQueueRef.current.shift();
      const result = await loadSingleGraph(form, field, filters);
      if (resolve) resolve(result);

      // Small delay between loads to prevent overwhelming
      await new Promise(r => setTimeout(r, 50));
    }

    isProcessingQueue.current = false;
  };

  // Queue a graph for loading
  const queueGraphLoad = (form, field, filters) => {
    return new Promise((resolve) => {
      graphLoadQueueRef.current.push({ form, field, filters, resolve });
      processGraphQueue();
    });
  };

  // Load all initial graphs (once)
  const loadInitialGraphs = async () => {
    if (graphLoadingStarted.current || filteredForms.length === 0) {
      return;
    }

    graphLoadingStarted.current = true;
    console.log("📊 Loading initial graphs...");

    const formsWithFields = filteredForms.filter(
      form => form.reportFields && form.reportFields.length > 0
    );

    const currentFilters = { ...frontPageFilters };
    lastLoadedFiltersRef.current = JSON.stringify(currentFilters);

    // Load graphs in sequence to avoid overwhelming
    for (const form of formsWithFields) {
      const field = form.reportFields[0];
      if (field) {
        await queueGraphLoad(form, field, currentFilters);
      }
    }

    setInitialGraphsLoaded(true);
    console.log("✅ All initial graphs loaded");
  };

  // Single effect for initial load
  useEffect(() => {
    if (!initialGraphsLoaded && filteredForms.length > 0 && !graphLoadingStarted.current) {
      loadInitialGraphs();
    }
  }, [filteredForms]);

  // Single effect for filter changes with debouncing
  useEffect(() => {
    if (!initialGraphsLoaded) return;
    if (loading) return;

    const currentFiltersKey = JSON.stringify({
      division: frontPageFilters.division,
      district: frontPageFilters.district,
      ulb_type: frontPageFilters.ulb_type,
      ulb_class: frontPageFilters.ulb_class,
      ulb_name: frontPageFilters.ulb_name,
    });

    // Skip if filters haven't changed
    if (lastLoadedFiltersRef.current === currentFiltersKey) {
      return;
    }

    // Debounce filter changes
    const timer = setTimeout(async () => {
      console.log("🔄 Reloading graphs due to filter change...");

      lastLoadedFiltersRef.current = currentFiltersKey;

      const formsWithFields = filteredForms.filter(
        form => form.reportFields && form.reportFields.length > 0
      );

      const currentFilters = { ...frontPageFilters };

      // Reset loaded state for graphs that need reload
      const graphsToReload = [];
      for (const form of formsWithFields) {
        const field = form.reportFields[0];
        if (field) {
          const graphKey = `${form.id}_${field.field_id || field.column_name}`;
          graphsToReload.push(graphKey);
          setLoadedGraphs(prev => ({ ...prev, [graphKey]: false }));
        }
      }

      // Load graphs with new filters
      for (const form of formsWithFields) {
        const field = form.reportFields[0];
        if (field) {
          await queueGraphLoad(form, field, currentFilters);
        }
      }

      console.log("✅ Graphs reloaded with new filters");
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
    initialGraphsLoaded,
    loading,
    filteredForms
  ]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      graphLoadQueueRef.current = [];
      isProcessingQueue.current = false;
    };
  }, []);


  const [quickFilter, setQuickFilter] = useState({
    field: null,
    value: null,
    filteredData: [],
    isActive: false,
    fieldDisplayName: null
  });

  // Dashboard config for parts visibility (add after quickFilter)
const [dashboardConfig, setDashboardConfig] = useState({
  showFrontPageFilters: true,
  showNormalCardView: true,
  showCompactCardView: true,
  showDataPointView: true,
  showReportCards: true,
  showAnalysisSection: true,
  showJointGraph: true,
  showDivisionWiseAnalysis: true,
  showRankingTable: true,
  showKeyMetricsSummary: true,
  showRowDataTable: true
});

  // Add near your other state declarations (around line 100-150)
  const [useFiltersInForm, setUseFiltersInForm] = useState(true); // Controls whether filters apply to form view
  const [formViewFilters, setFormViewFilters] = useState({}); // Separate filters for form view

  // New state for report-enabled fields
  const [reportCalculations, setReportCalculations] = useState({});
  const [reportEnabledFields, setReportEnabledFields] = useState([]);
  const [loadingReportFields, setLoadingReportFields] = useState(false);

  // Add this function near your other calculation functions
  const recalculateWithQuickFilter = useCallback((filteredDataToUse) => {
    if (!filteredDataToUse || filteredDataToUse.length === 0) {
      // If no filtered data, use original data
      if (originalFormData.length > 0) {
        const calculations = processReportCalculations(originalFormData, reportEnabledFields);
        setReportCalculations(calculations);
      }
      return;
    }

    // Process calculations using the filtered data
    const calculations = {};

    reportEnabledFields.forEach((field) => {
      const actualColumnName = field.column_name || field.field_id;
      const operator = field.operator || "sum";

      const values = filteredDataToUse
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      if (values.length === 0) {
        calculations[field.field_id] = {
          value: 0,
          operator,
          count: 0,
          display_name: field.display_name || field.field_id,
        };
        return;
      }

      let result = 0;
      switch (operator.toLowerCase()) {
        case "sum":
          result = values.reduce((a, b) => a + b, 0);
          break;
        case "avg":
        case "average":
          result = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case "min":
          result = Math.min(...values);
          break;
        case "max":
          result = Math.max(...values);
          break;
        case "count":
          result = values.length;
          break;
        default:
          result = values.reduce((a, b) => a + b, 0);
      }

      calculations[field.field_id] = {
        value: result,
        operator,
        count: values.length,
        display_name: field.display_name || field.field_id,
      };
    });

    setReportCalculations(calculations);

    // Show notification about the filter
    if (quickFilter.isActive) {
      notification.info({
        message: "Quick Filter Applied",
        description: `Showing calculations for records where ${quickFilter.field} = ${quickFilter.value}`,
        duration: 3,
      });
    }
  }, [reportEnabledFields, originalFormData, quickFilter]);


  // State for field hierarchy
  const [fieldHierarchy, setFieldHierarchy] = useState({});
  const [fieldParentMap, setFieldParentMap] = useState({});

  // State for ULB type and class filters
  const [ulbTypes, setUlbTypes] = useState([]);
  const [ulbClasses, setUlbClasses] = useState([]);

  // Add this state to track if filters are being applied
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);

  // Add these near your other state declarations (around line 100-150)
  const [divisionWiseData, setDivisionWiseData] = useState([]);
  const [loadingDivisionData, setLoadingDivisionData] = useState(false);
  const [selectedDivisionForSummary, setSelectedDivisionForSummary] = useState(null);

  // FRONT PAGE FILTERS


  // Add this near your other state declarations
  const [cardSizeMode, setCardSizeMode] = useState(() => {
    // Load saved preference from sessionStorage, default to compact
    const saved = sessionStorage.getItem("card_size_mode");
    return saved === "normal" ? "normal" : "compact";
  });

  useEffect(() => {
    // Load saved card size preference
    const savedCardSize = sessionStorage.getItem("card_size_mode");
    if (savedCardSize === "compact") {
      setCardSizeMode("compact");
    }
  }, []);

  const [frontPageFilterOptions, setFrontPageFilterOptions] = useState({
    divisions: [],
    districts: [],
    ulbTypes: [],
    ulbClasses: [],
    ulbs: [],
  });
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const isVisitor = () => {
    if (!userRole) return false;

    const visitorRoles = [
      "VISITOR",
      "Visitor",
      "GUEST",
      "guest",
      "VIEWER",
      "viewer",
    ];
    return visitorRoles.includes(userRole);
  };

  // Filter states for selected form

  const handleFilterChange = (filterType, filterValue) => {
    setFilters((prev) => ({ ...prev, [filterType]: filterValue }));
  };

  const handleClearFilters = () => {
    setFilters({
      division: null,
      district: null,
      ulb_name: null,
      ulb_type: null,
      ulb_class: null,
    });
  };
  const [availableFilters, setAvailableFilters] = useState({
    districts: [],
    divisions: [],
    ulbs: [],
  });

  // Graph states
  const [selectedGraphColumn, setSelectedGraphColumn] = useState(null);
  const [columnGraphData, setColumnGraphData] = useState(null);
  const [loadingColumnGraph, setLoadingColumnGraph] = useState(false);
  const [activeGraphTab, setActiveGraphTab] = useState("auto-graphs");
  const [hierarchicalGraphs, setHierarchicalGraphs] = useState([]);
  const [showAggregated, setShowAggregated] = useState(true);

  // Graph card state
  const [fieldGraphsData, setFieldGraphsData] = useState({});
  const [loadingGraphs, setLoadingGraphs] = useState({});

  const [viewMode, setViewMode] = useState("single");
  // Pagination for data view
  const [dataPagination, setDataPagination] = useState({
    current: 1,
    pageSize: 500,
    total: 0,
  });

  // Data view modal
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [dataModalPagination, setDataModalPagination] = useState({
    current: 1,
    pageSize: 500,
    total: 0,
  });

  // Modal filter state
  const [modalFilters, setModalFilters] = useState({
    district: null,
    division: null,
    ulb: null,
    ulb_type: null,
    ulb_class: null,
    searchText: "",
  });
  // Add this ref to track if initial load has been done
  const initialLoadDone = useRef(false);

  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userDistrictId, setUserDistrictId] = useState(null);
  const [userDivisionId, setUserDivisionId] = useState(null);
  const [userUlbId, setUserUlbId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Cache for reference data
  const [referenceCache, setReferenceCache] = useState({
    districts: {},
    divisions: {},
    ulbs: {},
    populations: {},
  });

  // New state for multiple operations
  const [selectedFilterForm, setSelectedFilterForm] = useState(null);
  const [filterFormFields, setFilterFormFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState("sum");
  const [selectedOperations, setSelectedOperations] = useState([]);

  const initializationStarted = useRef(false);
  // Add this with your other state declarations
  const [operationThresholds, setOperationThresholds] = useState({});
  const [showThresholdInput, setShowThresholdInput] = useState(false);
  // Add near your other state declarations (around line 100-150):
  const [percentageSecondField, setPercentageSecondField] = useState(null);
  const [showPercentageSecondField, setShowPercentageSecondField] = useState(false);

  // For hierarchical drill-down navigation in division-wise analysis
  const [selectedDivisionForAnalysis, setSelectedDivisionForAnalysis] = useState(null);
  const [selectedDistrictForAnalysis, setSelectedDistrictForAnalysis] = useState(null);
  const [showDistrictView, setShowDistrictView] = useState(false);
  const [showUlbView, setShowUlbView] = useState(false);
  const [districtDataCache, setDistrictDataCache] = useState(new Map());
  const [ulbDataCache, setUlbDataCache] = useState(new Map());
  const [expandedDivisions, setExpandedDivisions] = useState(new Set());
  const [expandedDistricts, setExpandedDistricts] = useState(new Map());

  // Re-run ranking calculations when filters change while analysis is showing
  useEffect(() => {
    // Only run if we have selected operations AND analysis is showing
    if (selectedOperations.length === 0) return;
    if (!showAnalysisResults) return;

    // Check if we're inside a form or on front page
    const isInsideForm = selectedForm !== null;

    // Determine which filters to use
    const currentFilters = isInsideForm ? filters : frontPageFilters;

    // Check if any filter has changed
    const hasFilters = currentFilters.division ||
      currentFilters.district ||
      currentFilters.ulb_type ||
      currentFilters.ulb_class ||
      currentFilters.ulb_name;

    if (!hasFilters) return;

    // Debounce to avoid too many API calls
    const timer = setTimeout(() => {
      console.log("🔄 Re-running ranking calculations due to filter change");
      console.log("Current filters:", currentFilters);

      // Re-run calculations with current filters
      performCalculations();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    filters.division,
    filters.district,
    filters.ulb_type,
    filters.ulb_class,
    filters.ulb_name,
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
    selectedOperations.length,
    showAnalysisResults,
    selectedForm
  ]);

  // Add this after the AUTO-ANALYSIS useEffect (around line 2300-2400)

  // Dedicated useEffect for ranking operations when filters change
  useEffect(() => {
    // Only run if we have ranking operations AND analysis is showing
    const hasRankingOperation = selectedOperations.some(op => op.operation === "ranking");
    if (!hasRankingOperation) return;
    if (!showAnalysisResults) return;

    // Check if we're inside a form or on front page
    const isInsideForm = selectedForm !== null;

    // Determine which filters to use
    const currentFilters = isInsideForm ? filters : frontPageFilters;

    // Check if any filter has changed
    const hasFilters = currentFilters.division ||
      currentFilters.district ||
      currentFilters.ulb_type ||
      currentFilters.ulb_class ||
      currentFilters.ulb_name;

    if (!hasFilters) return;

    // Debounce to avoid too many API calls
    const timer = setTimeout(() => {
      console.log("🔄 Re-running ranking due to filter change");

      // Show loading indicator
      setLoadingCalculation(true);

      // Re-run calculations
      performCalculations();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    filters.division,
    filters.district,
    filters.ulb_type,
    filters.ulb_class,
    filters.ulb_name,
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
    showAnalysisResults,
    selectedForm
  ]);

  const operations = [
    { value: "sum", label: "Sum (Σ)", description: "Total" },
    { value: "avg", label: "Average (Avg)", description: "Average" },
    { value: "min", label: "Minimum", description: "Min value" },
    { value: "max", label: "Maximum", description: "Max value" },
    { value: "lte", label: "Lessthan", description: "Filter values ≤ threshold" },
    { value: "gte", label: "Greaterthan", description: "Filter values ≥ threshold" },
    { value: "percentage", label: "Percentage (%)", description: "Calculate percentage of second data point" },
    { value: "ranking", label: "Ranking", description: "Show top/bottom ULBs" }, // ADD THIS
  ];

  // SIDEBAR LOGIC - FIXED TO MATCH FIRST VERSION
  const BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= BREAKPOINT,
  );
  const [sbCollapsed, setSbCollapsed] = useState(() => {
    const saved = sessionStorage.getItem("sidebar_collapsed");
    return saved !== null ? JSON.parse(saved) : true; // Start collapsed by default
  });
  const LS_KEY = "dma_sb_collapsed";

  // Fixed base margin for content
  const baseMargin = isMobile ? 0 : 80;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= BREAKPOINT);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(LS_KEY, String(sbCollapsed));
    } catch { }
  }, [sbCollapsed]);

  // Add body class for sidebar expanded state
  // useEffect(() => {
  //   if (!sbCollapsed) {
  //     document.body.classList.add("sidebar-expanded");
  //   } else {
  //     document.body.classList.remove("sidebar-expanded");
  //   }

  //   return () => {
  //     document.body.classList.remove("sidebar-expanded");
  //   };
  // }, [sbCollapsed]);
  // Add this useEffect near your other useEffects (around line where you have other useEffects)

  // Add this useEffect after your frontPageFilters state declaration
  useEffect(() => {
    // Only auto-apply if there's at least one filter selected
    const hasAnyFilter = frontPageFilters.division ||
      frontPageFilters.district ||
      frontPageFilters.ulb_type ||
      frontPageFilters.ulb_class ||
      frontPageFilters.ulb_name;

    if (hasAnyFilter) {
      // Debounce to avoid too many API calls when user is typing/selecting
      const debounceTimer = setTimeout(() => {
        console.log("🔄 Auto-applying filters:", frontPageFilters);
        applyFrontPageFilters();
      }, 500); // 500ms delay - adjust as needed

      return () => clearTimeout(debounceTimer);
    } else {
      // If all filters are cleared, reset to show all data
      const debounceTimer = setTimeout(() => {
        console.log("🔄 All filters cleared, loading all data");
        loadForms({});
        setIsFilterApplied(false);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [frontPageFilters.division, frontPageFilters.district, frontPageFilters.ulb_type, frontPageFilters.ulb_class, frontPageFilters.ulb_name]);


  useEffect(() => {
    if (rawDataForAnalysis.length > 0 && calculationResults.length > 0) {
      const data = calculateHierarchicalBreakdown();
      setBreakdownData(data);
      if (!selectedBreakdownField && calculationResults.length > 0) {
        setSelectedBreakdownField(calculationResults[0].fieldId);
      }
    }
  }, [rawDataForAnalysis, calculationResults]);

  // Add these state variables near your other state declarations

  // Replace the graph loading useEffect with this version

  // Add this effect to handle filter changes separately (only reload if filters actually changed)
  const lastFiltersRef = useRef(null);

  useEffect(() => {
    // Only reload graphs if filters actually changed AND initial graphs are loaded
    if (!initialGraphsLoaded) return;

    const currentFiltersKey = JSON.stringify({
      division: frontPageFilters.division,
      district: frontPageFilters.district,
      ulb_type: frontPageFilters.ulb_type,
      ulb_class: frontPageFilters.ulb_class,
      ulb_name: frontPageFilters.ulb_name,
    });

    const lastFiltersKey = lastFiltersRef.current;

    // Check if filters actually changed
    if (lastFiltersKey !== currentFiltersKey) {
      lastFiltersRef.current = currentFiltersKey;

      // Reset loaded state to allow reloading with new filters
      setInitialGraphsLoaded(false);
      setLoadedGraphs({});
      setStableGraphData({});

      // Reload graphs with new filters after a small delay
      const timer = setTimeout(() => {
        const reloadGraphs = async () => {
          console.log("📊 Reloading graphs due to filter change");
          // Manually trigger reload
          if (filteredForms.length > 0) {
            const formsWithImportantFields = filteredForms.filter(
              (form) => form.reportFields && form.reportFields.length > 0,
            );

            for (let i = 0; i < formsWithImportantFields.length; i++) {
              const form = formsWithImportantFields[i];
              const importantField = form.reportFields[0];
              if (importantField) {
                const formId = form.id;
                const graphKey = `${formId}_${importantField.field_id || importantField.column_name}`;

                const currentFilters = { ...frontPageFilters };
                console.log(`📊 Reloading graph for form ${formId} with new filters`);

                setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: true }));

                try {
                  const graphData = await fetchImportantFieldGraph(
                    formId,
                    importantField,
                    currentFilters,
                  );

                  if (graphData) {
                    setStableGraphData((prev) => ({ ...prev, [graphKey]: graphData }));
                    setFormGraphData((prev) => ({ ...prev, [graphKey]: graphData }));
                    setLoadedGraphs((prev) => ({ ...prev, [graphKey]: true }));
                  }
                } catch (error) {
                  console.error(`Error reloading graph for form ${formId}:`, error);
                } finally {
                  setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: false }));
                }
              }
            }
            setInitialGraphsLoaded(true);
          }
        };
        reloadGraphs();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [frontPageFilters.division, frontPageFilters.district, frontPageFilters.ulb_type, frontPageFilters.ulb_class, frontPageFilters.ulb_name, initialGraphsLoaded, filteredForms]);
  const [allDataPoints, setAllDataPoints] = useState([]);
  const [loadingAllDataPoints, setLoadingAllDataPoints] = useState(false);
  // Add this useEffect near your other useEffects
  useEffect(() => {
    if (allDataPoints.length > 0) {
      if (selectedSubjectForm) {
        const filtered = allDataPoints.filter(
          dp => String(dp.formId) === String(selectedSubjectForm.id)
        );
        setFilteredDataPoints(filtered);
      } else {
        setFilteredDataPoints(allDataPoints);
      }
    }
  }, [allDataPoints, selectedSubjectForm]);
  // Function to get user's location names from IDs
  // Function to get user's location names from IDs
  // ==================== USER ROLE FUNCTIONS ====================
  // 1. Update the getCurrentUser function to store user data in state properly
  // Add this state to track if user data is loaded
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  useEffect(() => {
    // Skip auto-apply on initial load
    if (!userDataLoaded) return;

    // Check if any filter has a value
    const hasActiveFilters = frontPageFilters.division ||
      frontPageFilters.district ||
      frontPageFilters.ulb_type ||
      frontPageFilters.ulb_class ||
      frontPageFilters.ulb_name;

    // Debounce to prevent too many API calls
    const timer = setTimeout(() => {
      if (hasActiveFilters) {
        console.log("🔄 Auto-applying filters:", {
          division: frontPageFilters.division,
          district: frontPageFilters.district,
          ulb_type: frontPageFilters.ulb_type,
          ulb_class: frontPageFilters.ulb_class,
          ulb_name: frontPageFilters.ulb_name
        });

        // Apply the filters
        applyFrontPageFilters();
      } else {
        // If all filters are cleared, reload all data
        console.log("🔄 All filters cleared, loading all data");
        loadForms({});
        setIsFilterApplied(false);
      }
    }, 500); // 500ms debounce delay - adjust as needed for better UX

    return () => clearTimeout(timer);
  }, [
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
    userDataLoaded // Only run after user data is loaded
  ]);
  // Add this useEffect after your existing useEffects (around line where you have other useEffects)

  // AUTO-ANALYSIS: Automatically perform calculations when filters change
  useEffect(() => {
    // Only run if we have selected operations and a selected form
    if (selectedOperations.length === 0) return;
    if (!selectedForm) return;

    // Check if we're inside a form (not on front page)
    const isInsideForm = selectedForm !== null;
    if (!isInsideForm) return;

    // Debounce to avoid too many API calls
    const timer = setTimeout(() => {
      console.log("🔄 Auto-analysis triggered due to filter change");
      console.log("Current filters:", {
        division: filters.division,
        district: filters.district,
        ulb_type: filters.ulb_type,
        ulb_class: filters.ulb_class,
        ulb_name: filters.ulb_name
      });

      // Set the selected filter form to the current form
      setSelectedFilterForm(selectedForm);

      // Perform calculations automatically
      performCalculations();
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [
    filters.division,
    filters.district,
    filters.ulb_type,
    filters.ulb_class,
    filters.ulb_name,
    selectedForm,
    selectedOperations.length // Re-run when operations change
  ]);
  // Add this function in the main component (after other helper functions like formatValue)
  const calculateHierarchicalBreakdown = () => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return null;
    if (!calculationResults || calculationResults.length === 0) return null;

    // Get available fields from calculationResults
    const availableFieldsForBreakdown = calculationResults.map((result) => ({
      fieldId: result.fieldId,
      fieldName: result.fieldName,
      columnName: result.columnName,
      operation: result.operation,
      result: result.result,
      details: result.details,
    }));

    const breakdown = {
      division: {},
      district: {},
      ulb: {}
    };

    // For each selected operation/field
    availableFieldsForBreakdown.forEach((field) => {
      // Division level breakdown
      const divisionMap = new Map();
      // District level breakdown
      const districtMap = new Map();
      // ULB level breakdown
      const ulbMap = new Map();

      rawDataForAnalysis.forEach((row) => {
        const divisionName = row.division_name || row.division || "Unknown Division";
        const districtName = row.district_name || row.district || "Unknown District";
        const ulbName = row.ulb_name || row.ulb || "Unknown ULB";

        const value = parseFloat(
          String(row[field.columnName] || "").replace(/,/g, "")
        );

        if (!isNaN(value) && isFinite(value)) {
          // Division level
          if (!divisionMap.has(divisionName)) {
            divisionMap.set(divisionName, { sum: 0, count: 0, values: [] });
          }
          const divisionData = divisionMap.get(divisionName);
          divisionData.sum += value;
          divisionData.count++;
          divisionData.values.push(value);

          // District level
          const districtKey = `${divisionName}|${districtName}`;
          if (!districtMap.has(districtKey)) {
            districtMap.set(districtKey, {
              division: divisionName,
              district: districtName,
              sum: 0,
              count: 0,
              values: []
            });
          }
          const districtData = districtMap.get(districtKey);
          districtData.sum += value;
          districtData.count++;
          districtData.values.push(value);

          // ULB level
          const ulbKey = `${divisionName}|${districtName}|${ulbName}`;
          if (!ulbMap.has(ulbKey)) {
            ulbMap.set(ulbKey, {
              division: divisionName,
              district: districtName,
              ulb: ulbName,
              sum: 0,
              count: 0,
              values: []
            });
          }
          const ulbData = ulbMap.get(ulbKey);
          ulbData.sum += value;
          ulbData.count++;
          ulbData.values.push(value);
        }
      });

      // Process division data
      const divisionData = [];
      divisionMap.forEach((data, name) => {
        divisionData.push({
          name: name,
          total: data.sum,
          average: data.sum / data.count,
          count: data.count,
          min: Math.min(...data.values),
          max: Math.max(...data.values)
        });
      });
      breakdown.division[field.fieldId] = divisionData.sort((a, b) => a.name.localeCompare(b.name));

      // Process district data
      const districtData = [];
      districtMap.forEach((data) => {
        districtData.push({
          division: data.division,
          district: data.district,
          total: data.sum,
          average: data.sum / data.count,
          count: data.count,
          min: Math.min(...data.values),
          max: Math.max(...data.values)
        });
      });
      breakdown.district[field.fieldId] = districtData.sort((a, b) => {
        if (a.division !== b.division) return a.division.localeCompare(b.division);
        return a.district.localeCompare(b.district);
      });

      // Process ULB data
      const ulbData = [];
      ulbMap.forEach((data) => {
        ulbData.push({
          division: data.division,
          district: data.district,
          ulb: data.ulb,
          total: data.sum,
          average: data.sum / data.count,
          count: data.count,
          min: Math.min(...data.values),
          max: Math.max(...data.values)
        });
      });
      breakdown.ulb[field.fieldId] = ulbData.sort((a, b) => {
        if (a.division !== b.division) return a.division.localeCompare(b.division);
        if (a.district !== b.district) return a.district.localeCompare(b.district);
        return a.ulb.localeCompare(b.ulb);
      });
    });

    return breakdown;
  };
  // Add this function in the main component (after calculateHierarchicalBreakdown function)
  const calculateDivisionWiseData = () => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];
    if (!calculationResults || calculationResults.length === 0) return [];

    const divisionMap = new Map();
    const availableFields = calculationResults;

    // Process each record
    rawDataForAnalysis.forEach((row) => {
      const divisionName = row.division_name || row.division || "Unknown Division";

      if (!divisionMap.has(divisionName)) {
        divisionMap.set(divisionName, {
          division: divisionName,
          counts: {},
          totals: {},
          records: []
        });
      }

      const divisionData = divisionMap.get(divisionName);
      divisionData.records.push(row);

      // Calculate values for each field - ALWAYS USE SUM for division cards
      availableFields.forEach((field) => {
        const value = parseFloat(
          String(row[field.columnName] || "").replace(/,/g, "")
        );

        if (!isNaN(value) && isFinite(value)) {
          if (!divisionData.totals[field.fieldId]) {
            divisionData.totals[field.fieldId] = 0;
            divisionData.counts[field.fieldId] = 0;
          }
          divisionData.totals[field.fieldId] += value;
          divisionData.counts[field.fieldId]++;
        }
      });
    });

    // Build the division data array - ALWAYS SHOW SUM (total) for division cards
    const divisionDataArray = [];
    divisionMap.forEach((data, divisionName) => {
      const divisionEntry = {
        division: divisionName,
        metrics: {}
      };

      availableFields.forEach((field) => {
        const total = data.totals[field.fieldId] || 0;
        const count = data.counts[field.fieldId] || 0;

        // ALWAYS show SUM (total) for division cards
        // This allows users to see which division has smallest/largest total
        divisionEntry.metrics[field.fieldId] = {
          value: total,  // Show total sum, not min/max
          fieldName: field.fieldName,
          operation: "sum",  // Override to show as SUM
          count: count
        };
      });

      divisionDataArray.push(divisionEntry);
    });

    return divisionDataArray.sort((a, b) => a.division.localeCompare(b.division));
  };

  const calculateDistrictWiseData = (divisionName) => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];
    if (!calculationResults || calculationResults.length === 0) return [];

    // Filter data for the selected division
    const divisionData = rawDataForAnalysis.filter(row =>
      (row.division_name || row.division) === divisionName
    );

    if (divisionData.length === 0) return [];

    const districtMap = new Map();
    const availableFields = calculationResults;

    // Process each record - calculate TOTAL (sum) per district
    divisionData.forEach((row) => {
      const districtName = row.district_name || row.district || "Unknown District";

      if (!districtMap.has(districtName)) {
        districtMap.set(districtName, {
          district: districtName,
          division: divisionName,
          totals: {},      // Store sum totals per field
          counts: {},      // Store record counts
          records: []
        });
      }

      const districtData = districtMap.get(districtName);
      districtData.records.push(row);

      // Calculate TOTAL (sum) for each field
      availableFields.forEach((field) => {
        const value = parseFloat(
          String(row[field.columnName] || "").replace(/,/g, "")
        );

        if (!isNaN(value) && isFinite(value)) {
          if (!districtData.totals[field.fieldId]) {
            districtData.totals[field.fieldId] = 0;
            districtData.counts[field.fieldId] = 0;
          }
          districtData.totals[field.fieldId] += value;
          districtData.counts[field.fieldId]++;
        }
      });
    });

    // Build the district data array - For MIN/MAX operations, we show the TOTAL sum
    const districtDataArray = [];
    districtMap.forEach((data, districtName) => {
      const districtEntry = {
        district: districtName,
        division: data.division,
        metrics: {}
      };

      availableFields.forEach((field) => {
        const total = data.totals[field.fieldId] || 0;
        const count = data.counts[field.fieldId] || 0;

        // For district level, we show the TOTAL (sum) for MIN/MAX operations
        // This allows finding which district has smallest/largest TOTAL
        districtEntry.metrics[field.fieldId] = {
          value: total,  // Show the total sum for this district
          fieldName: field.fieldName,
          operation: "total",  // Mark as total for display
          originalOperation: field.operation,  // Keep original operation for reference
          count: count
        };
      });

      districtDataArray.push(districtEntry);
    });

    // Sort districts alphabetically
    return districtDataArray.sort((a, b) => a.district.localeCompare(b.district));
  };

  // Calculate ULB-wise data for a specific district
  const calculateUlbWiseData = (divisionName, districtName) => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];
    if (!calculationResults || calculationResults.length === 0) return [];

    // Filter data for the selected division and district
    const districtData = rawDataForAnalysis.filter(row =>
      (row.division_name || row.division) === divisionName &&
      (row.district_name || row.district) === districtName
    );

    if (districtData.length === 0) return [];

    const ulbMap = new Map();
    const availableFields = calculationResults;

    // Process each record
    districtData.forEach((row) => {
      const ulbName = row.ulb_name || row.ulb || "Unknown ULB";

      if (!ulbMap.has(ulbName)) {
        ulbMap.set(ulbName, {
          ulb: ulbName,
          district: districtName,
          division: divisionName,
          counts: {},
          totals: {},
          mins: {},
          maxs: {},
          records: []
        });
      }

      const ulbData = ulbMap.get(ulbName);
      ulbData.records.push(row);

      // Calculate values for each field
      availableFields.forEach((field) => {
        const value = parseFloat(
          String(row[field.columnName] || "").replace(/,/g, "")
        );

        if (!isNaN(value) && isFinite(value)) {
          if (!ulbData.totals[field.fieldId]) {
            ulbData.totals[field.fieldId] = 0;
            ulbData.counts[field.fieldId] = 0;
            ulbData.mins[field.fieldId] = Infinity;
            ulbData.maxs[field.fieldId] = -Infinity;
          }
          ulbData.totals[field.fieldId] += value;
          ulbData.counts[field.fieldId]++;
          ulbData.mins[field.fieldId] = Math.min(ulbData.mins[field.fieldId], value);
          ulbData.maxs[field.fieldId] = Math.max(ulbData.maxs[field.fieldId], value);
        }
      });
    });

    // Build the ULB data array
    const ulbDataArray = [];
    ulbMap.forEach((data, ulbName) => {
      const ulbEntry = {
        ulb: ulbName,
        district: data.district,
        division: data.division,
        metrics: {}
      };

      availableFields.forEach((field) => {
        const total = data.totals[field.fieldId] || 0;
        const count = data.counts[field.fieldId] || 0;
        let value = 0;

        // Use the original operation for ULB level (MIN/MAX/SUM/AVG)
        switch (field.operation) {
          case "sum":
            value = total;
            break;
          case "avg":
            value = count > 0 ? total / count : 0;
            break;
          case "min":
            value = data.mins[field.fieldId] !== Infinity ? data.mins[field.fieldId] : 0;
            break;
          case "max":
            value = data.maxs[field.fieldId] !== -Infinity ? data.maxs[field.fieldId] : 0;
            break;
          case "count":
            value = data.records.length;
            break;
          default:
            value = total;
        }

        ulbEntry.metrics[field.fieldId] = {
          value: value,
          fieldName: field.fieldName,
          operation: field.operation,
          count: count
        };
      });

      ulbDataArray.push(ulbEntry);
    });

    return ulbDataArray.sort((a, b) => a.ulb.localeCompare(b.ulb));
  };
  // Add this useEffect near your other useEffects
  useEffect(() => {
    if (rawDataForAnalysis.length > 0 && calculationResults.length > 0) {
      const divisionData = calculateDivisionWiseData();
      setDivisionWiseData(divisionData);
      console.log("📊 Division-wise data calculated:", divisionData.length, "divisions");
    }
  }, [rawDataForAnalysis, calculationResults]);
  // Update the useEffect that calculates division data to also handle drill-down
  useEffect(() => {
    if (rawDataForAnalysis.length > 0 && calculationResults.length > 0) {
      // Calculate division-wise data
      const divisionData = calculateDivisionWiseData();
      setDivisionWiseData(divisionData);
      console.log("📊 Division-wise data calculated:", divisionData.length, "divisions");

      // If we have a selected division, calculate district-wise data
      if (selectedDivision) {
        const districtData = calculateDistrictWiseData(selectedDivision);
        setDistrictWiseData(districtData);
        console.log("📊 District-wise data calculated for", selectedDivision, ":", districtData.length, "districts");
      }

      // If we have a selected district, calculate ULB-wise data
      if (selectedDivision && selectedDistrict) {
        const ulbData = calculateUlbWiseData(selectedDivision, selectedDistrict);
        setUlbWiseData(ulbData);
        console.log("📊 ULB-wise data calculated for", selectedDistrict, ":", ulbData.length, "ULBs");
      }
    }
  }, [rawDataForAnalysis, calculationResults, selectedDivision, selectedDistrict]);

  // 1. Update the getCurrentUser function
  const getCurrentUser = () => {
    try {
      setLoadingUser(true);
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        console.log("📋 Loaded user data:", user);

        setCurrentUser(user);

        // Determine role
        const roleName =
          user.role_name || user.role || user.userRole || user.roleId;
        setUserRole(roleName);
        console.log("👤 User Role:", roleName);

        // Get division ID
        const divisionId = user.division_id || user.divisionId || user.division;
        setUserDivisionId(divisionId);
        console.log("📍 User Division ID:", divisionId);

        // Get district ID
        const districtId = user.district_id || user.districtId || user.district;
        setUserDistrictId(districtId);
        console.log("📍 User District ID:", districtId);

        // Get ULB ID
        const ulbId = user.ulb_id || user.ulbId || user.ulb;
        setUserUlbId(ulbId);
        console.log("📍 User ULB ID:", ulbId);

        setUserDataLoaded(true);
        return user;
      } else {
        console.warn("⚠️ No user found in sessionStorage");
        setUserDataLoaded(true);
      }
    } catch (error) {
      console.error("Error loading current user:", error);
      setUserDataLoaded(true);
    } finally {
      setLoadingUser(false);
    }
  };

  // 2. Function to get user's location names from reference data
  const getUserLocationNames = () => {
    const locationInfo = {
      divisionName: null,
      districtName: null,
      ulbName: null,
      hasDivision: false,
      hasDistrict: false,
      hasUlb: false,
    };

    // Get division name from ID
    if (
      userDivisionId &&
      referenceCache.divisions &&
      referenceCache.divisions[userDivisionId]
    ) {
      locationInfo.divisionName = referenceCache.divisions[userDivisionId].name;
      locationInfo.hasDivision = true;
      console.log("📍 Found division name:", locationInfo.divisionName);
    } else if (userDivisionId) {
      console.log(
        "⚠️ Division ID found but no division data in cache:",
        userDivisionId,
      );
    }

    // Get district name from ID
    if (
      userDistrictId &&
      referenceCache.districts &&
      referenceCache.districts[userDistrictId]
    ) {
      locationInfo.districtName = referenceCache.districts[userDistrictId].name;
      locationInfo.hasDistrict = true;
      console.log("📍 Found district name:", locationInfo.districtName);
    } else if (userDistrictId) {
      console.log(
        "⚠️ District ID found but no district data in cache:",
        userDistrictId,
      );
    }

    // Get ULB name from ID
    if (userUlbId && referenceCache.ulbs && referenceCache.ulbs[userUlbId]) {
      locationInfo.ulbName = referenceCache.ulbs[userUlbId].ulb_name;
      locationInfo.hasUlb = true;
      console.log("📍 Found ULB name:", locationInfo.ulbName);
    } else if (userUlbId) {
      console.log("⚠️ ULB ID found but no ULB data in cache:", userUlbId);
    }

    return locationInfo;
  };

  // 3. Updated setUserBasedFilters function
  // Add a ref to track if auto-filters have been applied
  const autoFiltersApplied = useRef(false);

  const setUserBasedFilters = () => {
    // Prevent multiple applications
    if (autoFiltersApplied.current) {
      console.log("✅ Auto-filters already applied, skipping duplicate");
      return;
    }

    // Wait for both user data and reference data to be loaded
    if (!userDataLoaded) {
      console.log("⏳ Waiting for user data to load...");
      return;
    }

    if (
      !referenceCache.divisions ||
      Object.keys(referenceCache.divisions).length === 0
    ) {
      console.log("⏳ Waiting for reference data to load...");
      return;
    }

    if (!currentUser) {
      console.log("⚠️ No current user found after data load");
      return;
    }

    // Get user role and location information
    const role = userRole;
    const locationNames = getUserLocationNames();

    console.log("🔧 Setting user-based filters for role:", role);
    console.log("📍 Location names found:", locationNames);
    console.log("👤 Current user:", currentUser);

    const newFilters = { ...frontPageFilters };
    let filterApplied = false;
    let appliedFiltersList = [];

    // ==================== APPLY FILTERS BASED ON USER ROLE ====================

    // Division Admin - apply division filter only
    if (
      role === "DIVISION_ADMIN" ||
      role === "division_admin" ||
      role === "DIVISIONAL_OFFICER" ||
      role === "divisional_officer" ||
      role === "Divisionalcommissioner"
    ) {
      if (locationNames.divisionName) {
        newFilters.division = locationNames.divisionName;
        filterApplied = true;
        appliedFiltersList.push(`Division: ${locationNames.divisionName}`);
        console.log("✅ Applied division filter:", locationNames.divisionName);
      } else if (userDivisionId && referenceCache.divisions[userDivisionId]) {
        // Try to get division name from cache if we have the ID but name wasn't found
        const divisionName = referenceCache.divisions[userDivisionId].name;
        if (divisionName) {
          newFilters.division = divisionName;
          filterApplied = true;
          appliedFiltersList.push(`Division: ${divisionName}`);
          console.log("✅ Applied division filter from cache:", divisionName);
        } else {
          console.warn(
            "⚠️ Division Admin but no division name found. Division ID:",
            userDivisionId,
          );
          console.log(
            "Available divisions in cache:",
            referenceCache.divisions,
          );
        }
      } else {
        console.warn("⚠️ Division Admin but no division information available");
      }
    }
    // District Admin - apply division and district filters
    else if (role === "DISTRICT_ADMIN" || role === "district_admin") {
      if (locationNames.divisionName) {
        newFilters.division = locationNames.divisionName;
        appliedFiltersList.push(`Division: ${locationNames.divisionName}`);
        filterApplied = true;
        console.log(
          "✅ Applied division filter for District Admin:",
          locationNames.divisionName,
        );
      } else if (userDivisionId && referenceCache.divisions[userDivisionId]) {
        const divisionName = referenceCache.divisions[userDivisionId].name;
        if (divisionName) {
          newFilters.division = divisionName;
          appliedFiltersList.push(`Division: ${divisionName}`);
          filterApplied = true;
          console.log(
            "✅ Applied division filter from cache for District Admin:",
            divisionName,
          );
        }
      }

      if (locationNames.districtName) {
        newFilters.district = locationNames.districtName;
        appliedFiltersList.push(`District: ${locationNames.districtName}`);
        filterApplied = true;
        console.log("✅ Applied district filter:", locationNames.districtName);
      } else if (userDistrictId && referenceCache.districts[userDistrictId]) {
        const districtName = referenceCache.districts[userDistrictId].name;
        if (districtName) {
          newFilters.district = districtName;
          appliedFiltersList.push(`District: ${districtName}`);
          filterApplied = true;
          console.log("✅ Applied district filter from cache:", districtName);
        }
      }
    }
    // ULB Admin - apply division, district, and ULB filters
    else if (
      role === "ULB_ADMIN" ||
      role === "ulb_admin" ||
      role === "ULB_OFFICER" ||
      role === "ULB_CHIEF_OFFICIER"
    ) {
      if (locationNames.divisionName) {
        newFilters.division = locationNames.divisionName;
        appliedFiltersList.push(`Division: ${locationNames.divisionName}`);
        filterApplied = true;
        console.log(
          "✅ Applied division filter for ULB Admin:",
          locationNames.divisionName,
        );
      } else if (userDivisionId && referenceCache.divisions[userDivisionId]) {
        const divisionName = referenceCache.divisions[userDivisionId].name;
        if (divisionName) {
          newFilters.division = divisionName;
          appliedFiltersList.push(`Division: ${divisionName}`);
          filterApplied = true;
          console.log(
            "✅ Applied division filter from cache for ULB Admin:",
            divisionName,
          );
        }
      }

      if (locationNames.districtName) {
        newFilters.district = locationNames.districtName;
        appliedFiltersList.push(`District: ${locationNames.districtName}`);
        filterApplied = true;
        console.log(
          "✅ Applied district filter for ULB Admin:",
          locationNames.districtName,
        );
      } else if (userDistrictId && referenceCache.districts[userDistrictId]) {
        const districtName = referenceCache.districts[userDistrictId].name;
        if (districtName) {
          newFilters.district = districtName;
          appliedFiltersList.push(`District: ${districtName}`);
          filterApplied = true;
          console.log(
            "✅ Applied district filter from cache for ULB Admin:",
            districtName,
          );
        }
      }

      if (locationNames.ulbName) {
        newFilters.ulb_name = locationNames.ulbName;
        appliedFiltersList.push(`ULB: ${locationNames.ulbName}`);
        filterApplied = true;
        console.log("✅ Applied ULB filter:", locationNames.ulbName);
      } else if (userUlbId && referenceCache.ulbs[userUlbId]) {
        const ulbName = referenceCache.ulbs[userUlbId].ulb_name;
        if (ulbName) {
          newFilters.ulb_name = ulbName;
          appliedFiltersList.push(`ULB: ${ulbName}`);
          filterApplied = true;
          console.log("✅ Applied ULB filter from cache:", ulbName);
        }
      }
    }
    // Full access (Super Admin, DMA, etc.) - no auto-filters
    else if (hasFullAccess()) {
      console.log("👑 Super Admin/Full Access - no auto-filters applied");
    }
    // Unknown role - no filters applied
    else {
      console.log("⚠️ Unknown user role:", role, "- no auto-filters applied");
    }

    // ==================== APPLY THE FILTERS IF ANY WERE APPLIED ====================

    if (filterApplied && appliedFiltersList.length > 0) {
      console.log("✅ Setting filters:", newFilters);
      console.log("✅ Applied filters list:", appliedFiltersList);

      // Update the front page filters state
      setFrontPageFilters(newFilters);

      // Mark that filters are applied
      setIsFilterApplied(true);

      // Mark that auto-filters have been applied to prevent duplicates
      autoFiltersApplied.current = true;

      // Show notification to the user


      // Apply the filters and load data (only once)
      // Pass the new filters directly to ensure they're used
      loadForms(newFilters);
    } else {
      console.log("ℹ️ No auto-filters applied for role:", role);

      // Still load forms without any filters
      loadForms(newFilters);
    }
  };

  // 4. Update loadAllReferenceData function
  const loadAllReferenceData = async () => {
    try {
      console.log("Loading all reference data...");

      // Load divisions
      try {
        const divisionsResponse = await axios.get(
          `${API_BASE_URL}/api/references/divisions`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          },
        );
        if (divisionsResponse.data.success) {
          const divisionsMap = {};
          divisionsResponse.data.data.forEach((div) => {
            divisionsMap[div.id] = div;
          });
          setReferenceCache((prev) => ({ ...prev, divisions: divisionsMap }));
          console.log("✅ Divisions loaded:", Object.keys(divisionsMap).length);
        }
      } catch (error) {
        console.error("Error loading divisions:", error);
      }

      // Load districts
      try {
        const districtsResponse = await axios.get(
          `${API_BASE_URL}/api/references/districts`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          },
        );
        if (districtsResponse.data.success) {
          const districtsMap = {};
          districtsResponse.data.data.forEach((dist) => {
            districtsMap[dist.id] = dist;
          });
          setReferenceCache((prev) => ({ ...prev, districts: districtsMap }));
          console.log("✅ Districts loaded:", Object.keys(districtsMap).length);
        }
      } catch (error) {
        console.error("Error loading districts:", error);
      }

      // Load ULBs
      try {
        const ulbsResponse = await axios.get(
          `${API_BASE_URL}/api/references/ulbs`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          },
        );
        if (ulbsResponse.data.success) {
          const ulbsMap = {};
          ulbsResponse.data.data.forEach((ulb) => {
            ulbsMap[ulb.id] = ulb;
          });
          setReferenceCache((prev) => ({ ...prev, ulbs: ulbsMap }));
          console.log("✅ ULBs loaded:", Object.keys(ulbsMap).length);
        }
      } catch (error) {
        console.error("Error loading ULBs:", error);
      }

      // After loading reference data, check if we can set filters
      console.log(
        "📊 Reference data loaded, attempting to set user-based filters...",
      );
      setUserBasedFilters();
    } catch (error) {
      console.error("Error loading reference data:", error);
    }
  };

  const loadDashboardConfig = async () => {
  try {
    const token = sessionStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard-config`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.data.success && response.data.dashboardConfig) {
      setDashboardConfig({
        showFrontPageFilters: response.data.dashboardConfig.showFrontPageFilters !== false,
        showNormalCardView: response.data.dashboardConfig.showNormalCardView !== false,
        showCompactCardView: response.data.dashboardConfig.showCompactCardView !== false,
        showDataPointView: response.data.dashboardConfig.showDataPointView !== false,
        showReportCards: response.data.dashboardConfig.showReportCards !== false,
        showAnalysisSection: response.data.dashboardConfig.showAnalysisSection !== false,
        showJointGraph: response.data.dashboardConfig.showJointGraph !== false,
        showDivisionWiseAnalysis: response.data.dashboardConfig.showDivisionWiseAnalysis !== false,
        showRankingTable: response.data.dashboardConfig.showRankingTable !== false,
        showKeyMetricsSummary: response.data.dashboardConfig.showKeyMetricsSummary !== false,
        showRowDataTable: response.data.dashboardConfig.showRowDataTable !== false,
         layout: response.data.dashboardConfig.layout || {}
      });
      console.log('✅ Dashboard config loaded');
    }
  } catch (error) {
    console.error('Error loading dashboard config:', error);
  }
};

  const loadDashboardBackgroundImages = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.dashboardConfig) {
        const images = response.data.dashboardConfig.heroImages || [];  // ✅ 'images' defined here
        setDashboardBgImages(images);
        console.log('🖼️ Loaded background images from DB:', images.length);
        console.log('📸 Images:', images);

        // जर DB मध्ये images असतील तर त्याचा पहिला image current मध्ये set करा
        if (images.length > 0) {
          setCurrentBgImageIndex(0);
        }
      } else {
        console.log('⚠️ No images found in database, using default');
        setDashboardBgImages([]);
      }
    } catch (error) {
      console.error('Error loading dashboard background images:', error);
      setDashboardBgImages([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      // Prevent multiple initializations
      if (initializationStarted.current) {
        console.log("⚠️ Initialization already started, skipping");
        return;
      }

      initializationStarted.current = true;

      console.log("🚀 Starting initialization...");

      getCurrentUser();
      const connected = await checkApiConnection();
      if (connected) {
        await loadAllReferenceData();
        await loadFrontPageFilterOptions();
        await loadForms();
        // 🔽 ही line येथे add करा 🔽
        await loadDashboardBackgroundImages();
        await loadDashboardConfig();
      }
      console.log("✅ Initialization complete");
    };
    init();
  }, []);

  // Add this near your other useEffects (around line 700)
  useEffect(() => {
    if (userDataLoaded && isVisitor()) {
      notification.info({
        message: "View Only Mode",
        description:
          "You are logged in as a Visitor. You can view all data but analysis features (Subject selection, calculations) are disabled.",
        duration: 6,
        placement: "topRight",
      });
    }
  }, [userDataLoaded, userRole]);
  // 6. Add useEffect to watch for when both user data and reference data are ready
  useEffect(() => {
    if (
      userDataLoaded &&
      referenceCache.divisions &&
      Object.keys(referenceCache.divisions).length > 0
    ) {
      console.log(
        "✅ Both user data and reference data are ready, applying filters...",
      );
      setUserBasedFilters();
    }
  }, [userDataLoaded, referenceCache.divisions]);

  const hasFullAccess = () => {
    if (!userRole) return false;

    const fullAccessRoles = [
      "SUPER_ADMIN",
      "super_admin",
      "ADMIN",
      "DMA_DESK_OFFICER",
      "DMA_USER",
      "dma_user",

    ];
    return fullAccessRoles.includes(userRole);
  };

  const filterDataByUserRole = (data) => {
    if (!data || !currentUser || data.length === 0) return data;
    if (hasFullAccess()) return data;
    return data;
  };

  const getUserLocationInfo = () => {
    if (userRole === "DISTRICT_ADMIN" || userRole === "district_admin") {
      return { type: "District", id: userDistrictId };
    } else if (
      userRole === "DIVISION_ADMIN" ||
      userRole === "division_admin" ||
      userRole === "DIVISIONAL_OFFICER" ||
      userRole === "divisional_officer"
    ) {
      return { type: "Division", id: userDivisionId };
    } else if (
      userRole === "ULB_ADMIN" ||
      userRole === "ulb_admin" ||
      userRole === "ULB_OFFICER" ||
      userRole === "ULB_CHIEF_OFFICIER"
    ) {
      return { type: "ULB", id: userUlbId };
    }
    return null;
  };

  // ==================== FRONT PAGE FILTER FUNCTIONS ====================

  // Function to update districts based on current filters
  const updateDistrictsBasedOnFilters = async () => {
    try {
      const districtsResponse = await axios.get(
        `${API_BASE_URL}/api/references/districts`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      let allDistricts = [];
      if (districtsResponse.data.success && districtsResponse.data.data) {
        allDistricts = districtsResponse.data.data;
      }

      let filteredDistricts = allDistricts.map((d) => d.name).sort();

      // If we have ULB data, filter districts based on Type and Class
      if (frontPageFilters.ulb_type || frontPageFilters.ulb_class) {
        const ulbsResponse = await axios.get(
          `${API_BASE_URL}/api/references/ulbs`,
          {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
          },
        );

        let ulbData = [];
        if (ulbsResponse.data.success && ulbsResponse.data.data) {
          ulbData = ulbsResponse.data.data;
        }

        // Filter ULBs by type and class first
        let relevantUlbs = [...ulbData];

        if (frontPageFilters.ulb_type) {
          relevantUlbs = relevantUlbs.filter((u) => {
            const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
            return ulbType === frontPageFilters.ulb_type;
          });
        }

        if (frontPageFilters.ulb_class) {
          relevantUlbs = relevantUlbs.filter((u) => {
            const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
            return ulbClass === frontPageFilters.ulb_class;
          });
        }

        // Get unique district IDs from filtered ULBs
        const districtIdsFromUlbs = [...new Set(
          relevantUlbs
            .map((u) => u.district_id)
            .filter(Boolean)
        )];

        // Filter districts based on these IDs
        if (districtIdsFromUlbs.length > 0) {
          filteredDistricts = allDistricts
            .filter((d) => districtIdsFromUlbs.includes(d.id))
            .map((d) => d.name)
            .sort();
        } else {
          filteredDistricts = [];
        }
      }

      // Apply division filter if it exists
      if (frontPageFilters.division && filteredDistricts.length > 0) {
        const divisionInfo = Object.values(referenceCache.divisions).find(
          (d) => d.name === frontPageFilters.division
        );
        if (divisionInfo) {
          filteredDistricts = filteredDistricts.filter((districtName) => {
            const district = allDistricts.find(d => d.name === districtName);
            return district && district.division_id === divisionInfo.id;
          });
        }
      }

      setFrontPageFilterOptions((prev) => ({
        ...prev,
        districts: filteredDistricts,
      }));

      console.log("📊 Updated districts:", {
        totalDistricts: filteredDistricts.length,
        sampleDistricts: filteredDistricts.slice(0, 5),
        filters: {
          type: frontPageFilters.ulb_type,
          class: frontPageFilters.ulb_class,
          division: frontPageFilters.division
        }
      });

      return filteredDistricts;
    } catch (error) {
      console.error("Error updating districts:", error);
      return [];
    }
  };

  // Update the loadFrontPageFilterOptions function
  const loadFrontPageFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      console.log("🔍 Loading front page filter options...");

      // Fetch divisions
      const divisionsResponse = await axios.get(
        `${API_BASE_URL}/api/references/divisions`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      // Fetch districts
      const districtsResponse = await axios.get(
        `${API_BASE_URL}/api/references/districts`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      // Fetch ULBs with their types and classes
      const ulbsResponse = await axios.get(
        `${API_BASE_URL}/api/references/ulbs`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      console.log("📡 API Responses:", {
        divisions: divisionsResponse.data,
        districts: districtsResponse.data,
        ulbs: ulbsResponse.data,
      });

      // Process divisions
      let divisions = [];
      if (divisionsResponse.data.success && divisionsResponse.data.data) {
        divisions = divisionsResponse.data.data
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
        console.log("✅ Divisions loaded:", divisions.length);
      } else if (Array.isArray(divisionsResponse.data)) {
        divisions = divisionsResponse.data
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
      } else if (divisionsResponse.data.divisions) {
        divisions = divisionsResponse.data.divisions
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
      }

      // Process districts
      let districts = [];
      if (districtsResponse.data.success && districtsResponse.data.data) {
        districts = districtsResponse.data.data
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
        console.log("✅ Districts loaded:", districts.length);
      } else if (Array.isArray(districtsResponse.data)) {
        districts = districtsResponse.data
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
      } else if (districtsResponse.data.districts) {
        districts = districtsResponse.data.districts
          .map((d) => d.name)
          .filter(Boolean)
          .sort();
      }

      // Process ULBs with their types and classes
      let ulbTypesList = [];
      let ulbClassesList = [];
      let ulbsList = [];

      let ulbData = [];
      if (ulbsResponse.data.success && ulbsResponse.data.data) {
        ulbData = ulbsResponse.data.data;
      } else if (Array.isArray(ulbsResponse.data)) {
        ulbData = ulbsResponse.data;
      } else if (ulbsResponse.data.ulbs) {
        ulbData = ulbsResponse.data.ulbs;
      }

      console.log("📊 ULB Data sample:", ulbData.slice(0, 3));

      if (ulbData.length > 0) {
        // Extract ULB Types - try different possible field names
        ulbTypesList = [
          ...new Set(
            ulbData
              .map((u) => {
                return u.ulb_type || u.type || u.ulbType || u.category;
              })
              .filter((t) => t && t !== null && t !== ""),
          ),
        ].sort();

        // Extract ULB Classes - try different possible field names
        ulbClassesList = [
          ...new Set(
            ulbData
              .map((u) => {
                return u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              })
              .filter((c) => c && c !== null && c !== ""),
          ),
        ].sort();

        // Extract ULB Names
        ulbsList = ulbData
          .map((u) => u.ulb_name || u.name || u.ulbName)
          .filter(Boolean)
          .sort();

        console.log("✅ ULB Types loaded:", ulbTypesList);
        console.log("✅ ULB Classes loaded:", ulbClassesList);
        console.log("✅ ULBs count:", ulbsList.length);
      }

      setFrontPageFilterOptions({
        divisions: divisions,
        districts: districts,
        ulbTypes: ulbTypesList,
        ulbClasses: ulbClassesList,
        ulbs: ulbsList,
      });

      console.log("🎯 Final filter options set:", {
        divisionsCount: divisions.length,
        districtsCount: districts.length,
        ulbTypesCount: ulbTypesList.length,
        ulbClassesCount: ulbClassesList.length,
        ulbsCount: ulbsList.length,
      });

      // If still empty, show warning
      if (
        divisions.length === 0 &&
        districts.length === 0 &&
        ulbTypesList.length === 0
      ) {
        console.warn("⚠️ No filter options loaded. Check API responses.");

      }
    } catch (error) {
      console.error("Error loading front page filters:", error);
      console.error("Error details:", error.response?.data || error.message);

      // Set empty arrays to avoid undefined errors
      setFrontPageFilterOptions({
        divisions: [],
        districts: [],
        ulbTypes: [],
        ulbClasses: [],
        ulbs: [],
      });
    } finally {
      setLoadingFilters(false);
    }
  };

  // Updated handleFrontPageDivisionChange
  const handleFrontPageDivisionChange = async (value) => {
    setFrontPageFilters((prev) => ({
      ...prev,
      division: value,
      district: null,
      ulb_name: null,
    }));

    if (value) {
      try {
        const divisionData = await axios.get(
          `${API_BASE_URL}/api/references/divisions`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          },
        );

        const divisionInfo = divisionData.data.data.find(
          (d) => d.name === value,
        );
        if (divisionInfo) {
          const districtsResponse = await axios.get(
            `${API_BASE_URL}/api/references/districts`,
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
              },
            },
          );

          let filteredDistricts = districtsResponse.data.data
            .filter((d) => d.division_id === divisionInfo.id)
            .map((d) => d.name)
            .sort();

          const ulbsResponse = await axios.get(
            `${API_BASE_URL}/api/references/ulbs`,
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
              },
            },
          );

          let ulbData = [];
          if (ulbsResponse.data.success && ulbsResponse.data.data) {
            ulbData = ulbsResponse.data.data;
          } else if (Array.isArray(ulbsResponse.data)) {
            ulbData = ulbsResponse.data;
          } else if (ulbsResponse.data.ulbs) {
            ulbData = ulbsResponse.data.ulbs;
          }

          // Filter ULBs by division
          let filteredUlbs = ulbData
            .filter((u) => u.division_id === divisionInfo.id)
            .map((u) => u.ulb_name || u.name || u.ulbName)
            .filter(Boolean)
            .sort();

          // Apply type filter if it exists
          if (frontPageFilters.ulb_type) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              const ulbType = ulb.ulb_type || ulb.type || ulb.ulbType || ulb.category;
              return ulbType === frontPageFilters.ulb_type;
            });

            // Also filter districts based on type
            filteredDistricts = filteredDistricts.filter((districtName) => {
              return ulbData.some((u) => {
                const ulbDistrict = u.district_name || u.district;
                const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
                return ulbDistrict === districtName &&
                  ulbType === frontPageFilters.ulb_type &&
                  u.division_id === divisionInfo.id;
              });
            });
          }

          // Apply class filter if it exists
          if (frontPageFilters.ulb_class) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              const ulbClass = ulb.class || ulb.ulb_class || ulb.ulbClass || ulb.ulbclass;
              return ulbClass === frontPageFilters.ulb_class;
            });

            // Also filter districts based on class
            filteredDistricts = filteredDistricts.filter((districtName) => {
              return ulbData.some((u) => {
                const ulbDistrict = u.district_name || u.district;
                const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
                return ulbDistrict === districtName &&
                  ulbClass === frontPageFilters.ulb_class &&
                  u.division_id === divisionInfo.id;
              });
            });
          }

          setFrontPageFilterOptions((prev) => ({
            ...prev,
            districts: filteredDistricts,
            ulbs: filteredUlbs,
          }));
        }
      } catch (error) {
        console.error("Error fetching districts for division:", error);
      }
    } else {
      // Reset to show all districts and ULBs with type/class filters applied
      await refreshUlbsList();
      await updateDistrictsBasedOnFilters();
    }

    setTimeout(() => {
      applyFrontPageFilters();
    }, 100);
  };

  // Updated handleFrontPageDistrictChange
  const handleFrontPageDistrictChange = async (value) => {
    setFrontPageFilters((prev) => ({
      ...prev,
      district: value,
      ulb_name: null,
    }));

    if (value) {
      try {
        const districtData = await axios.get(
          `${API_BASE_URL}/api/references/districts`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          },
        );

        const districtInfo = districtData.data.data.find(
          (d) => d.name === value,
        );
        if (districtInfo) {
          const ulbsResponse = await axios.get(
            `${API_BASE_URL}/api/references/ulbs`,
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
              },
            },
          );

          let ulbData = [];
          if (ulbsResponse.data.success && ulbsResponse.data.data) {
            ulbData = ulbsResponse.data.data;
          } else if (Array.isArray(ulbsResponse.data)) {
            ulbData = ulbsResponse.data;
          } else if (ulbsResponse.data.ulbs) {
            ulbData = ulbsResponse.data.ulbs;
          }

          // Filter ULBs by district
          let filteredUlbs = ulbData
            .filter((u) => u.district_id === districtInfo.id)
            .map((u) => u.ulb_name || u.name || u.ulbName)
            .filter(Boolean)
            .sort();

          // Apply type filter if it exists
          if (frontPageFilters.ulb_type) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              const ulbType = ulb.ulb_type || ulb.type || ulb.ulbType || ulb.category;
              return ulbType === frontPageFilters.ulb_type;
            });
          }

          // Apply class filter if it exists
          if (frontPageFilters.ulb_class) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              const ulbClass = ulb.class || ulb.ulb_class || ulb.ulbClass || ulb.ulbclass;
              return ulbClass === frontPageFilters.ulb_class;
            });
          }

          setFrontPageFilterOptions((prev) => ({
            ...prev,
            ulbs: filteredUlbs,
          }));
        }
      } catch (error) {
        console.error("Error fetching ULBs for district:", error);
      }
    } else if (frontPageFilters.division) {
      // If district is cleared but division exists, show ULBs for that division with type/class filters
      await handleFrontPageDivisionChange(frontPageFilters.division);
    } else {
      // Reset to show all ULBs with type/class filters applied
      await refreshUlbsList();
      await updateDistrictsBasedOnFilters();
    }

    setTimeout(() => {
      applyFrontPageFilters();
    }, 100);
  };

  // Updated handleFrontPageUlbTypeChange
  const handleFrontPageUlbTypeChange = async (value) => {
    setFrontPageFilters((prev) => ({
      ...prev,
      ulb_type: value,
      ulb_name: null,
      district: null, // Reset district when type changes
    }));

    try {
      const ulbsResponse = await axios.get(
        `${API_BASE_URL}/api/references/ulbs`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        },
      );

      let ulbData = [];
      if (ulbsResponse.data.success && ulbsResponse.data.data) {
        ulbData = ulbsResponse.data.data;
      } else if (Array.isArray(ulbsResponse.data)) {
        ulbData = ulbsResponse.data;
      } else if (ulbsResponse.data.ulbs) {
        ulbData = ulbsResponse.data.ulbs;
      }

      // Get all divisions and districts for filtering
      const divisionsResponse = await axios.get(
        `${API_BASE_URL}/api/references/divisions`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      const districtsResponse = await axios.get(
        `${API_BASE_URL}/api/references/districts`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      let divisions = [];
      if (divisionsResponse.data.success && divisionsResponse.data.data) {
        divisions = divisionsResponse.data.data;
      }

      let allDistricts = [];
      if (districtsResponse.data.success && districtsResponse.data.data) {
        allDistricts = districtsResponse.data.data;
      }

      if (value) {
        // Filter ULBs by selected type
        let filteredUlbs = ulbData
          .filter((u) => {
            const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
            return ulbType === value;
          })
          .map((u) => u.ulb_name || u.name || u.ulbName)
          .filter(Boolean)
          .sort();

        // Get unique division IDs from filtered ULBs
        const divisionIdsFromUlbs = [...new Set(
          ulbData
            .filter((u) => {
              const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
              return ulbType === value;
            })
            .map((u) => u.division_id)
            .filter(Boolean)
        )];

        // Filter divisions based on ULBs that have the selected type
        let filteredDivisions = divisions
          .filter((d) => divisionIdsFromUlbs.includes(d.id))
          .map((d) => d.name)
          .sort();

        // Get unique district IDs from filtered ULBs
        const districtIdsFromUlbs = [...new Set(
          ulbData
            .filter((u) => {
              const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
              return ulbType === value;
            })
            .map((u) => u.district_id)
            .filter(Boolean)
        )];

        // Filter districts based on ULBs that have the selected type
        let filteredDistricts = allDistricts
          .filter((d) => districtIdsFromUlbs.includes(d.id))
          .map((d) => d.name)
          .sort();

        // Get updated ULB Classes based on selected type
        let filteredClasses = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
                return ulbType === value;
              })
              .map((u) => {
                return u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              })
              .filter((c) => c && c !== null && c !== "")
          ),
        ].sort();

        // Apply division filter if it exists
        if (frontPageFilters.division) {
          const divisionInfo = divisions.find(
            (d) => d.name === frontPageFilters.division
          );
          if (divisionInfo) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              return ulb && ulb.division_id === divisionInfo.id;
            });

            filteredDistricts = filteredDistricts.filter((districtName) => {
              const district = allDistricts.find(
                (d) => d.name === districtName
              );
              return district && district.division_id === divisionInfo.id;
            });

            filteredClasses = [
              ...new Set(
                ulbData
                  .filter((u) => {
                    const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
                    const divisionMatch = u.division_id === divisionInfo.id;
                    return ulbType === value && divisionMatch;
                  })
                  .map((u) => {
                    return u.class || u.ulb_class || u.ulbClass || u.ulbclass;
                  })
                  .filter((c) => c && c !== null && c !== "")
              ),
            ].sort();
          }
        }

        // Apply district filter if it exists
        if (frontPageFilters.district) {
          const districtInfo = allDistricts.find(
            (d) => d.name === frontPageFilters.district
          );
          if (districtInfo) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              return ulb && ulb.district_id === districtInfo.id;
            });
          }
        }

        // Apply any existing class filter if present
        if (frontPageFilters.ulb_class && filteredClasses.length > 0) {
          filteredClasses = filteredClasses.filter(
            c => c === frontPageFilters.ulb_class
          );
          filteredUlbs = filteredUlbs.filter((ulbName) => {
            const ulb = ulbData.find(
              (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
            );
            const ulbClass = ulb.class || ulb.ulb_class || ulb.ulbClass || ulb.ulbclass;
            return ulbClass === frontPageFilters.ulb_class;
          });
        }

        setFrontPageFilterOptions((prev) => ({
          ...prev,
          divisions: filteredDivisions,
          districts: filteredDistricts,
          ulbs: filteredUlbs,
          ulbClasses: filteredClasses,
        }));

        console.log("📊 Updated filter options after ULB Type change:", {
          ulbType: value,
          divisionsCount: filteredDivisions.length,
          districtsCount: filteredDistricts.length,
          ulbsCount: filteredUlbs.length,
          ulbClassesCount: filteredClasses.length,
        });
      } else {
        // Reset to show all options when type is cleared
        await refreshUlbsList();
        await updateDistrictsBasedOnFilters();
      }
    } catch (error) {
      console.error("Error filtering by ULB type:", error);
    }

    setTimeout(() => {
      applyFrontPageFilters();
    }, 100);
  };

  // Updated handleFrontPageUlbClassChange
  const handleFrontPageUlbClassChange = async (value) => {
    setFrontPageFilters((prev) => ({
      ...prev,
      ulb_class: value,
      ulb_name: null,
      district: null, // Reset district when class changes
    }));

    try {
      const ulbsResponse = await axios.get(
        `${API_BASE_URL}/api/references/ulbs`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        },
      );

      let ulbData = [];
      if (ulbsResponse.data.success && ulbsResponse.data.data) {
        ulbData = ulbsResponse.data.data;
      } else if (Array.isArray(ulbsResponse.data)) {
        ulbData = ulbsResponse.data;
      } else if (ulbsResponse.data.ulbs) {
        ulbData = ulbsResponse.data.ulbs;
      }

      // Get all divisions and districts for filtering
      const divisionsResponse = await axios.get(
        `${API_BASE_URL}/api/references/divisions`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      const districtsResponse = await axios.get(
        `${API_BASE_URL}/api/references/districts`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      let divisions = [];
      if (divisionsResponse.data.success && divisionsResponse.data.data) {
        divisions = divisionsResponse.data.data;
      }

      let allDistricts = [];
      if (districtsResponse.data.success && districtsResponse.data.data) {
        allDistricts = districtsResponse.data.data;
      }

      if (value) {
        // Filter ULBs by selected class
        let filteredUlbs = ulbData
          .filter((u) => {
            const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
            return ulbClass === value;
          })
          .map((u) => u.ulb_name || u.name || u.ulbName)
          .filter(Boolean)
          .sort();

        // Get unique division IDs from filtered ULBs
        const divisionIdsFromUlbs = [...new Set(
          ulbData
            .filter((u) => {
              const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              return ulbClass === value;
            })
            .map((u) => u.division_id)
            .filter(Boolean)
        )];

        // Filter divisions based on ULBs that have the selected class
        let filteredDivisions = divisions
          .filter((d) => divisionIdsFromUlbs.includes(d.id))
          .map((d) => d.name)
          .sort();

        // Get unique district IDs from filtered ULBs
        const districtIdsFromUlbs = [...new Set(
          ulbData
            .filter((u) => {
              const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              return ulbClass === value;
            })
            .map((u) => u.district_id)
            .filter(Boolean)
        )];

        // Filter districts based on ULBs that have the selected class
        let filteredDistricts = allDistricts
          .filter((d) => districtIdsFromUlbs.includes(d.id))
          .map((d) => d.name)
          .sort();

        // Get updated ULB Types based on selected class
        let filteredTypes = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
                return ulbClass === value;
              })
              .map((u) => {
                return u.ulb_type || u.type || u.ulbType || u.category;
              })
              .filter((t) => t && t !== null && t !== "")
          ),
        ].sort();

        // Apply type filter if it exists
        if (frontPageFilters.ulb_type) {
          filteredUlbs = filteredUlbs.filter((ulbName) => {
            const ulb = ulbData.find(
              (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
            );
            const ulbType = ulb.ulb_type || ulb.type || ulb.ulbType || ulb.category;
            return ulbType === frontPageFilters.ulb_type;
          });

          filteredDivisions = filteredDivisions.filter((divisionName) => {
            const division = divisions.find((d) => d.name === divisionName);
            if (!division) return false;
            return ulbData.some((u) => {
              const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
              return u.division_id === division.id &&
                ulbClass === value &&
                ulbType === frontPageFilters.ulb_type;
            });
          });

          filteredDistricts = filteredDistricts.filter((districtName) => {
            const district = allDistricts.find((d) => d.name === districtName);
            if (!district) return false;
            return ulbData.some((u) => {
              const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
              return u.district_id === district.id &&
                ulbClass === value &&
                ulbType === frontPageFilters.ulb_type;
            });
          });
        }

        // Apply division filter if it exists
        if (frontPageFilters.division) {
          const divisionInfo = divisions.find(
            (d) => d.name === frontPageFilters.division
          );
          if (divisionInfo) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              return ulb && ulb.division_id === divisionInfo.id;
            });

            filteredDistricts = filteredDistricts.filter((districtName) => {
              const district = allDistricts.find(
                (d) => d.name === districtName
              );
              return district && district.division_id === divisionInfo.id;
            });
          }
        }

        // Apply district filter if it exists
        if (frontPageFilters.district) {
          const districtInfo = allDistricts.find(
            (d) => d.name === frontPageFilters.district
          );
          if (districtInfo) {
            filteredUlbs = filteredUlbs.filter((ulbName) => {
              const ulb = ulbData.find(
                (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
              );
              return ulb && ulb.district_id === districtInfo.id;
            });
          }
        }

        setFrontPageFilterOptions((prev) => ({
          ...prev,
          divisions: filteredDivisions,
          districts: filteredDistricts,
          ulbs: filteredUlbs,
          ulbTypes: filteredTypes,
        }));

        console.log("📊 Updated filter options after ULB Class change:", {
          ulbClass: value,
          divisionsCount: filteredDivisions.length,
          districtsCount: filteredDistricts.length,
          ulbsCount: filteredUlbs.length,
          ulbTypesCount: filteredTypes.length,
        });
      } else {
        // Reset ULBs list when class is cleared, but preserve other filters
        if (frontPageFilters.ulb_type) {
          await handleFrontPageUlbTypeChange(frontPageFilters.ulb_type);
        } else if (frontPageFilters.division) {
          await handleFrontPageDivisionChange(frontPageFilters.division);
        } else if (frontPageFilters.district) {
          await handleFrontPageDistrictChange(frontPageFilters.district);
        } else {
          await loadFrontPageFilterOptions();
        }
        await updateDistrictsBasedOnFilters();
      }
    } catch (error) {
      console.error("Error filtering ULBs by class:", error);
    }

    setTimeout(() => {
      applyFrontPageFilters();
    }, 100);
  };

  // Updated refreshUlbsList function
  const refreshUlbsList = async () => {
    try {
      const ulbsResponse = await axios.get(
        `${API_BASE_URL}/api/references/ulbs`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        },
      );

      let ulbData = [];
      if (ulbsResponse.data.success && ulbsResponse.data.data) {
        ulbData = ulbsResponse.data.data;
      } else if (Array.isArray(ulbsResponse.data)) {
        ulbData = ulbsResponse.data;
      } else if (ulbsResponse.data.ulbs) {
        ulbData = ulbsResponse.data.ulbs;
      }

      // Get all districts for filtering
      const districtsResponse = await axios.get(
        `${API_BASE_URL}/api/references/districts`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      let allDistricts = [];
      if (districtsResponse.data.success && districtsResponse.data.data) {
        allDistricts = districtsResponse.data.data;
      }

      let filteredUlbs = ulbData
        .map((u) => u.ulb_name || u.name || u.ulbName)
        .filter(Boolean)
        .sort();

      let filteredClasses = [
        ...new Set(
          ulbData
            .map((u) => {
              return u.class || u.ulb_class || u.ulbClass || u.ulbclass;
            })
            .filter((c) => c && c !== null && c !== "")
        ),
      ].sort();

      let filteredTypes = [
        ...new Set(
          ulbData
            .map((u) => {
              return u.ulb_type || u.type || u.ulbType || u.category;
            })
            .filter((t) => t && t !== null && t !== "")
        ),
      ].sort();

      let filteredDistricts = allDistricts
        .map((d) => d.name)
        .filter(Boolean)
        .sort();

      // Apply type filter
      if (frontPageFilters.ulb_type) {
        filteredUlbs = filteredUlbs.filter((ulbName) => {
          const ulb = ulbData.find(
            (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
          );
          const ulbType = ulb.ulb_type || ulb.type || ulb.ulbType || ulb.category;
          return ulbType === frontPageFilters.ulb_type;
        });

        filteredClasses = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
                return ulbType === frontPageFilters.ulb_type;
              })
              .map((u) => {
                return u.class || u.ulb_class || u.ulbClass || u.ulbclass;
              })
              .filter((c) => c && c !== null && c !== "")
          ),
        ].sort();

        filteredDistricts = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbType = u.ulb_type || u.type || u.ulbType || u.category;
                return ulbType === frontPageFilters.ulb_type;
              })
              .map((u) => {
                const district = allDistricts.find(d => d.id === u.district_id);
                return district ? district.name : null;
              })
              .filter(Boolean)
          ),
        ].sort();
      }

      // Apply class filter
      if (frontPageFilters.ulb_class) {
        filteredUlbs = filteredUlbs.filter((ulbName) => {
          const ulb = ulbData.find(
            (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
          );
          const ulbClass = ulb.class || ulb.ulb_class || ulb.ulbClass || ulb.ulbclass;
          return ulbClass === frontPageFilters.ulb_class;
        });

        filteredTypes = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
                return ulbClass === frontPageFilters.ulb_class;
              })
              .map((u) => {
                return u.ulb_type || u.type || u.ulbType || u.category;
              })
              .filter((t) => t && t !== null && t !== "")
          ),
        ].sort();

        filteredDistricts = [
          ...new Set(
            ulbData
              .filter((u) => {
                const ulbClass = u.class || u.ulb_class || u.ulbClass || u.ulbclass;
                return ulbClass === frontPageFilters.ulb_class;
              })
              .map((u) => {
                const district = allDistricts.find(d => d.id === u.district_id);
                return district ? district.name : null;
              })
              .filter(Boolean)
          ),
        ].sort();
      }

      // Apply division filter
      if (frontPageFilters.division) {
        const divisionInfo = Object.values(referenceCache.divisions).find(
          (d) => d.name === frontPageFilters.division
        );
        if (divisionInfo) {
          filteredUlbs = filteredUlbs.filter((ulbName) => {
            const ulb = ulbData.find(
              (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
            );
            return ulb && ulb.division_id === divisionInfo.id;
          });

          filteredDistricts = filteredDistricts.filter((districtName) => {
            const district = allDistricts.find(
              (d) => d.name === districtName
            );
            return district && district.division_id === divisionInfo.id;
          });
        }
      }

      // Apply district filter
      if (frontPageFilters.district) {
        const districtInfo = allDistricts.find(
          (d) => d.name === frontPageFilters.district
        );
        if (districtInfo) {
          filteredUlbs = filteredUlbs.filter((ulbName) => {
            const ulb = ulbData.find(
              (u) => (u.ulb_name || u.name || u.ulbName) === ulbName
            );
            return ulb && ulb.district_id === districtInfo.id;
          });
        }
      }

      setFrontPageFilterOptions((prev) => ({
        ...prev,
        ulbs: filteredUlbs,
        ulbClasses: filteredClasses,
        ulbTypes: filteredTypes,
        districts: filteredDistricts,
      }));

      console.log("📊 Refreshed filter options:", {
        ulbsCount: filteredUlbs.length,
        ulbClassesCount: filteredClasses.length,
        ulbTypesCount: filteredTypes.length,
        districtsCount: filteredDistricts.length,
      });
    } catch (error) {
      console.error("Error refreshing ULBs list:", error);
    }
  };

  // Updated handleFrontPageUlbChange
  const handleFrontPageUlbChange = (value) => {
    setFrontPageFilters((prev) => ({
      ...prev,
      ulb_name: value,
    }));

    setTimeout(() => {
      applyFrontPageFilters();
    }, 100);
  };

  const resetFrontPageFilters = async () => {
    // Reset the auto-filters applied flag
    autoFiltersApplied.current = false;
    setPreventReload(false);

    // Get user-based forced filters
    const forcedFilters = {};

    if (!hasFullAccess()) {
      const role = userRole;
      const locationNames = getUserLocationNames();

      if (
        role === "DIVISIONAL_OFFICER" ||
        role === "division_admin" ||
        role === "DIVISION_ADMIN"
      ) {
        if (locationNames.divisionName) {
          forcedFilters.division = locationNames.divisionName;
        }
      }
    }

    setFrontPageFilters({
      division: forcedFilters.division || null,
      district: forcedFilters.district || null,
      ulb_type: null,
      ulb_class: null,
      ulb_name: forcedFilters.ulb_name || null,
    });

    await loadFrontPageFilterOptions();
    setIsFilterApplied(Object.keys(forcedFilters).length > 0);

    // Load forms with the reset filters (auto-apply)
    await loadForms(forcedFilters);

    setShowAllCards(false);
    setShowAnalysisResults(false);
    setSelectedOperations([]);
    setCalculationResults([]);
    setSelectedFilterForm(null);
    setSelectedField(null);
    setRawDataForAnalysis([]);
    setAnalysisDataColumns([]);
    setHierarchicalGraphData(null);
    setSelectedDivisionForGraph(null);
    setSelectedDistrictForGraph(null);

    // Re-enable auto-filters if needed
    if (Object.keys(forcedFilters).length > 0) {
      autoFiltersApplied.current = true;
      setPreventReload(true);
    }
  };
  const applyFrontPageFilters = async () => {
    setLoading(true);
    setIsFilterApplied(true);

    // Log the filters being applied
    console.log("🎯 Applying front page filters:", frontPageFilters);

    // Create a filters object that includes all selected filters
    const activeFilters = { ...frontPageFilters };

    // Load forms with the filters
    await loadForms(activeFilters);

    setShowAllCards(false);
    setLoading(false);

    const filterSummary = [];
    if (frontPageFilters.division)
      filterSummary.push(`Division: ${frontPageFilters.division}`);
    if (frontPageFilters.district)
      filterSummary.push(`District: ${frontPageFilters.district}`);
    if (frontPageFilters.ulb_type)
      filterSummary.push(`ULB Type: ${frontPageFilters.ulb_type}`);
    if (frontPageFilters.ulb_class)
      filterSummary.push(`ULB Class: ${frontPageFilters.ulb_class}`);
    if (frontPageFilters.ulb_name)
      filterSummary.push(`ULB: ${frontPageFilters.ulb_name}`);


  };

  // ==================== REFERENCE DATA FUNCTIONS ====================

  const resolveDistrictId = (id, field = "name") => {
    if (!id || id === null || id === "") return id;
    const district = referenceCache.districts[id];
    return district ? district[field] || district.name || id : id;
  };

  const resolveDivisionId = (id, field = "name") => {
    if (!id || id === null || id === "") return id;
    const division = referenceCache.divisions[id];
    return division ? division[field] || division.name || id : id;
  };

  const resolveUlbId = (id, field = "ulb_name") => {
    if (!id || id === null || id === "") return id;
    const ulb = referenceCache.ulbs[id];
    return ulb ? ulb[field] || ulb.ulb_name || id : id;
  };

  const resolveUlbType = (id) => {
    if (!id || id === null || id === "") return id;
    const ulb = referenceCache.ulbs[id];
    return ulb ? ulb.ulb_type || id : id;
  };

  const resolveUlbClass = (id) => {
    if (!id || id === null || id === "") return id;
    const ulb = referenceCache.ulbs[id];
    return ulb ? ulb.class || id : id;
  };

  const resolveIdsInRow = (row) => {
    if (!row) return row;

    const resolvedRow = { ...row };

    if (row.district_id !== undefined) {
      const resolved = resolveDistrictId(row.district_id);
      if (resolved !== row.district_id) {
        resolvedRow.district_id = resolved;
        resolvedRow.district_name = resolved;
        resolvedRow["District"] = resolved;
        resolvedRow["जिल्हा"] = resolved;
      }
    }

    if (row.division_id !== undefined) {
      const resolved = resolveDivisionId(row.division_id);
      if (resolved !== row.division_id) {
        resolvedRow.division_id = resolved;
        resolvedRow.division_name = resolved;
        resolvedRow["Division"] = resolved;
        resolvedRow["विभाग"] = resolved;
      }
    }

    if (row.ulb_id !== undefined) {
      const resolved = resolveUlbId(row.ulb_id);
      if (resolved !== row.ulb_id) {
        resolvedRow.ulb_id = resolved;
        resolvedRow.ulb_name = resolved;
        resolvedRow["ULB Name"] = resolved;
        resolvedRow["Municipality Name"] = resolved;
        resolvedRow["मनपा/न.प. चे नाव"] = resolved;
      }

      const resolvedType = resolveUlbType(row.ulb_id);
      if (resolvedType !== row.ulb_id) {
        resolvedRow.ulb_type = resolvedType;
      }

      const resolvedClass = resolveUlbClass(row.ulb_id);
      if (resolvedClass !== row.ulb_id) {
        resolvedRow.class = resolvedClass;
      }
    }

    return resolvedRow;
  };

  const resolveIdsInData = (data) => {
    if (!data || !Array.isArray(data)) return data;
    return data.map((row) => resolveIdsInRow(row));
  };

  // ==================== API FUNCTIONS ====================
  const checkApiConnection = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/reports/test`, {
        timeout: 500000,
      });
      setApiStatus(response.data);
      return true;
    } catch (error) {
      console.error("API Connection Failed:", error.message);
      setError(`API Connection Failed: ${error.message}`);

      return false;
    }
  };

  // Add these helper functions at the top of your component (after the other helper functions)
  // REPLACE the hasWorkflowActionColumn and getApprovedSubmissionsOnly functions with this:

  // Helper function to check if form submission data has workflow_action column
  const checkWorkflowColumnInData = (data) => {
    if (!data || data.length === 0) return false;
    // Check if the first record has workflow_action property
    return 'workflow_action' in data[0];
  };

  // Helper function to get only approved submissions
  const getApprovedSubmissionsOnly = (formId, data) => {
    try {
      // First check if workflow_action column exists in the data
      const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];

      if (!hasWorkflowColumn) {
        console.log(`📊 Form ${formId}: No workflow_action column found in data - skipping form`);
        return []; // Return empty array to skip this form
      }

      // Filter data to only include approved submissions
      const approvedData = data.filter(row => {
        const action = row.status;
        // Check for various possible values for approval
        return action === 'approve' ||
          action === 'approved' ||
          action === 'APPROVED' ||
          action === 'Approve' ||
          action === 'APPROVE';
      });

      console.log(`📊 Form ${formId}: ${approvedData.length} approved submissions (from ${data.length} total)`);
      return approvedData;
    } catch (error) {
      console.error(`Error filtering approved submissions for form ${formId}:`, error);
      return [];
    }
  };

  // REPLACE the entire loadForms function with this version:

  const loadForms = async (appliedFilters = null) => {
    const filtersToUse = appliedFilters || {};

    const hasFilters = appliedFilters !== null && (
      appliedFilters.division ||
      appliedFilters.district ||
      appliedFilters.ulb_type ||
      appliedFilters.ulb_class ||
      appliedFilters.ulb_name
    );

    const shouldApplyFilters = hasFilters;

    console.log("📊 loadForms - shouldApplyFilters:", shouldApplyFilters, "filtersToUse:", filtersToUse);

    const filtersKey = JSON.stringify(filtersToUse);
    if (isApplyingFilters && lastAppliedFilters === filtersKey) {
      console.log("⚠️ Skipping duplicate loadForms call with same filters");
      return;
    }

    try {
      setIsApplyingFilters(true);
      setLastAppliedFilters(filtersKey);
      setLoading(true);

      // ========== STEP 1: Get approval status for all forms from form_metadata ==========
      let approvedFormsMap = new Map();
      try {
        const approvalResponse = await axios.get(
          `${API_BASE_URL}/api/reports/forms/approval-status/all`,
          {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
          }
        );

        if (approvalResponse.data.success && approvalResponse.data.forms) {
          approvalResponse.data.forms.forEach(form => {
            approvedFormsMap.set(form.id, form.is_approved === true || form.is_approved === 1);
          });
          console.log(`📋 Loaded approval status for ${approvedFormsMap.size} forms`);
        }
      } catch (approvalError) {
        console.error("Error fetching approval status:", approvalError);
        // Continue without approval filter if API fails
      }

      const response = await axios.get(`${API_BASE_URL}/api/reports/forms`, {
        timeout: 500000,
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      });

      if (response.data.success) {
        let formsData = response.data.forms || [];

        // Exclude form 100
        formsData = formsData.filter((form) => {
          const formId = parseInt(form.id);
          if (formId === 100) {
            console.log(`Excluding form: ${form.displayName || form.name || form.formName || form.tableName} (ID: ${form.id})`);
            return false;
          }
          return true;
        });

        // ========== STEP 2: Filter forms by is_approved = true ==========
        const approvedFormsData = formsData.filter(form => {
          const formId = parseInt(form.id);
          const isFormApproved = approvedFormsMap.get(formId);

          if (!isFormApproved) {
            console.log(`⏭️ Skipping form ${form.id} - is_approved = false in form_metadata`);
            return false;
          }

          console.log(`✅ Form ${form.id} is approved, processing...`);
          return true;
        });

        console.log(`📋 Total forms: ${formsData.length}, Approved in metadata: ${approvedFormsData.length}`);

        if (approvedFormsData.length === 0) {
          console.warn(`⚠️ No forms marked as approved in form_metadata. Only DMA/Super Admin can approve forms.`);
        }

        const enhancedForms = await Promise.all(
          approvedFormsData.map(async (form) => {
            try {
              const params = new URLSearchParams({
                limit: "10000",
                all_fields: "true",
              });

              if (shouldApplyFilters) {
                if (filtersToUse.division) params.set("division", filtersToUse.division);
                if (filtersToUse.district) params.set("district", filtersToUse.district);
                if (filtersToUse.ulb_type) params.set("ulb_type", filtersToUse.ulb_type);
                if (filtersToUse.ulb_class) params.set("ulb_class", filtersToUse.ulb_class);
                if (filtersToUse.ulb_name) params.set("ulb_name", filtersToUse.ulb_name);
              }

              const dataResponse = await axios
                .get(
                  `${API_BASE_URL}/api/reports/forms/${form.id}/data?${params}`,
                  {
                    headers: {
                      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    },
                  },
                )
                .catch(() => ({ data: { success: false, data: [] } }));

              if (dataResponse.data.success && dataResponse.data.data.length > 0) {
                let data = dataResponse.data.data;
                data = resolveIdsInData(data);

                // ========== STEP 3: Filter to approved submissions only (workflow_action) ==========
                const approvedSubmissions = getApprovedSubmissionsOnly(form.id, data);

                if (approvedSubmissions.length === 0) {
                  console.log(`⏭️ Skipping form ${form.id} - No approved submissions found`);
                  return null;
                }

                let filteredData = [...approvedSubmissions];

                // Apply additional filters if needed
                if (shouldApplyFilters) {
                  if (filtersToUse.district) {
                    filteredData = filteredData.filter(
                      (row) =>
                        row.district_name === filtersToUse.district ||
                        row.district === filtersToUse.district,
                    );
                  }
                  if (filtersToUse.division) {
                    filteredData = filteredData.filter(
                      (row) =>
                        row.division_name === filtersToUse.division ||
                        row.division === filtersToUse.division,
                    );
                  }
                  if (filtersToUse.ulb_name) {
                    filteredData = filteredData.filter(
                      (row) =>
                        row.ulb_name === filtersToUse.ulb_name ||
                        row.ulb === filtersToUse.ulb_name,
                    );
                  }
                  if (filtersToUse.ulb_type) {
                    filteredData = filteredData.filter(
                      (row) => row.ulb_type === filtersToUse.ulb_type,
                    );
                  }
                  if (filtersToUse.ulb_class) {
                    filteredData = filteredData.filter(
                      (row) => row.class === filtersToUse.ulb_class,
                    );
                  }
                }

                // Get report-enabled fields
                const reportResponse = await axios
                  .get(
                    `${API_BASE_URL}/api/reports/forms/${form.id}/schema?report_enabled=true`,
                    {
                      headers: {
                        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                      },
                    },
                  )
                  .catch(() => ({
                    data: { success: false, reportEnabledFields: [] },
                  }));

                let reportFields = [];
                let reportCalculations = {};
                let importantFields = [];

                if (reportResponse.data.success && reportResponse.data.reportEnabledFields) {
                  reportFields = reportResponse.data.reportEnabledFields;
                  importantFields = reportFields.filter(
                    (field) => field.report_important === true,
                  );

                  if (importantFields.length === 0) {
                    importantFields = reportFields;
                  }

                  // Calculate values based on approved data only
                  importantFields.forEach((field) => {
                    const actualColumnName = field.column_name || field.field_id;
                    const operator = field.operator || "sum";

                    const values = filteredData
                      .map((row) => {
                        const val = row[actualColumnName];
                        if (val === undefined || val === null || val === "")
                          return null;
                        const strVal = String(val).replace(/,/g, "");
                        const numVal = parseFloat(strVal);
                        return !isNaN(numVal) && isFinite(numVal)
                          ? numVal
                          : null;
                      })
                      .filter((v) => v !== null);

                    let result = 0;

                    if (values.length > 0) {
                      switch (operator.toLowerCase()) {
                        case "sum":
                          result = values.reduce((a, b) => a + b, 0);
                          break;
                        case "avg":
                        case "average":
                          result =
                            values.reduce((a, b) => a + b, 0) / values.length;
                          break;
                        case "min":
                          result = Math.min(...values);
                          break;
                        case "max":
                          result = Math.max(...values);
                          break;
                        case "count":
                          result = values.length;
                          break;
                        default:
                          result = values.reduce((a, b) => a + b, 0);
                      }
                    }

                    reportCalculations[field.field_id] = {
                      value: result,
                      operator: operator,
                      display_name: field.display_name || field.field_id,
                      is_important: field.report_important === true,
                      column_name: actualColumnName,
                      recordCount: values.length,
                      totalRecords: filteredData.length,
                    };
                  });
                }

                // Build report fields
                const importantReportFields = Object.values(reportCalculations)
                  .filter((calc) => calc.is_important === true)
                  .map((calc) => ({
                    display_name: calc.display_name,
                    operator: calc.operator,
                    value: calc.value,
                    column_name: calc.column_name,
                    recordCount: calc.recordCount,
                    totalRecords: calc.totalRecords,
                  }));

                const topReportFields =
                  importantReportFields.length > 0
                    ? importantReportFields.slice(0, 6)
                    : Object.values(reportCalculations)
                      .slice(0, 3)
                      .map((calc) => ({
                        display_name: calc.display_name,
                        operator: calc.operator,
                        value: calc.value,
                        column_name: calc.column_name,
                        recordCount: calc.recordCount,
                        totalRecords: calc.totalRecords,
                      }));

                let filteredRecordCount = 0;
                if (importantReportFields.length > 0) {
                  filteredRecordCount = importantReportFields[0].totalRecords;
                }

                return {
                  ...form,
                  displayName:
                    form.name ||
                    form.formName ||
                    form.tableName ||
                    `Form ${form.id}`,
                  numericFieldCount:
                    form.structureInfo?.numericColumns?.length || 0,
                  totalExpected: form.totalExpected || 395,
                  reportFields: topReportFields,
                  reportFieldsCount: Object.keys(reportCalculations).length,
                  importantFieldsCount: importantReportFields.length,
                  filteredRecordCount: filteredRecordCount,
                  allReportCalculations: reportCalculations,
                };
              }

              return null;
            } catch (error) {
              console.error(`Error processing form ${form.id}:`, error);
              return null;
            }
          }),
        );

        // Filter out null entries
        let validForms = enhancedForms.filter(form => form !== null);

        // Sort forms
        const priorityOrder = [
          633, 713, 722, 705, 712, 795, 632, 790, 825, 785, 794, 789, 635, 792, 787, 793,
        ];

        const sortedForms = validForms.sort((a, b) => {
          const idA = parseInt(a.id);
          const idB = parseInt(b.id);

          const priorityA = priorityOrder.indexOf(idA);
          const priorityB = priorityOrder.indexOf(idB);

          if (priorityA !== -1 && priorityB !== -1) {
            return priorityA - priorityB;
          }
          if (priorityA !== -1) {
            return -1;
          }
          if (priorityB !== -1) {
            return 1;
          }
          return idA - idB;
        });

        console.log(`📋 Forms loaded with both conditions:`);
        console.log(`   - is_approved = true in form_metadata: ${approvedFormsData.length}`);
        console.log(`   - Has approved submissions: ${sortedForms.length}`);

        if (sortedForms.length === 0) {
          console.warn(`⚠️ No forms found meeting both conditions.`);
        }

        setForms(sortedForms);
        setFilteredForms(sortedForms);
      }
    } catch (error) {
      console.error("Error loading forms:", error);

    } finally {
      setLoading(false);
      setIsApplyingFilters(false);
    }
  };
  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredForms(forms);
    } else {
      const filtered = forms.filter(
        (form) =>
          form.displayName.toLowerCase().includes(value.toLowerCase()) ||
          form.tableName?.toLowerCase().includes(value.toLowerCase()) ||
          form.name?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredForms(filtered);
    }
    setShowAllCards(false);
  };
  const handleColumnSearch = async (value) => {
    setColumnSearchTerm(value);
    setSearchingByColumn(true);

    if (!value.trim()) {
      // If column search is cleared, restore filtered forms based on name search only
      if (searchTerm.trim()) {
        const filtered = forms.filter(
          (form) =>
            form.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.tableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setFilteredForms(filtered);
      } else {
        setFilteredForms(forms);
      }
      setSearchingByColumn(false);
      return;
    }

    // Show loading state
    setLoading(true);

    try {
      // Search through each form's columns
      const formsWithMatchingColumns = [];

      for (const form of forms) {
        try {
          // Fetch form structure to get all columns
          const structureResponse = await axios.get(
            `${API_BASE_URL}/api/reports/forms/${form.id}/structure`,
            {
              headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
            },
          );

          if (structureResponse.data.success && structureResponse.data.fields) {
            // Check if any field matches the search term
            const hasMatchingColumn = structureResponse.data.fields.some((field) => {
              const fieldLabel = field.label || field.id || field.column_name || "";
              const fieldId = field.field_id || field.id || "";
              const columnName = field.column_name || "";

              return fieldLabel.toLowerCase().includes(value.toLowerCase()) ||
                fieldId.toLowerCase().includes(value.toLowerCase()) ||
                columnName.toLowerCase().includes(value.toLowerCase());
            });

            if (hasMatchingColumn) {
              formsWithMatchingColumns.push(form);
            }
          }
        } catch (error) {
          console.error(`Error fetching structure for form ${form.id}:`, error);
          // Continue with other forms
        }
      }

      // Also apply name search filter if present
      let finalFiltered = formsWithMatchingColumns;
      if (searchTerm.trim()) {
        finalFiltered = formsWithMatchingColumns.filter(
          (form) =>
            form.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.tableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }

      setFilteredForms(finalFiltered);
      setShowAllCards(false);

      // if (finalFiltered.length === 0) {
      //   notification.info({
      //     message: "No Results Found",
      //     description: `No forms contain a column matching "${value}"`,
      //     duration: 3,
      //   });
      // }
    } catch (error) {
      console.error("Error searching columns:", error);

    } finally {
      setLoading(false);
      setSearchingByColumn(false);
    }
  };

  const handleFormColumnSearch = (value) => {
    setFormColumnSearchTerm(value);

    if (!value.trim()) {
      // Reset to show all report fields
      setFilteredReportFields(reportEnabledFields);
      return;
    }

    // Filter report fields based on search term
    const filtered = reportEnabledFields.filter((field) => {
      const fieldName = (field.display_name || field.field_id || "").toLowerCase();
      const columnName = (field.column_name || "").toLowerCase();
      const searchValue = value.toLowerCase();

      return fieldName.includes(searchValue) || columnName.includes(searchValue);
    });

    setFilteredReportFields(filtered);

    // if (filtered.length === 0) {
    //   notification.info({
    //     message: "No Results Found",
    //     description: `No data points match "${value}" in this form`,
    //     duration: 2,
    //   });
    // }
  };

  const fetchFormFieldLabels = async (formId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/structure`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.fields) {
        const fieldMap = {};
        response.data.fields.forEach((field) => {
          fieldMap[field.id] = field.label || field.id;
        });
        return fieldMap;
      }
      return {};
    } catch (error) {
      console.error("Error fetching field labels:", error);
      return {};
    }
  };

  const buildFieldHierarchy = async (formId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.fields) {
        const fields = response.data.fields;
        const parentMap = {};
        const hierarchy = {};

        fields.forEach((field) => {
          const parentId =
            field.parent_id || field.parentId || field.parent || null;
          const fieldInfo = {
            fieldId: field.field_id,
            columnName: field.column_name || field.field_id,
            type: field.field_type,
            enLabel: field.en_label,
            mrLabel: field.mr_label,
            parentId: parentId,
            headingLevel: field.heading_level || null,
          };
          hierarchy[field.field_id] = fieldInfo;
          if (parentId) {
            if (!parentMap[parentId]) parentMap[parentId] = [];
            parentMap[parentId].push(fieldInfo);
          }
        });

        setFieldParentMap(parentMap);
        setFieldHierarchy(hierarchy);
      }
    } catch (error) {
      console.error("Error building field hierarchy:", error);
    }
  };

  const fetchReportEnabledFields = async (formId) => {
    try {
      setLoadingReportFields(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema?report_enabled=true`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.reportEnabledFields) {
        setReportEnabledFields(response.data.reportEnabledFields);
        setFilteredReportFields(response.data.reportEnabledFields); // Initialize filtered fields
        return response.data.reportEnabledFields;
      }
      return [];
    } catch (error) {
      console.error("Error fetching report-enabled fields:", error);
      return [];
    } finally {
      setLoadingReportFields(false);
    }
  };

  const processReportCalculations = (data, reportFields) => {
    if (
      !data ||
      data.length === 0 ||
      !reportFields ||
      reportFields.length === 0
    )
      return {};

    const calculations = {};

    reportFields.forEach((field) => {
      const actualColumnName = field.column_name || field.field_id;
      const operator = field.operator || "sum";

      const values = data
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      if (values.length === 0) {
        calculations[field.field_id] = {
          value: 0,
          operator,
          count: 0,
          display_name: field.display_name || field.field_id,
        };
        return;
      }

      let result = 0;
      switch (operator.toLowerCase()) {
        case "sum":
          result = values.reduce((a, b) => a + b, 0);
          break;
        case "avg":
        case "average":
          result = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case "min":
          result = Math.min(...values);
          break;
        case "max":
          result = Math.max(...values);
          break;
        case "count":
          result = values.length;
          break;
        default:
          result = values.reduce((a, b) => a + b, 0);
      }

      calculations[field.field_id] = {
        value: result,
        operator,
        count: values.length,
        display_name: field.display_name || field.field_id,
      };
    });

    return calculations;
  };

  const loadFilterOptions = async (formId) => {
    try {
      // Don't set loading state to avoid UI flicker
      const params = new URLSearchParams();

      // Apply user role filters (these are mandatory based on user's role)
      if (!hasFullAccess()) {
        if (userRole === "DISTRICT_ADMIN" || userRole === "district_admin") {
          if (userDistrictId) params.set("district_id", userDistrictId);
        } else if (
          userRole === "DIVISION_ADMIN" ||
          userRole === "division_admin" ||
          userRole === "DIVISIONAL_OFFICER" ||
          userRole === "divisional_officer"
        ) {
          if (userDivisionId) params.set("division_id", userDivisionId);
        } else if (
          userRole === "ULB_ADMIN" ||
          userRole === "ulb_admin" ||
          userRole === "ULB_OFFICER" ||
          userRole === "ULB_CHIEF_OFFICIER"
        ) {
          if (userUlbId) params.set("ulb_id", userUlbId);
        }
      }

      // IMPORTANT: Use the current form filters (these are form-specific, NOT front page)
      const currentFilters = filters; // Use the current filters state which is separate from front page

      if (currentFilters.division) {
        params.set("division", currentFilters.division);
        // Also try to get division ID from reference data
        const divisionInfo = Object.values(referenceCache.divisions).find(
          (d) => d.name === currentFilters.division,
        );
        if (divisionInfo) {
          params.set("division_id", divisionInfo.id);
        }
      }

      if (currentFilters.district) {
        params.set("district", currentFilters.district);
        // Also try to get district ID
        const districtInfo = Object.values(referenceCache.districts).find(
          (d) => d.name === currentFilters.district,
        );
        if (districtInfo) {
          params.set("district_id", districtInfo.id);
        }
      }

      if (currentFilters.ulb_type) {
        params.set("ulb_type", currentFilters.ulb_type);
      }

      if (currentFilters.ulb_class) {
        params.set("ulb_class", currentFilters.ulb_class);
      }

      // Log the filters being applied for debugging
      const appliedParams = Object.fromEntries(params);
      console.log("📡 loadFilterOptions: Loading filter options with form-specific filters ONLY (front page filters NOT applied):", appliedParams);

      // Check if front page filters exist but are not being used
      const hasFrontPageFilters = frontPageFilters.division ||
        frontPageFilters.district ||
        frontPageFilters.ulb_type ||
        frontPageFilters.ulb_class ||
        frontPageFilters.ulb_name;

      if (hasFrontPageFilters) {
        console.log("⚠️ Note: frontPageFilters exist but are NOT being applied to form filter options:", frontPageFilters);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/filter-options?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success) {
        let districts = response.data.districts || [];
        let divisions = response.data.divisions || [];
        let ulbs = response.data.ulbs || [];

        console.log("📡 Raw filter options from API:", {
          divisionsCount: divisions.length,
          districtsCount: districts.length,
          ulbsCount: ulbs.length,
        });

        // If we have a division filter but API returned no districts, use reference data
        if (currentFilters.division && districts.length === 0) {
          console.log("⚠️ API returned no districts for division, using reference data");
          const divisionInfo = Object.values(referenceCache.divisions).find(
            (d) => d.name === currentFilters.division,
          );

          if (divisionInfo && referenceCache.districts) {
            districts = Object.values(referenceCache.districts)
              .filter((d) => d.division_id === divisionInfo.id)
              .map((d) => d.name)
              .sort();
            console.log("📍 Manually filtered districts by division:", districts.length);
          }
        }

        // If we have a district filter but API returned no ULBs, use reference data
        if (currentFilters.district && ulbs.length === 0) {
          console.log("⚠️ API returned no ULBs for district, using reference data");
          const districtInfo = Object.values(referenceCache.districts).find(
            (d) => d.name === currentFilters.district,
          );

          if (districtInfo && referenceCache.ulbs) {
            ulbs = Object.values(referenceCache.ulbs)
              .filter((u) => u.district_id === districtInfo.id)
              .map((u) => u.ulb_name)
              .sort();
            console.log("📍 Manually filtered ULBs by district:", ulbs.length);
          }
        }

        // Also filter divisions if needed (based on user role)
        if (!hasFullAccess() && userDivisionId && divisions.length > 0) {
          const userDivisionName = referenceCache.divisions[userDivisionId]?.name;
          if (userDivisionName) {
            divisions = divisions.filter(d => d === userDivisionName);
            console.log("📍 Filtered divisions by user role:", divisions.length);
          }
        }

        setAvailableFilters({
          districts: districts,
          divisions: divisions,
          ulbs: ulbs,
        });
        setUlbTypes(response.data.ulbTypes || []);
        setUlbClasses(response.data.ulbClasses || []);

        console.log("✅ Filter options loaded successfully:", {
          divisions: divisions.length,
          districts: districts.length,
          ulbs: ulbs.length,
          ulbTypes: response.data.ulbTypes?.length || 0,
          ulbClasses: response.data.ulbClasses?.length || 0,
        });

        return true;
      } else {
        console.warn("⚠️ loadFilterOptions: API response indicated failure:", response.data);
        setAvailableFilters({ districts: [], divisions: [], ulbs: [] });
        setUlbTypes([]);
        setUlbClasses([]);
        return false;
      }
    } catch (error) {
      console.error("❌ Error loading filter options:", error);
      console.error("Error details:", error.response?.data || error.message);

      // Don't show error notification for 404 or 400 errors (just means no data)
      if (error.response?.status !== 404 && error.response?.status !== 400) {

      }

      setAvailableFilters({ districts: [], divisions: [], ulbs: [] });
      setUlbTypes([]);
      setUlbClasses([]);
      return false;
    }
  };

  const loadDependentFilterOptions = async (
    selectedDivision = null,
    selectedDistrict = null,
    selectedUlbType = null,
    selectedUlbClass = null,
  ) => {
    try {
      if (!selectedForm) return;

      const params = new URLSearchParams();
      if (selectedDivision) {
        params.set("division", selectedDivision);
        // Also try to get division ID from reference data
        const divisionInfo = Object.values(referenceCache.divisions).find(
          (d) => d.name === selectedDivision,
        );
        if (divisionInfo) {
          params.set("division_id", divisionInfo.id);
        }
      }
      if (selectedDistrict) params.set("district", selectedDistrict);
      if (selectedUlbType) params.set("ulb_type", selectedUlbType);
      if (selectedUlbClass) params.set("ulb_class", selectedUlbClass);

      console.log(
        "📡 Loading dependent filter options with params:",
        Object.fromEntries(params),
      );

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${selectedForm.id}/filter-options?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success) {
        let districts = response.data.districts || [];
        let ulbs = response.data.ulbs || [];

        // If we have a division selected, filter districts locally using reference data
        if (selectedDivision && districts.length === 0) {
          const divisionInfo = Object.values(referenceCache.divisions).find(
            (d) => d.name === selectedDivision,
          );
          if (divisionInfo && referenceCache.districts) {
            districts = Object.values(referenceCache.districts)
              .filter((d) => d.division_id === divisionInfo.id)
              .map((d) => d.name)
              .sort();
            console.log(
              "📍 Manually filtered districts by division:",
              districts.length,
            );
          }
        }

        // If we have a district selected, filter ULBs locally using reference data
        if (selectedDistrict && ulbs.length === 0) {
          const districtInfo = Object.values(referenceCache.districts).find(
            (d) => d.name === selectedDistrict,
          );
          if (districtInfo && referenceCache.ulbs) {
            ulbs = Object.values(referenceCache.ulbs)
              .filter((u) => u.district_id === districtInfo.id)
              .map((u) => u.ulb_name)
              .sort();
            console.log("📍 Manually filtered ULBs by district:", ulbs.length);
          }
        }

        setAvailableFilters({
          districts: districts,
          divisions: response.data.divisions || [],
          ulbs: ulbs,
        });
        setUlbTypes(response.data.ulbTypes || []);
        setUlbClasses(response.data.ulbClasses || []);

        console.log("✅ Dependent filter options updated:", {
          districtsCount: districts.length,
          ulbsCount: ulbs.length,
        });
      }
    } catch (error) {
      console.error("Error loading dependent filter options:", error);
    }
  };

  const detectNumericColumnsFromData = (data) => {
    if (!data || data.length === 0) return [];

    const numericColumns = [];
    const sampleSize = Math.min(data.length, 50);
    const fields = Object.keys(data[0] || {}).filter(
      (key) =>
        !key.startsWith("_") &&
        !key.endsWith("_formatted") &&
        !key.endsWith("_numeric") &&
        key !== "id" &&
        key !== "created_at" &&
        key !== "updated_at",
    );

    fields.forEach((field) => {
      let numericCount = 0;
      let totalChecked = 0;

      for (let i = 0; i < sampleSize; i++) {
        const value = data[i][field];
        if (value !== null && value !== undefined && value !== "") {
          totalChecked++;
          const strValue = String(value).replace(/,/g, "");
          const numValue = parseFloat(strValue);
          if (!isNaN(numValue) && isFinite(numValue)) numericCount++;
        }
      }

      if (totalChecked > 0 && numericCount / totalChecked >= 0.8) {
        numericColumns.push({
          field: field,
          displayName: fieldLabels[field] || formatFieldName(field),
          sampleValue: data[0][field],
        });
      }
    });

    return numericColumns;
  };

  const getUniqueFilterValues = (fieldName) => {
    if (!formData || formData.length === 0 || !fieldName) return [];
    const values = new Set();
    formData.forEach((row) => {
      if (
        row[fieldName] &&
        typeof row[fieldName] === "string" &&
        row[fieldName].trim() !== ""
      ) {
        values.add(row[fieldName]);
      }
    });
    return Array.from(values).sort();
  };

  const loadFormData = async (formId, currentFilters = {}) => {
    try {
      setLoadingFormData(true);
      setColumnGraphData(null);
      setHierarchicalGraphs([]);
      setReportCalculations({});
      // Clear field graphs data when loading new form data
      setFieldGraphsData({});

      const params = new URLSearchParams({
        limit: "10000", // Increased limit to get more records for accurate totals
        all_fields: "true",
      });

      // Apply user role filters (these are mandatory based on user's role)
      if (!hasFullAccess()) {
        if (userRole === "DISTRICT_ADMIN" || userRole === "district_admin") {
          if (userDistrictId) params.set("district_id", userDistrictId);
        } else if (
          userRole === "DIVISION_ADMIN" ||
          userRole === "division_admin" ||
          userRole === "DIVISIONAL_OFFICER" ||
          userRole === "divisional_officer"
        ) {
          if (userDivisionId) params.set("division_id", userDivisionId);
        } else if (
          userRole === "ULB_ADMIN" ||
          userRole === "ulb_admin" ||
          userRole === "ULB_OFFICER" ||
          userRole === "ULB_CHIEF_OFFICIER"
        ) {
          if (userUlbId) params.set("ulb_id", userUlbId);
        }
      }

      // IMPORTANT: Only apply the currentFilters that are passed (these are form-specific)
      if (currentFilters.district) params.set("district", currentFilters.district);
      if (currentFilters.district_id) params.set("district_id", currentFilters.district_id);
      if (currentFilters.division) params.set("division", currentFilters.division);
      if (currentFilters.division_id) params.set("division_id", currentFilters.division_id);
      if (currentFilters.ulb_name) params.set("ulb_name", currentFilters.ulb_name);
      if (currentFilters.ulb_id) params.set("ulb_id", currentFilters.ulb_id);
      if (currentFilters.ulb_type) params.set("ulb_type", currentFilters.ulb_type);
      if (currentFilters.ulb_class) params.set("ulb_class", currentFilters.ulb_class);

      // Log the filters being applied for debugging
      const appliedFilters = Object.fromEntries(params);
      console.log("🔍 loadFormData: Fetching data with filters:", appliedFilters);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/data?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success) {
        let data = response.data.data || [];
        console.log(`📊 Raw data received for form ${formId}: ${data.length} records`);

        data = resolveIdsInData(data);

        // ========== CRITICAL: Filter to only approved submissions ==========
        const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];

        let approvedData = [...data];

        if (hasWorkflowColumn) {
          const originalCount = data.length;
          approvedData = data.filter(row => {
            const action = row.status;
            // Check for various possible values for approval
            return action === 'approve' ||
              action === 'approved' ||
              action === 'APPROVED' ||
              action === 'Approve' ||
              action === 'APPROVE';
          });
          console.log(`📊 Form ${formId}: Filtered to ${approvedData.length} approved submissions (from ${originalCount} total)`);

          if (approvedData.length === 0) {
            notification.info({
              message: "No Approved Data",
              description: "This form has no approved submissions to display.",
              duration: 3,
            });
          }
        } else {
          console.log(`📊 Form ${formId}: No workflow_action column found - using all data`);
        }

        // Apply user role filtering if needed
        let filteredData = [...approvedData];
        if (!hasFullAccess()) {
          filteredData = filterDataByUserRole(filteredData);
        }

        // Enhance data with formatted numeric fields
        const enhancedData = filteredData.map((row) => {
          const enhancedRow = { ...row };
          Object.keys(row).forEach((key) => {
            if (!key.startsWith("_")) {
              const strValue = String(row[key]).replace(/,/g, "");
              const numValue = parseFloat(strValue);
              if (!isNaN(numValue) && isFinite(numValue)) {
                enhancedRow[`${key}_formatted`] = numValue.toLocaleString();
                enhancedRow[`${key}_numeric`] = numValue;
              }
            }
          });
          return enhancedRow;
        });

        // ========== FIX: Store ORIGINAL DATA for State Total ==========
        // We need to fetch ALL approved data without any location filters for the State Total
        // This is a separate API call to get the complete dataset
        const stateTotalParams = new URLSearchParams({
          limit: "10000",
          all_fields: "true",
        });

        // Only apply role-based filters for State Total (no location filters)
        if (!hasFullAccess()) {
          if (userRole === "DISTRICT_ADMIN" || userRole === "district_admin") {
            if (userDistrictId) stateTotalParams.set("district_id", userDistrictId);
          } else if (
            userRole === "DIVISION_ADMIN" ||
            userRole === "division_admin" ||
            userRole === "DIVISIONAL_OFFICER" ||
            userRole === "divisional_officer"
          ) {
            if (userDivisionId) stateTotalParams.set("division_id", userDivisionId);
          } else if (
            userRole === "ULB_ADMIN" ||
            userRole === "ulb_admin" ||
            userRole === "ULB_OFFICER" ||
            userRole === "ULB_CHIEF_OFFICIER"
          ) {
            if (userUlbId) stateTotalParams.set("ulb_id", userUlbId);
          }
        }

        let originalDataForTotal = [];

        // Only fetch original data if we don't have it already or if this is initial load
        if (originalFormData.length === 0 || (Object.keys(currentFilters).length === 0 && !currentFilters.division && !currentFilters.district)) {
          try {
            console.log("📊 Fetching State Total data (unfiltered approved data)...");
            const stateTotalResponse = await axios.get(
              `${API_BASE_URL}/api/reports/forms/${formId}/data?${stateTotalParams}`,
              {
                headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
              },
            );

            if (stateTotalResponse.data.success) {
              let totalData = stateTotalResponse.data.data || [];
              totalData = resolveIdsInData(totalData);

              // Filter to approved submissions only
              if (totalData.length > 0 && 'status' in totalData[0]) {
                totalData = totalData.filter(row => {
                  const action = row.status;
                  return action === 'approve' ||
                    action === 'approved' ||
                    action === 'APPROVED' ||
                    action === 'Approve' ||
                    action === 'APPROVE';
                });
              }

              // Apply role-based filters for State Total
              if (!hasFullAccess()) {
                totalData = filterDataByUserRole(totalData);
              }

              originalDataForTotal = totalData;
              setOriginalFormData([...totalData]);
              console.log(`📦 Stored original data for State Total: ${totalData.length} records`);
            }
          } catch (error) {
            console.error("Error fetching State Total data:", error);
            // Fallback to current filtered data
            originalDataForTotal = [...enhancedData];
            if (originalFormData.length === 0) {
              setOriginalFormData([...enhancedData]);
            }
          }
        } else {
          // Use existing originalFormData
          originalDataForTotal = [...originalFormData];
          console.log(`📦 Using existing original data: ${originalDataForTotal.length} records`);
        }

        // Set the current filtered data for display
        setFormData(enhancedData);
        setDataPagination((prev) => ({ ...prev, total: enhancedData.length }));
        setDataModalPagination((prev) => ({
          ...prev,
          total: enhancedData.length,
        }));

        // Detect numeric columns from the data
        const detectedNumericColumns = detectNumericColumnsFromData(enhancedData);
        setAvailableNumericColumns(detectedNumericColumns);

        // Process form fields
        let fieldNames = [];
        if (response.data.fields && Array.isArray(response.data.fields)) {
          fieldNames = response.data.fields.map((field) =>
            typeof field === "string" ? field : field.id || String(field),
          );
        }

        if (fieldNames.length === 0 && data.length > 0) {
          const allKeys = new Set();
          data.forEach((row) => {
            Object.keys(row).forEach((key) => {
              if (
                !key.startsWith("_") &&
                key !== "_id" &&
                key !== "_form_id" &&
                key !== "_status" &&
                key !== "_created_at"
              ) {
                allKeys.add(key);
              }
            });
          });
          fieldNames = Array.from(allKeys);
        }

        setFormFields(fieldNames);
        setNumericFields(response.data.numericColumns || []);

        // Process report-enabled fields using ORIGINAL DATA for State Total
        if (
          response.data.reportEnabledFields &&
          response.data.reportEnabledFields.length > 0
        ) {
          setReportEnabledFields(response.data.reportEnabledFields);

          // Calculate using the original data (State Total)
          if (originalDataForTotal.length > 0) {
            const calculations = processReportCalculations(
              originalDataForTotal,
              response.data.reportEnabledFields,
            );
            setReportCalculations(calculations);
            console.log("📊 Report calculations based on original data:", calculations);
          } else if (enhancedData.length > 0) {
            // Fallback to current data if original data not available
            const calculations = processReportCalculations(
              enhancedData,
              response.data.reportEnabledFields,
            );
            setReportCalculations(calculations);
            console.log("📊 Report calculations based on filtered data (fallback):", calculations);
          }
        } else {
          const reportFields = await fetchReportEnabledFields(formId);
          if (reportFields.length > 0 && originalDataForTotal.length > 0) {
            const calculations = processReportCalculations(
              originalDataForTotal,
              reportFields,
            );
            setReportCalculations(calculations);
          } else if (reportFields.length > 0 && enhancedData.length > 0) {
            const calculations = processReportCalculations(
              enhancedData,
              reportFields,
            );
            setReportCalculations(calculations);
          }
        }

        // Generate auto graphs
        const graphableColumns = detectedNumericColumns.filter(
          (col) => !shouldExcludeFromGraphs(col.field, col.displayName),
        );

        if (graphableColumns.length > 0 && !selectedGraphColumn) {
          setSelectedGraphColumn(graphableColumns[0].field);
        }

        generateAutoGraphs(enhancedData, graphableColumns);

        console.log(`✅ loadFormData complete: Loaded ${enhancedData.length} records (filtered), ${originalDataForTotal.length} records (State Total)`);

      } else {
        console.warn("⚠️ loadFormData: API response indicated failure:", response.data);
        // notification.warning({
        //   message: "No Data Available",
        //   description: response.data.message || "No data found for the selected filters",
        //   duration: 3,
        // });
        setFormData([]);
        setFormFields([]);
        setReportCalculations({});
      }
    } catch (error) {
      console.error("❌ Error loading form data:", error);
      console.error("Error details:", error.response?.data || error.message);

      setFormData([]);
      setFormFields([]);
      setReportCalculations({});
    } finally {
      setLoadingFormData(false);
    }
  };
  // ==================== GRAPH FUNCTIONS ====================
  const generateAutoGraphs = (data, numericColumns) => {
    if (!data || data.length === 0 || !numericColumns.length) return;

    const graphs = [];
    const divisionField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") ||
            f.toLowerCase().includes("विभाग")),
      ) || "division_name";
    const districtField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") ||
            f.toLowerCase().includes("जिल्हा")),
      ) || "district_name";
    const ulbField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb") ||
            f.toLowerCase().includes("मनपा") ||
            f.toLowerCase().includes("municipality")),
      ) || "ulb_name";

    let groupByField = divisionField;
    let groupLabel = "Division";

    if (
      filters.ulb_name ||
      filters.ulb_id ||
      filters.ulb_type ||
      filters.ulb_class
    ) {
      groupByField = null;
      groupLabel = "Record";
    } else if (filters.district || filters.district_id) {
      groupByField = ulbField;
      groupLabel = "ULB";
    } else if (filters.division || filters.division_id) {
      groupByField = districtField;
      groupLabel = "District";
    }

    numericColumns.slice(0, 5).forEach((col) => {
      if (!col.field) return;

      if (groupByField) {
        const groupedData = {};
        data.forEach((row) => {
          if (row._isTotalRow) return;
          const groupValue = row[groupByField] || `Unknown ${groupLabel}`;
          const value = parseFloat(row[col.field]) || 0;
          if (!groupedData[groupValue])
            groupedData[groupValue] = { name: groupValue, value: 0, count: 0 };
          groupedData[groupValue].value += value;
          groupedData[groupValue].count++;
        });

        const chartData = Object.values(groupedData)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((item) => ({
            name: item.count > 1 ? `${item.name} (${item.count})` : item.name,
            value: item.value,
          }));

        if (chartData.length > 0) {
          graphs.push({
            type: "bar",
            title: `${col.displayName} by ${groupLabel}`,
            data: chartData,
            config: {
              fieldName: col.field,
              xAxisLabel: groupLabel,
              yAxisLabel: col.displayName,
              isAggregated: true,
              groupCount: chartData.length,
              recordCount: data.length,
            },
          });
        }
      }
    });

    setHierarchicalGraphs(graphs);
  };

  const generateColumnGraph = async (
    columnName,
    displayName,
    fieldId = null,
    chartType = "bar",
  ) => {
    if (!selectedForm || !columnName) return null;

    const graphKey = fieldId || columnName;

    try {
      setLoadingGraphs((prev) => ({ ...prev, [graphKey]: true }));
      setFieldGraphsData((prev) => ({
        ...prev,
        [graphKey]: {
          ...prev[graphKey],
          showGraph: false,
          isGraphReady: false,
          chartType: chartType,
        },
      }));

      const params = new URLSearchParams({
        limit: "10000",
        all_fields: "true",
      });

      // Check if user is Division Admin
      const isDivisionAdmin = !hasFullAccess() && (
        userRole === "DIVISION_ADMIN" ||
        userRole === "division_admin" ||
        userRole === "DIVISIONAL_OFFICER" ||
        userRole === "divisional_officer" ||
        userRole === "Divisionalcommissioner"
      );

      // Check if user is District Admin
      const isDistrictAdmin = !hasFullAccess() && (
        userRole === "DISTRICT_ADMIN" ||
        userRole === "district_admin"
      );

      // Get Division Admin's division name
      let divisionAdminDivisionName = null;
      if (isDivisionAdmin && userDivisionId) {
        divisionAdminDivisionName = referenceCache.divisions[userDivisionId]?.name;
        console.log("🏢 Division Admin graph - filtering for division:", divisionAdminDivisionName);
      }

      // Get District Admin's district name
      let districtAdminDistrictName = null;
      if (isDistrictAdmin && userDistrictId) {
        districtAdminDistrictName = referenceCache.districts[userDistrictId]?.name;
        console.log("🏢 District Admin detected - filtering for district:", districtAdminDistrictName);
      }

      // Apply filters to API params
      if (divisionAdminDivisionName) {
        params.set("division", divisionAdminDivisionName);
        console.log("📊 Applying division filter to graph API:", divisionAdminDivisionName);
      } else if (filters.division) {
        params.set("division", filters.division);
      } else if (filters.division_id) {
        params.set("division_id", filters.division_id);
      }

      // CRITICAL: For District Admin, apply district filter to API
      if (districtAdminDistrictName) {
        params.set("district", districtAdminDistrictName);
        console.log("📊 District Admin - applying district filter to graph API:", districtAdminDistrictName);
      } else if (filters.district) {
        params.set("district", filters.district);
        console.log("📊 Applying district filter to graph API:", filters.district);
      }

      if (filters.district_id) params.set("district_id", filters.district_id);
      if (filters.ulb_name) params.set("ulb_name", filters.ulb_name);
      if (filters.ulb_id) params.set("ulb_id", filters.ulb_id);
      if (filters.ulb_type) params.set("ulb_type", filters.ulb_type);
      if (filters.ulb_class) params.set("ulb_class", filters.ulb_class);

      console.log("📡 Graph API params:", Object.fromEntries(params));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${selectedForm.id}/data?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.data) {
        let data = response.data.data;
        data = resolveIdsInData(data);

        const divisionField = formFields.find(
          (f) =>
            f &&
            (f.toLowerCase().includes("division") ||
              f.toLowerCase().includes("विभाग")),
        );
        const districtField = formFields.find(
          (f) =>
            f &&
            (f.toLowerCase().includes("district") ||
              f.toLowerCase().includes("जिल्हा")),
        );
        const ulbField = formFields.find(
          (f) =>
            f &&
            (f.toLowerCase().includes("ulb") ||
              f.toLowerCase().includes("मनपा") ||
              f.toLowerCase().includes("municipality")),
        );
        const ulbTypeField = formFields.find(
          (f) =>
            f &&
            (f.toLowerCase().includes("ulb_type") ||
              f.toLowerCase().includes("type")),
        );
        const classField = formFields.find(
          (f) =>
            f &&
            (f.toLowerCase().includes("class") ||
              f.toLowerCase().includes("वर्ग")),
        );

        let aggregationLevel = "record";
        let groupByField = null;
        let groupLabel = "Record";

        // Apply client-side division filter if needed (double-check)
        let filteredData = [...data];

        // For Division Admin, ensure we only show data from their division
        if (divisionAdminDivisionName && divisionField) {
          const originalCount = filteredData.length;
          filteredData = filteredData.filter(row => {
            const rowDivision = row[divisionField] || row.division_name || row.division;
            return rowDivision === divisionAdminDivisionName;
          });
          console.log(`📊 Division Admin filter applied: ${filteredData.length} records from ${originalCount} (Division: ${divisionAdminDivisionName})`);
        }

        // For District Admin, ensure we only show data from their district
        if (districtAdminDistrictName && districtField) {
          const originalCount = filteredData.length;
          filteredData = filteredData.filter(row => {
            const rowDistrict = row[districtField] || row.district_name || row.district;
            return rowDistrict === districtAdminDistrictName;
          });
          console.log(`📊 District Admin filter applied: ${filteredData.length} records from ${originalCount} (District: ${districtAdminDistrictName})`);
        }

        // Apply regular district filter client-side if needed
        if (filters.district && districtField && !districtAdminDistrictName) {
          const originalCount = filteredData.length;
          filteredData = filteredData.filter(row => {
            const rowDistrict = row[districtField] || row.district_name || row.district;
            return rowDistrict === filters.district;
          });
          console.log(`📊 District filter applied: ${filteredData.length} records from ${originalCount} (District: ${filters.district})`);
        }

        // Determine aggregation level based on filters
        // CRITICAL: For District Admin OR if district filter is present, show ULB level graph
        if (districtAdminDistrictName || filters.district || filters.district_id) {
          // District filter applied or District Admin - show ULB level
          groupByField = ulbField;
          aggregationLevel = "ulb";
          groupLabel = "ULB";
          console.log("📊 District Admin/District filter detected - showing ULB level graph");
        }
        // For Division Admin with no district filter, show district level
        else if (divisionAdminDivisionName) {
          groupByField = districtField;
          aggregationLevel = "district";
          groupLabel = "District";
          console.log("🏢 Division Admin graph - showing DISTRICT level graph");
        }
        // For ULB level filters
        else if (
          filters.ulb_name ||
          filters.ulb_id ||
          filters.ulb_type ||
          filters.ulb_class
        ) {
          aggregationLevel = "record";
          groupLabel = "Record";
        }
        // For Division level filters (non-admin)
        else if (filters.division || filters.division_id) {
          groupByField = districtField;
          aggregationLevel = "district";
          groupLabel = "District";
        }
        // No filters
        else {
          groupByField = divisionField;
          aggregationLevel = "division";
          groupLabel = "Division";
        }

        // ... rest of the function remains the same ...

        let chartData;
        let stats;
        let values;

        if (groupByField && aggregationLevel !== "record") {
          const groupedData = {};
          const allValues = [];

          filteredData.forEach((row) => {
            let groupValue = row[groupByField] || `Unknown ${groupLabel}`;

            // For Division Admin, also verify division match
            if (divisionAdminDivisionName && divisionField) {
              const rowDivision = row[divisionField];
              if (rowDivision && rowDivision !== divisionAdminDivisionName) {
                return; // Skip rows not in this division
              }
            }

            // For District Admin, also verify district match
            if (districtAdminDistrictName && districtField) {
              const rowDistrict = row[districtField];
              if (rowDistrict && rowDistrict !== districtAdminDistrictName) {
                return; // Skip rows not in this district
              }
            }

            // For regular district filter, also verify district match (double-check)
            if (filters.district && districtField && !districtAdminDistrictName) {
              const rowDistrict = row[districtField];
              if (rowDistrict && rowDistrict !== filters.district) {
                return; // Skip rows not in this district
              }
            }

            const value = parseFloat(
              String(row[columnName] || "").replace(/,/g, ""),
            );

            if (!isNaN(value) && isFinite(value)) {
              if (!groupedData[groupValue]) {
                groupedData[groupValue] = {
                  name: groupValue,
                  originalName: groupValue,
                  value: 0,
                  count: 0,
                };
              }

              groupedData[groupValue].value += value;
              groupedData[groupValue].count++;
              allValues.push(value);
            }
          });

          chartData = Object.values(groupedData)
            .map((group) => ({
              name: group.name,
              originalName: group.originalName,
              value: group.value,
              avgValue: group.value / group.count,
              count: group.count,
            }))
            .sort((a, b) =>
              (a.originalName || "").localeCompare(b.originalName || ""),
            );

          const aggregatedValues = chartData.map((d) => d.value);
          stats = {
            min:
              aggregatedValues.length > 0 ? Math.min(...aggregatedValues) : 0,
            max:
              aggregatedValues.length > 0 ? Math.max(...aggregatedValues) : 0,
            avg:
              aggregatedValues.length > 0
                ? aggregatedValues.reduce((sum, v) => sum + v, 0) /
                aggregatedValues.length
                : 0,
            total: aggregatedValues.length,
            sum: aggregatedValues.reduce((sum, v) => sum + v, 0),
            aggregatedBy: groupLabel,
            recordCount: filteredData.length,
          };

          values = allValues;
        } else {
          // ... record level processing ...
          const individualValues = filteredData
            .map((row) => {
              const value = row[columnName];
              if (value === null || value === undefined || value === "")
                return null;

              const strValue = String(value).replace(/,/g, "");
              const numValue = parseFloat(strValue);
              if (!isNaN(numValue) && isFinite(numValue)) {
                return {
                  value: numValue,
                  raw: value,
                  recordId: row._id,
                  division: row[divisionField],
                  district: row[districtField],
                  ulb: row[ulbField],
                  ulb_type: row[ulbTypeField],
                  class: row[classField],
                };
              }
              return null;
            })
            .filter((v) => v !== null);

          if (individualValues.length === 0) {
            setLoadingGraphs((prev) => ({ ...prev, [graphKey]: false }));
            return;
          }

          individualValues.sort((a, b) => a.value - b.value);

          chartData = individualValues.map((item, index) => ({
            name: `Record ${index + 1}`,
            value: item.value,
            raw: item.raw,
            recordId: item.recordId,
            division: item.division,
            district: item.district,
            ulb: item.ulb,
            ulb_type: item.ulb_type,
            class: item.class,
          }));

          stats = {
            min: Math.min(...individualValues.map((v) => v.value)),
            max: Math.max(...individualValues.map((v) => v.value)),
            avg:
              individualValues.reduce((sum, v) => sum + v.value, 0) /
              individualValues.length,
            total: individualValues.length,
            sum: individualValues.reduce((sum, v) => sum + v.value, 0),
            aggregatedBy: "record",
          };

          values = individualValues;
        }

        const generatedGraph = {
          column: columnName,
          displayName: fieldLabels[columnName] || formatFieldName(columnName),
          data: chartData,
          stats: stats,
          values: values,
          aggregationLevel: aggregationLevel,
          groupLabel: groupLabel,
          showGraph: true,
          isGraphReady: true,
          chartType: chartType,
          total: stats.sum,
          divisionFiltered: !!divisionAdminDivisionName,
          divisionName: divisionAdminDivisionName,
          districtFiltered: !!(districtAdminDistrictName || filters.district),
          districtName: districtAdminDistrictName || filters.district,
        };

        setFieldGraphsData((prev) => ({
          ...prev,
          [graphKey]: generatedGraph,
        }));

        return generatedGraph;
      }
    } catch (error) {
      console.error("Error generating column graph:", error);
    } finally {
      setLoadingGraphs((prev) => ({ ...prev, [graphKey]: false }));
    }
    return null;
  };
  const toggleChartType = (graphKey) => {
    setFieldGraphsData((prev) => {
      const currentData = prev[graphKey];
      if (!currentData) return prev;

      const newChartType = currentData.chartType === "bar" ? "pie" : "bar";

      return {
        ...prev,
        [graphKey]: {
          ...currentData,
          chartType: newChartType,
        },
      };
    });
  };

  const hideGraph = (graphKey) => {
    setFieldGraphsData((prev) => ({
      ...prev,
      [graphKey]: { ...prev[graphKey], showGraph: false },
    }));
  };

  const toggleGraph = (graphKey) => {
    setFieldGraphsData((prev) => ({
      ...prev,
      [graphKey]: { ...prev[graphKey], showGraph: !prev[graphKey]?.showGraph },
    }));
  };

const handleFormSelect = async (form, colorIndex = null) => {
  // CRITICAL: Set loading state first
  setLoadingFormData(true);

  // Clear ALL previous form data FIRST before setting new form
  setColumnGraphData(null);
  setHierarchicalGraphs([]);
  setReportCalculations({});
  setFieldGraphsData({});
  setFormGraphData({});
  setLoadedGraphs({});
  setStableGraphData({});
  setLoadingFormGraphs({});

  // Clear analysis results
  setShowAnalysisResults(false);
  setSelectedOperations([]);
  setCalculationResults([]);
  setRawDataForAnalysis([]);
  setAnalysisDataColumns([]);
  setHierarchicalStatistics({});
  setSelectedStatsField(null);
  setBreakdownData(null);
  setSelectedBreakdownField(null);

  // Clear X and Y axis selections
  setXAxisSelectedFields(['auto']);
  setYAxisSelectedFields([]);

  // Clear data point selections
  setSelectedDataPointId(null);
  setSelectedField(null);
  setSelectedFilterForm(null);

  // Clear graph modal
  setGraphModalData(null);
  setGraphModalVisible(false);

  // Reset graph loading flags
  graphLoadingStarted.current = false;
  graphLoadQueueRef.current = [];
  isProcessingQueue.current = false;
  initialGraphsLoadedRef.current = false;

  // Clear any pending timeouts
  Object.values(graphLoadTimeouts).forEach(timeoutId => {
    if (timeoutId) clearTimeout(timeoutId);
  });
  setGraphLoadTimeouts({});

  // Clear form data
  setFormData([]);
  setFormFields([]);

  // Force a small delay to ensure state updates are processed
  await new Promise(resolve => setTimeout(resolve, 50));

  // Now set the new form
  setSelectedForm(form);
  setSelectedFormColorIndex(colorIndex);

  // Fetch field labels
  const labels = await fetchFormFieldLabels(form.id);
  setFieldLabels(labels);

  // Load reference data
  await loadAllReferenceData();

  // Build field hierarchy
  await buildFieldHierarchy(form.id);

  // Reset form column search
  setFormColumnSearchTerm("");
  setSearchingFormColumns(false);

  // Reset form data
  setOriginalFormData([]);

  // IMPORTANT: Store front page filters separately but don't apply them automatically
  const savedFrontPageFilters = { ...frontPageFilters };

  // Initialize form filters WITHOUT the front page filters
  const initialFormFilters = {
    division: null,
    district: null,
    ulb_name: null,
    ulb_type: null,
    ulb_class: null,
  };

  console.log("🎯 Initializing form filters with EMPTY filters");
  setFilters(initialFormFilters);
  setFormViewFilters(initialFormFilters);

  // Store the front page filters for reference
  setFrontPageFilters(savedFrontPageFilters);

  // Load filter options with empty filters
  await loadFilterOptions(form.id);

  // Load form data with EMPTY filters
  await loadFormData(form.id, initialFormFilters);

  // Reset field graphs data
  setFieldGraphsData({});

  // Reset modal filters
  setModalFilters({
    district: null,
    division: null,
    ulb: null,
    ulb_type: null,
    ulb_class: null,
    searchText: "",
  });

  // Clear loading state
  setLoadingFormData(false);

  console.log(`✅ Form ${form.id} loaded and refreshed completely`);

  // ========== CHECK USER ROLE ==========
  // DMA, Super Admin, and DMA Desk Officer - show full form view
  // All other users - auto-open View All Data modal

  // Check if user has full access (DMA, Super Admin, DMA Desk Officer)
  const isFullAccessUser = hasFullAccess(); // This now includes DMA_DESK_OFFICER

  if (!isFullAccessUser) {
    // For non-full-access users, auto-open the View All Data modal
    setTimeout(() => {
      setDataModalVisible(true);
      resetModalFilters();

      // Show notification based on user role
      let message = "";
      const isUlbAdmin = !hasFullAccess() && (
        userRole === "ULB_ADMIN" ||
        userRole === "ulb_admin" ||
        userRole === "ULB_OFFICER" ||
        userRole === "ULB_CHIEF_OFFICIER"
      );

      const isDistrictAdmin = !hasFullAccess() && (
        userRole === "DISTRICT_ADMIN" ||
        userRole === "district_admin"
      );

      const isDivisionAdmin = !hasFullAccess() && (
        userRole === "DIVISION_ADMIN" ||
        userRole === "division_admin" ||
        userRole === "DIVISIONAL_OFFICER" ||
        userRole === "divisional_officer" ||
        userRole === "Divisionalcommissioner"
      );

      if (isUlbAdmin) {
        message = "Showing data for your ULB only. Use filters to explore other records if permitted.";
      } else if (isDistrictAdmin) {
        message = "Showing data for your district only. Use filters to explore other records if permitted.";
      } else if (isDivisionAdmin) {
        message = "Showing data for your division only. Use filters to explore other records if permitted.";
      } else {
        message = "Showing all data for this form. Use filters to narrow down results.";
      }

      notification.info({
        message: "Data Loaded",
        description: message,
        duration: 4,
      });
    }, 300);
  }
  // For DMA, Super Admin, and DMA Desk Officer, they see the full form view (no auto-modal)
};
  // Add this useEffect to handle ULB user auto-fullscreen when form is selected
  useEffect(() => {
    // Check if user is ULB Admin
    const isUlbAdmin = !hasFullAccess() && (
      userRole === "ULB_ADMIN" ||
      userRole === "ulb_admin" ||
      userRole === "ULB_OFFICER" ||
      userRole === "ULB_CHIEF_OFFICIER"
    );

    // Check if user is District Admin
    const isDistrictAdmin = !hasFullAccess() && (
      userRole === "DISTRICT_ADMIN" ||
      userRole === "district_admin"
    );

    // Check if user is Division Admin
    const isDivisionAdmin = !hasFullAccess() && (
      userRole === "DIVISION_ADMIN" ||
      userRole === "division_admin" ||
      userRole === "DIVISIONAL_OFFICER" ||
      userRole === "divisional_officer" ||
      userRole === "Divisionalcommissioner"
    );

    // If ULB admin, District Admin, or Division Admin and a form is selected, automatically open the View All Data modal
    if ((isUlbAdmin || isDistrictAdmin || isDivisionAdmin) && selectedForm && !dataModalVisible) {
      // Small delay to ensure form data is loaded
      const timer = setTimeout(() => {
        setDataModalVisible(true);
        resetModalFilters();

        // Optional: Show a notification to let them know data is filtered based on their role
        let message = "";
        if (isUlbAdmin) {
          message = "Showing data for your ULB only. Use filters to explore other records if permitted.";
        } else if (isDistrictAdmin) {
          message = "Showing data for your district only. Use filters to explore other records if permitted.";
        } else if (isDivisionAdmin) {
          message = "Showing data for your division only. Use filters to explore other records if permitted.";
        }

        if (message) {
          notification.info({
            message: "Location Filter Applied",
            description: message,
            duration: 5,
          });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [selectedForm, userRole, dataModalVisible, hasFullAccess]);
  // Update handleUlbTypeChange function
  const handleUlbTypeChange = async (value) => {
    console.log("ULB Type changed to:", value);
    const newFilters = { ...filters, ulb_type: value };
    setFilters(newFilters);
    await loadDependentFilterOptions(
      filters.division,
      filters.district,
      value,
      filters.ulb_class,
    );
    // Add this line to reload form data when filter changes
    if (selectedForm) {
      await loadFormData(selectedForm.id, newFilters);
    }
  };

  // Update handleUlbClassChange function
  const handleUlbClassChange = async (value) => {
    console.log("ULB Class changed to:", value);
    const newFilters = { ...filters, ulb_class: value };
    setFilters(newFilters);
    await loadDependentFilterOptions(
      filters.division,
      filters.district,
      filters.ulb_type,
      value,
    );
    // Add this line to reload form data when filter changes
    if (selectedForm) {
      await loadFormData(selectedForm.id, newFilters);
    }
  };

  // Update handleDivisionChange function
  // Update handleDivisionChange function
  const handleDivisionChange = async (value) => {
    console.log("🏢 Division changed to:", value);

    const newFilters = {
      ...filters,
      division: value,
      division_id: null,
      district: null,  // Reset district when division changes
      district_id: null,
      ulb_name: null,  // Reset ULB when division changes
      ulb_id: null,
    };
    setFilters(newFilters);
    setFormViewFilters(newFilters);

    if (!value) {
      resetFilters();
      return;
    }

    // Get division ID from reference data
    const divisionInfo = Object.values(referenceCache.divisions).find(
      (d) => d.name === value,
    );
    if (divisionInfo) {
      newFilters.division_id = divisionInfo.id;
    }

    // Load dependent filter options (this should populate districts)
    await loadDependentFilterOptions(value, null, null, null);

    // Reload form data with the new division filter
    if (selectedForm) {
      console.log("🔄 Reloading form data with division filter:", value);
      await loadFormData(selectedForm.id, newFilters);
    }

    // Note: The useEffect will automatically trigger performCalculations
  };

  // Update handleDistrictChange function
  const handleDistrictChange = async (value) => {
    console.log("🏢 District changed to:", value);

    const newFilters = {
      ...filters,
      district: value,
      district_id: null,
      ulb_name: null,  // Reset ULB when district changes
      ulb_id: null,
    };
    setFilters(newFilters);

    if (!value) {
      if (filters.division) {
        // If division filter exists, reload districts for that division
        await loadDependentFilterOptions(filters.division, null, null, null);
        // Reload form data with division filter only
        if (selectedForm) {
          await loadFormData(selectedForm.id, {
            ...filters,
            district: null,
            ulb_name: null,
          });
        }
      } else {
        resetFilters();
      }
      return;
    }

    // Get district ID from reference data
    const districtInfo = Object.values(referenceCache.districts).find(
      (d) => d.name === value,
    );
    if (districtInfo) {
      newFilters.district_id = districtInfo.id;
    }

    // Load ULBs for this district
    await loadDependentFilterOptions(filters.division, value, null, null);

    // Reload form data with the new district filter
    if (selectedForm) {
      console.log("🔄 Reloading form data with district filter:", value);
      await loadFormData(selectedForm.id, newFilters);
    }

    // Note: The useEffect will automatically trigger performCalculations
  };

  // Update handleUlbChange function
  const handleUlbChange = async (value) => {
    console.log("🏢 ULB changed to:", value);

    const newFilters = { ...filters, ulb_name: value, ulb_id: null };
    setFilters(newFilters);

    if (!value) {
      if (filters.district) {
        // Reload with district filter only
        const districtFilters = {
          ...newFilters,
          district: filters.district,
          district_id: filters.district_id,
        };
        if (selectedForm) {
          await loadFormData(selectedForm.id, districtFilters);
        }
      } else if (filters.division) {
        // Reload with division filter only
        const divisionFilters = {
          ...newFilters,
          division: filters.division,
          division_id: filters.division_id,
        };
        if (selectedForm) {
          await loadFormData(selectedForm.id, divisionFilters);
        }
      } else {
        // Reset to no location filter
        resetFilters();
      }
      return;
    }

    // Important: Always reload form data with the updated filters
    if (selectedForm) {
      await loadFormData(selectedForm.id, newFilters);
    }

    // Note: The useEffect will automatically trigger performCalculations
  };

  const resetFilters = () => {
    const newFilters = {
      district: null,
      district_id: null,
      division: null,
      division_id: null,
      ulb_name: null,
      ulb_id: null,
      ulb_type: null,
      ulb_class: null,
    };

    // Set filters to empty
    setFilters(newFilters);
    setFormViewFilters(newFilters);

    // Also reset selected data point
    setSelectedDataPointId(null);

    // Force reset of available filters options to trigger UI update
    setAvailableFilters({
      districts: [],
      divisions: [],
      ulbs: [],
    });
    setUlbTypes([]);
    setUlbClasses([]);

    // Clear any selected operations and analysis results
    setSelectedOperations([]);
    setCalculationResults([]);
    setShowAnalysisResults(false);
    setRawDataForAnalysis([]);
    setAnalysisDataColumns([]);

    if (selectedForm) {
      // Reload filter options with empty filters
      loadFilterOptions(selectedForm.id);
      // Reload form data with empty filters
      loadFormData(selectedForm.id, newFilters);
    }

    console.log(":white_check_mark: Filters reset to empty:", newFilters);
  };

  const applyFilters = () => {
    if (selectedForm) loadFormData(selectedForm.id, filters);
  };

  const toggleAggregated = () => {
    setShowAggregated(!showAggregated);
    if (selectedGraphColumn) generateColumnGraph(selectedGraphColumn);
  };

  const resetModalFilters = () => {
    setModalFilters({
      district: null,
      division: null,
      ulb: null,
      ulb_type: null,
      ulb_class: null,
      searchText: "",
    });
    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleGraphClick = (graph) => {
    // This function is kept for compatibility but no longer used
  };

  // ==================== HIERARCHICAL GRAPH FUNCTIONS ====================
  const generateHierarchicalGraph = (data, columnName, displayName) => {
    if (!data || data.length === 0) return;

    // Determine current graph level based on filters
    let currentLevel = "division";
    let groupByField = "division_name";
    let groupLabel = "Division";

    if (selectedDistrictForGraph) {
      currentLevel = "ulb";
      groupByField = "ulb_name";
      groupLabel = "ULB";
    } else if (selectedDivisionForGraph) {
      currentLevel = "district";
      groupByField = "district_name";
      groupLabel = "District";
    } else if (frontPageFilters.ulb_name) {
      currentLevel = "record";
      groupByField = null;
      groupLabel = "Record";
    } else if (frontPageFilters.district) {
      currentLevel = "ulb";
      groupByField = "ulb_name";
      groupLabel = "ULB";
    } else if (frontPageFilters.division) {
      currentLevel = "district";
      groupByField = "district_name";
      groupLabel = "District";
    }

    // Apply additional filters
    let filteredData = [...data];

    if (selectedDivisionForGraph) {
      filteredData = filteredData.filter(
        (row) =>
          row.division_name === selectedDivisionForGraph ||
          row.division === selectedDivisionForGraph,
      );
    }

    if (selectedDistrictForGraph) {
      filteredData = filteredData.filter(
        (row) =>
          row.district_name === selectedDistrictForGraph ||
          row.district === selectedDistrictForGraph,
      );
    }

    if (frontPageFilters.division && !selectedDivisionForGraph) {
      filteredData = filteredData.filter(
        (row) =>
          row.division_name === frontPageFilters.division ||
          row.division === frontPageFilters.division,
      );
    }

    if (frontPageFilters.district && !selectedDistrictForGraph) {
      filteredData = filteredData.filter(
        (row) =>
          row.district_name === frontPageFilters.district ||
          row.district === frontPageFilters.district,
      );
    }

    if (frontPageFilters.ulb_name) {
      filteredData = filteredData.filter(
        (row) =>
          row.ulb_name === frontPageFilters.ulb_name ||
          row.ulb === frontPageFilters.ulb_name,
      );
    }

    if (frontPageFilters.ulb_type) {
      filteredData = filteredData.filter(
        (row) => row.ulb_type === frontPageFilters.ulb_type,
      );
    }

    if (frontPageFilters.ulb_class) {
      filteredData = filteredData.filter(
        (row) => row.class === frontPageFilters.ulb_class,
      );
    }

    if (filteredData.length === 0) {
      setHierarchicalGraphData(null);
      return;
    }

    let chartData = [];
    let stats = {};

    if (groupByField && currentLevel !== "record") {
      const groupedData = {};
      filteredData.forEach((row) => {
        let groupValue = row[groupByField] || `Unknown ${groupLabel}`;
        const value = parseFloat(
          String(row[columnName] || "").replace(/,/g, ""),
        );

        if (!isNaN(value) && isFinite(value)) {
          if (!groupedData[groupValue]) {
            groupedData[groupValue] = {
              name: groupValue,
              value: 0,
              count: 0,
              values: [],
            };
          }
          groupedData[groupValue].value += value;
          groupedData[groupValue].count++;
          groupedData[groupValue].values.push(value);
        }
      });

      chartData = Object.values(groupedData)
        .map((group) => ({
          name: group.name,
          value: group.value,
          average: group.value / group.count,
          count: group.count,
          min: Math.min(...group.values),
          max: Math.max(...group.values),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const values = chartData.map((d) => d.value);
      stats = {
        totalSum: values.reduce((a, b) => a + b, 0),
        average:
          values.length > 0
            ? values.reduce((a, b) => a + b, 0) / values.length
            : 0,
        min: values.length > 0 ? Math.min(...values) : 0,
        max: values.length > 0 ? Math.max(...values) : 0,
        groupCount: chartData.length,
        recordCount: filteredData.length,
      };
    } else {
      chartData = filteredData
        .map((row, index) => {
          const value = parseFloat(
            String(row[columnName] || "").replace(/,/g, ""),
          );
          if (!isNaN(value) && isFinite(value)) {
            return {
              name: `Record ${index + 1}`,
              value: value,
              originalName: `Record ${index + 1}`,
              division: row.division_name || row.division,
              district: row.district_name || row.district,
              ulb: row.ulb_name || row.ulb,
              ulb_type: row.ulb_type,
              class: row.class,
            };
          }
          return null;
        })
        .filter((v) => v !== null);

      const values = chartData.map((d) => d.value);
      stats = {
        totalSum: values.reduce((a, b) => a + b, 0),
        average:
          values.length > 0
            ? values.reduce((a, b) => a + b, 0) / values.length
            : 0,
        min: values.length > 0 ? Math.min(...values) : 0,
        max: values.length > 0 ? Math.max(...values) : 0,
        groupCount: chartData.length,
        recordCount: filteredData.length,
      };
    }

    setHierarchicalGraphData({
      data: chartData,
      level: currentLevel,
      label: groupLabel,
      columnName: columnName,
      displayName: displayName,
      totalSum: stats.totalSum,
      average: stats.average,
      recordCount: stats.recordCount,
      groupCount: stats.groupCount,
      min: stats.min,
      max: stats.max,
    });
  };

  const handleBarClick = (data) => {
    if (hierarchicalGraphData && hierarchicalGraphData.level === "division") {
      setSelectedDivisionForGraph(data.name);
      setSelectedDistrictForGraph(null);
      generateHierarchicalGraph(
        rawDataForAnalysis,
        hierarchicalGraphData.columnName,
        hierarchicalGraphData.displayName,
      );
    } else if (
      hierarchicalGraphData &&
      hierarchicalGraphData.level === "district"
    ) {
      setSelectedDistrictForGraph(data.name);
      generateHierarchicalGraph(
        rawDataForAnalysis,
        hierarchicalGraphData.columnName,
        hierarchicalGraphData.displayName,
      );
    }
  };

  const goBackInGraph = () => {
    if (selectedDistrictForGraph) {
      setSelectedDistrictForGraph(null);
      generateHierarchicalGraph(
        rawDataForAnalysis,
        hierarchicalGraphData.columnName,
        hierarchicalGraphData.displayName,
      );
    } else if (selectedDivisionForGraph) {
      setSelectedDivisionForGraph(null);
      generateHierarchicalGraph(
        rawDataForAnalysis,
        hierarchicalGraphData.columnName,
        hierarchicalGraphData.displayName,
      );
    }
  };

  // ==================== ANALYSIS FUNCTIONS ====================
  const loadFilterForms = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/reports/forms`, {
        timeout: 500000,
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      });

      if (response.data.success) {
        let formsData = response.data.forms || [];
        formsData = formsData.filter((form) => {
          const formId = parseInt(form.id);
          return formId !== 100;
        });

        setForms(formsData);
        return formsData;
      }
    } catch (error) {
      console.error("Error loading forms for filter:", error);

    }
  };

  const loadFilterFormFields = async (formId) => {
    try {
      setLoadingReportFields(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema?report_enabled=true`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.reportEnabledFields) {
        setFilterFormFields(response.data.reportEnabledFields);
        return response.data.reportEnabledFields;
      }
      return [];
    } catch (error) {
      console.error("Error fetching report-enabled fields:", error);

      return [];
    } finally {
      setLoadingReportFields(false);
    }
  };


  const addOperation = () => {
    if (!selectedField) {
      notification.warning({
        message: "No Data Point Selected",
        description: "Please select a data point first",
        duration: 3,
      });
      return;
    }

    // ✅ FIX: Define formId FIRST (before using it)
    let formId = selectedField.formId;
    if (!formId && selectedForm) {
      formId = parseInt(selectedForm.id);
    }
    if (!formId && selectedFilterForm) {
      formId = parseInt(selectedFilterForm.id);
    }

    if (!formId) {
      console.error("❌ addOperation - No formId found");
      notification.error({
        message: "Error",
        description: "Could not determine form ID for this operation",
        duration: 3,
      });
      return;
    }

    // For percentage operation, we need a second field
    if (selectedOperation === "percentage" && !percentageSecondField) {
      notification.warning({
        message: "Second Data Point Required",
        description: "Please select a second data point to calculate percentage",
        duration: 4,
      });
      return;
    }

    // For ranking operation, we need ranking type
    if (selectedOperation === "ranking") {
      if (!rankingType) {
        notification.warning({
          message: "Ranking Type Required",
          description: "Please select Top, Bottom, or All",
          duration: 4,
        });
        return;
      }

      // Validate ranking count for Top/Bottom
      if (rankingType !== "all") {
        const count = parseInt(rankingCount);
        if (isNaN(count) || count < 1) {
          notification.warning({
            message: "Invalid Number",
            description: "Please enter a valid number of ULBs (minimum 1)",
            duration: 4,
          });
          return;
        }
        if (count > 500) {
          notification.warning({
            message: "Number Too High",
            description: "Maximum number of ULBs is 500",
            duration: 4,
          });
          return;
        }
      }

      // rankingOrder is required for ALL types now
      if (!rankingOrder) {
        notification.warning({
          message: "Sort Order Required",
          description: "Please select Ascending or Descending order",
          duration: 4,
        });
        return;
      }
    }

    // Check if threshold is needed for LTE/GTE operations
    if (selectedOperation === "lte" || selectedOperation === "gte") {
      const threshold = operationThresholds[selectedField.field_id];
      if (!threshold && threshold !== 0) {
        notification.warning({
          message: "Threshold Value Required",
          description: `Please enter a threshold value for ${selectedOperation === "lte" ? "≤" : "≥"} operation`,
          duration: 4,
        });
        return;
      }

      if (isNaN(parseFloat(threshold))) {
        notification.warning({
          message: "Invalid Threshold",
          description: "Please enter a valid numeric threshold value",
          duration: 3,
        });
        return;
      }
    }

    // ✅ NOW create the newOperation object (formId is already defined)
    const newOperation = {
      id: Date.now(),
      fieldId: selectedField.field_id,
      fieldName: selectedField.display_name || selectedField.field_id,
      operation: selectedOperation,
      columnName: selectedField.column_name || selectedField.field_id,
      formId: formId,
      formName: selectedField.formDisplayName || (selectedForm ? selectedForm.displayName : selectedField.formName || `Form ${formId}`),
      threshold: (selectedOperation === "lte" || selectedOperation === "gte")
        ? parseFloat(operationThresholds[selectedField.field_id])
        : null,
      percentageSecondFieldId: selectedOperation === "percentage" ? percentageSecondField?.field_id : null,
      percentageSecondFieldName: selectedOperation === "percentage" ? percentageSecondField?.display_name || percentageSecondField?.field_id : null,
      percentageSecondColumnName: selectedOperation === "percentage" ? percentageSecondField?.column_name || percentageSecondField?.field_id : null,
      percentageSecondFormId: selectedOperation === "percentage" ? (percentageSecondField?.formId || formId) : null,
      rankingType: selectedOperation === "ranking" ? rankingType : null,
      rankingOrder: selectedOperation === "ranking" ? rankingOrder : null,
      rankingCount: selectedOperation === "ranking" && rankingType !== "all" ? parseInt(rankingCount) : null,
    };

    console.log("✅ addOperation - Created operation:", newOperation);

    setSelectedOperations([...selectedOperations, newOperation]);
    setSelectedOperation("sum");
    setShowPercentageSecondField(false);
    setPercentageSecondField(null);
    setShowRankingOptions(false);
    setRankingType("top");
    setRankingOrder("desc");
    setRankingCount(10);
    setShowRankingCountInput(false);

    // Clear threshold for this field after adding
    if (selectedOperation === "lte" || selectedOperation === "gte") {
      setOperationThresholds(prev => {
        const newThresholds = { ...prev };
        delete newThresholds[selectedField.field_id];
        return newThresholds;
      });
    }
  };
  const removeOperation = (id) => {
    setSelectedOperations(selectedOperations.filter((op) => op.id !== id));
  };

  const clearAllOperations = () => {
    setSelectedOperations([]);
    setCalculationResults([]);
    setShowAnalysisResults(false);
    setRawDataForAnalysis([]);
    setAnalysisDataColumns([]);
    setHierarchicalGraphData(null);
    setSelectedDivisionForGraph(null);
    setSelectedDistrictForGraph(null);

    setRankingType("top");
    setRankingOrder("desc");
    setRankingCount(10);
    setShowRankingCountInput(false);
    setShowRankingOptions(false);
  };

  const performCalculations = async () => {
    // Validate thresholds for LTE/GTE operations
    const hasInvalidThreshold = selectedOperations.some(op => {
      if (op.operation === "lte" || op.operation === "gte") {
        // Validation logic if needed
      }
      return false;
    });

    if (hasInvalidThreshold) return;

    // Save current X and Y selections before calculations (to preserve them)
    const savedXSelections = [...xAxisSelectedFields];
    const savedYSelections = [...yAxisSelectedFields];
    const savedChartType = jointChartType;

    console.log("💾 Saved selections before calculations:", {
      xAxis: savedXSelections,
      yAxis: savedYSelections,
      chartType: savedChartType
    });

    try {
      setLoadingCalculation(true);
      // Clear previous results
      setCalculationResults([]);
      setRawDataForAnalysis([]);
      setHierarchicalGraphData(null);
      setHierarchicalStatistics({});
      setSelectedStatsField(null);

      // CRITICAL FIX: Ensure each operation has a valid formId
      const validatedOperations = selectedOperations.map(op => {
        if (!op.formId && selectedForm) {
          console.log(`🔧 Fixing operation "${op.fieldName}" - adding formId from selectedForm: ${selectedForm.id}`);
          return { ...op, formId: parseInt(selectedForm.id) };
        }
        return op;
      });

      // Group operations by form ID
      const operationsByForm = {};
      validatedOperations.forEach(op => {
        const formIdKey = String(op.formId);
        if (!operationsByForm[formIdKey]) {
          operationsByForm[formIdKey] = [];
        }
        operationsByForm[formIdKey].push(op);
      });

      console.log("📊 Operations grouped by form:", operationsByForm);
      console.log("📊 Available forms:", forms.map(f => ({ id: f.id, name: f.displayName, type: typeof f.id })));

      // Determine which filters to use based on context
      const isInsideForm = selectedForm !== null;
      const baseFilters = isInsideForm ? filters : frontPageFilters;

      console.log("🔍 performCalculations - Context:", {
        isInsideForm,
        selectedFormId: selectedForm?.id,
        filtersToUse: {
          division: baseFilters.division,
          district: baseFilters.district,
          ulb_type: baseFilters.ulb_type,
          ulb_class: baseFilters.ulb_class,
          ulb_name: baseFilters.ulb_name
        }
      });

      // Fetch data for each form separately
      const allResults = [];
      let combinedRawData = [];
      let allColumns = new Set();

      // Store percentage results per ULB for threshold filtering
      const percentageResultsMap = new Map(); // Map of fieldId -> Map of ulbName -> {percentage, totalValue, district, division}

      for (const [formIdKey, ops] of Object.entries(operationsByForm)) {
        // Try multiple ways to find the form
        let form = null;

        // Method 1: Direct string comparison
        form = forms.find(f => String(f.id) === String(formIdKey));

        // Method 2: Numeric comparison
        if (!form) {
          const numericFormId = parseInt(formIdKey);
          if (!isNaN(numericFormId)) {
            form = forms.find(f => parseInt(f.id) === numericFormId);
          }
        }

        // Method 3: Check if formId matches any form's id as string
        if (!form) {
          form = forms.find(f => f.id?.toString() === formIdKey || f.id === formIdKey);
        }

        // Method 4: If inside a form and still not found, use the selectedForm
        if (!form && isInsideForm && selectedForm) {
          form = selectedForm;
          console.log(`🔧 Using selectedForm as fallback for formId ${formIdKey}: ${form.id}`);
        }

        if (!form) {
          console.error(`❌ Form ${formIdKey} not found in forms list.`);
          console.log("Available forms:", forms.map(f => ({ id: f.id, name: f.displayName, type: typeof f.id })));
          continue;
        }

        console.log(`✅ Found form ${form.id} (${form.displayName}) for operations:`, ops.map(o => o.fieldName));

        const params = new URLSearchParams({
          limit: "10000",
          all_fields: "true",
        });

        // Apply filters to API params
        if (baseFilters.division) params.set("division", baseFilters.division);
        if (baseFilters.district) params.set("district", baseFilters.district);
        if (baseFilters.ulb_type) params.set("ulb_type", baseFilters.ulb_type);
        if (baseFilters.ulb_class) params.set("ulb_class", baseFilters.ulb_class);
        if (baseFilters.ulb_name) params.set("ulb_name", baseFilters.ulb_name);

        console.log(`📡 Fetching data for form ${form.id} with params:`, Object.fromEntries(params));

        const response = await axios.get(
          `${API_BASE_URL}/api/reports/forms/${form.id}/data?${params}`,
          {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
          },
        );

        if (response.data.success) {
          let data = response.data.data || [];
          data = resolveIdsInData(data);

          // Filter to only approved submissions if workflow_action column exists
          const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];
          if (hasWorkflowColumn) {
            const originalCount = data.length;
            data = data.filter(row => {
              const action = row.status;
              return action === 'approve' ||
                action === 'approved' ||
                action === 'APPROVED' ||
                action === 'Approve' ||
                action === 'APPROVE';
            });
            console.log(`📊 Form ${form.id}: Filtered to ${data.length} approved submissions (from ${originalCount} total)`);
          }

          let filteredData = [...data];

          // Apply client-side filtering
          if (baseFilters.division) {
            const divisionValue = baseFilters.division;
            filteredData = filteredData.filter(
              (row) => (row.division_name || row.division) === divisionValue
            );
            console.log(`  ✅ Division filter applied: ${filteredData.length} records`);
          }
          if (baseFilters.district) {
            const districtValue = baseFilters.district;
            filteredData = filteredData.filter(
              (row) => (row.district_name || row.district) === districtValue
            );
            console.log(`  ✅ District filter applied: ${filteredData.length} records`);
          }
          if (baseFilters.ulb_type) {
            filteredData = filteredData.filter(
              (row) => row.ulb_type === baseFilters.ulb_type
            );
          }
          if (baseFilters.ulb_class) {
            filteredData = filteredData.filter(
              (row) => row.class === baseFilters.ulb_class
            );
          }
          if (baseFilters.ulb_name) {
            const ulbValue = baseFilters.ulb_name;
            filteredData = filteredData.filter(
              (row) => row.ulb_name === ulbValue || row.ulb === ulbValue
            );
          }

          if (filteredData.length === 0) {
            console.warn(`⚠️ No data found for form ${form.id} after filtering`);
            for (const operation of ops) {
              allResults.push({
                ...operation,
                result: 0,
                error: "No data available with current filters",
                details: null,
                rawData: [],
                recordCount: 0,
                totalRecords: 0,
              });
            }
            continue;
          }

          console.log(`✅ Form ${form.id}: ${filteredData.length} records available`);

          // Add to combined raw data (for location columns)
          combinedRawData = [...combinedRawData, ...filteredData];

          // FIRST PASS: Process percentage operations to build percentage maps
          for (const operation of ops) {
            if (operation.operation === "percentage" && operation.percentageSecondColumnName) {
              const ulbData = {};

              filteredData.forEach((row) => {
                const ulbName = row.ulb_name || row.ulb || "Unknown ULB";
                const districtName = row.district_name || row.district || "Unknown District";
                const divisionName = row.division_name || row.division || "Unknown Division";

                const value1 = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
                const value2 = parseFloat(String(row[operation.percentageSecondColumnName] || "").replace(/,/g, ""));

                if (!isNaN(value1) && isFinite(value1) && !isNaN(value2) && isFinite(value2) && value2 !== 0) {
                  if (!ulbData[ulbName]) {
                    ulbData[ulbName] = {
                      sum1: 0,
                      sum2: 0,
                      count: 0,
                      division: divisionName,
                      district: districtName,
                      ulb: ulbName
                    };
                  }
                  ulbData[ulbName].sum1 += value1;
                  ulbData[ulbName].sum2 += value2;
                  ulbData[ulbName].count++;
                }
              });

              const ulbPercentageMap = new Map();
              const percentages = [];

              Object.values(ulbData).forEach((data) => {
                const percentage = (data.sum1 / data.sum2) * 100;
                const cappedPercentage = Math.min(percentage, 100);

                percentages.push({
                  ulb: data.ulb,
                  district: data.district,
                  division: data.division,
                  percentage: cappedPercentage,
                  sum1: data.sum1,
                  sum2: data.sum2
                });

                ulbPercentageMap.set(data.ulb, {
                  percentage: cappedPercentage,
                  totalValue: data.sum1,
                  district: data.district,
                  division: data.division
                });
              });

              percentageResultsMap.set(operation.fieldId, ulbPercentageMap);

              // Store the percentage map in the operation for later use
              operation.ulbPercentageMap = ulbPercentageMap;
            }
          }

          // SECOND PASS: Process all operations
          for (const operation of ops) {
            let result = 0;
            let details = null;
            let rawData = [];

            const values = filteredData
              .map((row) => {
                const val = row[operation.columnName];
                if (val === undefined || val === null || val === "") return null;
                const strVal = String(val).replace(/,/g, "");
                const numVal = parseFloat(strVal);
                return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
              })
              .filter((v) => v !== null);

            switch (operation.operation) {
              case "sum":
                result = values.reduce((a, b) => a + b, 0);
                details = { total: result, count: values.length };
                rawData = values.map((v, i) => ({ index: i + 1, value: v }));
                break;
              case "avg":
                result = values.reduce((a, b) => a + b, 0) / values.length;
                details = { average: result, count: values.length, sum: values.reduce((a, b) => a + b, 0) };
                rawData = values.map((v, i) => ({ index: i + 1, value: v }));
                break;
              case "min":
                const minValue = Math.min(...values);
                const minIndex = filteredData.findIndex(record => {
                  const val = record[operation.columnName];
                  if (val === undefined || val === null) return false;
                  const numVal = parseFloat(String(val).replace(/,/g, ""));
                  return !isNaN(numVal) && numVal === minValue;
                });
                const minRecord = minIndex !== -1 ? filteredData[minIndex] : filteredData[0];
                result = minValue;
                details = {
                  minimum: minValue,
                  count: values.length,
                  location: {
                    division: minRecord?.division_name || minRecord?.division || "N/A",
                    district: minRecord?.district_name || minRecord?.district || "N/A",
                    ulb: minRecord?.ulb_name || minRecord?.ulb || "N/A",
                  },
                };
                rawData = values.map((v, i) => ({ index: i + 1, value: v, isMin: v === minValue }));
                break;
              case "max":
                const maxValue = Math.max(...values);
                const maxIndex = filteredData.findIndex(record => {
                  const val = record[operation.columnName];
                  if (val === undefined || val === null) return false;
                  const numVal = parseFloat(String(val).replace(/,/g, ""));
                  return !isNaN(numVal) && numVal === maxValue;
                });
                const maxRecord = maxIndex !== -1 ? filteredData[maxIndex] : filteredData[0];
                result = maxValue;
                details = {
                  maximum: maxValue,
                  count: values.length,
                  location: {
                    division: maxRecord?.division_name || maxRecord?.division || "N/A",
                    district: maxRecord?.district_name || maxRecord?.district || "N/A",
                    ulb: maxRecord?.ulb_name || maxRecord?.ulb || "N/A",
                  },
                };
                rawData = values.map((v, i) => ({ index: i + 1, value: v, isMax: v === maxValue }));
                break;
              case "count":
                result = values.length;
                details = { count: result };
                rawData = values.map((v, i) => ({ index: i + 1, value: v }));
                break;
              case "lte":
              case "gte": {
                const threshold = parseFloat(operation.threshold);
                const isLte = operation.operation === "lte";

                // IMPORTANT: Check if there's a percentage operation with the SAME field name
                // This allows "Total SC Population" threshold to apply to the percentage of "Total SC Population / Population"
                let matchingUlbs = [];
                let matchingPercentageValues = [];

                // Find if this field has a corresponding percentage operation
                const matchingPercentageOp = validatedOperations.find(op =>
                  op.operation === "percentage" &&
                  (op.fieldId === operation.fieldId || op.fieldName === operation.fieldName)
                );

                if (matchingPercentageOp && percentageResultsMap.has(matchingPercentageOp.fieldId)) {
                  // This threshold applies to the PERCENTAGE of this field
                  const percentageMap = percentageResultsMap.get(matchingPercentageOp.fieldId);
                  console.log(`📊 Applying threshold ${operation.operation} ${threshold} to percentage of ${operation.fieldName}`);

                  for (const [ulbName, percentData] of percentageMap.entries()) {
                    const percentage = percentData.percentage;
                    const isMatching = isLte ? percentage <= threshold : percentage >= threshold;
                    if (isMatching) {
                      matchingUlbs.push({
                        ulb: ulbName,
                        district: percentData.district,
                        division: percentData.division,
                        percentage: percentage,
                        totalValue: percentData.totalValue
                      });
                      matchingPercentageValues.push(percentage);
                    }
                  }

                  result = matchingUlbs.length;
                  details = {
                    count: matchingUlbs.length,
                    threshold: operation.threshold,
                    operator: isLte ? "≤" : "≥",
                    totalULBs: percentageMap.size,
                    matchingPercentage: percentageMap.size > 0 ? ((matchingUlbs.length / percentageMap.size) * 100).toFixed(1) : 0,
                    matchingUlbs: matchingUlbs,
                    isPercentageThreshold: true,
                    fieldName: operation.fieldName,
                    percentageFieldName: matchingPercentageOp.fieldName,
                    groupLabel: "ULBs"
                  };
                  rawData = matchingUlbs;
                } else {
                  // Regular threshold on raw numeric values
                  const matchingValues = values.filter(v => isLte ? v <= threshold : v >= threshold);
                  result = matchingValues.length;

                  const matchingRecords = filteredData.filter(row => {
                    const val = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
                    if (isNaN(val)) return false;
                    return isLte ? val <= threshold : val >= threshold;
                  });

                  // Find min/max locations
                  let minLocation = null;
                  let maxLocation = null;
                  if (matchingValues.length > 0) {
                    let minTotal = Infinity;
                    let minTotalRecord = null;
                    let maxTotal = -Infinity;
                    let maxTotalRecord = null;

                    matchingRecords.forEach(record => {
                      const total = parseFloat(String(record[operation.columnName] || "").replace(/,/g, ""));
                      if (!isNaN(total)) {
                        if (total < minTotal) {
                          minTotal = total;
                          minTotalRecord = record;
                        }
                        if (total > maxTotal) {
                          maxTotal = total;
                          maxTotalRecord = record;
                        }
                      }
                    });

                    if (minTotalRecord) {
                      minLocation = {
                        name: minTotalRecord.ulb_name || minTotalRecord.ulb || minTotalRecord.district_name || minTotalRecord.district || "N/A",
                        total: minTotal,
                        division: minTotalRecord.division_name || minTotalRecord.division,
                        district: minTotalRecord.district_name || minTotalRecord.district,
                        ulb: minTotalRecord.ulb_name || minTotalRecord.ulb
                      };
                    }

                    if (maxTotalRecord) {
                      maxLocation = {
                        name: maxTotalRecord.ulb_name || maxTotalRecord.ulb || maxTotalRecord.district_name || maxTotalRecord.district || "N/A",
                        total: maxTotal,
                        division: maxTotalRecord.division_name || maxTotalRecord.division,
                        district: maxTotalRecord.district_name || maxTotalRecord.district,
                        ulb: maxTotalRecord.ulb_name || maxTotalRecord.ulb
                      };
                    }
                  }

                  details = {
                    count: matchingValues.length,
                    threshold: operation.threshold,
                    operator: isLte ? "≤" : "≥",
                    totalValues: values.length,
                    matchingPercentage: values.length > 0 ? ((matchingValues.length / values.length) * 100).toFixed(1) : 0,
                    minLocation: minLocation,
                    maxLocation: maxLocation,
                    groupLabel: "records",
                    isPercentageThreshold: false
                  };
                  rawData = matchingValues.map((v, i) => ({ index: i + 1, value: v }));
                }
                break;
              }
              case "percentage": {
                // Percentage operation - already processed in first pass, just retrieve results
                if (operation.ulbPercentageMap) {
                  const percentages = [];
                  let totalPercentageSum = 0;
                  let minPercentage = Infinity;
                  let maxPercentage = -Infinity;
                  let minUlbData = null;
                  let maxUlbData = null;

                  for (const [ulbName, data] of operation.ulbPercentageMap.entries()) {
                    percentages.push({
                      ulb: ulbName,
                      district: data.district,
                      division: data.division,
                      percentage: data.percentage,
                      value1: data.totalValue
                    });
                    totalPercentageSum += data.percentage;
                    if (data.percentage < minPercentage) {
                      minPercentage = data.percentage;
                      minUlbData = { ulb: ulbName, district: data.district, division: data.division, percentage: data.percentage };
                    }
                    if (data.percentage > maxPercentage) {
                      maxPercentage = data.percentage;
                      maxUlbData = { ulb: ulbName, district: data.district, division: data.division, percentage: data.percentage };
                    }
                  }

                  const avgPercentage = percentages.length > 0 ? totalPercentageSum / percentages.length : 0;
                  result = avgPercentage;
                  details = {
                    averagePercentage: avgPercentage,
                    totalULBs: percentages.length,
                    minPercentage: minPercentage,
                    maxPercentage: maxPercentage,
                    minLocation: minUlbData,
                    maxLocation: maxUlbData,
                    percentagesData: percentages,
                    field1Name: operation.fieldName,
                    field2Name: operation.percentageSecondFieldName
                  };
                  rawData = percentages;
                } else {
                  result = 0;
                  details = { error: "No valid data for percentage calculation" };
                }
                break;
              }
              case "ranking":
                if (filteredData.length > 0) {
                  const ulbMap = new Map();

                  filteredData.forEach((row) => {
                    const ulbName = row.ulb_name || row.ulb || "Unknown ULB";
                    const districtName = row.district_name || row.district || "Unknown District";
                    const divisionName = row.division_name || row.division || "Unknown Division";
                    const value = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));

                    if (!isNaN(value) && isFinite(value)) {
                      if (!ulbMap.has(ulbName)) {
                        ulbMap.set(ulbName, {
                          ulb: ulbName,
                          district: districtName,
                          division: divisionName,
                          total: 0,
                          count: 0,
                          records: []
                        });
                      }
                      const ulbData = ulbMap.get(ulbName);
                      ulbData.total += value;
                      ulbData.count++;
                      ulbData.records.push(value);
                    }
                  });

                  let ulbArray = Array.from(ulbMap.values()).map(item => ({
                    ulb: item.ulb,
                    district: item.district,
                    division: item.division,
                    value: item.total,
                    count: item.count,
                    average: item.total / item.count
                  }));

                  const rankingTypeValue = operation.rankingType;
                  const rankingOrderValue = operation.rankingOrder; // 'asc' or 'desc'
                  const rankingCountValue = operation.rankingCount || 10;

                  // ✅ FIXED: Enhanced sorting logic for all ranking types
                  if (rankingTypeValue === "top") {
                    // For TOP: Show highest values first (descending) or lowest first (ascending)
                    if (rankingOrderValue === "desc") {
                      // Highest first (traditional Top)
                      ulbArray.sort((a, b) => b.value - a.value);
                    } else {
                      // Lowest first (unusual for Top, but supported)
                      ulbArray.sort((a, b) => a.value - b.value);
                    }
                    ulbArray = ulbArray.slice(0, rankingCountValue);
                  }
                  else if (rankingTypeValue === "bottom") {
                    // For BOTTOM: Show lowest values first (ascending) or highest first (descending)
                    if (rankingOrderValue === "asc") {
                      // Lowest first (traditional Bottom)
                      ulbArray.sort((a, b) => a.value - b.value);
                    } else {
                      // Highest first (unusual for Bottom, but supported)
                      ulbArray.sort((a, b) => b.value - a.value);
                    }
                    ulbArray = ulbArray.slice(0, rankingCountValue);
                  }
                  else if (rankingTypeValue === "all") {
                    // For "all", use the selected order
                    if (rankingOrderValue === "desc") {
                      ulbArray.sort((a, b) => b.value - a.value);
                    } else {
                      ulbArray.sort((a, b) => a.value - b.value);
                    }
                  }

                  const appliedFiltersSummary = [];
                  if (baseFilters.division) appliedFiltersSummary.push(`Division: ${baseFilters.division}`);
                  if (baseFilters.district) appliedFiltersSummary.push(`District: ${baseFilters.district}`);
                  if (baseFilters.ulb_type) appliedFiltersSummary.push(`ULB Type: ${baseFilters.ulb_type}`);
                  if (baseFilters.ulb_class) appliedFiltersSummary.push(`ULB Class: ${baseFilters.ulb_class}`);
                  if (baseFilters.ulb_name) appliedFiltersSummary.push(`ULB: ${baseFilters.ulb_name}`);

                  result = ulbArray.length;

                  // Create descriptive title based on selections
                  let rankingTitle = "";
                  if (rankingTypeValue === "top") {
                    rankingTitle = rankingOrderValue === "desc"
                      ? `Top ${rankingCountValue} ULBs by ${operation.fieldName} (Highest to Lowest)`
                      : `Top ${rankingCountValue} ULBs by ${operation.fieldName} (Lowest to Highest)`;
                  } else if (rankingTypeValue === "bottom") {
                    rankingTitle = rankingOrderValue === "asc"
                      ? `Bottom ${rankingCountValue} ULBs by ${operation.fieldName} (Lowest to Highest)`
                      : `Bottom ${rankingCountValue} ULBs by ${operation.fieldName} (Highest to Lowest)`;
                  } else {
                    rankingTitle = rankingOrderValue === "desc"
                      ? `All ULBs by ${operation.fieldName} (Highest to Lowest)`
                      : `All ULBs by ${operation.fieldName} (Lowest to Highest)`;
                  }

                  details = {
                    rankingType: rankingTypeValue,
                    rankingOrder: rankingOrderValue,
                    rankingCount: rankingCountValue,
                    data: ulbArray,
                    fieldName: operation.fieldName,
                    totalULBs: ulbMap.size,
                    totalRecords: filteredData.length,
                    appliedFilters: appliedFiltersSummary,
                    rankingTitle: rankingTitle,
                    operation: "sum"
                  };
                  rawData = ulbArray;
                } else {
                  result = 0;
                  details = { error: "No data available for ranking" };
                }
                break;
              default:
                result = values.reduce((a, b) => a + b, 0);
                details = { total: result, count: values.length };
            }

            allResults.push({
              ...operation,
              result: result,
              error: null,
              details: details,
              rawData: rawData,
              recordCount: typeof result === 'number' ? result : (result?.length || 0),
              totalRecords: filteredData.length,
            });

            allColumns.add(operation.columnName);
          }

          // Add location columns from this form's data
          if (filteredData.length > 0) {
            const firstRow = filteredData[0];
            if (firstRow.division_name || firstRow.division) allColumns.add("division_name");
            if (firstRow.district_name || firstRow.district) allColumns.add("district_name");
            if (firstRow.ulb_name || firstRow.ulb) allColumns.add("ulb_name");
            if (firstRow.ulb_type) allColumns.add("ulb_type");
            if (firstRow.class) allColumns.add("class");
          }
        } else {
          console.error(`❌ API error for form ${form.id}:`, response.data);
        }
      }

      if (allResults.length === 0) {
        setLoadingCalculation(false);
        return;
      }

      setCalculationResults(allResults);
      setRawDataForAnalysis(combinedRawData);
      setAnalysisDataColumns(Array.from(allColumns).filter(col => col));
      setShowAnalysisResults(true);

      // Restore selections after calculations are complete
      if (savedXSelections.length > 0 || savedYSelections.length > 0) {
        console.log("🔄 Restoring selections after calculations:", {
          xAxis: savedXSelections,
          yAxis: savedYSelections
        });
        setXAxisSelectedFields(savedXSelections);
        setYAxisSelectedFields(savedYSelections);
        setJointChartType(savedChartType);
      }

      // Calculate hierarchical statistics for results that have data
      if (allResults.length > 0 && combinedRawData.length > 0) {
        const statisticsMap = {};
        for (const result of allResults) {
          if (result.operation === "min" || result.operation === "max") {
            const stats = calculateHierarchicalStatistics(combinedRawData, result.columnName, result.fieldName);
            if (stats) {
              statisticsMap[result.fieldId] = {
                ...stats,
                operation: result.operation,
                fieldId: result.fieldId,
                fieldName: result.fieldName,
                formId: result.formId,
              };
            }
          } else if (result.operation === "lte" || result.operation === "gte") {
            if (result.details) {
              statisticsMap[result.fieldId] = {
                fieldDisplayName: result.fieldName,
                operation: result.operation,
                fieldId: result.fieldId,
                fieldName: result.fieldName,
                threshold: result.threshold,
                matchingCount: result.details.count,
                totalCount: result.details.totalULBs || result.details.totalValues || result.totalRecords,
                matchingPercentage: result.details.matchingPercentage,
                groupLabel: result.details.groupLabel || "records",
                minLocation: result.details.minLocation,
                maxLocation: result.details.maxLocation,
                sumOfMatching: result.details.sumOfMatching,
                averageOfMatching: result.details.averageOfMatching,
                isPercentageThreshold: result.details.isPercentageThreshold || false,
                matchingUlbs: result.details.matchingUlbs
              };
            }
          }
        }
        setHierarchicalStatistics(statisticsMap);
        if (allResults.length > 0 && !selectedStatsField) {
          setSelectedStatsField(allResults[0].fieldId);
        }
      }

      const successCount = allResults.filter(r => !r.error).length;
      const errorCount = allResults.filter(r => r.error).length;

    } catch (error) {
      console.error("❌ Error performing calculations:", error);
    } finally {
      setLoadingCalculation(false);
    }
  };
  // Function to get matching ULBs for LTE/GTE condition
  // Function to get matching ULBs for LTE/GTE condition
  const getMatchingUlbs = async (fieldId, columnName, operation, threshold, filtersToUse) => {
    try {
      // Determine which filters to use
      const isInsideFormView = selectedForm !== null;
      const activeFilters = isInsideFormView ? filters : frontPageFilters;

      const params = new URLSearchParams({
        limit: "10000",
        all_fields: "true",
      });

      // Apply filters to API params
      if (activeFilters.division) params.set("division", activeFilters.division);
      if (activeFilters.district) params.set("district", activeFilters.district);
      if (activeFilters.ulb_type) params.set("ulb_type", activeFilters.ulb_type);
      if (activeFilters.ulb_class) params.set("ulb_class", activeFilters.ulb_class);
      if (activeFilters.ulb_name) params.set("ulb_name", activeFilters.ulb_name);

      // Find the form ID for this field
      let formId = null;
      if (isInsideFormView && selectedForm) {
        formId = selectedForm.id;
      } else {
        // Try to find form from calculation results
        const result = calculationResults.find(r => r.fieldId === fieldId);
        if (result) {
          formId = result.formId;
        }
      }

      if (!formId) {
        console.error("Cannot find form ID for field", fieldId);
        return [];
      }

      console.log("📡 Fetching ULBs for condition:", { fieldId, columnName, operation, threshold, formId });

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/data?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.data) {
        let data = response.data.data;
        data = resolveIdsInData(data);

        // Filter to only approved submissions
        const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];
        let approvedData = [...data];

        if (hasWorkflowColumn) {
          approvedData = data.filter(row => {
            const action = row.status;
            return action === 'approve' ||
              action === 'approved' ||
              action === 'APPROVED' ||
              action === 'Approve' ||
              action === 'APPROVE';
          });
        }

        console.log(`📊 Approved data count: ${approvedData.length}`);

        // Apply location filters
        let filteredData = [...approvedData];

        if (activeFilters.division) {
          filteredData = filteredData.filter(
            (row) => (row.division_name || row.division) === activeFilters.division
          );
        }
        if (activeFilters.district) {
          filteredData = filteredData.filter(
            (row) => (row.district_name || row.district) === activeFilters.district
          );
        }
        if (activeFilters.ulb_type) {
          filteredData = filteredData.filter(
            (row) => row.ulb_type === activeFilters.ulb_type
          );
        }
        if (activeFilters.ulb_class) {
          filteredData = filteredData.filter(
            (row) => row.class === activeFilters.ulb_class
          );
        }
        if (activeFilters.ulb_name) {
          filteredData = filteredData.filter(
            (row) => (row.ulb_name || row.ulb) === activeFilters.ulb_name
          );
        }

        console.log(`📊 Filtered data count: ${filteredData.length}`);

        // Group by ULB and calculate totals
        const ulbMap = new Map();

        filteredData.forEach((row) => {
          const ulbName = row.ulb_name || row.ulb || "Unknown ULB";
          const districtName = row.district_name || row.district || "Unknown District";
          const divisionName = row.division_name || row.division || "Unknown Division";

          // Parse the value for this specific field
          let value = 0;
          let hasValue = false;

          // Try to get the value from the row using the column name
          const rawValue = row[columnName];
          if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
            const numVal = parseFloat(String(rawValue).replace(/,/g, ""));
            if (!isNaN(numVal) && isFinite(numVal)) {
              value = numVal;
              hasValue = true;
            }
          }

          // If no value found, also try looking for the field_id as column name
          if (!hasValue) {
            const altValue = row[fieldId];
            if (altValue !== undefined && altValue !== null && altValue !== "") {
              const numVal = parseFloat(String(altValue).replace(/,/g, ""));
              if (!isNaN(numVal) && isFinite(numVal)) {
                value = numVal;
                hasValue = true;
              }
            }
          }

          if (hasValue) {
            const key = `${divisionName}|${districtName}|${ulbName}`;
            if (!ulbMap.has(key)) {
              ulbMap.set(key, {
                ulb: ulbName,
                district: districtName,
                division: divisionName,
                total: 0,
                count: 0,
                values: []
              });
            }
            const ulbData = ulbMap.get(key);
            ulbData.total += value;
            ulbData.count++;
            ulbData.values.push(value);
          }
        });

        console.log(`📊 Total ULBs found: ${ulbMap.size}`);

        // Filter ULBs based on condition
        const matchingUlbs = [];
        const isLte = operation === "lte";
        const thresholdNum = parseFloat(threshold);

        for (const [key, data] of ulbMap.entries()) {
          // For each ULB, check if its TOTAL meets the condition
          const isMatching = isLte ? data.total <= thresholdNum : data.total >= thresholdNum;
          if (isMatching) {
            matchingUlbs.push({
              ulb: data.ulb,
              district: data.district,
              division: data.division,
              total: data.total,
              count: data.count
            });
          }
        }

        console.log(`📊 Matching ULBs: ${matchingUlbs.length}`);

        // Sort by total (highest first for gte, lowest first for lte)
        matchingUlbs.sort((a, b) => isLte ? a.total - b.total : b.total - a.total);

        return matchingUlbs;
      }

      return [];
    } catch (error) {
      console.error("Error fetching matching ULBs:", error);
      return [];
    }
  };
  const clearAnalysis = () => {
    setShowAnalysisResults(false);
    setSelectedOperations([]);
    setCalculationResults([]);
    setSelectedFilterForm(null);
    setSelectedField(null);
    setRawDataForAnalysis([]);
    setAnalysisDataColumns([]);
    setHierarchicalGraphData(null);
    setSelectedDivisionForGraph(null);
    setSelectedDistrictForGraph(null);
    setHierarchicalStatistics({});
    setSelectedStatsField(null);

    // Also reset X and Y axis selections
    setXAxisSelectedFields(['auto']);
    setYAxisSelectedFields([]);

    // CRITICAL: Reset front page filters
    setFrontPageFilters({
      division: null,
      district: null,
      ulb_type: null,
      ulb_class: null,
      ulb_name: null,
    });

    setIsFilterApplied(false);

    console.log(":white_check_mark: Analysis cleared, front page filters reset");
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return "0";
    if (typeof value === "number") {
      if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
      return value.toString();
    }
    return value;
  };
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${value.toFixed(2)}%`;
  };

  // REPLACE the entire graph loading section (from around line 5600 to 5750) with this:

  // State to store graph data for each form
  const [formGraphData, setFormGraphData] = useState({});
  const [loadingFormGraphs, setLoadingFormGraphs] = useState({});

  // Function to fetch and generate graph data for important fields
  const fetchImportantFieldGraph = async (
    formId,
    field,
    filtersToUse = null,
  ) => {
    try {
      const params = new URLSearchParams({
        limit: "10000",
        all_fields: "true",
      });

      const filters = filtersToUse || frontPageFilters;

      if (filters.division) params.set("division", filters.division);
      if (filters.district) params.set("district", filters.district);
      if (filters.ulb_type) params.set("ulb_type", filters.ulb_type);
      if (filters.ulb_class) params.set("ulb_class", filters.ulb_class);
      if (filters.ulb_name) params.set("ulb_name", filters.ulb_name);

      console.log(`📊 Fetching graph data for form ${formId} with filters:`, Object.fromEntries(params));

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/data?${params}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.data) {
        let data = response.data.data;
        data = resolveIdsInData(data);

        let filteredData = [...data];
        if (filters.ulb_type) {
          filteredData = filteredData.filter(
            (row) => row.ulb_type === filters.ulb_type,
          );
        }
        if (filters.ulb_class) {
          filteredData = filteredData.filter(
            (row) => row.class === filters.ulb_class,
          );
        }
        if (filters.division) {
          filteredData = filteredData.filter(
            (row) =>
              row.division_name === filters.division ||
              row.division === filters.division,
          );
        }
        if (filters.district) {
          filteredData = filteredData.filter(
            (row) =>
              row.district_name === filters.district ||
              row.district === filters.district,
          );
        }
        if (filters.ulb_name) {
          filteredData = filteredData.filter(
            (row) =>
              row.ulb_name === filters.ulb_name || row.ulb === filters.ulb_name,
          );
        }

        if (filteredData.length === 0) {
          return {
            data: [],
            total: 0,
            displayName: field.display_name || field.field_id,
            operator: field.operator || "sum",
            recordCount: 0,
            hasData: false,
            appliedFilters: getActiveFiltersList(filters),
            groupLevel: "none",
          };
        }

        // Determine grouping level based on applied filters
        let groupByField = null;
        let groupLabel = "Record";
        let currentLevel = "record";

        if (filters.ulb_name) {
          groupByField = null;
          groupLabel = "Record";
          currentLevel = "record";
        } else if (filters.district) {
          groupByField = "ulb_name";
          groupLabel = "ULB";
          currentLevel = "ulb";
        } else if (filters.division) {
          groupByField = "district_name";
          groupLabel = "District";
          currentLevel = "district";
        } else if (filters.ulb_type || filters.ulb_class) {
          groupByField = "division_name";
          groupLabel = "Division";
          currentLevel = "division";
        } else {
          groupByField = "division_name";
          groupLabel = "Division";
          currentLevel = "division";
        }

        let chartData = [];
        let totalSum = 0;

        if (groupByField && currentLevel !== "record") {
          const groupedData = {};
          filteredData.forEach((row) => {
            let groupValue = row[groupByField] || `Unknown ${groupLabel}`;
            const actualColumnName = field.column_name || field.field_id;
            let value = row[actualColumnName];

            if (value !== undefined && value !== null && value !== "") {
              const numVal = parseFloat(String(value).replace(/,/g, ""));
              if (!isNaN(numVal) && isFinite(numVal)) {
                if (!groupedData[groupValue]) {
                  groupedData[groupValue] = {
                    name: groupValue,
                    value: 0,
                    count: 0,
                    originalName: groupValue,
                  };
                }
                groupedData[groupValue].value += numVal;
                groupedData[groupValue].count++;
              }
            }
          });

          chartData = Object.values(groupedData)
            .map((item) => ({
              name: item.name,
              value: item.value,
              average: item.value / item.count,
              count: item.count,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          totalSum = chartData.reduce((sum, item) => sum + item.value, 0);
        } else {
          chartData = filteredData
            .map((row, index) => {
              const actualColumnName = field.column_name || field.field_id;
              let value = row[actualColumnName];
              const numVal = parseFloat(String(value || "").replace(/,/g, ""));
              if (!isNaN(numVal) && isFinite(numVal)) {
                return {
                  name: `Record ${index + 1}`,
                  value: numVal,
                  division: row.division_name || row.division,
                  district: row.district_name || row.district,
                  ulb: row.ulb_name || row.ulb,
                };
              }
              return null;
            })
            .filter((v) => v !== null);

          totalSum = chartData.reduce((sum, item) => sum + item.value, 0);
        }

        const activeFilters = getActiveFiltersList(filters);

        console.log(
          `✅ Graph data for ${field.display_name}: ${chartData.length} ${groupLabel}s, total records: ${filteredData.length}, level: ${currentLevel}`,
        );

        return {
          data: chartData,
          total: totalSum,
          displayName: field.display_name || field.field_id,
          operator: field.operator || "sum",
          recordCount: filteredData.length,
          hasData: chartData.length > 0,
          appliedFilters: activeFilters,
          groupLevel: currentLevel,
          groupLabel: groupLabel,
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching graph data:", error);
      return null;
    }
  };

  // Helper function to get active filters list
  const getActiveFiltersList = (filters) => {
    const active = [];
    if (filters.division) active.push(`Division: ${filters.division}`);
    if (filters.district) active.push(`District: ${filters.district}`);
    if (filters.ulb_type) active.push(`ULB Type: ${filters.ulb_type}`);
    if (filters.ulb_class) active.push(`ULB Class: ${filters.ulb_class}`);
    if (filters.ulb_name) active.push(`ULB: ${filters.ulb_name}`);
    return active;
  };

  // Track if graphs are currently being loaded
  const isGraphsLoading = useRef(false);
  const lastLoadedFilters = useRef(null);

  // Function to load all graphs (can be called on demand)
  const loadAllGraphs = async () => {
    if (isGraphsLoading.current) {
      console.log("⏳ Graphs already loading, skipping...");
      return;
    }

    const currentFiltersKey = JSON.stringify({
      division: frontPageFilters.division,
      district: frontPageFilters.district,
      ulb_type: frontPageFilters.ulb_type,
      ulb_class: frontPageFilters.ulb_class,
      ulb_name: frontPageFilters.ulb_name,
    });

    // Skip if filters haven't changed
    if (lastLoadedFilters.current === currentFiltersKey) {
      console.log("📊 Filters unchanged, skipping graph reload");
      return;
    }

    const formsWithImportantFields = filteredForms.filter(
      (form) => form.reportFields && form.reportFields.length > 0,
    );

    if (formsWithImportantFields.length === 0) {
      console.log("📊 No forms with important fields found");
      return;
    }

    isGraphsLoading.current = true;
    console.log("📊 Loading graphs with filters:", frontPageFilters);

    for (let i = 0; i < formsWithImportantFields.length; i++) {
      const form = formsWithImportantFields[i];
      const importantField = form.reportFields[0];
      if (importantField) {
        const formId = form.id;
        const graphKey = `${formId}_${importantField.field_id || importantField.column_name}`;

        setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: true }));

        try {
          const graphData = await fetchImportantFieldGraph(
            formId,
            importantField,
            frontPageFilters,
          );

          if (graphData) {
            setStableGraphData((prev) => ({
              ...prev,
              [graphKey]: graphData,
            }));
            setFormGraphData((prev) => ({
              ...prev,
              [graphKey]: graphData,
            }));
            setLoadedGraphs((prev) => ({
              ...prev,
              [graphKey]: true,
            }));
          }
        } catch (error) {
          console.error(`Error fetching graph for form ${formId}:`, error);
        } finally {
          setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: false }));
        }
      }
    }

    lastLoadedFilters.current = currentFiltersKey;
    isGraphsLoading.current = false;
    setInitialGraphsLoaded(true);
    console.log("✅ All graphs loaded with current filters");
  };

  // Initial load - runs when filteredForms changes
  useEffect(() => {
    if (filteredForms.length > 0 && !initialGraphsLoaded) {
      loadAllGraphs();
    }
  }, [filteredForms]);

  // Reload graphs when filters change
  useEffect(() => {
    if (initialGraphsLoaded && filteredForms.length > 0) {
      // Debounce filter changes to avoid too many API calls
      const timer = setTimeout(() => {
        console.log("🔄 Filters changed, reloading graphs...");
        // Reset loaded state to force reload
        setInitialGraphsLoaded(false);
        setLoadedGraphs({});
        setStableGraphData({});
        loadAllGraphs();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
  ]);

  // Helper function to get active filters list

  const ImportantFieldGraph = React.memo(({
    field,
    formId,
    filters,
    formGraphData,
    loading,
    graphKey,
  }) => {
    const [chartType, setChartType] = useState("bar");

    // Use stableGraphData if available from parent context
    // The component receives formGraphData which contains the stable data
    const graphData = formGraphData[graphKey];
    const isLoading = loading && !graphData;

    // Memoize the chart content to prevent unnecessary re-renders
    const chartContent = React.useMemo(() => {
      // Show loading state
      if (isLoading) {
        return (
          <div style={{ padding: "20px", textAlign: "center", minHeight: 240 }}>
            <Spin size="small" />
            <div style={{ marginTop: 8, fontSize: "11px", color: "#8c8c8c" }}>
              Loading graph...
            </div>
          </div>
        );
      }

      // Show no data state
      if (!graphData || !graphData.hasData || !graphData.data || graphData.data.length === 0) {
        const activeFilterText = graphData?.appliedFilters?.length > 0
          ? ` with filters: ${graphData.appliedFilters.join(", ")}`
          : "";
        return (
          <div style={{ padding: "16px", textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              <BarChartOutlined /> No data available for this field{activeFilterText}
            </Text>
          </div>
        );
      }

      // Calculate values for display
      const totalValue = graphData.total;
      const isPieChart = chartType === "pie";
      const groupLabel = graphData.groupLabel ||
        (graphData.groupLevel === "division" ? "Division" :
          graphData.groupLevel === "district" ? "District" :
            graphData.groupLevel === "ulb" ? "ULB" : "Record");

      // Prepare pie chart data (limit to 12 items for performance)
      const pieData = graphData.data.slice(0, 12).map((item) => ({
        name: item.name.length > 20 ? item.name.substring(0, 17) + "..." : item.name,
        fullName: item.name,
        value: item.value,
      }));

      const hasActiveFilters = graphData.appliedFilters && graphData.appliedFilters.length > 0;

      // Render the appropriate chart
      return (
        <>
          <ResponsiveContainer width="100%" height={260}>
            {isPieChart ? (
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ""
                  }
                  outerRadius={70}
                  innerRadius={40}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const percentage = ((data.value / totalValue) * 100).toFixed(1);
                      return (
                        <div
                          style={{
                            background: "#fff",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            fontSize: "12px",
                            borderLeft: `3px solid ${PIE_COLORS[payload[0].payload.index % PIE_COLORS.length]}`,
                          }}
                        >
                          <div><strong>{data.fullName || data.name}</strong></div>
                          <div>Total {graphData.displayName}: {data.value.toLocaleString()}</div>
                          <div>Percentage: {percentage}%</div>
                          {data.count && <div>Records: {data.count}</div>}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            ) : (
              <BarChart
                data={graphData.data}
                margin={{ top: 10, right: 10, left: 30, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  interval={0}
                  tick={{ fontSize: 9 }}
                />
                <YAxis
                  tick={{ fontSize: 9 }}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toLocaleString();
                  }}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: "#fff",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            fontSize: "12px",
                            borderLeft: "3px solid #1890ff",
                          }}
                        >
                          <div><strong>{data.name}</strong></div>
                          <div>Total {graphData.displayName}: {data.value.toLocaleString()}</div>
                          {data.average && (
                            <div>
                              Average: {data.average.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          )}
                          {data.count && <div>Records: {data.count}</div>}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>

          <div
            style={{
              marginTop: "1px",
              textAlign: "center",
              fontSize: "10px",
              color: "#1890ff",
            }}
          >

          </div>
        </>
      );
    }, [graphData, isLoading, chartType]);

    // Don't render anything if no field provided
    if (!field) return null;

    return (
      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          background: "#fafbfc",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div>
            <Text strong style={{ fontSize: "12px", color: "#1890ff" }}>
              <BarChartOutlined /> {graphData?.displayName || field?.display_name || field?.field_id} by {graphData?.groupLabel || "Division"}
            </Text>
            {graphData?.appliedFilters?.length > 0 && (
              <div
                style={{ fontSize: "10px", color: "#fa8c16", marginTop: "2px" }}
              >
                Filtered: {graphData.appliedFilters.join(", ")}
              </div>
            )}
          </div>
          <Space size={4}>
            <Tooltip title="Bar Chart">
              <Button
                size="small"
                type={chartType !== "pie" ? "primary" : "default"}
                icon={<BarChartOutlined />}
                onClick={() => setChartType("bar")}
                style={{ padding: "0 6px", height: "24px" }}
              />
            </Tooltip>
            <Tooltip title="Pie Chart">
              <Button
                size="small"
                type={chartType === "pie" ? "primary" : "default"}
                icon={<PieChartOutlined />}
                onClick={() => setChartType("pie")}
                style={{ padding: "0 6px", height: "24px" }}
              />
            </Tooltip>
          </Space>
        </div>
        {chartContent}
      </div>
    );
  });

  // Initial graph loading - runs once when forms are loaded
  useEffect(() => {
    const loadInitialGraphs = async () => {
      if (graphLoadingStarted.current) return;
      if (!filteredForms.length) return;

      graphLoadingStarted.current = true;
      console.log("📊 Loading initial graphs for all forms...");

      const formsWithImportantFields = filteredForms.filter(
        (form) => form.reportFields && form.reportFields.length > 0,
      );

      const currentFilters = { ...frontPageFilters };
      lastLoadedFiltersRef.current = JSON.stringify(currentFilters);

      for (let i = 0; i < formsWithImportantFields.length; i++) {
        const form = formsWithImportantFields[i];
        const importantField = form.reportFields[0];
        if (importantField) {
          const formId = form.id;
          const graphKey = `${formId}_${importantField.field_id || importantField.column_name}`;

          if (stableGraphData[graphKey]) continue;

          setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: true }));

          try {
            const graphData = await fetchImportantFieldGraph(
              formId,
              importantField,
              currentFilters,
            );

            if (graphData) {
              setStableGraphData((prev) => ({
                ...prev,
                [graphKey]: graphData
              }));
              setFormGraphData((prev) => ({
                ...prev,
                [graphKey]: graphData
              }));
              setLoadedGraphs((prev) => ({
                ...prev,
                [graphKey]: true
              }));
            }
          } catch (error) {
            console.error(`Error fetching graph for form ${formId}:`, error);
          } finally {
            setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: false }));
          }
        }
      }

      setInitialGraphsLoaded(true);
      console.log("✅ All initial graphs loaded");
    };

    loadInitialGraphs();
  }, [filteredForms]); // Only runs when forms are first loaded

  // NEW: Reload graphs when filters change
  useEffect(() => {
    // Skip if initial graphs haven't loaded yet
    if (!initialGraphsLoaded) return;

    // Skip if we're still loading forms
    if (loading) return;

    // Debounce to avoid too many API calls
    const timer = setTimeout(async () => {
      console.log("🔄 Filters changed, reloading graphs with new filters:", frontPageFilters);

      const formsWithImportantFields = filteredForms.filter(
        (form) => form.reportFields && form.reportFields.length > 0,
      );

      if (formsWithImportantFields.length === 0) return;

      const currentFilters = { ...frontPageFilters };

      // Reload each graph with new filters
      for (let i = 0; i < formsWithImportantFields.length; i++) {
        const form = formsWithImportantFields[i];
        const importantField = form.reportFields[0];
        if (importantField) {
          const formId = form.id;
          const graphKey = `${formId}_${importantField.field_id || importantField.column_name}`;

          // Mark as loading
          setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: true }));
          setLoadedGraphs((prev) => ({ ...prev, [graphKey]: false }));

          try {
            const graphData = await fetchImportantFieldGraph(
              formId,
              importantField,
              currentFilters,
            );

            if (graphData) {
              setStableGraphData((prev) => ({
                ...prev,
                [graphKey]: graphData
              }));
              setFormGraphData((prev) => ({
                ...prev,
                [graphKey]: graphData
              }));
              setLoadedGraphs((prev) => ({
                ...prev,
                [graphKey]: true
              }));
              console.log(`✅ Graph reloaded for form ${formId} with new filters`);
            }
          } catch (error) {
            console.error(`Error reloading graph for form ${formId}:`, error);
          } finally {
            setLoadingFormGraphs((prev) => ({ ...prev, [graphKey]: false }));
          }
        }
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [
    frontPageFilters.division,
    frontPageFilters.district,
    frontPageFilters.ulb_type,
    frontPageFilters.ulb_class,
    frontPageFilters.ulb_name,
    initialGraphsLoaded,
    loading
  ]);
  // REMOVE the filter change useEffect entirely - it's causing the reloads

  useEffect(() => {
    return () => {
      Object.values(graphLoadTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, [graphLoadTimeouts]);
  // Update getOperationIcon:
  const getOperationIcon = (op) => {
    const opMap = {
      sum: <FiPlus />,
      avg: <TbMathAvg />,
      min: <TbMathMin />,
      max: <TbMathMax />,
      count: <FiHash />,
      multiply: <FiX />,
      divide: <FiDivide />,
      median: <FiTrendingUp />,
      mode: <FiTarget />,
      stddev: <FiTrendingDown />,
      lte: <span>≤</span>,
      gte: <span>≥</span>,
      percentage: <PercentageOutlined />, // ADD THIS
    };
    return opMap[op] || "📊";
  };

  // Update getOperationColor:
  const getOperationColor = (op) => {
    const colorMap = {
      sum: "#1890ff",
      avg: "#52c41a",
      min: "#fa8c16",
      max: "#f5222d",
      count: "#722ed1",
      multiply: "#13c2c2",
      divide: "#eb2f96",
      median: "#faad14",
      mode: "#a0d911",
      stddev: "#2f54eb",
      lte: "#13c2c2",
      gte: "#eb2f96",
      percentage: "#eb2f96", // ADD THIS
    };
    return colorMap[op] || "#1890ff";
  };
  // Function to calculate hierarchical statistics (Division, District, ULB level)
  const calculateHierarchicalStatistics = (
    data,
    fieldColumnName,
    fieldDisplayName,
  ) => {
    if (!data || data.length === 0) return null;

    // Helper to safely parse numeric values
    const parseNumericValue = (value) => {
      if (value === undefined || value === null || value === "") return null;
      const strVal = String(value).replace(/,/g, "");
      const numVal = parseFloat(strVal);
      return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
    };

    // Get field identifiers
    const divisionField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") ||
            f.toLowerCase().includes("विभाग")),
      ) || "division_name";

    const districtField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") ||
            f.toLowerCase().includes("जिल्हा")),
      ) || "district_name";

    const ulbField =
      formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb") ||
            f.toLowerCase().includes("मनपा") ||
            f.toLowerCase().includes("municipality")),
      ) || "ulb_name";

    // 1. DIVISION LEVEL STATISTICS
    const divisionMap = new Map();
    data.forEach((row) => {
      const divisionName = row[divisionField] || "Unknown Division";
      const value = parseNumericValue(row[fieldColumnName]);
      if (value !== null) {
        if (!divisionMap.has(divisionName)) {
          divisionMap.set(divisionName, { sum: 0, count: 0, values: [] });
        }
        const division = divisionMap.get(divisionName);
        division.sum += value;
        division.count++;
        division.values.push(value);
      }
    });

    let divisionStats = [];
    divisionMap.forEach((data, name) => {
      divisionStats.push({
        name: name,
        total: data.sum,
        average: data.sum / data.count,
        count: data.count,
        values: data.values,
      });
    });

    const divisionMin =
      divisionStats.length > 0
        ? divisionStats.reduce(
          (min, curr) => (curr.total < min.total ? curr : min),
          divisionStats[0],
        )
        : null;
    const divisionMax =
      divisionStats.length > 0
        ? divisionStats.reduce(
          (max, curr) => (curr.total > max.total ? curr : max),
          divisionStats[0],
        )
        : null;

    // 2. DISTRICT LEVEL STATISTICS
    const districtMap = new Map();
    data.forEach((row) => {
      const districtName = row[districtField] || "Unknown District";
      const divisionName = row[divisionField] || "Unknown Division";
      const value = parseNumericValue(row[fieldColumnName]);
      if (value !== null) {
        const key = `${districtName}|${divisionName}`;
        if (!districtMap.has(key)) {
          districtMap.set(key, {
            name: districtName,
            division: divisionName,
            sum: 0,
            count: 0,
            values: [],
          });
        }
        const district = districtMap.get(key);
        district.sum += value;
        district.count++;
        district.values.push(value);
      }
    });

    let districtStats = [];
    districtMap.forEach((data) => {
      districtStats.push({
        name: data.name,
        division: data.division,
        total: data.sum,
        average: data.sum / data.count,
        count: data.count,
      });
    });

    const districtMin =
      districtStats.length > 0
        ? districtStats.reduce(
          (min, curr) => (curr.total < min.total ? curr : min),
          districtStats[0],
        )
        : null;
    const districtMax =
      districtStats.length > 0
        ? districtStats.reduce(
          (max, curr) => (curr.total > max.total ? curr : max),
          districtStats[0],
        )
        : null;

    // 3. ULB LEVEL STATISTICS
    const ulbMap = new Map();
    data.forEach((row) => {
      const ulbName = row[ulbField] || "Unknown ULB";
      const districtName = row[districtField] || "Unknown District";
      const divisionName = row[divisionField] || "Unknown Division";
      const value = parseNumericValue(row[fieldColumnName]);
      if (value !== null) {
        const key = `${ulbName}|${districtName}|${divisionName}`;
        if (!ulbMap.has(key)) {
          ulbMap.set(key, {
            name: ulbName,
            district: districtName,
            division: divisionName,
            sum: 0,
            count: 0,
            values: [],
          });
        }
        const ulb = ulbMap.get(key);
        ulb.sum += value;
        ulb.count++;
        ulb.values.push(value);
      }
    });

    let ulbStats = [];
    ulbMap.forEach((data) => {
      ulbStats.push({
        name: data.name,
        district: data.district,
        division: data.division,
        total: data.sum,
        average: data.sum / data.count,
        count: data.count,
      });
    });

    const ulbMin =
      ulbStats.length > 0
        ? ulbStats.reduce(
          (min, curr) => (curr.total < min.total ? curr : min),
          ulbStats[0],
        )
        : null;
    const ulbMax =
      ulbStats.length > 0
        ? ulbStats.reduce(
          (max, curr) => (curr.total > max.total ? curr : max),
          ulbStats[0],
        )
        : null;

    return {
      division: {
        min: divisionMin,
        max: divisionMax,
        count: divisionStats.length,
      },
      district: {
        min: districtMin,
        max: districtMax,
        count: districtStats.length,
      },
      ulb: { min: ulbMin, max: ulbMax, count: ulbStats.length },
      fieldDisplayName: fieldDisplayName,
    };
  };
  // Calculate division data for a specific operation
  const calculateDivisionDataForOperation = (operation) => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];

    const divisionMap = new Map();

    rawDataForAnalysis.forEach((row) => {
      const divisionName = row.division_name || row.division || "Unknown Division";

      // Handle percentage calculation
      if (operation.operation === "percentage" && operation.percentageSecondColumnName) {
        const numerator = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
        const denominator = parseFloat(String(row[operation.percentageSecondColumnName] || "").replace(/,/g, ""));

        if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
          if (!divisionMap.has(divisionName)) {
            divisionMap.set(divisionName, { numerator: 0, denominator: 0, count: 0 });
          }
          const divData = divisionMap.get(divisionName);
          divData.numerator += numerator;
          divData.denominator += denominator;
          divData.count++;
        }
      }
      // Handle ranking - show sum of the field
      else if (operation.operation === "ranking") {
        const value = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
        if (!isNaN(value) && isFinite(value)) {
          if (!divisionMap.has(divisionName)) {
            divisionMap.set(divisionName, { sum: 0, count: 0, values: [] });
          }
          const divData = divisionMap.get(divisionName);
          divData.sum += value;
          divData.count++;
          divData.values.push(value);
        }
      }
      // Handle regular numeric operations
      else {
        const value = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));

        if (!isNaN(value) && isFinite(value)) {
          if (!divisionMap.has(divisionName)) {
            divisionMap.set(divisionName, { sum: 0, count: 0, values: [] });
          }
          const divData = divisionMap.get(divisionName);
          divData.sum += value;
          divData.count++;
          divData.values.push(value);
        }
      }
    });

    const divisionDataArray = [];

    for (const [divisionName, data] of divisionMap.entries()) {
      let displayValue = 0;

      if (operation.operation === "percentage") {
        if (data.denominator > 0) {
          displayValue = (data.numerator / data.denominator) * 100;
          displayValue = Math.min(displayValue, 100);
        }
      } else {
        switch (operation.operation) {
          case "sum":
            displayValue = data.sum;
            break;
          case "avg":
            displayValue = data.count > 0 ? data.sum / data.count : 0;
            break;
          case "min":
            displayValue = data.values.length > 0 ? Math.min(...data.values) : 0;
            break;
          case "max":
            displayValue = data.values.length > 0 ? Math.max(...data.values) : 0;
            break;
          case "count":
            displayValue = data.count;
            break;
          default:
            displayValue = data.sum;
        }
      }

      divisionDataArray.push({
        division: divisionName,
        value: displayValue,
        count: data.count,
        operation: operation.operation,
        fieldName: operation.fieldName,
        fieldId: operation.fieldId,
        numerator: data.numerator,
        denominator: data.denominator
      });
    }

    return divisionDataArray.sort((a, b) => a.division.localeCompare(b.division));
  };
  // Handle division click
  const handleDivisionClickForAnalysis = (divisionName) => {
    const districtData = calculateDistrictDataForDivision(divisionName);
    setDistrictDataCache(new Map().set(divisionName, districtData));
    setSelectedDivisionForAnalysis(divisionName);
    setShowDistrictView(true);
    setSelectedDistrictForAnalysis(null);
    setShowUlbView(false);
  };

  // Handle district click
  const handleDistrictClickForAnalysis = (districtName) => {
    const ulbData = calculateUlbDataForDistrict(selectedDivisionForAnalysis, districtName);
    setUlbDataCache(new Map().set(`${selectedDivisionForAnalysis}|${districtName}`, ulbData));
    setSelectedDistrictForAnalysis(districtName);
    setShowUlbView(true);
  };

  // Handle back to divisions
  const handleBackToDivisions = () => {
    setSelectedDivisionForAnalysis(null);
    setShowDistrictView(false);
    setSelectedDistrictForAnalysis(null);
    setShowUlbView(false);
  };

  // Handle back to districts
  const handleBackToDistricts = () => {
    setSelectedDistrictForAnalysis(null);
    setShowUlbView(false);
  };
  // Calculate district data for a specific division
  const calculateDistrictDataForDivision = (divisionName) => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];

    const districtMap = new Map();

    const divisionFilteredData = rawDataForAnalysis.filter(row =>
      (row.division_name || row.division) === divisionName
    );

    const displayableOperations = calculationResults.filter(op =>
      op.operation !== "lte" && op.operation !== "gte"
    );

    divisionFilteredData.forEach((row) => {
      const districtName = row.district_name || row.district || "Unknown District";

      displayableOperations.forEach((operation) => {
        if (operation.operation === "percentage" && operation.percentageSecondColumnName) {
          const numerator = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
          const denominator = parseFloat(String(row[operation.percentageSecondColumnName] || "").replace(/,/g, ""));

          if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
            const key = `${operation.fieldId}_${districtName}`;
            if (!districtMap.has(key)) {
              districtMap.set(key, { numerator: 0, denominator: 0, district: districtName, operationId: operation.fieldId, operation: operation });
            }
            const distData = districtMap.get(key);
            distData.numerator += numerator;
            distData.denominator += denominator;
          }
        } else {
          const value = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
          if (!isNaN(value) && isFinite(value)) {
            const key = `${operation.fieldId}_${districtName}`;
            if (!districtMap.has(key)) {
              districtMap.set(key, { sum: 0, count: 0, district: districtName, operationId: operation.fieldId, operation: operation });
            }
            const distData = districtMap.get(key);
            distData.sum += value;
            distData.count++;
          }
        }
      });
    });

    const districtDataMap = new Map();

    for (const [key, data] of districtMap.entries()) {
      if (!districtDataMap.has(data.district)) {
        districtDataMap.set(data.district, {});
      }
      const districtEntry = districtDataMap.get(data.district);

      let displayValue = 0;
      if (data.operation.operation === "percentage") {
        if (data.denominator > 0) {
          displayValue = (data.numerator / data.denominator) * 100;
          displayValue = Math.min(displayValue, 100);
        }
      } else {
        switch (data.operation.operation) {
          case "sum":
            displayValue = data.sum || 0;
            break;
          case "avg":
            displayValue = data.count > 0 ? (data.sum || 0) / data.count : 0;
            break;
          default:
            displayValue = data.sum || 0;
        }
      }

      districtEntry[data.operation.fieldId] = {
        value: displayValue,
        operation: data.operation.operation,
        fieldName: data.operation.fieldName
      };
    }

    const districtArray = [];
    for (const [districtName, metrics] of districtDataMap.entries()) {
      districtArray.push({
        district: districtName,
        metrics: metrics
      });
    }

    return districtArray.sort((a, b) => a.district.localeCompare(b.district));
  };

  // Calculate ULB data for a specific district
  const calculateUlbDataForDistrict = (divisionName, districtName) => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];

    const ulbMap = new Map();

    const filteredData = rawDataForAnalysis.filter(row =>
      (row.division_name || row.division) === divisionName &&
      (row.district_name || row.district) === districtName
    );

    const displayableOperations = calculationResults.filter(op =>
      op.operation !== "lte" && op.operation !== "gte"
    );

    filteredData.forEach((row) => {
      const ulbName = row.ulb_name || row.ulb || "Unknown ULB";

      displayableOperations.forEach((operation) => {
        if (operation.operation === "percentage" && operation.percentageSecondColumnName) {
          const numerator = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
          const denominator = parseFloat(String(row[operation.percentageSecondColumnName] || "").replace(/,/g, ""));

          if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
            const key = `${operation.fieldId}_${ulbName}`;
            if (!ulbMap.has(key)) {
              ulbMap.set(key, { numerator: 0, denominator: 0, ulb: ulbName, operationId: operation.fieldId, operation: operation });
            }
            const ulbData = ulbMap.get(key);
            ulbData.numerator += numerator;
            ulbData.denominator += denominator;
          }
        } else {
          const value = parseFloat(String(row[operation.columnName] || "").replace(/,/g, ""));
          if (!isNaN(value) && isFinite(value)) {
            const key = `${operation.fieldId}_${ulbName}`;
            if (!ulbMap.has(key)) {
              ulbMap.set(key, { sum: 0, count: 0, ulb: ulbName, operationId: operation.fieldId, operation: operation });
            }
            const ulbData = ulbMap.get(key);
            ulbData.sum += value;
            ulbData.count++;
          }
        }
      });
    });

    const ulbDataMap = new Map();

    for (const [key, data] of ulbMap.entries()) {
      if (!ulbDataMap.has(data.ulb)) {
        ulbDataMap.set(data.ulb, {});
      }
      const ulbEntry = ulbDataMap.get(data.ulb);

      let displayValue = 0;
      if (data.operation.operation === "percentage") {
        if (data.denominator > 0) {
          displayValue = (data.numerator / data.denominator) * 100;
          displayValue = Math.min(displayValue, 100);
        }
      } else {
        switch (data.operation.operation) {
          case "sum":
            displayValue = data.sum || 0;
            break;
          case "avg":
            displayValue = data.count > 0 ? (data.sum || 0) / data.count : 0;
            break;
          default:
            displayValue = data.sum || 0;
        }
      }

      ulbEntry[data.operation.fieldId] = {
        value: displayValue,
        operation: data.operation.operation,
        fieldName: data.operation.fieldName
      };
    }

    const ulbArray = [];
    for (const [ulbName, metrics] of ulbDataMap.entries()) {
      ulbArray.push({
        ulb: ulbName,
        metrics: metrics
      });
    }

    return ulbArray.sort((a, b) => a.ulb.localeCompare(b.ulb));
  };
  // Add this function in the main component (before renderAnalysisResults)
  const renderDivisionWiseCards = () => {
    if (!divisionWiseData || divisionWiseData.length === 0) return null;
    if (!calculationResults || calculationResults.length === 0) return null;

    const formatValue = (value, operation) => {
      if (value === undefined || value === null) return "0";
      if (operation === "avg") {
        return value.toFixed(2);
      }
      if (operation === "percentage") {
        return `${value.toFixed(2)}%`;
      }
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const getOperationColor = (op) => {
      const colorMap = {
        sum: "#1890ff",
        avg: "#52c41a",
        min: "#fa8c16",
        max: "#f5222d",
        count: "#722ed1",
        lte: "#13c2c2",
        gte: "#eb2f96",
        percentage: "#eb2f96",
        ranking: "#faad14"
      };
      return colorMap[op] || "#1890ff";
    };

    const getOperationIcon = (op) => {
      const icons = {
        sum: <FiPlus />,
        avg: <TbMathAvg />,
        min: <TbMathMin />,
        max: <TbMathMax />,
        count: <FiHash />,
        lte: <span>≤</span>,
        gte: <span>≥</span>,
        percentage: <PercentageOutlined />,
        ranking: <TrophyOutlined />
      };
      return icons[op] || "📊";
    };

    const getOperationLabel = (operation) => {
      switch (operation) {
        case 'sum': return 'Total';
        case 'avg': return 'Average';
        case 'min': return 'Minimum';
        case 'max': return 'Maximum';
        case 'count': return 'Count';
        case 'percentage': return 'Percentage';
        case 'ranking': return 'Ranking';
        default: return operation.toUpperCase();
      }
    };

    const displayableOperations = calculationResults.filter(op =>
      op.operation !== "lte" && op.operation !== "gte"
    );

    if (displayableOperations.length === 0) return null;

    // Calculate base division data
    const allOperationsData = [];
    const allDivisionsSet = new Set();

    for (const op of displayableOperations) {
      const opData = calculateDivisionDataForOperation(op);
      allOperationsData.push({
        operation: op,
        data: opData
      });
      opData.forEach(item => allDivisionsSet.add(item.division));
    }

    const allDivisions = Array.from(allDivisionsSet).sort();

    const getResponsiveGrid = () => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      if (isMobile) return { xs: 24 };
      if (typeof window !== 'undefined' && window.innerWidth < 992) return { xs: 24, sm: 12 };
      if (typeof window !== 'undefined' && window.innerWidth < 1200) return { xs: 24, sm: 12, md: 8 };
      return { xs: 24, sm: 12, md: 8, lg: 6 };
    };

    const getResponsiveFontSize = (type) => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      switch (type) {
        case 'title': return isMobile ? '14px' : '16px';
        case 'value': return isMobile ? '18px' : '20px';
        case 'icon': return isMobile ? '14px' : '16px';
        default: return isMobile ? '11px' : '12px';
      }
    };

    const getColorIndex = (name) => {
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash) % startColors.length;
    };

    // ========== VIEW 1: Show all divisions ==========
    if (!showDistrictView) {
      return (
        <div style={{ marginBottom: 32 }}>
          <div style={{
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <ApartmentOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
              <span style={{ fontSize: "20px", fontWeight: 600 }}>
                Division-wise Analysis
              </span>
              <Tag color="blue">{displayableOperations.length} Data Point{displayableOperations.length !== 1 ? 's' : ''}</Tag>
            </div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              💡 Click any division to see its districts
            </div>
          </div>

          <Row gutter={[16, 16]}>
            {allDivisions.map((division) => {
              const divisionOpsData = allOperationsData.map(opData => ({
                operation: opData.operation,
                item: opData.data.find(d => d.division === division)
              })).filter(item => item.item);

              const colorIndex = getColorIndex(division);
              const startColor = startColors[colorIndex % startColors.length];
              const endColor = endColors[colorIndex % endColors.length];
              const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
              const hoverColor = hoverColors[colorIndex % hoverColors.length];
              const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

              return (
                <Col key={division} {...getResponsiveGrid()}>
                  <Card
                    hoverable
                    className="radiant-card"
                    style={{
                      borderRadius: "14px",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: gradient,
                      border: "1px solid rgba(255,255,255,0.4)",
                      overflow: "hidden",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = hoverGradient;
                      e.currentTarget.style.transform = "translateY(-5px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = gradient;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                    onClick={() => handleDivisionClickForAnalysis(division)}
                    bodyStyle={{ padding: "16px" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                      <ApartmentOutlined style={{ fontSize: "24px", color: "#ffffff", textShadow: "0 2px 4px rgba(0,0,0,0.2)" }} />
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: "16px", color: "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                          {division}
                        </Text>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {divisionOpsData.slice(0, 4).map(({ operation, item }) => {
                        const opColor = getOperationColor(operation.operation);
                        const opIcon = getOperationIcon(operation.operation);

                        if (item.value === 0 && operation.operation !== "percentage") return null;

                        return (
                          <div
                            key={operation.fieldId}
                            style={{
                              flex: "1",
                              minWidth: "130px",
                              background: "rgba(255,255,255,0.95)",
                              borderRadius: "10px",
                              padding: "10px",
                              borderLeft: `3px solid ${opColor}`
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                              <span style={{ fontSize: "13px" }}>{opIcon}</span>
                              <Text style={{ fontSize: "11px", color: "#6c757d", flex: 1 }}>
                                {operation.fieldName}
                              </Text>
                              <Tag color="default" style={{ fontSize: "9px", padding: "0 6px" }}>
                                {getOperationLabel(operation.operation)}
                              </Tag>
                            </div>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: opColor }}>
                              {formatValue(item.value, operation.operation)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: "12px", textAlign: "center", color: "rgba(255,255,255,0.9)", fontSize: "11px" }}>
                      ▶ Click to view districts
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      );
    }

    // ========== VIEW 2: Show districts of selected division ==========
    if (showDistrictView && !showUlbView) {
      const districts = districtDataCache.get(selectedDivisionForAnalysis) || [];

      return (
        <div style={{ marginBottom: 32 }}>
          <div style={{
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Button
                icon={<LeftOutlined />}
                onClick={handleBackToDivisions}
                type="primary"
                style={{ borderRadius: "8px" }}
              >
                Back to All Divisions
              </Button>
              <EnvironmentOutlined style={{ fontSize: "18px", color: "#52c41a" }} />
              <span style={{ fontSize: "18px", fontWeight: 600 }}>
                Districts in {selectedDivisionForAnalysis}
              </span>
              <Tag color="green">{districts.length} districts</Tag>
            </div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              💡 Click any district to see its ULBs
            </div>
          </div>

          <Row gutter={[16, 16]}>
            {districts.map((districtItem, idx) => {
              const colorIndex = getColorIndex(districtItem.district);
              const startColor = startColors[colorIndex % startColors.length];
              const endColor = endColors[colorIndex % endColors.length];
              const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
              const hoverColor = hoverColors[colorIndex % hoverColors.length];
              const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

              return (
                <Col key={districtItem.district} {...getResponsiveGrid()}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: "14px",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: gradient,
                      border: "1px solid rgba(255,255,255,0.4)",
                      overflow: "hidden",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = hoverGradient;
                      e.currentTarget.style.transform = "translateY(-5px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = gradient;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                    onClick={() => handleDistrictClickForAnalysis(districtItem.district)}
                    bodyStyle={{ padding: "16px" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                      <EnvironmentOutlined style={{ fontSize: "24px", color: "#ffffff", textShadow: "0 2px 4px rgba(0,0,0,0.2)" }} />
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: "16px", color: "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                          {districtItem.district}
                        </Text>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {Object.entries(districtItem.metrics).slice(0, 4).map(([fieldId, metric]) => {
                        const originalOp = displayableOperations.find(op => op.fieldId === fieldId);
                        if (!originalOp) return null;
                        const opColor = getOperationColor(metric.operation);
                        const opIcon = getOperationIcon(metric.operation);

                        if (metric.value === 0 && metric.operation !== "percentage") return null;

                        return (
                          <div
                            key={fieldId}
                            style={{
                              flex: "1",
                              minWidth: "130px",
                              background: "rgba(255,255,255,0.95)",
                              borderRadius: "10px",
                              padding: "10px",
                              borderLeft: `3px solid ${opColor}`
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                              <span style={{ fontSize: "13px" }}>{opIcon}</span>
                              <Text style={{ fontSize: "11px", color: "#6c757d", flex: 1 }}>
                                {originalOp.fieldName}
                              </Text>
                              <Tag color="default" style={{ fontSize: "9px", padding: "0 6px" }}>
                                {getOperationLabel(metric.operation)}
                              </Tag>
                            </div>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: opColor }}>
                              {formatValue(metric.value, metric.operation)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: "12px", textAlign: "center", color: "rgba(255,255,255,0.9)", fontSize: "11px" }}>
                      ▶ Click to view ULBs
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      );
    }

    // ========== VIEW 3: Show ULBs of selected district ==========
    if (showUlbView) {
      const ulbs = ulbDataCache.get(`${selectedDivisionForAnalysis}|${selectedDistrictForAnalysis}`) || [];

      return (
        <div style={{ marginBottom: 32 }}>
          <div style={{
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Button
                icon={<LeftOutlined />}
                onClick={handleBackToDistricts}
                type="primary"
                style={{ borderRadius: "8px" }}
              >
                Back to Districts
              </Button>
              <Button
                icon={<DoubleLeftOutlined />}
                onClick={handleBackToDivisions}
                style={{ borderRadius: "8px" }}
              >
                Back to All Divisions
              </Button>
              <StarOutlined style={{ fontSize: "18px", color: "#faad14" }} />
              <span style={{ fontSize: "18px", fontWeight: 600 }}>
                ULBs in {selectedDistrictForAnalysis}
              </span>
              <Tag color="gold">{ulbs.length} ULBs</Tag>
            </div>
          </div>

          <Row gutter={[12, 12]}>
            {ulbs.map((ulbItem) => (
              <Col key={ulbItem.ulb} xs={24} sm={12} md={8} lg={6}>
                <Card
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #f0f0f0",
                    background: "#ffffff",
                    height: "100%",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                  }}
                  bodyStyle={{ padding: "14px" }}
                >
                  <Tooltip title={ulbItem.ulb}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: "13px",
                      marginBottom: "12px",
                      color: "#2c3e50",
                      borderBottom: "1px solid #f0f0f0",
                      paddingBottom: "8px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                      {ulbItem.ulb}
                    </div>
                  </Tooltip>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {Object.entries(ulbItem.metrics).slice(0, 5).map(([fieldId, metric]) => {
                      const originalOp = displayableOperations.find(op => op.fieldId === fieldId);
                      if (!originalOp) return null;
                      const opColor = getOperationColor(metric.operation);
                      const opIcon = getOperationIcon(metric.operation);

                      if (metric.value === 0 && metric.operation !== "percentage") return null;

                      return (
                        <div key={fieldId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ fontSize: "11px" }}>{opIcon}</span>
                            <Text style={{ fontSize: "10px", color: "#8c8c8c" }}>
                              {originalOp.fieldName.length > 20 ? originalOp.fieldName.substring(0, 17) + "..." : originalOp.fieldName}
                            </Text>
                          </div>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: opColor }}>
                            {formatValue(metric.value, metric.operation)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {Object.keys(ulbItem.metrics).length > 5 && (
                    <div style={{ fontSize: "9px", color: "#bfbfbf", textAlign: "center", marginTop: "10px", paddingTop: "6px", borderTop: "1px solid #f0f0f0" }}>
                      +{Object.keys(ulbItem.metrics).length - 5} more metrics
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      );
    }

    return null;
  };
  //11419
  const renderAnalysisResults = () => {
    if (!showAnalysisResults) return null;

    // Check if any operation is "ranking"
    const hasRankingOperation = calculationResults.some(
      result => result.operation === "ranking"
    );

    // Get ranking results if present
    const rankingResult = calculationResults.find(
      result => result.operation === "ranking"
    );

    // ========== FIX: Get ALL filter operations and properly link percentage with threshold ==========
    const getFilterOperations = () => {
      // Get all LTE/GTE operations
      const lteGteOps = calculationResults.filter(result =>
        result.operation === "lte" || result.operation === "gte"
      );

      // Get percentage operations
      const percentageOps = calculationResults.filter(result =>
        result.operation === "percentage"
      );

      // Get min/max operations
      const minMaxOps = calculationResults.filter(result =>
        result.operation === "min" || result.operation === "max"
      );

      // Get ranking operations - treat as filter when rankingType is "top" or "bottom"
      const rankingOps = calculationResults.filter(result =>
        result.operation === "ranking" && (result.rankingType === "top" || result.rankingType === "bottom")
      );

      return [...lteGteOps, ...percentageOps, ...minMaxOps, ...rankingOps];
    };

    const filterOperations = getFilterOperations();
    const hasMultipleFilterOps = filterOperations.length >= 2;

    // ========== FIX: Helper function to get condition text for display ==========
    const getOperationConditionText = (op) => {
      // Check if this threshold applies to a percentage operation
      const relatedPercentageOp = calculationResults.find(p =>
        p.operation === "percentage" && p.fieldName === op.fieldName
      );

      switch (op.operation) {
        case 'lte':
          if (relatedPercentageOp) {
            return `% ≤ ${parseFloat(op.threshold).toLocaleString()}%`;
          }
          return `≤ ${parseFloat(op.threshold).toLocaleString()}`;
        case 'gte':
          if (relatedPercentageOp) {
            return `% ≥ ${parseFloat(op.threshold).toLocaleString()}%`;
          }
          return `≥ ${parseFloat(op.threshold).toLocaleString()}`;
        case 'min': return `= Minimum`;
        case 'max': return `= Maximum`;
        case 'percentage': return op.percentageSecondFieldName ? `% of ${op.percentageSecondFieldName}` : '%';
        default: return '';
      }
    };

    // ========== FIX: Calculate matching ULBs count - properly links percentage with threshold ==========
    const calculateMatchingUlbsCount = () => {
      if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return 0;

      // Get all operations
      const allOperations = [...calculationResults];
      if (allOperations.length === 0) return 0;

      // Group data by ULB and calculate aggregates
      const ulbAggregates = new Map();

      rawDataForAnalysis.forEach((row) => {
        const ulbName = row.ulb_name || row.ulb;
        if (!ulbName) return;

        if (!ulbAggregates.has(ulbName)) {
          ulbAggregates.set(ulbName, {
            ulb: ulbName,
            district: row.district_name || row.district,
            division: row.division_name || row.division,
            values: {},      // For regular numeric fields
            percentages: {}  // For percentage calculations
          });
        }

        const ulbData = ulbAggregates.get(ulbName);

        // Collect values for regular operations
        allOperations.forEach(op => {
          if (op.operation !== "percentage") {
            const value = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
            if (!isNaN(value) && isFinite(value)) {
              if (!ulbData.values[op.fieldId]) {
                ulbData.values[op.fieldId] = 0;
              }
              ulbData.values[op.fieldId] += value;
            }
          }
        });

        // Collect values for percentage operations
        allOperations.forEach(op => {
          if (op.operation === "percentage" && op.percentageSecondColumnName) {
            const numerator = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
            const denominator = parseFloat(String(row[op.percentageSecondColumnName] || "").replace(/,/g, ""));

            if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
              if (!ulbData.percentages[op.fieldId]) {
                ulbData.percentages[op.fieldId] = { numerator: 0, denominator: 0 };
              }
              ulbData.percentages[op.fieldId].numerator += numerator;
              ulbData.percentages[op.fieldId].denominator += denominator;
            }
          }
        });
      });

      // Check each ULB against ALL conditions
      let matchingCount = 0;

      for (const [ulbName, ulbData] of ulbAggregates.entries()) {
        let meetsAll = true;

        for (const op of allOperations) {
          // Skip ranking operations
          if (op.operation === "ranking") continue;

          let conditionValue = null;
          let shouldCheck = true;

          // For percentage operation
          if (op.operation === "percentage") {
            const percentData = ulbData.percentages[op.fieldId];
            if (percentData && percentData.denominator > 0) {
              conditionValue = (percentData.numerator / percentData.denominator) * 100;
            } else {
              shouldCheck = false;
            }
          }
          // For LTE/GTE operation
          else if (op.operation === "lte" || op.operation === "gte") {
            // Check if this threshold should apply to a percentage
            const relatedPercentageOp = allOperations.find(p =>
              p.operation === "percentage" && p.fieldName === op.fieldName
            );

            if (relatedPercentageOp) {
              // Apply threshold to percentage value
              const percentData = ulbData.percentages[relatedPercentageOp.fieldId];
              if (percentData && percentData.denominator > 0) {
                conditionValue = (percentData.numerator / percentData.denominator) * 100;
              } else {
                shouldCheck = false;
              }
            } else {
              // Apply threshold to regular numeric value
              conditionValue = ulbData.values[op.fieldId] || 0;
              if (conditionValue === 0) shouldCheck = false;
            }
          }
          // For min/max operations
          else if (op.operation === "min" || op.operation === "max") {
            conditionValue = ulbData.values[op.fieldId] || 0;
            if (conditionValue === 0) shouldCheck = false;
          }

          if (!shouldCheck) {
            meetsAll = false;
            break;
          }

          // Check the condition
          if (op.operation === "lte") {
            const threshold = parseFloat(op.threshold);
            if (!(conditionValue <= threshold)) meetsAll = false;
          } else if (op.operation === "gte") {
            const threshold = parseFloat(op.threshold);
            if (!(conditionValue >= threshold)) meetsAll = false;
          } else if (op.operation === "min") {
            // Find global minimum for this field
            let minValue = Infinity;
            for (const [otherUlb, otherData] of ulbAggregates.entries()) {
              const otherValue = otherData.values[op.fieldId] || 0;
              if (otherValue > 0 && otherValue < minValue) {
                minValue = otherValue;
              }
            }
            if (conditionValue !== minValue) meetsAll = false;
          } else if (op.operation === "max") {
            // Find global maximum for this field
            let maxValue = -Infinity;
            for (const [otherUlb, otherData] of ulbAggregates.entries()) {
              const otherValue = otherData.values[op.fieldId] || 0;
              if (otherValue > maxValue) {
                maxValue = otherValue;
              }
            }
            if (conditionValue !== maxValue) meetsAll = false;
          } else if (op.operation === "percentage") {
            // For percentage operation without a threshold, always true
            // (percentage ops are only for display, not filtering)
          }

          if (!meetsAll) break;
        }

        if (meetsAll) {
          matchingCount++;
        }
      }

      return matchingCount;
    };


    // Filter out ID columns and other unwanted columns
    const filteredColumns = analysisDataColumns.filter((col) => {
      if (col.endsWith("_id") || col.includes("_id")) return false;
      if (col === "division_id" || col === "district_id" || col === "ulb_id")
        return false;
      if (col === "division" && analysisDataColumns.includes("division_name"))
        return false;
      if (col === "district" && analysisDataColumns.includes("district_name"))
        return false;
      if (col === "ulb" && analysisDataColumns.includes("ulb_name"))
        return false;
      return true;
    });

    // Separate location columns from data point columns
    const locationFields = ["division_name", "district_name", "ulb_name", "ulb_type", "class"];
    const dataPointColumns = [];
    const locationColumns = [];

    filteredColumns.forEach((col) => {
      // Check if this is a location field (from our calculation results data points)
      const isDataPointField = calculationResults.some(
        (result) => result.columnName === col || result.fieldName === col
      );

      if (locationFields.includes(col) ||
        col === "division" || col === "district" || col === "ulb" ||
        col === "ulb_type" || col === "class") {
        locationColumns.push(col);
      } else if (isDataPointField) {
        dataPointColumns.push(col);
      } else {
        // For any other columns, check if it's a location-like field
        const colLower = col.toLowerCase();
        if (colLower.includes("division") || colLower.includes("district") ||
          colLower.includes("ulb") || colLower.includes("type") ||
          colLower.includes("class")) {
          locationColumns.push(col);
        } else {
          dataPointColumns.push(col);
        }
      }
    });

    // Combine: Location columns first, then Data point columns
    const orderedColumns = [...locationColumns, ...dataPointColumns];

    // Function to get column title (unchanged)
    const getColumnTitle = (col) => {
      if (col === "division_name") return "Division";
      if (col === "district_name") return "District";
      if (col === "ulb_name") return "ULB";
      if (col === "ulb_type") return "ULB Type";
      if (col === "class") return "ULB Class";
      return formatFieldName(col);
    };

    // Create table columns with proper order
    const tableColumns = orderedColumns.map((col) => ({
      title: (
        <div
          style={{
            fontWeight: 600,
            fontSize: "13px",
            padding: "8px 4px",
            borderBottom: "2px solid #1890ff",
            display: "inline-block",
            whiteSpace: "nowrap",
          }}
        >
          {getColumnTitle(col)}
        </div>
      ),
      dataIndex: col,
      key: col,
      width:
        col === "division_name"
          ? 130
          : col === "district_name"
            ? 130
            : col === "ulb_name"
              ? 180
              : col === "ulb_type"
                ? 110
                : col === "class"
                  ? 90
                  : 120,
      ellipsis: true,
      align: "left",
      sorter: (a, b) => {
        const valA = a[col] || "";
        const valB = b[col] || "";
        return String(valA).localeCompare(String(valB));
      },
      render: (value) => {
        if (value === null || value === undefined)
          return <span style={{ color: "#ccc" }}>-</span>;
        if (typeof value === "number") {
          return (
            <div style={{ textAlign: "right", fontFamily: "monospace" }}>
              {value.toLocaleString()}
            </div>
          );
        }
        if (typeof value === "string" && value.length > 50)
          return value.substring(0, 47) + "...";
        return value;
      },
    }));

    // Create table columns


    const activeFilters = [];
    if (frontPageFilters.division)
      activeFilters.push(`Division: ${frontPageFilters.division}`);
    if (frontPageFilters.district)
      activeFilters.push(`District: ${frontPageFilters.district}`);
    if (frontPageFilters.ulb_type)
      activeFilters.push(`ULB Type: ${frontPageFilters.ulb_type}`);
    if (frontPageFilters.ulb_class)
      activeFilters.push(`ULB Class: ${frontPageFilters.ulb_class}`);
    if (frontPageFilters.ulb_name)
      activeFilters.push(`ULB: ${frontPageFilters.ulb_name}`);

    // Get all available fields for joint graph
    const availableFields = calculationResults.map((result) => ({
      fieldId: result.fieldId,
      fieldName: result.fieldName,
      columnName: result.columnName,
      operation: result.operation,
      result: result.result,
      details: result.details,
    }));

    // Handler for field selector change
    const handleStatsFieldChange = (fieldId) => {
      setSelectedStatsField(fieldId);
    };

    // Check if any of the selected operations are min or max
    const hasMinMaxOperation = calculationResults.some(
      (result) => result.operation === "min" || result.operation === "max"
    );

    // Function to render ranking table directly
    const renderRankingTable = () => {
      if (!rankingResult || !rankingResult.details || !rankingResult.details.data) return null;

      const rankingData = rankingResult.details.data;
      const rankingTitle = rankingResult.details.rankingTitle || "Ranking Results";
      const fieldName = rankingResult.fieldName;
      const totalULBs = rankingResult.details.totalULBs || 0;
      const totalSum = rankingData.reduce((sum, item) => sum + item.value, 0);

      const rankingColumns = [
        {
          title: "Rank",
          key: "rank",
          width: 70,
          render: (_, __, index) => index + 1,
        },
        {
          title: "ULB Name",
          dataIndex: "ulb",
          key: "ulb",
          sorter: (a, b) => a.ulb.localeCompare(b.ulb),
          fixed: "left",
        },
        {
          title: "District",
          dataIndex: "district",
          key: "district",
          sorter: (a, b) => a.district.localeCompare(b.district),
        },
        {
          title: "Division",
          dataIndex: "division",
          key: "division",
          sorter: (a, b) => a.division.localeCompare(b.division),
        },
        {
          title: `${fieldName} (Total)`,
          dataIndex: "value",
          key: "value",
          align: "right",
          sorter: (a, b) => a.value - b.value,
          render: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        },

      ];

      return (
        <Card

          style={{
            marginBottom: 24,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Table
            dataSource={rankingData}
            columns={rankingColumns}
            rowKey={(record, index) => `${record.ulb}_${index}`}
            pagination={{
              pageSize: 500,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ULBs`,
            }}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>
      );
    };

    // Function to render KPI cards (KEY MATRIX)
    // Replace the entire renderKPIcards function with this corrected version:

    const renderKPIcards = () => {
      if (!calculationResults || calculationResults.length === 0) return null;

      // Get the data that was actually used for calculations
      const filteredData = rawDataForAnalysis || [];

      // Calculate division totals for MIN/MAX operations (fallback)
      const calculateDivisionTotals = (columnName) => {
        const divisionTotals = {};

        filteredData.forEach((row) => {
          const divisionName = row.division_name || row.division || "Unknown Division";
          const value = parseFloat(String(row[columnName] || "").replace(/,/g, ""));

          if (!isNaN(value) && isFinite(value)) {
            if (!divisionTotals[divisionName]) {
              divisionTotals[divisionName] = 0;
            }
            divisionTotals[divisionName] += value;
          }
        });

        return divisionTotals;
      };

      // Helper function to get the location with min/max value at the appropriate level
      const getLocationForMinMax = (columnName, operation, level = 'ulb') => {
        if (!filteredData.length) return null;

        // ALWAYS use ulb_name field for location
        const locationField = 'ulb_name';

        // Group data by ULB
        const ulbTotals = {};
        const ulbDetails = {};

        filteredData.forEach((row) => {
          const ulbName = row.ulb_name || row.ulb || 'Unknown ULB';
          const districtName = row.district_name || row.district || 'Unknown District';
          const divisionName = row.division_name || row.division || 'Unknown Division';
          const value = parseFloat(String(row[columnName] || '').replace(/,/g, ''));

          if (!isNaN(value) && isFinite(value)) {
            if (!ulbTotals[ulbName]) {
              ulbTotals[ulbName] = 0;
              ulbDetails[ulbName] = {
                ulb: ulbName,
                district: districtName,
                division: divisionName
              };
            }
            ulbTotals[ulbName] += value;
          }
        });

        if (Object.keys(ulbTotals).length === 0) return null;

        // Find min or max
        if (operation === 'min') {
          let minValue = Infinity;
          let minUlb = '';
          for (const [ulb, total] of Object.entries(ulbTotals)) {
            if (total < minValue) {
              minValue = total;
              minUlb = ulb;
            }
          }
          const details = ulbDetails[minUlb];
          return {
            location: minUlb,
            value: minValue,
            district: details?.district,
            division: details?.division
          };
        } else if (operation === 'max') {
          let maxValue = -Infinity;
          let maxUlb = '';
          for (const [ulb, total] of Object.entries(ulbTotals)) {
            if (total > maxValue) {
              maxValue = total;
              maxUlb = ulb;
            }
          }
          const details = ulbDetails[maxUlb];
          return {
            location: maxUlb,
            value: maxValue,
            district: details?.district,
            division: details?.division
          };
        }

        return null;
      };

      // ========== HANDLE CLICK FUNCTION ==========
      const handleLocationClick = async (result) => {
        if (result.operation === "lte" || result.operation === "gte") {
          setLocationModalLoading(true);
          setLocationModalVisible(true);

          // Get the current active filters
          const isInsideFormView = selectedForm !== null;
          const activeFilters = isInsideFormView ? filters : frontPageFilters;

          const matchingUlbs = await getMatchingUlbs(
            result.fieldId,
            result.columnName,
            result.operation,
            result.threshold,
            activeFilters
          );

          const operatorSymbol = result.operation === "lte" ? "≤" : "≥";
          const title = `${result.fieldName} ${operatorSymbol} ${result.threshold?.toLocaleString()}`;

          setLocationModalData({
            title: title,
            locations: matchingUlbs,
            fieldName: result.fieldName,
            threshold: result.threshold,
            operator: result.operation
          });
          setLocationModalLoading(false);
        }
      };

      // ========== FORMAT VALUE FUNCTION ==========
      const formatValue = (value, operation, details = null) => {
        if (value === undefined || value === null) return "0";

        // Convert to number if it's a string
        let numValue = value;
        if (typeof value === 'string') {
          // Remove commas and convert to number
          numValue = parseFloat(value.replace(/,/g, ''));
        }

        // Check if it's a valid number
        if (isNaN(numValue) || typeof numValue !== 'number') {
          return String(value);
        }

        if (operation === "avg") {
          return numValue.toFixed(2);
        }
        if (operation === "percentage") {
          return `${numValue.toFixed(2)}%`;
        }
        if (operation === "lte" || operation === "gte") {
          return numValue.toLocaleString() + ` ${details?.groupLabel || "ULBs"}`;
        }
        return numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
      };

      return (
        <div style={{
          marginBottom: 0,
          background: "#FFFFFF",
          borderRadius: "0px",
          padding: "16px",
        }}>
          <div className="section-header" style={{ marginBottom: 16 }}>
            <CalculatorOutlined className="section-icon" />
            <span className="section-title"> Key Metrics Summary</span>
          </div>

          <Row gutter={[12, 12]}>
            {calculationResults.map((result, index) => {
              const operationIcon = getOperationIcon(result.operation);
              const operationColor = getOperationColor(result.operation);

              let displayValue = result.result;
              let additionalInfo = "";
              let locationInfo = null;

              // ========== FIXED: Handle percentage operation display with numerator/denominator ==========
              if (result.operation === "percentage") {
                // Safely format the average percentage
                let formattedAvg = "0.00";
                if (result.result !== undefined && result.result !== null) {
                  const avgNum = typeof result.result === 'number' ? result.result : parseFloat(String(result.result).replace(/,/g, ''));
                  formattedAvg = !isNaN(avgNum) ? avgNum.toFixed(2) : "0.00";
                }

                // Safely format min and max percentages
                let minPercentageDisplay = "0.00";
                let maxPercentageDisplay = "0.00";
                let minLocationDisplay = "N/A";
                let maxLocationDisplay = "N/A";

                if (result.details?.minPercentage !== undefined && result.details?.minPercentage !== null) {
                  const minNum = typeof result.details.minPercentage === 'number' ? result.details.minPercentage : parseFloat(String(result.details.minPercentage).replace(/,/g, ''));
                  minPercentageDisplay = !isNaN(minNum) ? minNum.toFixed(2) : "0.00";
                  minLocationDisplay = result.details?.minLocation?.ulb || 'N/A';
                }

                if (result.details?.maxPercentage !== undefined && result.details?.maxPercentage !== null) {
                  const maxNum = typeof result.details.maxPercentage === 'number' ? result.details.maxPercentage : parseFloat(String(result.details.maxPercentage).replace(/,/g, ''));
                  maxPercentageDisplay = !isNaN(maxNum) ? maxNum.toFixed(2) : "0.00";
                  maxLocationDisplay = result.details?.maxLocation?.ulb || 'N/A';
                }

                // ========== FIX: Get numerator and denominator totals from percentagesData ==========
                let totalNumerator = 0;
                let totalDenominator = 0;

                // Check if percentagesData exists and has data
                if (result.details?.percentagesData && Array.isArray(result.details.percentagesData) && result.details.percentagesData.length > 0) {
                  // Calculate totals from all ULB data
                  result.details.percentagesData.forEach(item => {
                    totalNumerator += item.value1 || 0;
                    totalDenominator += item.value2 || 0;
                  });
                }

                // If still no data, try to get from min/max location data (which might have it)
                if (totalDenominator === 0 && result.details?.minLocation && result.details.minLocation.numerator !== undefined) {
                  totalNumerator = (result.details.minLocation.numerator || 0) + (result.details.maxLocation?.numerator || 0);
                  totalDenominator = (result.details.minLocation.denominator || 0) + (result.details.maxLocation?.denominator || 0);
                }

                // If still no data, try to get from percentagesData even if it's not stored correctly
                // This is a fallback to show at least something
                if (totalDenominator === 0 && result.details?.percentagesData) {
                  // Try to recalculate from the raw percentages data
                  for (const key in result.details) {
                    if (result.details[key] && typeof result.details[key] === 'object' && result.details[key].numerator !== undefined) {
                      totalNumerator += result.details[key].numerator || 0;
                      totalDenominator += result.details[key].denominator || 0;
                    }
                  }
                }

                // Create the display value with numerator/denominator (matching Division-wise cards)
                // Format: "12.96% (282,641 / 2,181,362)"
                const displayValueWithRatio = totalNumerator > 0 && totalDenominator > 0
                  ? `${formattedAvg}% (${totalNumerator.toLocaleString()} / ${totalDenominator.toLocaleString()})`
                  : `${formattedAvg}%`;

                additionalInfo = (
                  <div style={{ marginTop: "4px" }}>
                    <div style={{ fontSize: "11px", color: "#fa8c16" }}>
                      ↓ Min: {minLocationDisplay} ({minPercentageDisplay}%)
                    </div>
                    <div style={{ fontSize: "11px", color: "#f5222d" }}>
                      ↑ Max: {maxLocationDisplay} ({maxPercentageDisplay}%)
                    </div>
                  </div>
                );

                // Show the percentage with ratio in the main value area (like Division-wise cards)
                displayValue = displayValueWithRatio;
              }

              // Handle ranking operation display
              if (result.operation === "ranking") {
                const rankingData = result.details?.data || [];
                const totalSum = rankingData.reduce((sum, item) => sum + item.value, 0);

                additionalInfo = (
                  <div style={{ marginTop: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#8c8c8c", marginBottom: "4px" }}>
                      Total {result.fieldName}: {totalSum.toLocaleString()}
                    </div>
                  </div>
                );
                displayValue = `${rankingData.length} ULBs`;
              }

              // For MIN and MAX operations, find the ULB with smallest/largest TOTAL
              if (result.operation === "min" || result.operation === "max") {
                if (filteredData.length > 0) {
                  // Get ULB level location info
                  locationInfo = getLocationForMinMax(result.columnName, result.operation);

                  if (locationInfo) {
                    displayValue = locationInfo.value;
                    // Show ULB name with district in parentheses for clarity
                    const locationDisplay = locationInfo.district
                      ? `${locationInfo.location} (${locationInfo.district})`
                      : locationInfo.location;
                    additionalInfo = ` (${result.operation === 'min' ? 'Lowest' : 'Highest'}: ${locationDisplay})`;
                  } else {
                    // Fallback to division level if ULB not found
                    const divisionTotals = calculateDivisionTotals(result.columnName);
                    if (Object.keys(divisionTotals).length > 0) {
                      if (result.operation === "min") {
                        let minTotal = Infinity;
                        let minDivision = "";
                        for (const [division, total] of Object.entries(divisionTotals)) {
                          if (total < minTotal) {
                            minTotal = total;
                            minDivision = division;
                          }
                        }
                        displayValue = minTotal;
                        additionalInfo = ` (Lowest Division: ${minDivision})`;
                      } else if (result.operation === "max") {
                        let maxTotal = -Infinity;
                        let maxDivision = "";
                        for (const [division, total] of Object.entries(divisionTotals)) {
                          if (total > maxTotal) {
                            maxTotal = total;
                            maxDivision = division;
                          }
                        }
                        displayValue = maxTotal;
                        additionalInfo = ` (Highest Division: ${maxDivision})`;
                      }
                    }
                  }
                }
              }

              return (
                <Col
                  xs={12}
                  sm={8}
                  md={6}
                  lg={4}
                  xl={3}
                  key={result.id || index}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: "8px",
                      padding: "12px",
                      border: `1px solid ${operationColor}`,
                      borderLeft: `3px solid ${operationColor}`,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      transition: "all 0.2s ease",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#495057",
                        marginBottom: "6px",
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                      }}
                    >
                      {result.fieldName}
                    </div>

                    {/* For LTE/GTE operations - make the number clickable */}
                    {(result.operation === "lte" || result.operation === "gte") && result.details ? (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: operationColor,
                          borderTop: "1px solid #e9ecef",
                          paddingTop: "6px",
                          marginTop: "2px",
                          cursor: "pointer",
                          textDecoration: "underline"
                        }}
                        onClick={() => handleLocationClick(result)}
                      >
                        {formatValue(displayValue, result.operation, result.details)}
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: operationColor,
                          borderTop: "1px solid #e9ecef",
                          paddingTop: "6px",
                          marginTop: "2px",
                        }}
                      >
                        {formatValue(displayValue, result.operation, result.details)}
                      </div>
                    )}

                    {/* Display additional info (min/max/average) */}
                    {additionalInfo && (
                      <div style={{ marginTop: "8px" }}>
                        {additionalInfo}
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      );
    };

    // Function to prepare data for scatter plot (X vs Y values)
    const prepareScatterData = () => {
      if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return [];
      if (selectedXAxisFields.length === 0 || selectedYAxisFields.length === 0) return [];

      // Get the X-axis field (e.g., Area) and Y-axis field (e.g., Population)
      const xAxisField = selectedXAxisFields[0];
      const yAxisField = selectedYAxisFields[0];

      if (!xAxisField || !yAxisField) return [];

      // Determine grouping level based on filters
      const isInsideFormView = selectedForm !== null;
      const activeFilters = isInsideFormView ? filters : frontPageFilters;

      // Determine what each point represents (Division, District, or ULB)
      let groupByField = "division_name";
      let groupLabel = "Division";
      let pointType = "division";

      if (activeFilters.ulb_name) {
        groupByField = null;
        groupLabel = "Record";
        pointType = "record";
      } else if (activeFilters.district) {
        groupByField = "ulb_name";
        groupLabel = "ULB";
        pointType = "ulb";
      } else if (activeFilters.division) {
        groupByField = "district_name";
        groupLabel = "District";
        pointType = "district";
      } else {
        groupByField = "division_name";
        groupLabel = "Division";
        pointType = "division";
      }

      // If showing individual records, don't aggregate
      if (pointType === "record") {
        return rawDataForAnalysis
          .filter(row => {
            // Apply filters
            if (activeFilters.division && (row.division_name || row.division) !== activeFilters.division) return false;
            if (activeFilters.district && (row.district_name || row.district) !== activeFilters.district) return false;
            if (activeFilters.ulb_type && row.ulb_type !== activeFilters.ulb_type) return false;
            if (activeFilters.ulb_class && row.class !== activeFilters.ulb_class) return false;
            if (activeFilters.ulb_name && (row.ulb_name || row.ulb) !== activeFilters.ulb_name) return false;
            return true;
          })
          .map((row, index) => {
            const xValue = parseFloat(String(row[xAxisField.columnName] || "").replace(/,/g, ""));
            const yValue = parseFloat(String(row[yAxisField.columnName] || "").replace(/,/g, ""));

            if (isNaN(xValue) || !isFinite(xValue) || isNaN(yValue) || !isFinite(yValue)) return null;

            return {
              name: `Record ${index + 1}`,
              x: xValue,
              y: yValue,
              count: 1,
              division: row.division_name || row.division,
              district: row.district_name || row.district,
              ulb: row.ulb_name || row.ulb,
              pointType: "record"
            };
          })
          .filter(point => point !== null);
      }

      // Group data by geographical level
      const groupedData = {};

      rawDataForAnalysis.forEach((row) => {
        // Apply filters
        if (activeFilters.division && (row.division_name || row.division) !== activeFilters.division) return;
        if (activeFilters.district && (row.district_name || row.district) !== activeFilters.district) return;
        if (activeFilters.ulb_type && row.ulb_type !== activeFilters.ulb_type) return;
        if (activeFilters.ulb_class && row.class !== activeFilters.ulb_class) return;
        if (activeFilters.ulb_name && (row.ulb_name || row.ulb) !== activeFilters.ulb_name) return;

        const groupValue = row[groupByField] || `Unknown ${groupLabel}`;

        // Parse X-axis value (e.g., Area)
        const xValue = parseFloat(String(row[xAxisField.columnName] || "").replace(/,/g, ""));
        // Parse Y-axis value (e.g., Population)
        const yValue = parseFloat(String(row[yAxisField.columnName] || "").replace(/,/g, ""));

        if (!isNaN(xValue) && isFinite(xValue) && !isNaN(yValue) && isFinite(yValue)) {
          if (!groupedData[groupValue]) {
            groupedData[groupValue] = {
              name: groupValue,
              totalX: 0,
              totalY: 0,
              count: 0,
              division: row.division_name || row.division,
              district: row.district_name || row.district,
              ulb: row.ulb_name || row.ulb,
              pointType: pointType
            };
          }
          groupedData[groupValue].totalX += xValue;
          groupedData[groupValue].totalY += yValue;
          groupedData[groupValue].count++;
        }
      });

      // Convert to array for scatter plot
      return Object.values(groupedData).map(item => ({
        name: item.name,
        x: item.totalX,  // X-axis: Total Area (or whatever field)
        y: item.totalY,  // Y-axis: Total Population (or whatever field)
        count: item.count,
        division: item.division,
        district: item.district,
        ulb: item.ulb,
        pointType: item.pointType,
        avgX: item.totalX / item.count,
        avgY: item.totalY / item.count
      }));
    };


    const renderJointGraph = () => {
      if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return null;

      // Get selected X-axis fields
      const selectedXFields = xAxisSelectedFields
        .filter(value => value !== 'auto')
        .map(value => {
          const option = xAxisOptions.find(opt => opt.value === value);
          if (option && option.type === 'dataPoint') {
            return calculationResults.find(r => r.fieldId === option.fieldId);
          }
          return null;
        })
        .filter(Boolean);

      // Get selected Y-axis fields
      const selectedYFields = yAxisSelectedFields
        .map(value => {
          const option = yAxisOptions.find(opt => opt.value === value);
          if (option) {
            return calculationResults.find(r => r.fieldId === option.fieldId);
          }
          return null;
        })
        .filter(Boolean);

      // If no Y-axis fields selected, show message
      if (selectedYFields.length === 0) {
        return (
          <Empty
            description="Please select at least one data point for Y-axis"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "60px 0" }}
          />
        );
      }

      // Check if auto mode is selected for X-axis
      const isAutoMode = xAxisSelectedFields.includes('auto');

      // Determine current drill level based on active filters
      let currentLevel = "division";
      let groupByField = "division_name";
      let groupLabel = "Division";
      let locationIdField = "division_id";

      if (activeFiltersForGraph.district) {
        currentLevel = "ulb";
        groupByField = "ulb_name";
        groupLabel = "ULB";
        locationIdField = "ulb_id";
      } else if (activeFiltersForGraph.division) {
        currentLevel = "district";
        groupByField = "district_name";
        groupLabel = "District";
        locationIdField = "district_id";
      } else {
        currentLevel = "division";
        groupByField = "division_name";
        groupLabel = "Division";
        locationIdField = "division_id";
      }

      // Determine X-axis type
      let xAxisType = "system";
      let xAxisField = null;

      if (!isAutoMode && selectedXFields.length > 0) {
        xAxisType = "dataPoint";
        xAxisField = selectedXFields[0];
      }

      // Apply location filters to data
      let filteredDataForGraph = [...rawDataForAnalysis];

      if (activeFiltersForGraph.division) {
        const divisionValue = activeFiltersForGraph.division;
        filteredDataForGraph = filteredDataForGraph.filter(
          (row) => (row.division_name || row.division) === divisionValue
        );
      }

      if (activeFiltersForGraph.district) {
        const districtValue = activeFiltersForGraph.district;
        filteredDataForGraph = filteredDataForGraph.filter(
          (row) => (row.district_name || row.district) === districtValue
        );
      }

      if (activeFiltersForGraph.ulb_type) {
        filteredDataForGraph = filteredDataForGraph.filter(
          (row) => row.ulb_type === activeFiltersForGraph.ulb_type
        );
      }

      if (activeFiltersForGraph.ulb_class) {
        filteredDataForGraph = filteredDataForGraph.filter(
          (row) => row.class === activeFiltersForGraph.ulb_class
        );
      }

      if (activeFiltersForGraph.ulb_name) {
        const ulbValue = activeFiltersForGraph.ulb_name;
        filteredDataForGraph = filteredDataForGraph.filter(
          (row) => row.ulb_name === ulbValue || row.ulb === ulbValue
        );
      }

      if (filteredDataForGraph.length === 0) {
        return (
          <Empty
            description="No data available with current filters"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "60px 0" }}
          />
        );
      }

      // ========== FIX: Properly handle percentage operation ==========
      // Find the percentage operation in calculationResults
      const percentageOperation = calculationResults.find(
        result => result.operation === "percentage"
      );
      const hasPercentageOperation = !!percentageOperation;

      // Get the numerator and denominator fields from the percentage operation
      let numeratorField = null;
      let denominatorField = null;

      if (hasPercentageOperation && percentageOperation) {
        // Numerator field - this is the field we're calculating percentage of
        numeratorField = percentageOperation;

        // Denominator field - need to find it in calculationResults
        // The denominator field name is stored in percentageSecondFieldName
        if (percentageOperation.percentageSecondFieldName) {
          // Look for the denominator field in calculationResults
          denominatorField = calculationResults.find(r =>
            r.fieldName === percentageOperation.percentageSecondFieldName ||
            r.fieldId === percentageOperation.percentageSecondFieldId
          );

          // If not found, try to create a reference from the percentage operation's metadata
          if (!denominatorField && percentageOperation.percentageSecondColumnName) {
            denominatorField = {
              fieldId: percentageOperation.percentageSecondFieldId || `denom_${Date.now()}`,
              fieldName: percentageOperation.percentageSecondFieldName,
              columnName: percentageOperation.percentageSecondColumnName,
              operation: "sum",
              result: null,
              details: null
            };
          }
        }

        console.log("🔍 Percentage Operation Details:", {
          numeratorField: numeratorField?.fieldName,
          numeratorColumn: numeratorField?.columnName,
          denominatorFieldName: percentageOperation.percentageSecondFieldName,
          denominatorFieldId: percentageOperation.percentageSecondFieldId,
          denominatorColumn: percentageOperation.percentageSecondColumnName,
          foundDenominator: denominatorField?.fieldName
        });
      }

      // Check for LTE/GTE threshold
      const lteGteOperation = calculationResults.find(
        result => result.operation === "lte" || result.operation === "gte"
      );
      const hasThresholdFilter = !!lteGteOperation;
      const thresholdValue = lteGteOperation?.threshold ? parseFloat(lteGteOperation.threshold) : null;
      const thresholdOperator = lteGteOperation?.operation;
      const thresholdColor = thresholdOperator === "lte" ? "#13c2c2" : "#eb2f96";
      const thresholdFieldName = lteGteOperation?.fieldName;
      const thresholdColumnName = lteGteOperation?.columnName;

      // X and Y axis selection dropdowns
      const handleXAxisChange = (values) => {
        setXAxisSelectedFields(values);
      };

      const handleYAxisChange = (values) => {
        setYAxisSelectedFields(values);
      };

      // Check for scatter plot mode
      const isScatterPlot = jointChartType === "scatter";

      if (isScatterPlot) {
        const xDataField = selectedXFields[0];
        const yDataField = selectedYFields[0];

        if (!xDataField) {
          return (
            <div>
              <div className="section-header" style={{ marginBottom: 16 }}>
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Select
                    mode="multiple"
                    placeholder="Select X-axis"
                    value={xAxisSelectedFields}
                    onChange={handleXAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    <Option value="auto">Auto (Based on Filters)</Option>
                    <Option value="division">Division</Option>
                    <Option value="district">District</Option>
                    <Option value="ulb">ULB</Option>
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📊 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    mode="multiple"
                    placeholder="Select Y-axis data points"
                    value={yAxisSelectedFields}
                    onChange={handleYAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📈 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Button.Group size="small">
                    <Button
                      type={jointChartType === "bar" ? "primary" : "default"}
                      icon={<BarChartOutlined />}
                      onClick={() => setJointChartType("bar")}
                    >
                      Bar
                    </Button>
                    <Button
                      type={jointChartType === "line" ? "primary" : "default"}
                      icon={<LineChartOutlined />}
                      onClick={() => setJointChartType("line")}
                    >
                      Line
                    </Button>
                    <Button
                      type={jointChartType === "scatter" ? "primary" : "default"}
                      icon={<DotChartOutlined />}
                      onClick={() => setJointChartType("scatter")}
                    >
                      Scatter
                    </Button>
                  </Button.Group>
                </div>
              </div>
              <Empty
                description="Please select an X-axis data point for scatter plot"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "60px 0" }}
              />
            </div>
          );
        }

        if (!yDataField) {
          return (
            <div>
              <div className="section-header" style={{ marginBottom: 16 }}>
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Select
                    mode="multiple"
                    placeholder="Select X-axis"
                    value={xAxisSelectedFields}
                    onChange={handleXAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    <Option value="auto">Auto (Based on Filters)</Option>
                    <Option value="division">Division</Option>
                    <Option value="district">District</Option>
                    <Option value="ulb">ULB</Option>
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📊 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    mode="multiple"
                    placeholder="Select Y-axis data points"
                    value={yAxisSelectedFields}
                    onChange={handleYAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📈 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Button.Group size="small">
                    <Button
                      type={jointChartType === "bar" ? "primary" : "default"}
                      icon={<BarChartOutlined />}
                      onClick={() => setJointChartType("bar")}
                    >
                      Bar
                    </Button>
                    <Button
                      type={jointChartType === "line" ? "primary" : "default"}
                      icon={<LineChartOutlined />}
                      onClick={() => setJointChartType("line")}
                    >
                      Line
                    </Button>
                    <Button
                      type={jointChartType === "scatter" ? "primary" : "default"}
                      icon={<DotChartOutlined />}
                      onClick={() => setJointChartType("scatter")}
                    >
                      Scatter
                    </Button>
                  </Button.Group>
                </div>
              </div>
              <Empty
                description="Please select a Y-axis data point for scatter plot"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "60px 0" }}
              />
            </div>
          );
        }

        const scatterData = [];
        const groupedData = {};

        filteredDataForGraph.forEach((row) => {
          let xValue = parseFloat(String(row[xDataField.columnName] || "").replace(/,/g, ""));
          if (isNaN(xValue) || !isFinite(xValue)) return;

          const yValue = parseFloat(String(row[yDataField.columnName] || "").replace(/,/g, ""));
          if (isNaN(yValue) || !isFinite(yValue)) return;

          let groupValue = row[groupByField] || "Unknown";

          const key = String(groupValue);
          if (!groupedData[key]) {
            groupedData[key] = {
              name: key,
              totalX: 0,
              totalY: 0,
              count: 0
            };
          }
          groupedData[key].totalX += xValue;
          groupedData[key].totalY += yValue;
          groupedData[key].count++;
        });

        Object.values(groupedData).forEach(item => {
          scatterData.push({
            name: item.name,
            x: item.totalX / item.count,
            y: item.totalY / item.count,
            count: item.count
          });
        });

        if (scatterData.length === 0) {
          return (
            <div>
              <div className="section-header" style={{ marginBottom: 16 }}>
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Select
                    mode="multiple"
                    placeholder="Select X-axis"
                    value={xAxisSelectedFields}
                    onChange={handleXAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    <Option value="auto">Auto (Based on Filters)</Option>
                    <Option value="division">Division</Option>
                    <Option value="district">District</Option>
                    <Option value="ulb">ULB</Option>
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📊 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    mode="multiple"
                    placeholder="Select Y-axis data points"
                    value={yAxisSelectedFields}
                    onChange={handleYAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📈 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Button.Group size="small">
                    <Button
                      type={jointChartType === "bar" ? "primary" : "default"}
                      icon={<BarChartOutlined />}
                      onClick={() => setJointChartType("bar")}
                    >
                      Bar
                    </Button>
                    <Button
                      type={jointChartType === "line" ? "primary" : "default"}
                      icon={<LineChartOutlined />}
                      onClick={() => setJointChartType("line")}
                    >
                      Line
                    </Button>
                    <Button
                      type={jointChartType === "scatter" ? "primary" : "default"}
                      icon={<DotChartOutlined />}
                      onClick={() => setJointChartType("scatter")}
                    >
                      Scatter
                    </Button>
                  </Button.Group>
                </div>
              </div>
              <Empty
                description="No data available for scatter plot"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "60px 0" }}
              />
            </div>
          );
        }

        return (
          <div className="joint-graph-container">
            <div className="section">
              <div className="section-header">
                <DotChartOutlined className="section-icon" />
                <span className="section-title">Correlation Analysis</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Select
                    mode="multiple"
                    placeholder="Select X-axis"
                    value={xAxisSelectedFields}
                    onChange={handleXAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    <Option value="auto">Auto (Based on Filters)</Option>
                    <Option value="division">Division</Option>
                    <Option value="district">District</Option>
                    <Option value="ulb">ULB</Option>
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📊 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    mode="multiple"
                    placeholder="Select Y-axis data points"
                    value={yAxisSelectedFields}
                    onChange={handleYAxisChange}
                    size="small"
                    style={{ minWidth: "250px" }}
                    maxTagCount="responsive"
                    showSearch
                  >
                    {yAxisOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        📈 {option.label}
                      </Option>
                    ))}
                  </Select>

                  <Button.Group size="small">
                    <Button
                      type={jointChartType === "bar" ? "primary" : "default"}
                      icon={<BarChartOutlined />}
                      onClick={() => setJointChartType("bar")}
                    >
                      Bar
                    </Button>
                    <Button
                      type={jointChartType === "line" ? "primary" : "default"}
                      icon={<LineChartOutlined />}
                      onClick={() => setJointChartType("line")}
                    >
                      Line
                    </Button>
                    <Button
                      type={jointChartType === "scatter" ? "primary" : "default"}
                      icon={<DotChartOutlined />}
                      onClick={() => setJointChartType("scatter")}
                    >
                      Scatter
                    </Button>
                  </Button.Group>
                </div>
              </div>

              <div className="chart-metadata">
                <Space wrap>
                  <span className="metadata-label">X-Axis:</span>
                  <span className="metadata-value" style={{ fontWeight: 600, color: "#1890ff" }}>
                    {xDataField?.fieldName} (by {groupLabel})
                  </span>
                  <span className="metadata-divider">•</span>
                  <span className="metadata-label">Y-Axis:</span>
                  <span className="metadata-value" style={{ fontWeight: 600, color: "#52c41a" }}>
                    {yDataField?.fieldName}
                  </span>
                  <span className="metadata-divider">•</span>
                  <span className="metadata-label">Points:</span>
                  <span className="metadata-value" style={{ fontWeight: 600, color: "#722ed1" }}>
                    {scatterData.length} {groupLabel}s
                  </span>
                </Space>
              </div>

              <ResponsiveContainer width="100%" height={450}>
                <ScatterChart margin={{ top: 20, right: 30, left: 50, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name={`${xDataField?.fieldName} (${groupLabel})`}
                    label={{
                      value: `${xDataField?.fieldName} (${groupLabel})`,
                      position: "bottom",
                      offset: 0,
                      style: { fontSize: 12, fontWeight: 500 }
                    }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name={yDataField?.fieldName}
                    label={{
                      value: yDataField?.fieldName || "Y Axis",
                      angle: -90,
                      position: "left",
                      style: { fontSize: 12, fontWeight: 500 }
                    }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()}
                  />
                  <ZAxis dataKey="count" range={[60, 400]} name="Record Count" />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{
                            background: "#fff",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            minWidth: "220px"
                          }}>
                            <div><strong style={{ fontSize: "14px" }}>{data.name}</strong></div>
                            <div style={{ marginTop: "8px", borderTop: "1px solid #f0f0f0", paddingTop: "6px" }}>
                              <div><span style={{ color: "#1890ff" }}>{xDataField?.fieldName} ({groupLabel}):</span> <strong>{data.x.toLocaleString()}</strong></div>
                              <div><span style={{ color: "#52c41a" }}>{yDataField?.fieldName}:</span> <strong>{data.y.toLocaleString()}</strong></div>
                              <div><span style={{ color: "#722ed1" }}>Records:</span> <strong>{data.count}</strong></div>
                            </div>
                            <div style={{ marginTop: "6px", fontSize: "10px", color: "#1890ff", textAlign: "center" }}>
                              💡 Click to drill down
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter
                    data={scatterData}
                    fill="#1890ff"
                    shape="circle"
                    cursor="pointer"
                    onClick={(data) => {
                      if (data && data.payload && data.payload.name) {
                        handleBarClickForDrill(data.payload);
                      }
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      }

      // ==================== BAR / LINE CHART MODE ====================

      // Prepare chart data - Hierarchical grouping
      let chartData = [];

      // First, identify which ULBs meet the threshold condition (if any)
      const matchingUlbsSet = new Set();
      if (hasThresholdFilter && thresholdValue !== null && thresholdColumnName) {
        filteredDataForGraph.forEach((row) => {
          const ulbName = row.ulb_name || row.ulb;
          const thresholdVal = parseFloat(String(row[thresholdColumnName] || "").replace(/,/g, ""));

          if (!isNaN(thresholdVal) && isFinite(thresholdVal) && ulbName) {
            const isMatching = thresholdOperator === "lte"
              ? thresholdVal <= thresholdValue
              : thresholdVal >= thresholdValue;

            if (isMatching) {
              matchingUlbsSet.add(ulbName);
            }
          }
        });
      }

      const groupedData = {};

      // ========== FIX: Build list of fields to display ==========
      // Start with selected Y-axis fields
      let fieldsToDisplay = [...selectedYFields];

      // If percentage operation exists, add numerator and denominator as separate fields
      if (hasPercentageOperation && numeratorField) {
        // Remove the percentage field from Y-axis if it's selected (we'll add numerator and denominator instead)
        const percentageFieldIndex = fieldsToDisplay.findIndex(f => f.fieldId === numeratorField.fieldId);
        if (percentageFieldIndex !== -1) {
          fieldsToDisplay.splice(percentageFieldIndex, 1);
        }

        // Add numerator field
        const numeratorExists = fieldsToDisplay.some(f => f.fieldId === numeratorField.fieldId);
        if (!numeratorExists && numeratorField) {
          fieldsToDisplay.push(numeratorField);
        }

        // Add denominator field if found
        if (denominatorField) {
          const denominatorExists = fieldsToDisplay.some(f => f.fieldId === denominatorField.fieldId);
          if (!denominatorExists) {
            fieldsToDisplay.push(denominatorField);
          }
        }
      }

      // Debug log to see what fields will be displayed
      console.log("📊 Fields to display in chart:", fieldsToDisplay.map(f => ({
        name: f.fieldName,
        operation: f.operation,
        columnName: f.columnName
      })));

      filteredDataForGraph.forEach((row) => {
        let groupValue = null;
        let groupId = null;

        if (currentLevel === "division") {
          groupValue = row.division_name || row.division;
          groupId = row.division_id;
        } else if (currentLevel === "district") {
          groupValue = row.district_name || row.district;
          groupId = row.district_id;
        } else {
          groupValue = row.ulb_name || row.ulb;
          groupId = row.ulb_id;
        }

        if (!groupValue) return;

        let includeGroup = true;
        if (hasThresholdFilter && currentLevel !== "ulb") {
          const ulbName = row.ulb_name || row.ulb;
          includeGroup = matchingUlbsSet.has(ulbName);
        } else if (hasThresholdFilter && currentLevel === "ulb") {
          includeGroup = matchingUlbsSet.has(groupValue);
        }

        if (!includeGroup) return;

        if (!groupedData[groupValue]) {
          groupedData[groupValue] = {
            name: groupValue,
            originalName: groupValue,
            id: groupId,
            count: 0
          };

          // Initialize Y fields
          fieldsToDisplay.forEach((field) => {
            groupedData[groupValue][field.fieldName] = 0;
          });

          if (xAxisType === "dataPoint" && xAxisField) {
            groupedData[groupValue][`${xAxisField.fieldName}_x`] = 0;
          }

          if (hasThresholdFilter && currentLevel !== "ulb") {
            groupedData[groupValue].matchingUlbCount = 0;
          }
        }

        // Add values for all fields (including numerator and denominator)
        fieldsToDisplay.forEach((field) => {
          // Get the value from the row using the column name
          let value = 0;
          const rawValue = row[field.columnName];

          if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
            const parsedValue = parseFloat(String(rawValue).replace(/,/g, ""));
            if (!isNaN(parsedValue) && isFinite(parsedValue)) {
              value = parsedValue;
            }
          }

          groupedData[groupValue][field.fieldName] += value;
        });

        // Add X-axis value
        if (xAxisType === "dataPoint" && xAxisField) {
          const xValue = parseFloat(String(row[xAxisField.columnName] || "").replace(/,/g, ""));
          if (!isNaN(xValue) && isFinite(xValue)) {
            groupedData[groupValue][`${xAxisField.fieldName}_x`] += xValue;
          }
        }

        groupedData[groupValue].count++;

        if (hasThresholdFilter && currentLevel !== "ulb") {
          const ulbName = row.ulb_name || row.ulb;
          if (ulbName && matchingUlbsSet.has(ulbName)) {
            groupedData[groupValue].matchingUlbCount = (groupedData[groupValue].matchingUlbCount || 0) + 1;
          }
        }
      });

      // Format chart data
      chartData = Object.values(groupedData).map(item => {
        const result = {
          name: item.name,
          originalName: item.originalName,
          id: item.id,
          count: item.count
        };

        // Add Y values for all fields
        fieldsToDisplay.forEach(field => {
          result[field.fieldName] = item[field.fieldName] || 0;
        });

        if (xAxisType === "dataPoint" && xAxisField) {
          result.xValue = item[`${xAxisField.fieldName}_x`];
          result.name = `${item.name} (${item[`${xAxisField.fieldName}_x`].toLocaleString()})`;
        }

        if (hasThresholdFilter && currentLevel !== "ulb" && item.matchingUlbCount !== undefined) {
          result.name = `${item.name} (${item.matchingUlbCount} ULBs)`;
        }

        return result;
      }).sort((a, b) => a.name.localeCompare(b.name));

      if (chartData.length === 0) {
        const levelName = currentLevel === "division" ? "divisions" : currentLevel === "district" ? "districts" : "ULBs";
        const filterDescription = hasThresholdFilter && thresholdValue !== null && thresholdFieldName
          ? `No ${levelName} have ULBs meeting the condition ${thresholdFieldName} ${thresholdOperator === "lte" ? "≤" : "≥"} ${thresholdValue?.toLocaleString()}`
          : "No data available with current filters";
        return (
          <div>
            <div className="section-header" style={{ marginBottom: 16 }}>
              <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <Select
                  mode="multiple"
                  placeholder="Select X-axis"
                  value={xAxisSelectedFields}
                  onChange={handleXAxisChange}
                  size="small"
                  style={{ minWidth: "250px" }}
                  maxTagCount="responsive"
                  showSearch
                >
                  <Option value="auto">Auto (Based on Filters)</Option>
                  <Option value="division">Division</Option>
                  <Option value="district">District</Option>
                  <Option value="ulb">ULB</Option>
                  {yAxisOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      📊 {option.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  mode="multiple"
                  placeholder="Select Y-axis data points"
                  value={yAxisSelectedFields}
                  onChange={handleYAxisChange}
                  size="small"
                  style={{ minWidth: "250px" }}
                  maxTagCount="responsive"
                  showSearch
                >
                  {yAxisOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      📈 {option.label}
                    </Option>
                  ))}
                </Select>

                <Button.Group size="small">
                  <Button
                    type={jointChartType === "bar" ? "primary" : "default"}
                    icon={<BarChartOutlined />}
                    onClick={() => setJointChartType("bar")}
                  >
                    Bar
                  </Button>
                  <Button
                    type={jointChartType === "line" ? "primary" : "default"}
                    icon={<LineChartOutlined />}
                    onClick={() => setJointChartType("line")}
                  >
                    Line
                  </Button>
                  <Button
                    type={jointChartType === "scatter" ? "primary" : "default"}
                    icon={<DotChartOutlined />}
                    onClick={() => setJointChartType("scatter")}
                  >
                    Scatter
                  </Button>
                </Button.Group>
              </div>
            </div>
            <Empty
              description={filterDescription}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: "60px 0" }}
            />
          </div>
        );
      }

      const isLineChart = jointChartType === "line";

      const getActiveFiltersList = () => {
        const active = [];
        if (activeFiltersForGraph.division) active.push(`Division: ${activeFiltersForGraph.division}`);
        if (activeFiltersForGraph.district) active.push(`District: ${activeFiltersForGraph.district}`);
        if (activeFiltersForGraph.ulb_type) active.push(`ULB Type: ${activeFiltersForGraph.ulb_type}`);
        if (activeFiltersForGraph.ulb_class) active.push(`ULB Class: ${activeFiltersForGraph.ulb_class}`);
        if (activeFiltersForGraph.ulb_name) active.push(`ULB: ${activeFiltersForGraph.ulb_name}`);
        return active;
      };

      const activeFiltersList = getActiveFiltersList();

      const getXAxisDisplayLabel = () => {
        if (xAxisType === "dataPoint" && xAxisField) {
          return `${xAxisField.fieldName} (by ${groupLabel})`;
        }
        if (isAutoMode) return `Auto (${groupLabel} Level)`;
        return groupLabel;
      };

      const getDrillLevelDescription = () => {
        if (hasThresholdFilter) {
          return `🔍 Showing ${groupLabel}s that have ULBs where ${thresholdFieldName} ${thresholdOperator === "lte" ? "≤" : "≥"} ${thresholdValue?.toLocaleString()}`;
        }
        if (currentLevel === "division") return "📊 Division Level - Click any bar to see Districts within that division";
        if (currentLevel === "district") return "📍 District Level - Click any bar to see ULBs within that district";
        return "🏛️ ULB Level - Click any bar to apply ULB filter";
      };

      const handleBarClickForDrill = (data) => {
        if (!data || !data.name) return;

        let originalName = data.originalName || data.name;
        const match = originalName.match(/^(.*?)\s*\(\d+\s+ULBs\)$/);
        if (match) {
          originalName = match[1];
        }
        const xValueMatch = originalName.match(/^(.*?)\s*\([\d,]+\)$/);
        if (xValueMatch) {
          originalName = xValueMatch[1];
        }

        console.log("📊 Bar clicked:", { originalName, currentLevel, data });

        const savedXSelections = [...xAxisSelectedFields];
        const savedYSelections = [...yAxisSelectedFields];
        const savedChartType = jointChartType;

        if (currentLevel === "division") {
          console.log("📍 Drilling down to District level for division:", originalName);
          if (isInsideFormView) {
            handleDivisionChange(originalName);
          } else {
            handleFrontPageDivisionChange(originalName);
          }

          setTimeout(() => {
            setXAxisSelectedFields(savedXSelections);
            setYAxisSelectedFields(savedYSelections);
            setJointChartType(savedChartType);
            performCalculations();
          }, 150);
        }
        else if (currentLevel === "district") {
          console.log("📍 Drilling down to ULB level for district:", originalName);
          if (isInsideFormView) {
            handleDistrictChange(originalName);
          } else {
            handleFrontPageDistrictChange(originalName);
          }

          setTimeout(() => {
            setXAxisSelectedFields(savedXSelections);
            setYAxisSelectedFields(savedYSelections);
            setJointChartType(savedChartType);
            performCalculations();
          }, 150);
        }
        else if (currentLevel === "ulb") {
          console.log("📍 Applying ULB filter for:", originalName);
          if (isInsideFormView) {
            handleUlbChange(originalName);
          } else {
            handleFrontPageUlbChange(originalName);
          }

          setTimeout(() => {
            setXAxisSelectedFields(savedXSelections);
            setYAxisSelectedFields(savedYSelections);
            setJointChartType(savedChartType);
            performCalculations();
          }, 150);
        }
      };

      const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
          const dataPoint = payload[0].payload;
          let displayName = dataPoint.originalName || dataPoint.name;
          const match = displayName.match(/^(.*?)\s*\(\d+\s+ULBs\)$/);
          if (match) {
            displayName = match[1];
          }

          let xValueDisplay = "";
          if (xAxisType === "dataPoint" && xAxisField && dataPoint.xValue !== undefined) {
            xValueDisplay = (
              <div style={{ marginBottom: "8px", paddingBottom: "4px", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ color: "#1890ff" }}>{xAxisField.fieldName} ({groupLabel}):</span>
                <strong style={{ marginLeft: "8px" }}>{dataPoint.xValue?.toLocaleString()}</strong>
              </div>
            );
          }

          return (
            <div style={{ background: "#fff", padding: "12px 16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", minWidth: "260px" }}>
              <div><strong>{displayName}</strong></div>
              {dataPoint.id && (
                <div style={{ fontSize: "11px", color: "#8c8c8c", marginBottom: "8px" }}>
                  ID: {dataPoint.id}
                </div>
              )}
              {xValueDisplay}

              {hasThresholdFilter && currentLevel !== "ulb" && dataPoint.matchingUlbCount !== undefined && (
                <div style={{ marginBottom: "8px", paddingBottom: "4px", borderBottom: "1px solid #f0f0f0" }}>
                  <Tag color={thresholdOperator === "lte" ? "cyan" : "magenta"} style={{ marginRight: "4px" }}>
                    {thresholdOperator === "lte" ? "≤" : "≥"} {thresholdValue?.toLocaleString()}
                  </Tag>
                  <span style={{ fontSize: "12px" }}>
                    {dataPoint.matchingUlbCount} matching ULBs
                  </span>
                </div>
              )}

              {/* Show all fields in tooltip */}
              {fieldsToDisplay.map((field) => (
                <div key={field.fieldId} style={{ marginTop: "4px" }}>
                  <span style={{ color: getOperationColor(field.operation) }}>{field.fieldName}:</span>
                  <strong style={{ marginLeft: "8px" }}>
                    {dataPoint[field.fieldName]?.toLocaleString() || 0}
                  </strong>
                </div>
              ))}

              {currentLevel !== "ulb" && (
                <div style={{ marginTop: "8px", fontSize: "10px", color: "#1890ff", textAlign: "center" }}>
                  💡 Click to drill down
                </div>
              )}
            </div>
          );
        }
        return null;
      };

      const getSeriesColor = (index, fieldName = null) => {
        // Special colors for numerator and denominator
        if (hasPercentageOperation && numeratorField && fieldName === numeratorField.fieldName) {
          return "#1890ff"; // Blue for numerator
        }
        if (hasPercentageOperation && denominatorField && fieldName === denominatorField.fieldName) {
          return "#52c41a"; // Green for denominator
        }
        const colors = ["#1890ff", "#52c41a", "#fa8c16", "#f5222d", "#722ed1", "#13c2c2", "#eb2f96", "#faad14", "#a0d911", "#2f54eb"];
        return colors[index % colors.length];
      };

      // Build series keys - use fieldsToDisplay
      const seriesKeys = fieldsToDisplay.map(f => f.fieldName);

      return (
        <div className="joint-graph-container">
          <div className="section">
            <div className="section-header">
              {isLineChart ? <LineChartOutlined className="section-icon" /> : <BarChartOutlined className="section-icon" />}
              <span className="section-title">Comparative Analysis</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <Select
                  mode="multiple"
                  placeholder="Select X-axis"
                  value={xAxisSelectedFields}
                  onChange={handleXAxisChange}
                  size="small"
                  style={{ minWidth: "250px" }}
                  maxTagCount="responsive"
                  showSearch
                >
                  <Option value="auto">Auto (Based on Filters)</Option>
                  <Option value="division">Division</Option>
                  <Option value="district">District</Option>
                  <Option value="ulb">ULB</Option>
                  {yAxisOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      📊 {option.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  mode="multiple"
                  placeholder="Select Y-axis data points"
                  value={yAxisSelectedFields}
                  onChange={handleYAxisChange}
                  size="small"
                  style={{ minWidth: "250px" }}
                  maxTagCount="responsive"
                  showSearch
                >
                  {yAxisOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      📈 {option.label}
                    </Option>
                  ))}
                </Select>

                <Button.Group size="small">
                  <Button
                    type={jointChartType === "bar" ? "primary" : "default"}
                    icon={<BarChartOutlined />}
                    onClick={() => setJointChartType("bar")}
                  >
                    Bar
                  </Button>
                  <Button
                    type={jointChartType === "line" ? "primary" : "default"}
                    icon={<LineChartOutlined />}
                    onClick={() => setJointChartType("line")}
                  >
                    Line
                  </Button>
                  <Button
                    type={jointChartType === "scatter" ? "primary" : "default"}
                    icon={<DotChartOutlined />}
                    onClick={() => setJointChartType("scatter")}
                  >
                    Scatter
                  </Button>
                </Button.Group>
              </div>
            </div>

            <div className="chart-metadata">
              <Space wrap>
                <span className="metadata-label">X-Axis:</span>
                <span className="metadata-value" style={{ fontWeight: 600, color: "#1890ff" }}>
                  {getXAxisDisplayLabel()}
                </span>
                <span className="metadata-divider">•</span>
                <span className="metadata-label">Y-Axis:</span>
                <span className="metadata-value" style={{ fontWeight: 600, color: "#52c41a" }}>
                  {seriesKeys.join(", ")}
                </span>
                {activeFiltersList.length > 0 && (
                  <>
                    <span className="metadata-divider">•</span>
                    <FilterOutlined style={{ color: "#fa8c16", fontSize: 12 }} />
                    <span className="metadata-value">Filters: {activeFiltersList.join(", ")}</span>
                  </>
                )}
              </Space>
            </div>

            {/* Percentage Operation Banner - shows what fields are being compared */}
            {hasPercentageOperation && numeratorField && denominatorField && (
              <div style={{
                marginBottom: 16,
                padding: "8px 12px",
                background: "#e6f7ff",
                borderRadius: "8px",
                borderLeft: "3px solid #1890ff",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap"
              }}>
                <CalculatorOutlined style={{ color: "#1890ff", fontSize: "14px" }} />
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1890ff" }}>
                  Percentage Analysis:
                </span>
                <span style={{ fontSize: "12px" }}>
                  <strong style={{ color: "#1890ff" }}>{numeratorField.fieldName}</strong> (Numerator) vs
                  <strong style={{ color: "#52c41a", marginLeft: "8px" }}>{denominatorField.fieldName}</strong> (Denominator)
                </span>
                <Tag color="blue" style={{ margin: 0 }}>
                  {numeratorField.fieldName} / {denominatorField.fieldName}
                </Tag>
              </div>
            )}

            {/* Threshold Filter Banner */}
            {hasThresholdFilter && thresholdValue !== null && thresholdFieldName && (
              <div style={{
                marginBottom: 16,
                padding: "8px 12px",
                background: thresholdOperator === "lte" ? "#e6fffb" : "#fff0f6",
                borderRadius: "8px",
                borderLeft: `3px solid ${thresholdColor}`,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap"
              }}>
                <span style={{ fontSize: "13px", fontWeight: 500, color: thresholdColor }}>
                  {thresholdOperator === "lte" ? "🔽" : "🔼"} Filter Applied:
                </span>
                <span style={{ fontSize: "12px" }}>
                  Showing {groupLabel}s where {thresholdFieldName} {thresholdOperator === "lte" ? "≤" : "≥"} {thresholdValue?.toLocaleString()}
                </span>
                <Tag color={thresholdOperator === "lte" ? "cyan" : "magenta"} style={{ margin: 0 }}>
                  {chartData.length} {groupLabel}s meet condition
                </Tag>
              </div>
            )}

            <div style={{ marginBottom: 16, padding: "8px 12px", background: "#e6f7ff", borderRadius: "8px" }}>
              <span style={{ fontSize: "13px", fontWeight: 500, color: "#1890ff" }}>
                🔍 {getDrillLevelDescription()}
              </span>
            </div>

            <ResponsiveContainer width="100%" height={450}>
              {isLineChart ? (
                <LineChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 50, bottom: 80 }}
                  onClick={(e) => {
                    if (e && e.activePayload && e.activePayload.length) {
                      handleBarClickForDrill(e.activePayload[0].payload);
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  {seriesKeys.map((key, index) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={getSeriesColor(index, key)}
                      strokeWidth={2}
                      dot={{
                        cursor: "pointer",
                        r: 4,
                        onClick: (dotData) => {
                          if (dotData && dotData.payload) {
                            handleBarClickForDrill(dotData.payload);
                          }
                        }
                      }}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 50, bottom: 80 }}
                  onClick={(e) => {
                    if (e && e.activePayload && e.activePayload.length) {
                      handleBarClickForDrill(e.activePayload[0].payload);
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  {seriesKeys.map((key, index) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      fill={getSeriesColor(index, key)}
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(barData) => {
                        if (barData && barData.payload) {
                          handleBarClickForDrill(barData.payload);
                        }
                      }}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>

            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                💡 <strong>Tip:</strong> Select X-axis and Y-axis data points to compare them. Click on any bar to drill down.
                {hasPercentageOperation && denominatorField && " When a percentage operation is selected, both the numerator (Blue) and denominator (Green) fields are shown as separate bars."}
              </Text>
            </div>
          </div>
        </div>
      );
    };



    // Calculate matching count for display
    const matchingUlbsCount = calculateMatchingUlbsCount();

    return (
      <div style={{ marginTop: 0, paddingTop: 0 }}>  {/* ✅ Changed from marginTop: 24 to 0 */}

        {/* Empty Card removed - commented out already */}

        {showOnlyMatchingUlbs && hasMultipleFilterOps ? (
          renderMatchingUlbsList()
        ) : (
          <>
            {/* 1. KEY METRICS SUMMARY */}
            {dashboardConfig.showKeyMetricsSummary && renderKPIcards()}
            {renderKPIcards()}

           {/* 2. RANKING TABLE - ✅ Only show if enabled */}
{dashboardConfig.showRankingTable && hasRankingOperation && rankingResult && renderRankingTable()}


            {/* 3. DIVISION-WISE CARDS - ✅ Only show if enabled */}
{dashboardConfig.showDivisionWiseAnalysis && !hasRankingOperation && calculationResults.length > 0 && rawDataForAnalysis.length > 0 && renderDivisionWiseCards()}

            {/* 4. Analysis Results Card with Tabs */}
            {!hasRankingOperation && (
              <Card
                extra={
                  <Button
                    icon={<LeftOutlined />}
                    onClick={() => {
                      setShowAnalysisResults(false);
                      setSelectedOperations([]);
                      setCalculationResults([]);
                      setRawDataForAnalysis([]);
                      setAnalysisDataColumns([]);
                      setHierarchicalStatistics({});
                      setSelectedStatsField(null);

                      setFrontPageFilters({
                        division: null,
                        district: null,
                        ulb_type: null,
                        ulb_class: null,
                        ulb_name: null,
                      });

                      setIsFilterApplied(false);
                      loadForms({});
                      setXAxisSelectedFields(['auto']);
                      setYAxisSelectedFields([]);
                    }}
                  >
                    Back to Forms
                  </Button>
                }
                style={{
                  marginBottom: 0,  // ✅ Changed from 24 to 0 - removes bottom gap
                  marginTop: 0,     // ✅ Added - removes top gap
                  borderRadius: "16px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <Tabs
                  defaultActiveKey="graph"
                  type="card"
                  style={{ margin: 0 }}
                  tabBarStyle={{
                    margin: 0,
                    padding: "0 24px",
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: "16px",
                  }}
                 items={[
  // ✅ Graph Tab - Only show if enabled
  dashboardConfig.showJointGraph && {
    key: "graph",
    label: (
      <Space size="small">
        <AreaChartOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
        <span style={{ fontWeight: 500 }}>Comparative Analysis</span>
      </Space>
    ),
    children: <div style={{ padding: "24px" }}>{renderJointGraph()}</div>,
  },
  // ✅ Data Tab - Only show if enabled
  dashboardConfig.showRowDataTable && {
    key: "data",
    label: (
      <Space size="small">
        <TableOutlined style={{ fontSize: "16px", color: "#722ed1" }} />
        <span style={{ fontWeight: 500 }}>Row Data</span>
      </Space>
    ),
                      children: (
                        <div style={{ padding: "24px" }}>
                          {rawDataForAnalysis.length > 0 ? (
                            <>
                              <Table
                                dataSource={rawDataForAnalysis.slice(0, 100)}
                                columns={tableColumns}
                                rowKey={(record, index) => `data_${index}`}
                                scroll={{ x: "max-content", y: 500 }}
                                size="small"
                                pagination={{
                                  pageSize: 20,
                                  showSizeChanger: true,
                                  showTotal: (total, range) => (
                                    <span style={{ color: "#8c8c8c", fontSize: "12px" }}>
                                      {range[0]}-{range[1]} of {total} records
                                    </span>
                                  ),
                                  style: { marginTop: 16 },
                                }}
                                style={{ borderRadius: "8px", overflow: "hidden" }}
                              />
                              {rawDataForAnalysis.length > 100 && (
                                <div style={{ marginTop: 16, textAlign: "center" }}>
                                  <Text type="secondary" style={{ fontSize: "12px" }}>
                                    Showing first 100 records. Use filters to see more specific data.
                                  </Text>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ textAlign: "center", padding: 60 }}>
                              <TableOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
                              <div style={{ color: "#8c8c8c" }}>No data available</div>
                            </div>
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </div>
    );
  };

  const loadAllDataPoints = async () => {
    try {
      setLoadingAllDataPoints(true);
      const allPoints = [];

      for (const form of forms) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/reports/forms/${form.id}/schema?report_enabled=true`,
            {
              headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
            },
          );

          if (response.data.success && response.data.reportEnabledFields) {
            response.data.reportEnabledFields.forEach((field) => {
              allPoints.push({
                ...field,
                formId: form.id,
                formName: form.displayName || form.name || form.formName || `Form ${form.id}`,
                formDisplayName: form.displayName || form.name || form.formName || `Form ${form.id}`,
              });
            });
          }
        } catch (error) {
          console.error(`Error loading fields for form ${form.id}:`, error);
        }
      }

      setAllDataPoints(allPoints);
      setFilteredDataPoints(allPoints); // Initialize filtered data points
      console.log(`📊 Loaded ${allPoints.length} data points from ${forms.length} forms`);
    } catch (error) {
      console.error("Error loading all data points:", error);

    } finally {
      setLoadingAllDataPoints(false);
    }
  };
  // Add this useEffect for debugging selectedField changes
  useEffect(() => {
    console.log("🔍 [DEBUG] selectedField changed:", selectedField);
    console.log("🔍 [DEBUG] selectedField exists?", !!selectedField);
    if (selectedField) {
      console.log("🔍 [DEBUG] selectedField details:", {
        field_id: selectedField.field_id,
        display_name: selectedField.display_name,
        formId: selectedField.formId,
        formDisplayName: selectedField.formDisplayName
      });
    }
  }, [selectedField]);
  // Load all data points when forms are loaded
  useEffect(() => {
    if (forms.length > 0) {
      loadAllDataPoints();
    }
  }, [forms]);
  // Add this useEffect for debugging in your component (around line where other useEffects are)
  useEffect(() => {
    console.log("🔍 selectedField changed:", selectedField);
    console.log("🔍 selectedField exists?", !!selectedField);
    if (selectedField) {
      console.log("🔍 selectedField details:", {
        field_id: selectedField.field_id,
        display_name: selectedField.display_name,
        formId: selectedField.formId
      });
    }
  }, [selectedField]);
  // AUTO-ENABLE Advanced Analysis toggle when multiple LTE/GTE conditions exist
  // Auto-enable Advanced Analysis toggle when multiple filter operations exist
  useEffect(() => {
    // Check if we have ANY filter operations (lte, gte, min, max, percentage)
    const filterOperations = calculationResults.filter(result =>
      result.operation === "lte" ||
      result.operation === "gte" ||
      result.operation === "min" ||
      result.operation === "max" ||
      result.operation === "percentage"
    );

    const hasMultipleFilterOps = filterOperations.length >= 2;

    // Automatically enable the toggle when multiple conditions exist
    if (hasMultipleFilterOps && !showOnlyMatchingUlbs) {
      console.log("🔍 Multiple filter operations detected - Auto-enabling Advanced Analysis");
      setShowOnlyMatchingUlbs(true);
    }

    // Disable the toggle when conditions are removed
    if (!hasMultipleFilterOps && showOnlyMatchingUlbs) {
      console.log("🔍 Less than 2 filter operations - Auto-disabling Advanced Analysis");
      setShowOnlyMatchingUlbs(false);
    }
  }, [calculationResults]);

  //image slideshow
  // Auto-slideshow for background images from DB
  useEffect(() => {
    if (dashboardBgImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBgImageIndex((prev) => (prev + 1) % dashboardBgImages.length);
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [dashboardBgImages.length]);

  // जेव्हा dashboardBgImages बदलतील तेव्हा currentBgImageIndex reset करा
  useEffect(() => {
    if (dashboardBgImages.length > 0) {
      setCurrentBgImageIndex(0);
    }
  }, [dashboardBgImages]);
  // ==================== RENDER FRONT PAGE FILTER SECTION ====================
  const renderFrontPageFilterSection = () => {
  if (!dashboardConfig.showFrontPageFilters) return null;

  // ========== CHECK FOR ROLES THAT SHOULD HIDE THE FILTER SECTION ==========
  const isUlbAdmin = !hasFullAccess() && (
    userRole === "ULB_ADMIN" ||
    userRole === "ulb_admin" ||
    userRole === "ULB_OFFICER" ||
    userRole === "ULB_CHIEF_OFFICIER"
  );

  const isDivisionAdmin = !hasFullAccess() && (
    userRole === "DIVISION_ADMIN" ||
    userRole === "division_admin" ||
    userRole === "DIVISIONAL_OFFICER" ||
    userRole === "divisional_officer" ||
    userRole === "Divisionalcommissioner"
  );

  const isDistrictAdmin = !hasFullAccess() && (
    userRole === "DISTRICT_ADMIN" ||
    userRole === "district_admin"
  );

  // If ULB admin, Division Admin, or District Admin, hide the entire filter box
  if (isUlbAdmin || isDivisionAdmin || isDistrictAdmin) {
    return null;
  }

  const hasActiveFilters =
    frontPageFilters.division ||
    frontPageFilters.district ||
    frontPageFilters.ulb_type ||
    frontPageFilters.ulb_class ||
    frontPageFilters.ulb_name;

  // Check if filter should be disabled based on user role
  const isFilterDisabled = (filterType) => {
    if (hasFullAccess()) return false;

    const role = userRole;

    if (filterType === "division") {
      return (
        role === "DISTRICT_ADMIN" ||
        role === "district_admin" ||
        role === "DIVISION_ADMIN" ||
        role === "division_admin" ||
        role === "DIVISIONAL_OFFICER" ||
        role === "divisional_officer" ||
        role === "Divisionalcommissioner" ||
        role === "ULB_ADMIN" ||
        role === "ulb_admin" ||
        role === "ULB_OFFICER" ||
        role === "ULB_CHIEF_OFFICIER"
      );
    }

    if (filterType === "district") {
      return (
        role === "DISTRICT_ADMIN" ||
        role === "district_admin" ||
        role === "ULB_ADMIN" ||
        role === "ulb_admin" ||
        role === "ULB_OFFICER" ||
        role === "ULB_CHIEF_OFFICIER"
      );
    }

    if (filterType === "ulb") {
      return (
        role === "ULB_ADMIN" ||
        role === "ulb_admin" ||
        role === "ULB_OFFICER" ||
        role === "ULB_CHIEF_OFFICIER"
      );
    }

    return false;
  };

  // Get ALL filter operations (lte, gte, min, max, percentage) for advanced analysis
  const getFilterOperations = () => {
    // Get all LTE/GTE operations
    const lteGteOps = calculationResults.filter(result =>
      result.operation === "lte" || result.operation === "gte"
    );

    // Get percentage operations
    const percentageOps = calculationResults.filter(result =>
      result.operation === "percentage"
    );

    // Get min/max operations
    const minMaxOps = calculationResults.filter(result =>
      result.operation === "min" || result.operation === "max"
    );

    // Get ranking operations - include ALL ranking operations regardless of field
    const rankingOps = calculationResults.filter(result =>
      result.operation === "ranking" && (result.rankingType === "top" || result.rankingType === "bottom")
    );

    return [...lteGteOps, ...percentageOps, ...minMaxOps, ...rankingOps];
  };

  const filterOperations = getFilterOperations();
  const hasMultipleFilterOps = filterOperations.length >= 2;

  // Helper function to get condition text for an operation
  const getOperationConditionText = (op) => {
    switch (op.operation) {
      case 'lte': return `≤ ${parseFloat(op.threshold).toLocaleString()}`;
      case 'gte': return `≥ ${parseFloat(op.threshold).toLocaleString()}`;
      case 'min': return `= Minimum`;
      case 'max': return `= Maximum`;
      case 'percentage': return op.percentageSecondFieldName ? `/ ${op.percentageSecondFieldName}` : '%';
      default: return '';
    }
  };

  // Calculate matching ULBs count for all operations
  const calculateMatchingUlbsCount = () => {
    if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return 0;

    // Get all operations
    const allOperations = [...calculationResults];
    if (allOperations.length === 0) return 0;

    // Group data by ULB and calculate aggregates
    const ulbAggregates = new Map();

    rawDataForAnalysis.forEach((row) => {
      const ulbName = row.ulb_name || row.ulb;
      if (!ulbName) return;

      if (!ulbAggregates.has(ulbName)) {
        ulbAggregates.set(ulbName, {
          ulb: ulbName,
          district: row.district_name || row.district,
          division: row.division_name || row.division,
          values: {},      // For regular numeric fields
          percentages: {}   // For percentage calculations
        });
      }

      const ulbData = ulbAggregates.get(ulbName);

      // Collect values for regular operations
      allOperations.forEach(op => {
        if (op.operation !== "percentage") {
          const value = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
          if (!isNaN(value) && isFinite(value)) {
            if (!ulbData.values[op.fieldId]) {
              ulbData.values[op.fieldId] = 0;
            }
            ulbData.values[op.fieldId] += value;
          }
        }
      });

      // Collect values for percentage operations
      allOperations.forEach(op => {
        if (op.operation === "percentage" && op.percentageSecondColumnName) {
          const numerator = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
          const denominator = parseFloat(String(row[op.percentageSecondColumnName] || "").replace(/,/g, ""));

          if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
            if (!ulbData.percentages[op.fieldId]) {
              ulbData.percentages[op.fieldId] = { numerator: 0, denominator: 0 };
            }
            ulbData.percentages[op.fieldId].numerator += numerator;
            ulbData.percentages[op.fieldId].denominator += denominator;
          }
        }
      });
    });

    // Calculate percentage values for each ULB for the percentage operation
    const percentageOp = allOperations.find(op => op.operation === "percentage");
    const rankingOp = allOperations.find(op => op.operation === "ranking");

    // Create a map of ULB -> percentage value (if percentage operation exists)
    const ulbPercentageMap = new Map();
    if (percentageOp) {
      for (const [ulbName, ulbData] of ulbAggregates.entries()) {
        const percentData = ulbData.percentages[percentageOp.fieldId];
        if (percentData && percentData.denominator > 0) {
          const percentage = (percentData.numerator / percentData.denominator) * 100;
          ulbPercentageMap.set(ulbName, percentage);
        }
      }
    }

    // Get ranking based on the ranking operation's field (could be different from percentage field)
    let rankingUlbs = new Set();
    if (rankingOp && ulbAggregates.size > 0) {
      // Sort ULBs by the ranking field value
      const sortedUlbs = Array.from(ulbAggregates.entries())
        .map(([ulbName, ulbData]) => ({
          ulb: ulbName,
          value: ulbData.values[rankingOp.fieldId] || 0,
          district: ulbData.district,
          division: ulbData.division
        }))
        .sort((a, b) => {
          if (rankingOp.rankingType === "top") {
            return b.value - a.value;
          } else {
            return a.value - b.value;
          }
        });

      const rankingCount = rankingOp.rankingCount || 10;
      const topList = sortedUlbs.slice(0, rankingCount);
      topList.forEach(item => rankingUlbs.add(item.ulb));
    }

    // Check each ULB against ALL conditions
    let matchingCount = 0;

    for (const [ulbName, ulbData] of ulbAggregates.entries()) {
      let meetsAll = true;

      for (const op of allOperations) {
        if (op.operation === "ranking") {
          // Check if this ULB is in the ranking list (based on the ranking field)
          if (!rankingUlbs.has(ulbName)) {
            meetsAll = false;
            break;
          }
          continue;
        }

        // Skip ranking operations already handled
        if (op.operation === "ranking") continue;

        let conditionValue = null;
        let shouldCheck = true;

        // For percentage operation
        if (op.operation === "percentage") {
          const percentValue = ulbPercentageMap.get(ulbName);
          if (percentValue !== undefined) {
            conditionValue = percentValue;
          } else {
            shouldCheck = false;
          }
        }
        // For LTE/GTE operation
        else if (op.operation === "lte" || op.operation === "gte") {
          conditionValue = ulbData.values[op.fieldId] || 0;
          if (conditionValue === 0) shouldCheck = false;
        }
        // For min/max operations
        else if (op.operation === "min" || op.operation === "max") {
          conditionValue = ulbData.values[op.fieldId] || 0;
          if (conditionValue === 0) shouldCheck = false;
        }

        if (!shouldCheck) {
          meetsAll = false;
          break;
        }

        // Check the condition
        if (op.operation === "lte") {
          const threshold = parseFloat(op.threshold);
          if (!(conditionValue <= threshold)) meetsAll = false;
        } else if (op.operation === "gte") {
          const threshold = parseFloat(op.threshold);
          if (!(conditionValue >= threshold)) meetsAll = false;
        } else if (op.operation === "min") {
          // Find global minimum for this field
          let minValue = Infinity;
          for (const [otherUlb, otherData] of ulbAggregates.entries()) {
            const otherValue = otherData.values[op.fieldId] || 0;
            if (otherValue > 0 && otherValue < minValue) {
              minValue = otherValue;
            }
          }
          if (conditionValue !== minValue) meetsAll = false;
        } else if (op.operation === "max") {
          // Find global maximum for this field
          let maxValue = -Infinity;
          for (const [otherUlb, otherData] of ulbAggregates.entries()) {
            const otherValue = otherData.values[op.fieldId] || 0;
            if (otherValue > maxValue) {
              maxValue = otherValue;
            }
          }
          if (conditionValue !== maxValue) meetsAll = false;
        } else if (op.operation === "percentage") {
          // For percentage operation without a threshold, always true
        }

        if (!meetsAll) break;
      }

      if (meetsAll) {
        matchingCount++;
      }
    }

    return matchingCount;
  };

  return (
    <Card
      style={{
        marginBottom: 24,
        width: "100%",
        background: "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)",
        border: "none",
        borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      }}
      bodyStyle={{
        padding: "12px 16px",
        background: "transparent",
      }}
      size="small"
    >

      {/* SECTION 1: Location Filters */}
      <div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "flex-end",
            marginBottom: "20px",
          }}
        >
          {/* ULB Type Filter */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <Select
              placeholder="ULB Type"
              value={frontPageFilters.ulb_type}
              onChange={handleFrontPageUlbTypeChange}
              allowClear={!isFilterDisabled("ulb_type")}
              style={{ width: "100%" }}
              loading={loadingFilters}
              showSearch
              disabled={isFilterDisabled("ulb_type")}
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
            >
              <Option value="">All Types</Option>
              {frontPageFilterOptions.ulbTypes.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>

          {/* ULB Class Filter */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <Select
              placeholder="ULB Class"
              value={frontPageFilters.ulb_class}
              onChange={handleFrontPageUlbClassChange}
              allowClear={true}
              style={{ width: "100%" }}
              loading={loadingFilters}
              showSearch
              disabled={false}
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
              notFoundContent={
                loadingFilters ? (
                  <Spin size="small" />
                ) : (
                  "No classes available"
                )
              }
            >
              <Option value="">All Classes</Option>
              {frontPageFilterOptions.ulbClasses &&
                frontPageFilterOptions.ulbClasses.length > 0 ? (
                frontPageFilterOptions.ulbClasses.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))
              ) : (
                <Option disabled value="no-data">
                  No ULB Classes found
                </Option>
              )}
            </Select>
          </div>

          {/* Division Filter */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <Select
              placeholder="Division"
              value={frontPageFilters.division}
              onChange={handleFrontPageDivisionChange}
              allowClear={!isFilterDisabled("division")}
              style={{ width: "100%" }}
              loading={loadingFilters}
              showSearch
              disabled={isFilterDisabled("division")}
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
              notFoundContent={
                loadingFilters ? (
                  <Spin size="small" />
                ) : (
                  "No divisions available"
                )
              }
            >
              <Option value="">All Divisions</Option>
              {frontPageFilterOptions.divisions &&
                frontPageFilterOptions.divisions.length > 0 ? (
                frontPageFilterOptions.divisions.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))
              ) : (
                <Option disabled value="no-data">
                  No Divisions found
                </Option>
              )}
            </Select>
          </div>

          {/* District Filter */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <Select
              placeholder={
                frontPageFilters.division
                  ? "District"
                  : "District (Select Division first)"
              }
              value={frontPageFilters.district}
              onChange={handleFrontPageDistrictChange}
              allowClear={!isFilterDisabled("district")}
              style={{ width: "100%" }}
              loading={loadingFilters}
              showSearch
              disabled={
                isFilterDisabled("district") ||
                (!frontPageFilters.division && !hasFullAccess())
              }
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff" }}
            >
              <Option value="">All Districts</Option>
              {frontPageFilterOptions.districts.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>

          {/* ULB Filter */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <Select
              placeholder={
                frontPageFilters.district
                  ? "ULB"
                  : frontPageFilters.division
                    ? "Select District first"
                    : "ULB (Select Division/District first)"
              }
              value={frontPageFilters.ulb_name}
              onChange={handleFrontPageUlbChange}
              allowClear={!isFilterDisabled("ulb")}
              style={{ width: "100%" }}
              loading={loadingFilters}
              showSearch
              disabled={
                isFilterDisabled("ulb") ||
                (!frontPageFilters.district &&
                  !frontPageFilters.division &&
                  !hasFullAccess())
              }
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff" }}
            >
              <Option value="">All ULBs</Option>
              {frontPageFilterOptions.ulbs.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={applyFrontPageFilters}
              loading={loading}
              size="middle"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#ffffff",
                color: "#667eea",
                borderRadius: "8px",
                fontWeight: 500,
              }}
            >
              Apply
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={resetFrontPageFilters}
              size="middle"
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderColor: "rgba(255,255,255,0.3)",
                color: "#ffffff",
                borderRadius: "8px",
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* SECTION 2: Subject and Data Points Selection */}
      <div style={{ marginTop: hasMultipleFilterOps ? "0px" : "16px" }}>
        {/* Badge indicators */}
        {(selectedSubjectForm || selectedOperations.length > 0 || isVisitor()) && (
          <div
            style={{
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {isVisitor() && (
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "20px",
                  padding: "2px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                <WarningOutlined style={{ color: "#fa8c16", fontSize: "12px" }} />
                <span style={{ color: "#fa8c16", fontSize: "12px", fontWeight: 600 }}>
                  Read Only Mode
                </span>
              </div>
            )}
          </div>
        )}

        {/* HORIZONTAL SCROLL CONTAINER FOR SUBJECT FILTERS */}
        <div
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "thin",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "8px",
            marginBottom: "1px",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              gap: "12px",
              alignItems: "flex-end",
              flexWrap: "nowrap",
              minWidth: "100%",
            }}
          >
            {/* Subject (Form) Selection Dropdown */}
            <div style={{ flex: "0 0 auto", minWidth: "220px", maxWidth: "280px" }}>
              <Select
                placeholder="Select Subject (Form)"
                value={selectedSubjectForm ? String(selectedSubjectForm.id) : undefined}
                onChange={async (value) => {
                  if (!value) {
                    setSelectedSubjectForm(null);
                    setFilteredDataPoints(allDataPoints);
                    return;
                  }

                  const selectedForm = forms.find(f => String(f.id) === String(value));
                  setSelectedSubjectForm(selectedForm);

                  const filtered = allDataPoints.filter(
                    dp => String(dp.formId) === String(value)
                  );
                  setFilteredDataPoints(filtered);
                  setSelectedField(null);
                  setPercentageSecondField(null);
                  setShowPercentageSecondField(false);
                }}
                style={{ width: "100%" }}
                loading={loading || forms.length === 0}
                showSearch
                allowClear
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff" }}
                notFoundContent={loading ? <Spin size="small" /> : "No subjects available"}
                optionLabelProp="label"
              >
                {forms.map((form) => (
                  <Option
                    key={form.id}
                    value={String(form.id)}
                    label={form.displayName || form.tableName || `Form ${form.id}`}
                  >
                    <div style={{ padding: "4px 0" }}>
                      <div style={{ fontWeight: 500, fontSize: "13px", color: "#333" }}>
                        {form.displayName || form.tableName || `Form ${form.id}`}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>

            {/* Data Points Dropdown */}
            <div style={{ flex: "0 0 auto", minWidth: "220px", maxWidth: "280px" }}>
              <Select
                placeholder={
                  selectedSubjectForm
                    ? `Choose data point from ${selectedSubjectForm.displayName}`
                    : "Choose data point to analyze"
                }
                value={selectedField ? `${selectedField.formId}_${selectedField.field_id}` : undefined}
                onChange={async (value) => {
                  if (isVisitor()) {
                    notification.warning({
                      message: "Read Only Access",
                      description: "As a Visitor, you can view data but cannot perform analysis.",
                      duration: 3,
                    });
                    return;
                  }

                  if (!value) {
                    setSelectedField(null);
                    setSelectedFilterForm(null);
                    setSelectedOperations([]);
                    setCalculationResults([]);
                    setShowAnalysisResults(false);
                    setPercentageSecondField(null);
                    setShowPercentageSecondField(false);
                    return;
                  }

                  const underscoreIndex = value.indexOf('_');
                  const formId = value.substring(0, underscoreIndex);
                  const field_id = value.substring(underscoreIndex + 1);

                  const dataPointsToSearch = selectedSubjectForm ? filteredDataPoints : allDataPoints;
                  const selectedDataPoint = dataPointsToSearch.find(
                    dp => String(dp.formId) === String(formId) && dp.field_id === field_id
                  );

                  if (selectedDataPoint) {
                    const dataPointWithFormId = {
                      ...selectedDataPoint,
                      formId: parseInt(formId),
                    };

                    const form = forms.find(f => String(f.id) === String(formId));
                    setSelectedFilterForm(form);
                    setSelectedField(dataPointWithFormId);
                    setSelectedOperation("sum");
                    setShowThresholdInput(false);
                    setShowPercentageSecondField(false);
                    setPercentageSecondField(null);

                    await loadFilterFormFields(parseInt(formId));
                  }
                }}
                style={{ width: "100%" }}
                loading={loadingAllDataPoints}
                showSearch
                filterOption={(input, option) => {
                  if (!option || !option.children) return false;

                  const searchText = input?.toLowerCase() || "";

                  const getTextFromChildren = (children) => {
                    if (typeof children === 'string') return children;
                    if (React.isValidElement(children)) {
                      return getTextFromChildren(children.props.children);
                    }
                    if (Array.isArray(children)) {
                      return children.map(getTextFromChildren).join(' ');
                    }
                    return '';
                  };

                  const optionText = getTextFromChildren(option.children).toLowerCase();
                  const optionValue = String(option.value || "").toLowerCase();

                  return optionText.includes(searchText) || optionValue.includes(searchText);
                }}
                disabled={isVisitor()}
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff" }}
                notFoundContent={
                  loadingAllDataPoints ? <Spin size="small" /> :
                    selectedSubjectForm ? "No data points available for this subject" : "No data points available"
                }
                optionLabelProp="label"
              >
                {(selectedSubjectForm ? filteredDataPoints : allDataPoints).map((dataPoint) => {
                  const optionValue = `${dataPoint.formId}_${dataPoint.field_id}`;
                  const optionLabel = `${dataPoint.formDisplayName} - ${dataPoint.display_name || dataPoint.field_id}`;

                  return (
                    <Option
                      key={optionValue}
                      value={optionValue}
                      label={optionLabel}
                    >
                      <div style={{ padding: "4px 0" }}>
                        <div style={{ fontWeight: 500, fontSize: "13px", color: "#333" }}>
                          {dataPoint.display_name || dataPoint.field_id}
                        </div>
                        <div style={{ fontSize: "10px", color: "#8c8c8c" }}>
                          Subject: {dataPoint.formDisplayName}
                          {dataPoint.report_important && (
                            <StarOutlined style={{ color: "#faad14", marginLeft: "8px" }} />
                          )}
                        </div>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            </div>

            {/* Operation Selection */}
            <Select
              placeholder="Select Operation"
              value={selectedOperation}
              onChange={(value) => {
                setSelectedOperation(value);
                setShowThresholdInput(value === "lte" || value === "gte");
                setShowPercentageSecondField(value === "percentage");
                setShowRankingOptions(value === "ranking");
                if (value !== "percentage") {
                  setPercentageSecondField(null);
                }
                if (value !== "ranking") {
                  setRankingType("top");
                  setRankingOrder("desc");
                  setRankingCount(10);
                  setShowRankingCountInput(false);
                } else {
                  setShowRankingCountInput(rankingType === "top" || rankingType === "bottom");
                }
              }}
              style={{ width: "200px" }}
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff" }}
            >
              {operations.map((op) => (
                <Option key={op.value} value={op.value}>
                  <Space>
                    <span>{op.value === "lte" ? "less than equal to" : op.value === "gte" ? "greater than equal to" : op.label}</span>
                  </Space>
                </Option>
              ))}
            </Select>

            {/* Ranking Options with Number Input */}
            {showRankingOptions && selectedField && (
              <>
                {/* Ranking Type Select */}
                <div style={{ flex: "0 0 auto", minWidth: "150px", maxWidth: "180px" }}>
                  <Select
                    placeholder="Ranking Type"
                    value={rankingType}
                    onChange={(value) => {
                      setRankingType(value);
                      setShowRankingCountInput(value === "top" || value === "bottom");
                    }}
                    style={{ width: "100%" }}
                    size="middle"
                    dropdownStyle={{ backgroundColor: "#ffffff" }}
                  >
                    <Option value="top">Top</Option>
                    <Option value="bottom">Bottom</Option>
                    <Option value="all">All ULBs</Option>
                  </Select>
                </div>

                {/* Ranking Count Input - Shows for Top/Bottom */}
                {showRankingCountInput && rankingType !== "all" && (
                  <div style={{ flex: "0 0 auto", minWidth: "120px", maxWidth: "150px" }}>
                    <Input
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Number"
                      value={rankingCount}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setRankingCount(isNaN(value) ? 10 : Math.max(1, Math.min(500, value)));
                      }}
                      size="middle"
                      style={{ width: "100%" }}
                      addonAfter="ULBs"
                    />
                  </div>
                )}

                {/* Sort Order Dropdown - Shows for ALL ranking types */}
                <div style={{ flex: "0 0 auto", minWidth: "150px", maxWidth: "180px" }}>
                  <Select
                    placeholder="Sort Order"
                    value={rankingOrder}
                    onChange={setRankingOrder}
                    style={{ width: "100%" }}
                    size="middle"
                    dropdownStyle={{ backgroundColor: "#ffffff" }}
                  >
                    <Option value="desc">
                      {rankingType === "top" ? "Highest to Lowest ⬇️" :
                        rankingType === "bottom" ? "Lowest to Highest ⬆️" :
                          "Highest to Lowest (Descending)"}
                    </Option>
                    <Option value="asc">
                      {rankingType === "top" ? "Lowest to Highest ⬆️" :
                        rankingType === "bottom" ? "Highest to Lowest ⬇️" :
                          "Lowest to Highest (Ascending)"}
                    </Option>
                  </Select>
                </div>
              </>
            )}

            {/* Second Field Selection for Percentage */}
            {showPercentageSecondField && selectedField && (
              <div style={{ flex: "0 0 auto", minWidth: "220px", maxWidth: "280px" }}>
                <Select
                  placeholder="Select second data point (denominator)"
                  value={percentageSecondField ? `${percentageSecondField.formId}_${percentageSecondField.field_id}` : undefined}
                  onChange={async (value) => {
                    if (!value) {
                      setPercentageSecondField(null);
                      return;
                    }

                    const underscoreIndex = value.indexOf('_');
                    const formId = value.substring(0, underscoreIndex);
                    const field_id = value.substring(underscoreIndex + 1);

                    const dataPointsToSearch = selectedSubjectForm ? filteredDataPoints : allDataPoints;
                    const selectedDataPoint = dataPointsToSearch.find(
                      dp => String(dp.formId) === String(formId) && dp.field_id === field_id
                    );

                    if (selectedDataPoint) {
                      setPercentageSecondField({
                        ...selectedDataPoint,
                        formId: parseInt(formId),
                      });
                    }
                  }}
                  style={{ width: "100%" }}
                  loading={loadingAllDataPoints}
                  showSearch
                  size="middle"
                  dropdownStyle={{ backgroundColor: "#ffffff" }}
                  notFoundContent={
                    loadingAllDataPoints ? <Spin size="small" /> : "No data points available"
                  }
                  optionLabelProp="label"
                >
                  {(selectedSubjectForm ? filteredDataPoints : allDataPoints)
                    .filter(dp => dp.field_id !== selectedField?.field_id)
                    .map((dataPoint) => {
                      const optionValue = `${dataPoint.formId}_${dataPoint.field_id}`;
                      return (
                        <Option
                          key={optionValue}
                          value={optionValue}
                          label={`${dataPoint.formDisplayName} - ${dataPoint.display_name || dataPoint.field_id}`}
                        >
                          <div style={{ padding: "4px 0" }}>
                            <div style={{ fontWeight: 500, fontSize: "13px", color: "#333" }}>
                              {dataPoint.display_name || dataPoint.field_id}
                            </div>
                            <div style={{ fontSize: "10px", color: "#8c8c8c" }}>
                              Subject: {dataPoint.formDisplayName}
                            </div>
                          </div>
                        </Option>
                      );
                    })}
                </Select>
              </div>
            )}

            {/* Threshold Input (shows for LTE/GTE) */}
            {selectedField && (selectedOperation === "lte" || selectedOperation === "gte") && (
              <div style={{ flex: "0 0 auto", minWidth: "120px", maxWidth: "150px" }}>
                <Input
                  type="number"
                  placeholder={`Threshold ${selectedOperation === "lte" ? "≤" : "≥"}`}
                  value={operationThresholds[selectedField.field_id] || ""}
                  onChange={(e) => {
                    setOperationThresholds(prev => ({
                      ...prev,
                      [selectedField.field_id]: e.target.value
                    }));
                  }}
                  size="middle"
                  style={{ width: "100%" }}
                />
              </div>
            )}

            {/* Add Button */}
            {selectedField && (
              <div style={{ flex: "0 0 auto" }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addOperation}
                  style={{
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    borderRadius: "8px",
                    fontWeight: 500,
                  }}
                  disabled={isVisitor()}
                >
                  Add
                </Button>
              </div>
            )}

            {/* Clear All Button */}
            {selectedOperations.length > 0 && (
              <div style={{ flex: "0 0 auto" }}>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={clearAllOperations}
                  size="middle"
                  style={{ borderRadius: "8px" }}
                  disabled={isVisitor()}
                >
                  Clear All ({selectedOperations.length})
                </Button>
              </div>
            )}

            {/* Apply Analysis Button */}
            {selectedOperations.length > 0 && (
              <div style={{ flex: "0 0 auto" }}>
                <Button
                  type="primary"
                  icon={<CalculatorOutlined />}
                  onClick={() => {
                    // Validate percentage operations have second field
                    const hasInvalidPercentage = selectedOperations.some(op => {
                      if (op.operation === "percentage" && !op.percentageSecondFieldId) {
                        notification.warning({
                          message: "Missing Second Data Point",
                          description: `Please select a second data point for ${op.fieldName} percentage calculation`,
                          duration: 4,
                        });
                        return true;
                      }
                      return false;
                    });

                    if (hasInvalidPercentage) return;

                    const hasInvalidThreshold = selectedOperations.some(op => {
                      if (op.operation === "lte" || op.operation === "gte") {
                        // Validation logic if needed
                      }
                      return false;
                    });

                    if (hasInvalidThreshold) return;
                    performCalculations();
                  }}
                  loading={loadingCalculation}
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: "#ffffff",
                    color: "#667eea",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                  disabled={isVisitor()}
                >
                  Apply
                </Button>
              </div>
            )}

            {/* Advanced Analysis Toggle Section */}
            {hasMultipleFilterOps && (
              <div style={{
                marginTop: "0px",
                marginBottom: "0px",
                background: "#08519C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "5px"
              }}>
                <Space>
                  <FilterOutlined style={{ color: showOnlyMatchingUlbs ? "#52c41a" : "#1890ff" }} />
                  <span style={{ fontWeight: 500, color: "white" }}>Advanced Analysis</span>
                </Space>
                <Space>
                  <Switch
                    checked={showOnlyMatchingUlbs}
                    onChange={setShowOnlyMatchingUlbs}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                </Space>
              </div>
            )}
          </div>
        </div>

        {/* Selected Operations Tags (shows below) */}
        {selectedOperations.length > 0 && (
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              padding: "8px 12px",
              background: "rgba(255,255,255,0.15)",
              borderRadius: "8px",
            }}
          >
            {selectedOperations.map((op) => (
              <Tag
                key={op.id}
                closable
                onClose={() => removeOperation(op.id)}
                color={getOperationColor(op.operation)}
                style={{
                  padding: "4px 8px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {getOperationIcon(op.operation)} {op.fieldName}
                {(op.operation === "lte" || op.operation === "gte") && (
                  <span style={{ marginLeft: "4px" }}>
                    {op.operation === "lte" ? "≤" : "≥"} {op.threshold}
                  </span>
                )}
                {op.operation === "percentage" && op.percentageSecondFieldName && (
                  <span style={{ marginLeft: "4px" }}>
                    / {op.percentageSecondFieldName}
                  </span>
                )}
                {op.operation === "ranking" && (
                  <span style={{ marginLeft: "4px" }}>
                    {op.rankingType === "top" ? `Top ${op.rankingCount}` :
                      op.rankingType === "bottom" ? `Bottom ${op.rankingCount}` :
                        `All (${op.rankingOrder === "desc" ? "↓" : "↑"})`}
                  </span>
                )}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
  // Component to render hierarchical statistics
  // Component to render hierarchical statistics with field selector
  const renderHierarchicalStatistics = (
    statisticsMap,
    selectedFieldId,
    onFieldChange,
  ) => {
    if (!statisticsMap || Object.keys(statisticsMap).length === 0) return null;

    // Get all available fields
    const availableFields = Object.values(statisticsMap);

    // Get current selected field data
    const currentStats = selectedFieldId
      ? statisticsMap[selectedFieldId]
      : availableFields[0];
    if (!currentStats) return null;

    // Check if this is an LTE/GTE operation (has matchingLocations instead of division/district/ulb)
    const isLteGte = currentStats.operation === "lte" || currentStats.operation === "gte";

    // For LTE/GTE operations, show different UI
    if (isLteGte) {
      const {
        fieldDisplayName,
        operation,
        fieldName,
        threshold,
        matchingCount,
        totalCount,
        matchingPercentage,
        sumOfMatching,
        averageOfMatching,
        minLocation,
        maxLocation,
        groupLabel
      } = currentStats;

      const thresholdOperator = operation === "lte" ? "≤" : "≥";
      const thresholdColor = operation === "lte" ? "#13c2c2" : "#eb2f96";

      return (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <div
            style={{
              background: "#e6f7ff",
              padding: "16px 20px",
              borderRadius: "12px",
              border: "1px solid #91d5ff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FilterOutlined style={{ fontSize: "18px", color: thresholdColor }} />
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#0c0c0c",
                    }}
                  >
                    Threshold Filter Results
                  </div>
                </div>
              </div>
            </div>

            {/* Field Info Banner */}
            <div
              style={{
                background: "#ffffff",
                padding: "8px 12px",
                borderRadius: "6px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                borderLeft: `3px solid ${thresholdColor}`,
              }}
            >
              <span
                style={{ fontSize: "12px", fontWeight: 500, color: "#495057" }}
              >
                {getOperationIcon(operation)} {fieldDisplayName || fieldName}
              </span>
              <Tag color={thresholdColor === "#13c2c2" ? "cyan" : "magenta"}>
                {thresholdOperator} {threshold?.toLocaleString()}
              </Tag>
              <span style={{ fontSize: "11px", color: "#6c757d" }}>
                {matchingCount} of {totalCount} {groupLabel || "locations"} match ({matchingPercentage}%)
              </span>
            </div>

            <Row gutter={[16, 16]}>
              {/* Summary Statistics */}
              <Col xs={24} md={12}>
                <div
                  style={{
                    borderRadius: "8px",
                    background: "#ffffff",
                    border: "1px solid #e9ecef",
                    padding: "12px",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "12px",
                      borderBottom: "1px solid #e9ecef",
                      paddingBottom: "8px",
                    }}
                  >
                    <CalculatorOutlined style={{ fontSize: "14px", color: "#6c757d" }} />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#495057",
                      }}
                    >
                      Summary Statistics
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "11px", color: "#6c757d" }}>Matching Locations:</span>
                      <strong style={{ fontSize: "13px", color: thresholdColor }}>
                        {matchingCount} of {totalCount}
                      </strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "11px", color: "#6c757d" }}>Total of Matching:</span>
                      <strong style={{ fontSize: "13px", color: "#1890ff" }}>
                        {sumOfMatching?.toLocaleString()}
                      </strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "11px", color: "#6c757d" }}>Average of Matching:</span>
                      <strong style={{ fontSize: "13px", color: "#52c41a" }}>
                        {averageOfMatching?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Min/Max Locations */}
              <Col xs={24} md={12}>
                <div
                  style={{
                    borderRadius: "8px",
                    background: "#ffffff",
                    border: "1px solid #e9ecef",
                    padding: "12px",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "12px",
                      borderBottom: "1px solid #e9ecef",
                      paddingBottom: "8px",
                    }}
                  >
                    <TrophyOutlined style={{ fontSize: "14px", color: "#6c757d" }} />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#495057",
                      }}
                    >
                      {operation === "lte" ? "Smallest" : "Largest"} Locations
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {operation === "lte" ? (
                      minLocation && (
                        <div
                          style={{
                            background: "#f8f9fa",
                            padding: "8px",
                            borderRadius: "6px",
                          }}
                        >
                          <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                            Smallest Total:
                          </div>
                          <div style={{ fontWeight: 600, fontSize: "13px" }}>{minLocation.name}</div>
                          <div style={{ fontSize: "12px", color: thresholdColor }}>
                            {minLocation.total?.toLocaleString()}
                          </div>
                        </div>
                      )
                    ) : (
                      maxLocation && (
                        <div
                          style={{
                            background: "#f8f9fa",
                            padding: "8px",
                            borderRadius: "6px",
                          }}
                        >
                          <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                            Largest Total:
                          </div>
                          <div style={{ fontWeight: 600, fontSize: "13px" }}>{maxLocation.name}</div>
                          <div style={{ fontSize: "12px", color: thresholdColor }}>
                            {maxLocation.total?.toLocaleString()}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      );
    }

    // For MIN/MAX operations (original code)
    const { division, district, ulb, fieldDisplayName, operation, fieldName } =
      currentStats;

    // Helper to format value
    const formatValue = (value) => {
      if (value === undefined || value === null);
      if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const getOperationColorLocal = (op) => {
      const colors = {
        sum: "#1890ff",
        avg: "#52c41a",
        min: "#fa8c16",
        max: "#f5222d",
        count: "#722ed1",
        multiply: "#13c2c2",
        divide: "#eb2f96",
        median: "#faad14",
        mode: "#a0d911",
        stddev: "#2f54eb",
      };
      return colors[op] || "#1890ff";
    };

    return (
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <div
          style={{
            background: "#cadef1",
            padding: "16px 20px",
            borderRadius: "12px",
            border: "1px solid #0b0b0b",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TrophyOutlined style={{ fontSize: "18px", color: "#f2c40f" }} />
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#0c0c0c",
                  }}
                >
                  Min/Max by Location
                </div>
              </div>
            </div>

            {/* Field selector for multiple fields */}
            {availableFields.length > 1 && (
              <Select
                value={selectedFieldId}
                onChange={onFieldChange}
                size="small"
                style={{ minWidth: "180px", borderRadius: "6px" }}
                dropdownStyle={{ borderRadius: "6px" }}
                suffixIcon={<BarChartOutlined style={{ fontSize: "12px" }} />}
              >
                {availableFields.map((field) => (
                  <Option key={field.fieldId} value={field.fieldId}>
                    <Space size={4}>
                      <span style={{ fontSize: "12px" }}>
                        {getOperationIcon(field.operation)}
                      </span>
                      <span style={{ fontSize: "12px", fontWeight: 500 }}>
                        {field.fieldName}
                      </span>
                      <span style={{ fontSize: "10px", color: "#6c757d" }}>
                        ({field.operation.toUpperCase()})
                      </span>
                    </Space>
                  </Option>
                ))}
              </Select>
            )}
          </div>

          {/* Field Info Banner */}
          <div
            style={{
              background: "#e9ecef",
              padding: "6px 12px",
              borderRadius: "6px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{ fontSize: "11px", fontWeight: 500, color: "#495057" }}
            >
              {getOperationIcon(operation)} {fieldDisplayName}
            </span>
            <span style={{ fontSize: "10px", color: "#6c757d" }}>
              Analysis based on {operation.toUpperCase()} operation
            </span>
          </div>

          <Row gutter={[16, 16]}>
            {/* Division Level */}
            <Col xs={24} md={8}>
              <div
                style={{
                  borderRadius: "8px",
                  background: "#ffffff",
                  border: "1px solid #080808",
                  padding: "12px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "12px",
                    borderBottom: "1px solid #e9ecef",
                    paddingBottom: "8px",
                  }}
                >
                  <ApartmentOutlined
                    style={{ fontSize: "14px", color: "#6c757d" }}
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#495057",
                    }}
                  >
                    Division Level
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6c757d",
                      marginLeft: "auto",
                    }}
                  >
                    {division?.count || 0} divisions
                  </span>
                </div>

                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <div style={{ marginBottom: "12px", flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <FallOutlined
                        style={{ color: "#dc3545", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Minimum
                      </span>
                    </div>
                    {division?.min ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {division.min.name}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#dc3545",
                          }}
                        >
                          {formatValue(division.min.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          Avg: {formatValue(division.min.average)} |{" "}
                          {division.min.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <RiseOutlined
                        style={{ color: "#28a745", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Maximum
                      </span>
                    </div>
                    {division?.max ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {division.max.name}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#28a745",
                          }}
                        >
                          {formatValue(division.max.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          Avg: {formatValue(division.max.average)} |{" "}
                          {division.max.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            {/* District Level */}
            <Col xs={24} md={8}>
              <div
                style={{
                  borderRadius: "8px",
                  background: "#ffffff",
                  border: "1px solid #0e0e0e",
                  padding: "12px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "12px",
                    borderBottom: "1px solid #e9ecef",
                    paddingBottom: "8px",
                  }}
                >
                  <EnvironmentOutlined
                    style={{ fontSize: "14px", color: "#6c757d" }}
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#495057",
                    }}
                  >
                    District Level
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6c757d",
                      marginLeft: "auto",
                    }}
                  >
                    {district?.count || 0} districts
                  </span>
                </div>

                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <div style={{ marginBottom: "12px", flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <FallOutlined
                        style={{ color: "#dc3545", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Minimum
                      </span>
                    </div>
                    {district?.min ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {district.min.name}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6c757d",
                            marginBottom: "2px",
                          }}
                        >
                          Division: {district.min.division}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#dc3545",
                          }}
                        >
                          {formatValue(district.min.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          {district.min.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <RiseOutlined
                        style={{ color: "#28a745", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Maximum
                      </span>
                    </div>
                    {district?.max ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {district.max.name}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6c757d",
                            marginBottom: "2px",
                          }}
                        >
                          Division: {district.max.division}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#28a745",
                          }}
                        >
                          {formatValue(district.max.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          {district.max.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            {/* ULB Level */}
            <Col xs={24} md={8}>
              <div
                style={{
                  borderRadius: "8px",
                  background: "#ffffff",
                  border: "1px solid #070707",
                  padding: "12px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "12px",
                    borderBottom: "1px solid #e9ecef",
                    paddingBottom: "8px",
                  }}
                >


                  <span
                    style={{
                      fontSize: "10px",
                      color: "#6c757d",
                      marginLeft: "auto",
                    }}
                  >
                    {ulb?.count || 0} ULBs
                  </span>
                </div>

                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <div style={{ marginBottom: "12px", flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <FallOutlined
                        style={{ color: "#dc3545", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Minimum
                      </span>
                    </div>
                    {ulb?.min ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {ulb.min.name}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#6c757d",
                            marginBottom: "2px",
                          }}
                        >
                          District: {ulb.min.district} | Div: {ulb.min.division}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#dc3545",
                          }}
                        >
                          {formatValue(ulb.min.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          {ulb.min.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "6px",
                      }}
                    >
                      <RiseOutlined
                        style={{ color: "#28a745", fontSize: "12px" }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#6c757d",
                        }}
                      >
                        Maximum
                      </span>
                    </div>
                    {ulb?.max ? (
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "6px",
                          minHeight: "85px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: "12px",
                            color: "#495057",
                            marginBottom: "2px",
                          }}
                        >
                          {ulb.max.name}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#6c757d",
                            marginBottom: "2px",
                          }}
                        >
                          District: {ulb.max.district} | Div: {ulb.max.division}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#28a745",
                          }}
                        >
                          {formatValue(ulb.max.total)}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          {ulb.max.count} records
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#adb5bd",
                          textAlign: "center",
                          padding: "12px",
                          minHeight: "85px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  };
  // ==================== RENDER HIERARCHICAL COLUMNS FUNCTION ====================
  const renderHierarchicalColumns = () => {
    if (!formFields || formFields.length === 0) return [];

    // ========== COLUMNS TO EXCLUDE FROM DISPLAY ==========
    const EXCLUDE_COLUMNS = [
      'workflow_action',
      'workflow_action_at',
      'workflow_action_by',
      'workflow_action_by_name',
      'workflow_action_by_role',
      'workflow_action_comments',
      'workflow_sequence',
      '_workflow_status',
      'submission_workflow_id',
      'workflow_instance_id',
      'workflow_status',
      'workflow_stage',
      'workflow_updated_at',
      'progress',
      'status'
    ];

    const columnToFieldData = {};
    Object.values(fieldHierarchy).forEach((field) => {
      if (field.columnName) {
        columnToFieldData[field.columnName] = field;
      }
    });

    const locationFieldIdentifiers = [
      "division",
      "district",
      "ulb",
      "municipality",
      "ulb_type",
      "class",
      "विभाग",
      "जिल्हा",
      "मनपा",
      "न.प.",
      "प्रकार",
      "वर्ग",
    ];

    const locationFieldNames = formFields.filter((field) => {
      const fieldStr = String(field).toLowerCase();
      // Skip excluded columns
      if (EXCLUDE_COLUMNS.includes(field)) return false;
      if (EXCLUDE_COLUMNS.includes(fieldStr)) return false;
      return locationFieldIdentifiers.some((id) =>
        fieldStr.includes(id.toLowerCase()),
      );
    });

    const getLocationDisplayName = (field) => {
      const fieldLower = field.toLowerCase();
      if (fieldLower.includes("division") || fieldLower.includes("विभाग")) {
        return "Division";
      }
      if (fieldLower.includes("district") || fieldLower.includes("जिल्हा")) {
        return "District";
      }
      if (
        fieldLower.includes("ulb") ||
        fieldLower.includes("मनपा") ||
        fieldLower.includes("municipality")
      ) {
        return "ULB";
      }
      if (fieldLower.includes("ulb_type") || fieldLower.includes("प्रकार")) {
        return "ULB Type";
      }
      if (fieldLower.includes("class") || fieldLower.includes("वर्ग")) {
        return "ULB Class";
      }
      return fieldLabels[field] || formatFieldName(field);
    };

    const createFieldColumn = (fieldName, level = 0) => {
      // Skip excluded columns
      if (EXCLUDE_COLUMNS.includes(fieldName)) return null;
      if (EXCLUDE_COLUMNS.includes(fieldName.toLowerCase())) return null;

      const displayName = fieldLabels[fieldName] || formatFieldName(fieldName);
      const indentLevel = level * 20;
      const safeKey = `modal_field_${fieldName.replace(/[^a-zA-Z0-9]/g, "_")}`;

      return {
        title: (
          <Tooltip title={`${displayName} (${fieldName})`}>
            <div
              style={{
                marginLeft: indentLevel,
                fontWeight: level === 0 ? 600 : 500,
                fontSize: level === 0 ? "13px" : "12px",
                color: level === 0 ? "#333" : "#666",
              }}
            >
              {level > 0 ? "↳ ".repeat(level) : ""}
              {displayName}
            </div>
          </Tooltip>
        ),
        dataIndex: fieldName,
        key: safeKey,
        width: 150,
        sorter: (a, b) => {
          const valA = a[fieldName] || "";
          const valB = b[fieldName] || "";
          const numA = parseFloat(String(valA).replace(/,/g, ""));
          const numB = parseFloat(String(valB).replace(/,/g, ""));
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return String(valA).localeCompare(String(valB));
        },
        render: (value, record) => {
          if (value === null || value === undefined || value === "") {
            return "-";
          }
          const numValue = parseFloat(String(value).replace(/,/g, ""));
          if (!isNaN(numValue) && isFinite(numValue)) {
            return (
              <div style={{ textAlign: "right", marginLeft: level * 20 }}>
                {numValue}
              </div>
            );
          }
          if (String(value).match(/^\d{4}-\d{2}-\d{2}/)) {
            return new Date(value).toLocaleDateString();
          }
          const strValue = String(value);
          if (strValue.length > 50) {
            return (
              <Tooltip title={strValue}>
                <span style={{ marginLeft: level * 20 }}>
                  {strValue.substring(0, 47)}...
                </span>
              </Tooltip>
            );
          }
          return <span style={{ marginLeft: level * 20 }}>{value}</span>;
        },
      };
    };

    const headingMap = {};
    const childHeadingsMap = {};
    const fieldParentMapLocal = {};
    const rootFields = [];

    Object.values(columnToFieldData).forEach((field) => {
      if (field.type === "heading") {
        headingMap[field.columnName] = {
          columnName: field.columnName,
          label: field.enLabel || field.columnName,
          level: field.headingLevel || 1,
          parentId: field.parentId,
        };
      }
    });

    Object.values(columnToFieldData).forEach((field) => {
      if (field.type === "heading" && field.parentId) {
        const parentHeading = Object.values(columnToFieldData).find(
          (h) => h.fieldId === field.parentId && h.type === "heading",
        );
        if (parentHeading) {
          if (!childHeadingsMap[parentHeading.columnName]) {
            childHeadingsMap[parentHeading.columnName] = [];
          }
          childHeadingsMap[parentHeading.columnName].push(field.columnName);
        }
      }
    });

    Object.values(columnToFieldData).forEach((field) => {
      if (field.type !== "heading" && field.parentId) {
        const parentHeading = Object.values(columnToFieldData).find(
          (h) => h.fieldId === field.parentId && h.type === "heading",
        );
        if (parentHeading) {
          fieldParentMapLocal[field.columnName] = parentHeading.columnName;
        }
      }
    });

    Object.values(columnToFieldData).forEach((field) => {
      if (field.type !== "heading" && !field.parentId) {
        // Skip excluded columns
        if (EXCLUDE_COLUMNS.includes(field.columnName)) return;
        if (EXCLUDE_COLUMNS.includes(field.columnName?.toLowerCase())) return;
        rootFields.push(field.columnName);
      }
    });

    formFields.forEach((field) => {
      // Skip excluded columns
      if (EXCLUDE_COLUMNS.includes(field)) return;
      if (EXCLUDE_COLUMNS.includes(field?.toLowerCase())) return;

      if (
        !columnToFieldData[field] &&
        !locationFieldNames.includes(field) &&
        !rootFields.includes(field)
      ) {
        const isChildField = Object.keys(fieldParentMapLocal).includes(field);
        if (!isChildField) {
          rootFields.push(field);
        }
      }
    });

    const columns = [];

    locationFieldNames.forEach((field, index) => {
      let fieldName;
      if (typeof field === "string") {
        fieldName = field;
      } else if (field && typeof field === "object") {
        fieldName =
          field.id ||
          field.label ||
          field.value ||
          field.name ||
          `Field ${index + 1}`;
      } else {
        fieldName = String(field || `Field ${index + 1}`);
      }

      // Skip excluded columns
      if (EXCLUDE_COLUMNS.includes(fieldName)) return;
      if (EXCLUDE_COLUMNS.includes(fieldName?.toLowerCase())) return;

      const safeKey = `modal_loc_${index}_${fieldName.replace(/[^a-zA-Z0-9]/g, "_")}`;
      const displayName = getLocationDisplayName(fieldName);

      columns.push({
        title: (
          <Tooltip title={`${displayName} (${fieldName})`}>
            <span
              style={{
                fontWeight: 600,
                color: "#1890ff",
                fontSize: "13px",
              }}
            >
              {displayName}
            </span>
          </Tooltip>
        ),
        dataIndex: fieldName,
        key: safeKey,
        width: 150,
        fixed: index === 0 ? "left" : "left",
        sorter: (a, b) => {
          const valA = a[fieldName] || "";
          const valB = b[fieldName] || "";
          return String(valA).localeCompare(String(valB));
        },
        render: (value) => {
          if (value === null || value === undefined || value === "") {
            return "-";
          }
          return value;
        },
      });
    });

    const addedFields = new Set();

    rootFields.forEach((fieldName) => {
      // Skip excluded columns
      if (EXCLUDE_COLUMNS.includes(fieldName)) return;
      if (EXCLUDE_COLUMNS.includes(fieldName?.toLowerCase())) return;

      if (!addedFields.has(fieldName)) {
        const column = createFieldColumn(fieldName, 0);
        if (column) {
          columns.push(column);
          addedFields.add(fieldName);
        }
      }
    });

    const topLevelHeadings = Object.values(headingMap).filter(
      (h) => !h.parentId,
    );

    topLevelHeadings.forEach((heading) => {
      const headingSection = {
        title: (
          <div
            style={{
              fontWeight: 700,
              color: "#722ed1",
              fontSize: "14px",
              padding: "8px 0",
              borderBottom: "2px solid #722ed1",
              marginBottom: "4px",
            }}
          >
            {heading.label}
          </div>
        ),
        key: `heading_${heading.columnName}`,
        children: [],
      };

      const childHeadingNames = childHeadingsMap[heading.columnName] || [];

      childHeadingNames.forEach((childHeadingName) => {
        const childHeading = headingMap[childHeadingName];
        if (!childHeading) return;

        const subheadingSection = {
          title: (
            <div
              style={{
                fontWeight: 600,
                color: "#fa8c16",
                fontSize: "13px",
                padding: "4px 0 4px 20px",
                borderBottom: "1px dashed #fa8c16",
                marginBottom: "2px",
              }}
            >
              {childHeading.label}
            </div>
          ),
          key: `subheading_${childHeadingName}`,
          children: [],
        };

        Object.entries(fieldParentMapLocal).forEach(
          ([fieldName, parentName]) => {
            if (
              parentName === childHeadingName &&
              !addedFields.has(fieldName)
            ) {
              // Skip excluded columns
              if (EXCLUDE_COLUMNS.includes(fieldName)) return;
              if (EXCLUDE_COLUMNS.includes(fieldName?.toLowerCase())) return;

              const column = createFieldColumn(fieldName, 2);
              if (column) {
                subheadingSection.children.push(column);
                addedFields.add(fieldName);
              }
            }
          },
        );

        if (subheadingSection.children.length > 0) {
          headingSection.children.push(subheadingSection);
        }
      });

      Object.entries(fieldParentMapLocal).forEach(([fieldName, parentName]) => {
        if (parentName === heading.columnName && !addedFields.has(fieldName)) {
          // Skip excluded columns
          if (EXCLUDE_COLUMNS.includes(fieldName)) return;
          if (EXCLUDE_COLUMNS.includes(fieldName?.toLowerCase())) return;

          const column = createFieldColumn(fieldName, 1);
          if (column) {
            headingSection.children.push(column);
            addedFields.add(fieldName);
          }
        }
      });

      if (headingSection.children.length > 0) {
        columns.push(headingSection);
      }
    });

    Object.entries(fieldParentMapLocal).forEach(([fieldName, parentName]) => {
      if (!addedFields.has(fieldName)) {
        let found = false;
        Object.values(headingMap).forEach((heading) => {
          if (parentName === heading.columnName) {
            found = true;
          }
          const childNames = childHeadingsMap[heading.columnName] || [];
          childNames.forEach((childName) => {
            if (parentName === childName) {
              found = true;
            }
          });
        });
        if (!found) {
          // Skip excluded columns
          if (EXCLUDE_COLUMNS.includes(fieldName)) return;
          if (EXCLUDE_COLUMNS.includes(fieldName?.toLowerCase())) return;

          const column = createFieldColumn(fieldName, 0);
          if (column) {
            columns.push(column);
            addedFields.add(fieldName);
          }
        }
      }
    });

    return columns;
  };


  // ==================== RENDER INLINE GRAPH FUNCTION ====================
  const renderInlineGraph = (graphKey, graphData) => {
    if (!graphData || !graphData.showGraph) return null;

    const {
      column,
      displayName,
      data,
      stats,
      aggregationLevel,
      groupLabel,
      chartType = "bar",
    } = graphData;
    const isPieChart = chartType === "pie";

    // Handler for bar click - drill down to next level
    const handleGraphBarClick = (dataPoint) => {
      if (!dataPoint || !dataPoint.name) return;

      console.log("🔍 Graph bar clicked in Report Fields:", dataPoint.name);
      console.log("Current aggregation level:", aggregationLevel);

      // Determine current drill level based on aggregation level
      if (aggregationLevel === "division") {
        // Clicked on a Division bar - drill down to District level
        console.log("📊 Clicked on Division:", dataPoint.name);

        // Apply division filter
        handleDivisionChange(dataPoint.name);

        // Refresh calculations after a short delay
        setTimeout(() => {
          if (selectedOperations.length > 0) {
            performCalculations();
          }
        }, 100);
      }
      else if (aggregationLevel === "district") {
        // Clicked on a District bar - drill down to ULB level
        console.log("📊 Clicked on District:", dataPoint.name);

        // Apply district filter
        handleDistrictChange(dataPoint.name);

        // Refresh calculations after a short delay
        setTimeout(() => {
          if (selectedOperations.length > 0) {
            performCalculations();
          }
        }, 100);
      }
      // If aggregation level is "ulb" or "record", no further drill down
    };

    const pieData = data.slice(0, 12).map((item) => ({
      name: item.name.length > 25 ? item.name.substring(0, 22) + "..." : item.name,
      fullName: item.name,
      value: item.value,
      originalName: item.originalName,
      count: item.count,
      avgValue: item.avgValue,
    }));

    const totalValue = pieData.reduce((sum, item) => sum + item.value, 0);

    const CustomPieTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;
        const percentage = ((dataPoint.value / totalValue) * 100).toFixed(1);

        let clickInstruction = "";
        if (aggregationLevel === "division") {
          clickInstruction = "Click to view districts";
        } else if (aggregationLevel === "district") {
          clickInstruction = "Click to view ULBs";
        } else {
          clickInstruction = "No further drill down";
        }

        return (
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "12px 16px",
              border: "none",
              borderRadius: "12px",
              maxWidth: "320px",
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
              background: "rgba(255, 255, 255, 0.98)",
            }}
          >
            <div
              style={{
                borderBottom: "2px solid #1890ff",
                marginBottom: "10px",
                paddingBottom: "6px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <PieChartOutlined style={{ fontSize: "12px" }} />
                {dataPoint.fullName || dataPoint.name}
              </div>
            </div>
            <div style={{ fontSize: "12px", lineHeight: "1.5" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "6px",
                  padding: "3px 0",
                }}
              >
                <span style={{ color: "#8c8c8c" }}>Value:</span>
                <strong style={{ color: "#1890ff", fontSize: "13px" }}>
                  {dataPoint.value.toLocaleString()}
                </strong>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "6px",
                  padding: "3px 0",
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <span style={{ color: "#8c8c8c" }}>Percentage:</span>
                <strong style={{ color: "#52c41a" }}>{percentage}%</strong>
              </div>
              {dataPoint.count && aggregationLevel !== "record" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "3px 0",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <span style={{ color: "#8c8c8c" }}>Records in group:</span>
                  <strong style={{ color: "#faad14" }}>
                    {dataPoint.count.toLocaleString()}
                  </strong>
                </div>
              )}
              {aggregationLevel !== "ulb" && aggregationLevel !== "record" && (
                <div
                  style={{
                    marginTop: "8px",
                    paddingTop: "6px",
                    borderTop: "1px solid #f0f0f0",
                    fontSize: "10px",
                    color: "#1890ff",
                    textAlign: "center",
                  }}
                >
                  💡 {clickInstruction}
                </div>
              )}
            </div>
          </div>
        );
      }
      return null;
    };

    const CustomBarTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;

        let clickInstruction = "";
        if (aggregationLevel === "division") {
          clickInstruction = "Click to view districts";
        } else if (aggregationLevel === "district") {
          clickInstruction = "Click to view ULBs";
        } else {
          clickInstruction = "No further drill down";
        }

        return (
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "12px 16px",
              border: "none",
              borderRadius: "12px",
              maxWidth: "320px",
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
              background: "rgba(255, 255, 255, 0.98)",
            }}
          >
            <div
              style={{
                borderBottom: "2px solid #1890ff",
                marginBottom: "10px",
                paddingBottom: "6px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <BarChartOutlined style={{ fontSize: "12px" }} />
                {dataPoint.originalName || dataPoint.name}
              </div>
            </div>

            <div style={{ fontSize: "12px", lineHeight: "1.5" }}>
              {aggregationLevel !== "record" ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                      padding: "3px 0",
                    }}
                  >
                    <span style={{ color: "#8c8c8c" }}>Total {displayName}:</span>
                    <strong style={{ color: "#1890ff", fontSize: "13px" }}>
                      {dataPoint.value.toLocaleString()}
                    </strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                      padding: "3px 0",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ color: "#8c8c8c" }}>Average:</span>
                    <strong style={{ color: "#faad14" }}>
                      {dataPoint.avgValue.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "3px 0",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ color: "#8c8c8c" }}>Records in group:</span>
                    <strong style={{ color: "#52c41a" }}>
                      {dataPoint.count.toLocaleString()}
                    </strong>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                      padding: "3px 0",
                    }}
                  >
                    <span style={{ color: "#8c8c8c" }}>Value:</span>
                    <strong style={{ color: "#1890ff", fontSize: "13px" }}>
                      {dataPoint.raw}
                    </strong>
                  </div>
                  {dataPoint.division && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                        padding: "3px 0",
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <span style={{ color: "#8c8c8c" }}>Division:</span>
                      <span style={{ fontWeight: 500 }}>{dataPoint.division}</span>
                    </div>
                  )}
                  {dataPoint.district && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                        padding: "3px 0",
                      }}
                    >
                      <span style={{ color: "#8c8c8c" }}>District:</span>
                      <span style={{ fontWeight: 500 }}>{dataPoint.district}</span>
                    </div>
                  )}
                  {dataPoint.ulb && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                        padding: "3px 0",
                      }}
                    >
                      <span style={{ color: "#8c8c8c" }}>ULB:</span>
                      <span style={{ fontWeight: 500 }}>{dataPoint.ulb}</span>
                    </div>
                  )}
                </>
              )}
              {aggregationLevel !== "ulb" && aggregationLevel !== "record" && (
                <div
                  style={{
                    marginTop: "8px",
                    paddingTop: "6px",
                    borderTop: "1px solid #f0f0f0",
                    fontSize: "10px",
                    color: "#1890ff",
                    textAlign: "center",
                  }}
                >
                  💡 {clickInstruction}
                </div>
              )}
            </div>
          </div>
        );
      }
      return null;
    };

    return (
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "#fafbfc",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
          position: "relative",
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<span style={{ fontSize: "16px" }}>✕</span>}
          onClick={() => hideGraph(graphKey)}
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1,
            color: "#8c8c8c",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background:
              "linear-gradient(90deg, #1890ff, #52c41a, #faad14, #f5222d, #722ed1)",
            borderRadius: "12px 12px 0 0",
            opacity: 0.6,
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "16px",
            paddingBottom: "12px",
            borderBottom: "1px solid rgba(24, 144, 255, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #1890ff20, #1890ff08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1890ff",
                fontSize: "18px",
              }}
            >
              {isPieChart ? <PieChartOutlined /> : <BarChartOutlined />}
            </div>
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  letterSpacing: "-0.3px",
                }}
              >
                {aggregationLevel === "record"
                  ? "Detailed Distribution"
                  : `${groupLabel} Level Analysis`}
              </div>
              {aggregationLevel !== "record" && aggregationLevel !== "ulb" && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#8c8c8c",
                    marginTop: "2px",
                  }}
                >
                  💡 Click on any bar to drill down
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              background: "#ffffff",
              padding: "4px 12px",
              borderRadius: "20px",
              border: "1px solid #e9ecef",
            }}
          >
            <span style={{ fontSize: "12px", color: "#8c8c8c" }}>Chart Type:</span>
            <div
              style={{
                display: "flex",
                gap: "4px",
                background: "#f5f5f5",
                borderRadius: "16px",
                padding: "2px",
              }}
            >
              <Button
                type={!isPieChart ? "primary" : "default"}
                size="small"
                icon={<BarChartOutlined />}
                onClick={() => toggleChartType(graphKey)}
                style={{
                  borderRadius: "14px",
                  padding: "0 8px",
                  height: "28px",
                  fontSize: "12px",
                }}
              >
                Bar
              </Button>
              <Button
                type={isPieChart ? "primary" : "default"}
                size="small"
                icon={<PieChartOutlined />}
                onClick={() => toggleChartType(graphKey)}
                style={{
                  borderRadius: "14px",
                  padding: "0 8px",
                  height: "28px",
                  fontSize: "12px",
                }}
              >
                Pie
              </Button>
            </div>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            background: "#ffffff",
            borderRadius: "12px",
            padding: "8px",
            marginTop: "8px",
          }}
        >
          {isPieChart ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => {
                    const percentage = (percent * 100).toFixed(1);
                    return `${name} (${percentage}%)`;
                  }}
                  outerRadius={130}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={2}
                  onClick={(data) => {
                    if (data && data.payload) {
                      handleGraphBarClick(data.payload);
                    }
                  }}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                      style={{ cursor: aggregationLevel !== "ulb" && aggregationLevel !== "record" ? "pointer" : "default" }}
                      onClick={() => handleGraphBarClick(entry)}
                    />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomPieTooltip />} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{
                    fontSize: "11px",
                    maxHeight: "350px",
                    overflowY: "auto",
                  }}
                  formatter={(value, entry, index) => {
                    const item = pieData[index];
                    const percentage = item
                      ? ((item.value / totalValue) * 100).toFixed(1)
                      : "0";
                    return `${value} (${percentage}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={data}
                margin={{
                  top: 20,
                  right: 20,
                  left: 50,
                  bottom: aggregationLevel !== "record" ? 8 : 50,
                }}
                onClick={(chartData) => {
                  if (chartData && chartData.activePayload && chartData.activePayload.length) {
                    handleGraphBarClick(chartData.activePayload[0].payload);
                  }
                }}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1890ff" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#1890ff" stopOpacity={0.6} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e9ecef"
                  vertical={false}
                  strokeOpacity={0.5}
                />

                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={aggregationLevel !== "record" ? 80 : 60}
                  interval={0}
                  tick={{
                    fontSize: 10,
                    fill: "#5a6874",
                    fontWeight: 500,
                  }}
                  tickLine={false}
                  axisLine={{ stroke: "#e9ecef", strokeWidth: 1 }}
                  label={{
                    value: aggregationLevel === "record" ? "Records" : groupLabel,
                    position: "insideBottom",
                    offset: aggregationLevel !== "record" ? -60 : -45,
                    fontSize: 11,
                    fill: "#8c8c8c",
                    fontWeight: 500,
                  }}
                />

                <YAxis
                  label={{
                    value: displayName,
                    angle: -90,
                    position: "insideLeft",
                    offset: -35,
                    fontSize: 11,
                    fill: "#8c8c8c",
                    fontWeight: 500,
                    style: { textAnchor: "middle" },
                  }}
                  tick={{ fontSize: 10, fill: "#5a6874" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e9ecef", strokeWidth: 1 }}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toLocaleString();
                  }}
                />

                <RechartsTooltip content={<CustomBarTooltip />} />

                <Bar
                  dataKey="value"
                  fill="url(#barGradient)"
                  name={displayName}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={60}
                  animationDuration={600}
                  animationEasing="ease-out"
                  cursor={aggregationLevel !== "ulb" && aggregationLevel !== "record" ? "pointer" : "default"}
                  onClick={(data) => {
                    if (data && data.payload) {
                      handleGraphBarClick(data.payload);
                    }
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {aggregationLevel !== "record" && stats && !isPieChart && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px 16px",
              background: "#ffffff",
              borderRadius: "10px",
              border: "1px solid #e9ecef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #1890ff20, #1890ff08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#1890ff",
                }}
              >
                <RiseOutlined />
              </div>
              <div>
                <div style={{ fontSize: "10px", color: "#8c8c8c" }}>Key Insight</div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#2c3e50",
                  }}
                >
                  {data.length > 0 &&
                    data.reduce(
                      (max, item) => (item.value > max.value ? item : max),
                      data[0],
                    )?.name}{" "}
                  has the highest {displayName.toLowerCase()}
                  {stats.avg &&
                    `, which is ${((data.reduce((max, item) => (item.value > max.value ? item : max), data[0])?.value / stats.avg - 1) * 100).toFixed(1)}% above average`}
                </div>
              </div>
            </div>
          </div>
        )}

        {isPieChart && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px 16px",
              background: "#ffffff",
              borderRadius: "10px",
              border: "1px solid #e9ecef",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "#8c8c8c" }}>
              Total {displayName}:{" "}
              <strong style={{ color: "#1890ff" }}>
                {stats.sum.toLocaleString()}
              </strong>
              {aggregationLevel !== "record" &&
                ` across ${data.length} ${groupLabel}s`}
            </div>
            {aggregationLevel !== "ulb" && aggregationLevel !== "record" && (
              <div style={{ fontSize: "10px", color: "#1890ff", marginTop: "4px" }}>
                💡 Click on any pie slice to drill down
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  // ==================== RENDER REPORT ENABLED CARDS ====================
  const renderReportEnabledCards = () => {
    if (!reportCalculations || Object.keys(reportCalculations).length === 0)
      return null;

    const formatValue = (value, calculation) => {
      if (value === undefined || value === null);
      if (calculation.operator?.toLowerCase() === "percentage")
        return `${value.toFixed(2)}%`;
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const getHierarchicalBreakdown = (fieldId, calculation, activeFilters) => {
      // ========== FIX: Use quick filtered data if active ==========
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : formData;

      if (!dataToUse || dataToUse.length === 0) return [];

      const fieldInfo = reportEnabledFields.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      let groupByField = null;
      let breakdownLevel = "";

      // Check which filters are applied in order
      const divisionFilter = activeFilters.find((f) => f.type === "division");
      const districtFilter = activeFilters.find((f) => f.type === "district");

      if (districtFilter) {
        // If district filter is applied, break down by ULB
        groupByField =
          formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("ulb") ||
                f.toLowerCase().includes("मनपा") ||
                f.toLowerCase().includes("municipality")),
          ) || "ulb_name";
        breakdownLevel = "ulb";
      } else if (divisionFilter) {
        // If division filter is applied, break down by district
        groupByField =
          formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("district") ||
                f.toLowerCase().includes("जिल्हा")),
          ) || "district_name";
        breakdownLevel = "district";
      } else {
        // No filters, break down by division
        groupByField =
          formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("division") ||
                f.toLowerCase().includes("विभाग")),
          ) || "division_name";
        breakdownLevel = "division";
      }

      if (!groupByField) return [];

      const divisionFieldForFilter = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग")),
      ) || "division_name";
      const districtFieldForFilter = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा")),
      ) || "district_name";

      const filterConditions = (row) => {
        let matches = true;

        // Apply division filter if it exists
        if (divisionFilter) {
          const rowDivision = row[divisionFieldForFilter];
          if (rowDivision && String(rowDivision) !== String(divisionFilter.filterValue)) {
            matches = false;
          }
        }

        // Apply district filter if it exists
        if (districtFilter && matches) {
          const rowDistrict = row[districtFieldForFilter];
          if (rowDistrict && String(rowDistrict) !== String(districtFilter.filterValue)) {
            matches = false;
          }
        }

        return matches;
      };

      const groups = {};
      dataToUse.forEach((row) => {
        if (!filterConditions(row)) return;

        const groupValue = row[groupByField] || `Unknown ${breakdownLevel}`;
        const val = row[actualColumnName];

        if (val !== undefined && val !== null && val !== "") {
          const numVal = parseFloat(String(val).replace(/,/g, ""));
          if (!isNaN(numVal) && isFinite(numVal)) {
            if (!groups[groupValue]) {
              groups[groupValue] = {
                name: groupValue,
                values: [],
                division: row[divisionFieldForFilter],
                district: row[districtFieldForFilter],
                ulb: breakdownLevel === "ulb" ? row[groupByField] : null,
              };
            }
            groups[groupValue].values.push(numVal);
          }
        }
      });

      const results = [];
      Object.keys(groups).forEach((groupName) => {
        const group = groups[groupName];
        let result = 0;

        if (group.values.length > 0) {
          switch (operator.toLowerCase()) {
            case "sum":
              result = group.values.reduce((a, b) => a + b, 0);
              break;
            case "avg":
              result =
                group.values.reduce((a, b) => a + b, 0) / group.values.length;
              break;
            case "min":
              result = Math.min(...group.values);
              break;
            case "max":
              result = Math.max(...group.values);
              break;
            case "count":
              result = group.values.length;
              break;
            default:
              result = group.values.reduce((a, b) => a + b, 0);
          }
        }

        results.push({
          name: groupName,
          value: result,
          count: group.values.length,
          avgValue: group.values.length > 0 ? result / group.values.length : 0,
          breakdownLevel: breakdownLevel,
          division: group.division,
          district: group.district,
        });
      });

      return results.sort((a, b) => a.name.localeCompare(b.name));
    };

    // Enhanced getActiveFilters to capture District Admin's district from user role
    const getActiveFilters = () => {
      const list = [];

      const divisionField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग")),
      ) || "division_name";

      // Check for division from filters state first
      let divisionValue = filters.division || filters.division_id;

      // If no division in filters, check if user is Division Admin
      const isDivisionAdmin = !hasFullAccess() && (
        userRole === "DIVISION_ADMIN" ||
        userRole === "division_admin" ||
        userRole === "DIVISIONAL_OFFICER" ||
        userRole === "divisional_officer" ||
        userRole === "Divisionalcommissioner"
      );

      // For Division Admin, get their division from user data
      if (!divisionValue && isDivisionAdmin && userDivisionId) {
        const divisionName = referenceCache.divisions[userDivisionId]?.name;
        if (divisionName) {
          divisionValue = divisionName;
          console.log("🏢 Division Admin detected - using division from user role:", divisionName);
        }
      }

      if (divisionValue) {
        list.push({
          type: "division",
          label: "Division",
          value: divisionValue,
          fieldId: divisionField,
          filterValue: divisionValue,
        });
      }

      const districtField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा")),
      ) || "district_name";

      // Check for district from filters state first
      let districtValue = filters.district || filters.district_id;

      // If no district in filters, check if user is District Admin
      const isDistrictAdmin = !hasFullAccess() && (
        userRole === "DISTRICT_ADMIN" ||
        userRole === "district_admin"
      );

      // CRITICAL FIX: For District Admin, get their district from user data
      if (!districtValue && isDistrictAdmin && userDistrictId) {
        const districtName = referenceCache.districts[userDistrictId]?.name;
        if (districtName) {
          districtValue = districtName;
          console.log("🏢 District Admin detected - using district from user role:", districtName);
        }
      }

      if (districtValue) {
        list.push({
          type: "district",
          label: "District",
          value: districtValue,
          fieldId: districtField,
          filterValue: districtValue,
        });
      }

      const ulbField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb") ||
            f.toLowerCase().includes("मनपा") ||
            f.toLowerCase().includes("municipality")),
      ) || "ulb_name";
      if (filters.ulb_name) {
        list.push({
          type: "ulb",
          label: "ULB",
          value: filters.ulb_name,
          fieldId: ulbField,
          filterValue: filters.ulb_name,
        });
      }

      const ulbTypeField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb_type") || f.toLowerCase().includes("type")),
      ) || "ulb_type";
      if (filters.ulb_type) {
        list.push({
          type: "ulb_type",
          label: "ULB Type",
          value: filters.ulb_type,
          fieldId: ulbTypeField,
          filterValue: filters.ulb_type,
        });
      }

      const classField = formFields.find(
        (f) =>
          f && (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग")),
      ) || "class";
      if (filters.ulb_class) {
        list.push({
          type: "ulb_class",
          label: "ULB Class",
          value: filters.ulb_class,
          fieldId: classField,
          filterValue: filters.ulb_class,
        });
      }

      return list;
    };

    // Helper function to get the correct field name for a filter type
    const getFilterFieldForType = (filterType) => {
      switch (filterType) {
        case "division":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("division") ||
                f.toLowerCase().includes("विभाग")),
          ) || "division_name";
        case "district":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("district") ||
                f.toLowerCase().includes("जिल्हा")),
          ) || "district_name";
        case "ulb":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("ulb") ||
                f.toLowerCase().includes("मनपा") ||
                f.toLowerCase().includes("municipality")),
          ) || "ulb_name";
        case "ulb_type":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("ulb_type") ||
                f.toLowerCase().includes("type")),
          ) || "ulb_type";
        case "ulb_class":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("class") ||
                f.toLowerCase().includes("वर्ग")),
          ) || "class";
        default:
          return null;
      }
    };

    const getFilterSpecificValue = (
      fieldId,
      calculation,
      currentFilterType,
      currentFilterValue,
      allActiveFilters,
    ) => {
      // ========== FIX: Use quick filtered data if active ==========
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : (originalFormData && originalFormData.length > 0 ? originalFormData : formData);

      if (!dataToUse || dataToUse.length === 0 || !currentFilterValue) return null;

      const fieldInfo = reportEnabledFields.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      let filteredData = [...dataToUse];

      const filterOrder = {
        ulb_type: 0,
        ulb_class: 1,
        division: 2,
        district: 3,
        ulb: 4,
      };

      const currentIndex = filterOrder[currentFilterType] || 0;

      for (const filter of allActiveFilters) {
        if (filter.type === currentFilterType) continue;

        const filterIndex = filterOrder[filter.type] || 0;

        if (filterIndex < currentIndex) {
          const filterField = getFilterFieldForType(filter.type);
          if (filterField) {
            filteredData = filteredData.filter((row) => {
              const rowValue = row[filterField];
              return rowValue && String(rowValue) === String(filter.filterValue);
            });
          }
        }
      }

      const currentFilterField = getFilterFieldForType(currentFilterType);
      if (!currentFilterField) return null;

      filteredData = filteredData.filter((row) => {
        const rowValue = row[currentFilterField];
        return rowValue && String(rowValue) === String(currentFilterValue);
      });

      if (filteredData.length === 0) return null;

      const values = filteredData
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      if (values.length === 0) return null;

      switch (operator.toLowerCase()) {
        case "sum":
          return values.reduce((a, b) => a + b, 0);
        case "avg":
        case "average":
          return values.reduce((a, b) => a + b, 0) / values.length;
        case "min":
          return Math.min(...values);
        case "max":
          return Math.max(...values);
        case "count":
          return values.length;
        default:
          return values.reduce((a, b) => a + b, 0);
      }
    };

    const calculateStateTotal = (fieldId, calculation) => {
      // ========== FIX: Use quick filtered data if active ==========
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : (originalFormData.length > 0 ? originalFormData : formData);

      const fieldInfo = reportEnabledFields.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      const allValues = dataToUse
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      let originalTotal = 0;
      if (allValues.length > 0) {
        switch (operator.toLowerCase()) {
          case "sum":
            originalTotal = allValues.reduce((a, b) => a + b, 0);
            break;
          case "avg":
          case "average":
            originalTotal = allValues.reduce((a, b) => a + b, 0) / allValues.length;
            break;
          case "min":
            originalTotal = Math.min(...allValues);
            break;
          case "max":
            originalTotal = Math.max(...allValues);
            break;
          case "count":
            originalTotal = allValues.length;
            break;
          default:
            originalTotal = allValues.reduce((a, b) => a + b, 0);
        }
      }

      return formatValue(originalTotal, calculation);
    };

    const allFields = Object.entries(reportCalculations);

    const formatFieldName = (fieldId) => {
      return fieldId
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const getOperatorColor = (operator) => {
      const colors = {
        sum: "#1890ff",
        count: "#52c41a",
        average: "#fa8c16",
        percentage: "#eb2f96",
        max: "#722ed1",
        min: "#13c2c2",
      };
      return colors[operator?.toLowerCase()] || "#1890ff";
    };

    const getOperatorIcon = (operator) => {
      const icons = {
        count: <NumberOutlined />,
        average: <CalculatorOutlined />,
        percentage: <PercentageOutlined />,
        max: <FieldNumberOutlined />,
        min: <FieldNumberOutlined />,
      };
      return icons[operator?.toLowerCase()] || <BarChartOutlined />;
    };

    const ReportEnabledCardsCard = ({
      allFields,
      reportEnabledFields,
      activeFilters,
      onGenerateGraph,
      onApplyFilter,
      onClearFilters,
      viewMode,
      setViewMode,
    }) => {
      const [popupGraphData, setPopupGraphData] = useState(null);

      const sortedAllFields = [...allFields].sort((a, b) => {
        const [fieldIdA, calculationA] = a;
        const [fieldIdB, calculationB] = b;

        const fieldInfoA = reportEnabledFields.find(
          (f) => f.field_id === fieldIdA,
        );
        const fieldInfoB = reportEnabledFields.find(
          (f) => f.field_id === fieldIdB,
        );

        const isImportantA = fieldInfoA?.report_important === true;
        const isImportantB = fieldInfoB?.report_important === true;

        if (isImportantA && !isImportantB) return -1;
        if (!isImportantA && isImportantB) return 1;

        const valueA = calculationA.value || 0;
        const valueB = calculationB.value || 0;
        return valueB - valueA;
      });

      const getOperatorColor = (operator) => {
        const colors = {
          sum: "#1890ff",
          count: "#52c41a",
          average: "#fa8c16",
          percentage: "#eb2f96",
          max: "#722ed1",
          min: "#13c2c2",
        };
        return colors[operator?.toLowerCase()] || "#1890ff";
      };

      const formatValue = (value, calculation) => {
        if (value === undefined || value === null);
        if (calculation.operator?.toLowerCase() === "percentage")
          return `${value.toFixed(2)}%`;
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
      };

      const formatFieldName = (fieldId) => {
        return fieldId
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      };

      const handleViewModeChange = (checked) => {
        setViewMode(checked ? "double" : "single");
      };

      const openGraphPopup = (graphKey, graphData, fieldId, displayName) => {
        setPopupGraphData({
          key: graphKey,
          data: graphData,
          fieldId: fieldId,
          displayName: displayName,
          show: true,
        });
      };

      const handleBreakdownClick = (
        item,
        breakdownLevel,
        fieldInfo,
        calculation,
      ) => {
        if (breakdownLevel === "division") {
          onApplyFilter(
            "division",
            item.name,
            item.value,
            fieldInfo,
            calculation,
          );
        } else if (breakdownLevel === "district") {
          onApplyFilter(
            "district",
            item.name,
            item.value,
            fieldInfo,
            calculation,
          );
        } else if (breakdownLevel === "ulb") {
          message.info(`Viewing details for ULB: ${item.name}`);
        }
      };

      const getBreakdownLabel = (breakdownLevel) => {
        const labels = {
          division: "Divisions",
          district: "Districts",
          ulb: "ULBs",
        };
        return labels[breakdownLevel] || "Breakdown";
      };

      return (
        <div style={{ marginBottom: 22 }}>
          {/* Quick Filter Active Banner */}
          {quickFilter.isActive && (
            <div style={{
              marginBottom: 16,
              padding: "10px 16px",
              background: "#e6f7ff",
              borderRadius: "8px",
              borderLeft: "4px solid #1890ff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px"
            }}>
              <Space>
                <FilterOutlined style={{ color: "#1890ff", fontSize: "16px" }} />
                <span>
                  <strong>Quick Filter Applied:</strong> {quickFilter.fieldDisplayName} = "{quickFilter.value}"
                </span>
                <Tag color="blue">{quickFilter.filteredData.length} matching records</Tag>
              </Space>
              <Button
                size="small"
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  if (window.handleQuickFilterClear) {
                    window.handleQuickFilterClear();
                  }
                }}
              >
                Clear Quick Filter
              </Button>
            </div>
          )}

          <Card
            title={
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 500 }}>
                  {quickFilter.isActive ? "FILTERED DATA POINT VIEW" : "DATA POINT VIEW"}
                </span>
                <Space>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Change View
                  </span>
                  <Switch
                    checkedChildren="2 Cards"
                    unCheckedChildren="1 Card"
                    checked={viewMode === "double"}
                    onChange={handleViewModeChange}
                  />
                </Space>
              </Space>
            }
            style={{
              width: "100%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #f0f0f0",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div
              style={{
                display: viewMode === "double" ? "grid" : "flex",
                gridTemplateColumns:
                  viewMode === "double" ? "repeat(2, 1fr)" : "none",
                flexDirection: viewMode === "double" ? "row" : "column",
                gap: "16px",
                maxHeight: "calc(100vh - 300px)",
                overflowY: "auto",
                padding: "4px",
              }}
            >
              {sortedAllFields.map(([fieldId, calculation]) => {
                const fieldInfo = reportEnabledFields.find(
                  (f) => f.field_id === fieldId,
                );
                const isImportant = fieldInfo?.report_important === true;
                const displayName =
                  fieldInfo?.display_name ||
                  calculation.display_name ||
                  formatFieldName(fieldId);
                const operator = calculation.operator || "sum";
                const columnName = fieldInfo?.column_name || fieldId;

                const hierarchicalBreakdown = getHierarchicalBreakdown(
                  fieldId,
                  calculation,
                  activeFilters,
                );
                const breakdownLevel =
                  hierarchicalBreakdown.length > 0
                    ? hierarchicalBreakdown[0].breakdownLevel
                    : "division";
                const breakdownLabel = getBreakdownLabel(breakdownLevel);

                const graphKey = fieldId;
                const graphData = fieldGraphsData[graphKey];
                const isLoadingGraph = loadingGraphs[graphKey];

                // Auto-load graph if not loaded - pass current filters
                if (!graphData && !isLoadingGraph) {
                  setTimeout(() => {
                    generateColumnGraph(columnName, displayName, fieldId, "pie");
                  }, 100);
                }

                return (
                  <div
                    key={fieldId}
                    style={{
                      padding: "20px",
                      border: "1px solid #e9ecef",
                      borderRadius: "12px",
                      background: selectedFormColorIndex !== null
                        ? `rgba(${hexToRgb(CARD_START_COLORS[selectedFormColorIndex % CARD_START_COLORS.length])}, 0.08)`
                        : "#ffffff",
                      transition: "all 0.2s ease",
                      position: "relative",
                      height: "fit-content",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.08)";
                      e.currentTarget.style.borderColor =
                        getOperatorColor(operator);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 1px 2px rgba(0,0,0,0.03)";
                      e.currentTarget.style.borderColor = "#e9ecef";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: getOperatorColor(operator),
                        borderRadius: "3px 3px 0 0",
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        gap: "16px",
                        flexWrap: "wrap",
                        marginTop: isImportant ? 8 : 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flex: 1,
                          minWidth: "200px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "16px",
                              color: "#1a1a1a",
                              marginBottom: "4px",
                              display: "flex",
                              justifyContent: "start",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {displayName}
                            <div
                              style={{
                                textAlign: "start",
                                padding: "8px 16px",
                                background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5",
                                borderRadius: "8px",
                                minWidth: "120px",
                                borderLeft: quickFilter.isActive ? "3px solid #1890ff" : "none",
                              }}
                            >
                              <div style={{ fontSize: "12px", color: quickFilter.isActive ? "#1890ff" : "black" }}>
                                {quickFilter.isActive ? `Filtered Total (${quickFilter.fieldDisplayName} = ${quickFilter.value})` : "State Total"}
                              </div>
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: 700,
                                  color: "#1890ff",
                                  lineHeight: 1,
                                }}
                              >
                                {calculateStateTotal(fieldId, calculation)}
                              </div>
                              {quickFilter.isActive && (
                                <div style={{ fontSize: "10px", color: "#8c8c8c", marginTop: "4px" }}>
                                  Based on {quickFilter.filteredData.length} matching records
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          type="primary"
                          icon={<BarChartOutlined />}
                          onClick={async () => {
                            if (isLoadingGraph) return;

                            if (!graphData || !graphData.showGraph) {
                              setPopupGraphData({
                                key: graphKey,
                                data: null,
                                displayName,
                                show: true,
                                loading: true,
                              });

                              const generatedData = await onGenerateGraph(
                                columnName,
                                displayName,
                                fieldId,
                                graphData?.chartType || "bar",
                              );

                              const finalData =
                                generatedData ||
                                fieldGraphsData[graphKey] ||
                                null;
                              setPopupGraphData({
                                key: graphKey,
                                data: finalData,
                                displayName,
                                show: true,
                                loading: false,
                              });
                              return;
                            }

                            setPopupGraphData({
                              key: graphKey,
                              data: graphData,
                              displayName,
                              show: true,
                              loading: false,
                            });
                          }}
                          loading={isLoadingGraph}
                          size="middle"
                          style={{
                            borderRadius: "8px",
                            height: "36px",
                            padding: "0 16px",
                            fontWeight: 500,
                            fontSize: "13px",
                            background: getOperatorColor(operator),
                            border: "none",
                            boxShadow: "none",
                          }}
                        >
                          Graphical view
                        </Button>
                      </div>
                    </div>

                    {/* Active Filters Section */}
                    {activeFilters.length > 0 && (
                      <div
                        style={{
                          marginTop: "16px",
                          paddingTop: "12px",
                          borderTop: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            color: "#595959",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          Active Filters
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          {activeFilters.map((filter, idx) => {
                            const filterValue = getFilterSpecificValue(
                              fieldId,
                              calculation,
                              filter.type,
                              filter.filterValue,
                              activeFilters,
                            );
                            if (filterValue === null) return null;

                            return (
                              <div
                                key={`${filter.type}-${idx}`}
                                style={{
                                  flex: "1",
                                  minWidth: "180px",
                                  background: "#fffaf0",
                                  borderRadius: "8px",
                                  border: "1px solid #ffe7ba",
                                  padding: "10px 12px",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 500,
                                    color: "#fa8c16",
                                    marginBottom: "6px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  {filter.label}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      color: "#ad6800",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                    }}
                                  >
                                    {filter.filterValue}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#fa8c16",
                                    }}
                                  >
                                    {formatValue(filterValue, calculation)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Hierarchical Breakdown Section */}
                    {hierarchicalBreakdown.length > 0 && (
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: "#595959",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <div
                              style={{
                                width: "3px",
                                height: "12px",
                                background: getOperatorColor(operator),
                                borderRadius: "2px",
                              }}
                            />
                            {breakdownLabel}
                          </div>
                          {activeFilters.length > 0 && (
                            <Button
                              type="link"
                              size="small"
                              onClick={onClearFilters}
                              style={{ fontSize: "10px", padding: 0 }}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(140px, 1fr))",
                            gap: "8px",
                          }}
                        >
                          {hierarchicalBreakdown.map((item, idx) => (
                            <div
                              key={idx}
                              style={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              onClick={() =>
                                handleBreakdownClick(
                                  item,
                                  breakdownLevel,
                                  fieldInfo,
                                  calculation,
                                )
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                              }}
                            >
                              <div
                                style={{
                                  background: "#fafafa",
                                  borderRadius: "8px",
                                  padding: "8px 10px",
                                  border: "1px solid #f0f0f0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 500,
                                      color: "#1a1a1a",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                    }}
                                  >
                                    {item.name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: getOperatorColor(operator),
                                    }}
                                  >
                                    {formatValue(item.value, calculation)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isLoadingGraph && (
                      <div
                        style={{
                          marginTop: "20px",
                          textAlign: "center",
                          padding: "40px",
                        }}
                      >
                        <Spin size="large" />
                        <div style={{ marginTop: "12px", color: "#8c8c8c" }}>
                          Loading graph data...
                        </div>
                      </div>
                    )}

                    {viewMode === "single" &&
                      graphData &&
                      graphData.showGraph && (
                        <div
                          id={`graph-container-${graphKey}`}
                          style={{
                            transition: "opacity 0.25s ease",
                            opacity: 1,
                          }}
                        >
                          {renderInlineGraph(graphKey, graphData)}
                        </div>
                      )}
                  </div>
                );
              })}
              {sortedAllFields.length === 0 && (
                <Empty description="No report-enabled fields available" />
              )}
            </div>
          </Card>

          <Modal
            title={popupGraphData?.displayName || "Graph Analytics"}
            open={popupGraphData?.show || false}
            onCancel={() => setPopupGraphData(null)}
            footer={null}
            width={800}
            destroyOnClose
          >
            {popupGraphData?.loading ? (
              <div
                style={{
                  minHeight: 360,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Spin size="large" />
                <div style={{ marginTop: 12, color: "#8c8c8c" }}>
                  Loading graph...
                </div>
              </div>
            ) : popupGraphData?.data ? (
              renderInlineGraph(popupGraphData.key, popupGraphData.data)
            ) : (
              <div
                style={{
                  minHeight: 360,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#8c8c8c",
                }}
              >
                No graph data available
              </div>
            )}
          </Modal>
        </div>
      );
    };

    const activeFilters = getActiveFilters();

    const handleApplyFilter = (
      filterType,
      filterValue,
      value,
      fieldInfo,
      calculation,
    ) => {
      message.success(`Applied ${filterType} filter: ${filterValue}`);
      if (filterType === "division") {
        handleDivisionChange(filterValue);
      } else if (filterType === "district") {
        handleDistrictChange(filterValue);
      }
    };

    const handleClearFilters = () => {
      message.info("Cleared all filters");
      resetFilters();
    };

    return (
      <ReportEnabledCardsCard
        allFields={allFields}
        reportEnabledFields={reportEnabledFields}
        activeFilters={activeFilters}
        onGenerateGraph={generateColumnGraph}
        onApplyFilter={handleApplyFilter}
        onClearFilters={handleClearFilters}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
    );
  };

  const renderReportEnabledCardsWithFilter = (fieldsToShowParam = null) => {
    const fieldsToShow = fieldsToShowParam || (formColumnSearchTerm.trim() ? filteredReportFields : reportEnabledFields);

    if (!reportCalculations || Object.keys(reportCalculations).length === 0) return null;

    if (fieldsToShow.length === 0) {
      return (
        <Card
          style={{
            marginBottom: 24,
            borderRadius: "12px",
            background: "#fafafa",
            textAlign: "center",
            padding: "40px",
          }}
        >
          <Empty
            description={
              formColumnSearchTerm
                ? `No data points found matching "${formColumnSearchTerm}"`
                : "No report-enabled fields available"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    // Filter calculations to only show matching fields
    const filteredCalculations = {};
    fieldsToShow.forEach((field) => {
      if (reportCalculations[field.field_id]) {
        filteredCalculations[field.field_id] = reportCalculations[field.field_id];
      }
    });

    const allFields = Object.entries(filteredCalculations);

    const sortedAllFields = [...allFields].sort((a, b) => {
      const [fieldIdA, calculationA] = a;
      const [fieldIdB, calculationB] = b;

      const fieldInfoA = fieldsToShow.find((f) => f.field_id === fieldIdA);
      const fieldInfoB = fieldsToShow.find((f) => f.field_id === fieldIdB);

      const isImportantA = fieldInfoA?.report_important === true;
      const isImportantB = fieldInfoB?.report_important === true;

      if (isImportantA && !isImportantB) return -1;
      if (!isImportantA && isImportantB) return 1;

      const valueA = calculationA.value || 0;
      const valueB = calculationB.value || 0;
      return valueB - valueA;
    });

    const formatValue = (value, calculation, operation = null) => {
      if (value === undefined || value === null) return "0";

      // Handle percentage
      if (calculation.operator?.toLowerCase() === "percentage") {
        return `${value.toFixed(2)}%`;
      }

      // Check if this is an average operation
      const operator = calculation.operator || operation;
      if (operator?.toLowerCase() === "avg" || operator?.toLowerCase() === "average") {
        return value.toFixed(2);
      }

      // For sum/min/max/count - show as is with proper formatting
      if (typeof value === 'number') {
        return value.toLocaleString(undefined, {
          maximumFractionDigits: 2,
          useGrouping: false
        });
      }

      return value.toString();
    };

    const getOperatorColor = (operator) => {
      const colors = {
        sum: "#1890ff",
        count: "#52c41a",
        average: "#fa8c16",
        percentage: "#eb2f96",
        max: "#722ed1",
        min: "#13c2c2",
      };
      return colors[operator?.toLowerCase()] || "#1890ff";
    };

    const getOperatorIcon = (operator) => {
      const icons = {
        count: <NumberOutlined />,
        average: <CalculatorOutlined />,
        percentage: <PercentageOutlined />,
        max: <FieldNumberOutlined />,
        min: <FieldNumberOutlined />,
      };
      return icons[operator?.toLowerCase()] || <BarChartOutlined />;
    };

    const formatFieldName = (fieldId) => {
      return fieldId
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    // Enhanced getActiveFilters to capture Division Admin's division from user role
    const getActiveFilters = () => {
      const list = [];

      const divisionField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग")),
      ) || "division_name";

      // Check for division from filters state first
      let divisionValue = filters.division || filters.division_id;

      // If no division in filters, check if user is Division Admin
      const isDivisionAdmin = !hasFullAccess() && (
        userRole === "DIVISION_ADMIN" ||
        userRole === "division_admin" ||
        userRole === "DIVISIONAL_OFFICER" ||
        userRole === "divisional_officer" ||
        userRole === "Divisionalcommissioner"
      );

      // For Division Admin, get their division from user data
      if (!divisionValue && isDivisionAdmin && userDivisionId) {
        const divisionName = referenceCache.divisions[userDivisionId]?.name;
        if (divisionName) {
          divisionValue = divisionName;
          console.log("🏢 Division Admin detected - using division from user role:", divisionName);
        }
      }

      if (divisionValue) {
        list.push({
          type: "division",
          label: "Division",
          value: divisionValue,
          fieldId: divisionField,
          filterValue: divisionValue,
        });
      }

      const districtField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा")),
      ) || "district_name";
      const districtValue = filters.district || filters.district_id;
      if (districtValue) {
        list.push({
          type: "district",
          label: "District",
          value: districtValue,
          fieldId: districtField,
          filterValue: districtValue,
        });
      }

      const ulbField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb") ||
            f.toLowerCase().includes("मनपा") ||
            f.toLowerCase().includes("municipality")),
      ) || "ulb_name";
      if (filters.ulb_name) {
        list.push({
          type: "ulb",
          label: "ULB",
          value: filters.ulb_name,
          fieldId: ulbField,
          filterValue: filters.ulb_name,
        });
      }

      const ulbTypeField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb_type") || f.toLowerCase().includes("type")),
      ) || "ulb_type";
      if (filters.ulb_type) {
        list.push({
          type: "ulb_type",
          label: "ULB Type",
          value: filters.ulb_type,
          fieldId: ulbTypeField,
          filterValue: filters.ulb_type,
        });
      }

      const classField = formFields.find(
        (f) =>
          f && (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग")),
      ) || "class";
      if (filters.ulb_class) {
        list.push({
          type: "ulb_class",
          label: "ULB Class",
          value: filters.ulb_class,
          fieldId: classField,
          filterValue: filters.ulb_class,
        });
      }

      return list;
    };

    const activeFilters = getActiveFilters();

    // Enhanced getHierarchicalBreakdown to properly show districts for Division Admins and ULBs for District filter
    const getHierarchicalBreakdown = (fieldId, calculation, activeFilters) => {
      // Use quick filtered data if active
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : formData;

      if (!dataToUse || dataToUse.length === 0) return [];

      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      // Get sample row to check available fields
      const sampleRow = dataToUse[0];
      console.log("🔍 Available fields in filtered data:", Object.keys(sampleRow || {}));

      // Define possible field names for each level
      const possibleDivisionFields = [
        'division_name', 'division', 'Division', 'विभाग',
        'division_name_formatted', 'division_label'
      ];

      const possibleDistrictFields = [
        'district_name', 'district', 'District', 'जिल्हा',
        'district_name_formatted', 'district_label'
      ];

      const possibleUlbFields = [
        'ulb_name', 'ulb', 'ULB Name', 'मनपा/न.प. चे नाव',
        'municipality', 'ulb_label', 'ULB'
      ];

      // Find which fields exist in the data
      let divisionField = null;
      for (const field of possibleDivisionFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") {
          divisionField = field;
          break;
        }
      }

      let districtField = null;
      for (const field of possibleDistrictFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") {
          districtField = field;
          break;
        }
      }

      let ulbField = null;
      for (const field of possibleUlbFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") {
          ulbField = field;
          break;
        }
      }

      console.log("🔍 Found fields - Division:", divisionField, "District:", districtField, "ULB:", ulbField);

      const divisionFilter = activeFilters.find((f) => f.type === "division");
      const districtFilter = activeFilters.find((f) => f.type === "district");

      // Check user roles
      const isDivisionAdmin = !hasFullAccess() && (
        userRole === "DIVISION_ADMIN" ||
        userRole === "division_admin" ||
        userRole === "DIVISIONAL_OFFICER" ||
        userRole === "divisional_officer" ||
        userRole === "Divisionalcommissioner"
      );

      const isDistrictAdmin = !hasFullAccess() && (
        userRole === "DISTRICT_ADMIN" ||
        userRole === "district_admin"
      );

      // Determine grouping level based on what's available and what filters are applied
      let groupByField = null;
      let breakdownLevel = "";
      let shouldForceUlbs = false;
      let shouldForceDistricts = false;

      // Priority 1: If ULB filter is applied, show detailed records (no grouping)
      if (activeFilters.some(f => f.type === "ulb")) {
        groupByField = null;
        breakdownLevel = "record";
        console.log("📊 ULB filter applied - showing record level");
      }
      // Priority 2: For District Admin, force ULB level
      else if (isDistrictAdmin && ulbField) {
        groupByField = ulbField;
        breakdownLevel = "ulb";
        console.log("📊 District Admin - showing ULB level");
      }
      // Priority 3: If district filter is applied (and no ULB filter), show ULBs
      else if (districtFilter && ulbField && !activeFilters.some(f => f.type === "ulb")) {
        groupByField = ulbField;
        breakdownLevel = "ulb";
        console.log("📊 District filter applied - showing ULB level");
      }
      // Priority 4: For Division Admin with no district filter, show districts
      else if (isDivisionAdmin && districtField && !districtFilter) {
        groupByField = districtField;
        breakdownLevel = "district";
        console.log("📊 Division Admin - showing DISTRICT level");
      }
      // Priority 5: If division filter is applied (and no district/ulb filters), show districts
      else if (divisionFilter && districtField && !districtFilter && !activeFilters.some(f => f.type === "ulb")) {
        groupByField = districtField;
        breakdownLevel = "district";
        console.log("📊 Division filter applied - showing DISTRICT level");
      }
      // Priority 6: Default - show divisions if available
      else if (divisionField) {
        groupByField = divisionField;
        breakdownLevel = "division";
        console.log("📊 No filters - showing DIVISION level");
      }
      // Priority 7: Fallback to district if division not available
      else if (districtField) {
        groupByField = districtField;
        breakdownLevel = "district";
        console.log("📊 Fallback - showing DISTRICT level");
      }
      // Priority 8: Fallback to ULB if nothing else
      else if (ulbField) {
        groupByField = ulbField;
        breakdownLevel = "ulb";
        console.log("📊 Fallback - showing ULB level");
      }
      else {
        console.warn("⚠️ No location fields found in filtered data");
        return [];
      }

      // If no grouping field (record level), return individual records
      if (!groupByField || breakdownLevel === "record") {
        const results = [];
        dataToUse.forEach((row, idx) => {
          const val = row[actualColumnName];
          if (val !== undefined && val !== null && val !== "") {
            const numVal = parseFloat(String(val).replace(/,/g, ""));
            if (!isNaN(numVal) && isFinite(numVal)) {
              results.push({
                name: `Record ${idx + 1}`,
                value: numVal,
                count: 1,
                avgValue: numVal,
                breakdownLevel: "record",
                division: row[divisionField] || row.division_name || row.division,
                district: row[districtField] || row.district_name || row.district,
                ulb: row[ulbField] || row.ulb_name || row.ulb,
              });
            }
          }
        });
        return results;
      }

      // Use the correct filter fields based on what's available
      const divisionFieldForFilter = divisionField || 'division_name';
      const districtFieldForFilter = districtField || 'district_name';

      const filterConditions = (row) => {
        let matches = true;

        // Apply division filter if it exists
        if (divisionFilter) {
          const rowDivision = row[divisionFieldForFilter];
          if (rowDivision && String(rowDivision) !== String(divisionFilter.filterValue)) {
            matches = false;
          }
        }

        // Apply district filter if it exists
        if (districtFilter && matches) {
          const rowDistrict = row[districtFieldForFilter];
          if (rowDistrict && String(rowDistrict) !== String(districtFilter.filterValue)) {
            matches = false;
          }
        }

        return matches;
      };

      // Group the data
      const groups = {};
      dataToUse.forEach((row) => {
        if (!filterConditions(row)) return;

        let groupValue = row[groupByField];

        // Handle null/undefined/empty group values
        if (!groupValue || groupValue === null || groupValue === undefined || groupValue === "") {
          groupValue = `Unknown ${breakdownLevel}`;
        }

        const val = row[actualColumnName];

        if (val !== undefined && val !== null && val !== "") {
          const numVal = parseFloat(String(val).replace(/,/g, ""));
          if (!isNaN(numVal) && isFinite(numVal)) {
            if (!groups[groupValue]) {
              groups[groupValue] = {
                name: groupValue,
                values: [],
                division: row[divisionFieldForFilter] || row.division_name || row.division,
                district: row[districtFieldForFilter] || row.district_name || row.district,
                ulb: breakdownLevel === "ulb" ? row[groupByField] : row[ulbField] || row.ulb_name || row.ulb,
              };
            }
            groups[groupValue].values.push(numVal);
          }
        }
      });

      // Calculate results for each group
      const results = [];
      Object.keys(groups).forEach((groupName) => {
        const group = groups[groupName];
        let result = 0;

        if (group.values.length > 0) {
          switch (operator.toLowerCase()) {
            case "sum":
              result = group.values.reduce((a, b) => a + b, 0);
              break;
            case "average":
            case "avg":
              result = group.values.reduce((a, b) => a + b, 0) / group.values.length;
              break;
            case "min":
              result = Math.min(...group.values);
              break;
            case "max":
              result = Math.max(...group.values);
              break;
            case "count":
              result = group.values.length;
              break;
            default:
              result = group.values.reduce((a, b) => a + b, 0);
          }
        }

        results.push({
          name: groupName,
          value: result,
          count: group.values.length,
          avgValue: group.values.length > 0 ? result / group.values.length : 0,
          breakdownLevel: breakdownLevel,
          division: group.division,
          district: group.district,
          ulb: group.ulb,
        });
      });

      console.log(`📊 Breakdown results for ${fieldId}: ${results.length} ${breakdownLevel}s, total value: ${results.reduce((sum, r) => sum + r.value, 0)}`);

      return results.sort((a, b) => a.name.localeCompare(b.name));
    };

    const getFilterFieldForType = (filterType) => {
      switch (filterType) {
        case "division":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग")),
          ) || "division_name";
        case "district":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा")),
          ) || "district_name";
        case "ulb":
          return formFields.find(
            (f) =>
              f &&
              (f.toLowerCase().includes("ulb") ||
                f.toLowerCase().includes("मनपा") ||
                f.toLowerCase().includes("municipality")),
          ) || "ulb_name";
        case "ulb_type":
          return formFields.find(
            (f) =>
              f && (f.toLowerCase().includes("ulb_type") || f.toLowerCase().includes("type")),
          ) || "ulb_type";
        case "ulb_class":
          return formFields.find(
            (f) =>
              f && (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग")),
          ) || "class";
        default:
          return null;
      }
    };

    const getFilterSpecificValue = (
      fieldId,
      calculation,
      currentFilterType,
      currentFilterValue,
      allActiveFilters,
    ) => {
      // Use quick filtered data if active
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : (originalFormData && originalFormData.length > 0 ? originalFormData : formData);

      if (!dataToUse || dataToUse.length === 0 || !currentFilterValue) return null;

      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      let filteredData = [...dataToUse];

      const filterOrder = {
        ulb_type: 0,
        ulb_class: 1,
        division: 2,
        district: 3,
        ulb: 4,
      };

      const currentIndex = filterOrder[currentFilterType] || 0;

      for (const filter of allActiveFilters) {
        if (filter.type === currentFilterType) continue;

        const filterIndex = filterOrder[filter.type] || 0;

        if (filterIndex < currentIndex) {
          const filterField = getFilterFieldForType(filter.type);
          if (filterField) {
            filteredData = filteredData.filter((row) => {
              const rowValue = row[filterField];
              return rowValue && String(rowValue) === String(filter.filterValue);
            });
          }
        }
      }

      const currentFilterField = getFilterFieldForType(currentFilterType);
      if (!currentFilterField) return null;

      filteredData = filteredData.filter((row) => {
        const rowValue = row[currentFilterField];
        return rowValue && String(rowValue) === String(currentFilterValue);
      });

      if (filteredData.length === 0) return null;

      const values = filteredData
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      if (values.length === 0) return null;

      switch (operator.toLowerCase()) {
        case "sum":
          return values.reduce((a, b) => a + b, 0);
        case "avg":
        case "average":
          return values.reduce((a, b) => a + b, 0) / values.length;
        case "min":
          return Math.min(...values);
        case "max":
          return Math.max(...values);
        case "count":
          return values.length;
        default:
          return values.reduce((a, b) => a + b, 0);
      }
    };

    const handleApplyFilter = (filterType, filterValue, value, fieldInfo, calculation) => {
      message.success(`Applied ${filterType} filter: ${filterValue}`);
      if (filterType === "division") {
        handleDivisionChange(filterValue);
      } else if (filterType === "district") {
        handleDistrictChange(filterValue);
      } else if (filterType === "ulb") {
        handleUlbChange(filterValue);
      }

      // Reload form data with the new filter
      if (selectedForm) {
        const newFilters = { ...filters, [filterType]: filterValue };
        setTimeout(() => {
          loadFormData(selectedForm.id, newFilters);
          // Also refresh calculations if any
          if (selectedOperations.length > 0) {
            performCalculations();
          }
        }, 100);
      }
    };

    const handleClearFilters = () => {
      message.info("Cleared all filters");
      resetFilters();
    };

    const calculateStateTotal = (fieldId, calculation) => {
      // Use quick filtered data if active
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0
        ? quickFilter.filteredData
        : (originalFormData.length > 0 ? originalFormData : formData);

      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";

      const allValues = dataToUse
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      let originalTotal = 0;
      if (allValues.length > 0) {
        switch (operator.toLowerCase()) {
          case "sum":
            originalTotal = allValues.reduce((a, b) => a + b, 0);
            break;
          case "avg":
          case "average":
            originalTotal = allValues.reduce((a, b) => a + b, 0) / allValues.length;
            break;
          case "min":
            originalTotal = Math.min(...allValues);
            break;
          case "max":
            originalTotal = Math.max(...allValues);
            break;
          case "count":
            originalTotal = allValues.length;
            break;
          default:
            originalTotal = allValues.reduce((a, b) => a + b, 0);
        }
      }

      return formatValue(originalTotal, calculation);
    };

    return (
      <div style={{ marginBottom: 22 }}>

        <Card
          title={null}
          style={{
            width: "100%",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #f0f0f0",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "12px",
          }}
          bodyStyle={{
            padding: "0",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Custom Header with Gradient */}
          <div
            style={{
              background: "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)",
              padding: "9px 11px",
              borderRadius: "12px 12px 0 0",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <span style={{ fontWeight: 600, fontSize: "13px", color: "#ffffff" }}>

              </span>
              {activeFilters.length > 0 && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  style={{ fontSize: "11px", height: "28px" }}
                >
                  <ReloadOutlined /> Clear Filters ({activeFilters.length})
                </Button>
              )}
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: viewMode === "double" ? "grid" : "flex",
                gridTemplateColumns: viewMode === "double" ? "repeat(2, 1fr)" : "none",
                flexDirection: viewMode === "double" ? "row" : "column",
                gap: "16px",
                maxHeight: "calc(100vh - 300px)",
                overflowY: "auto",
                padding: "4px",
              }}
            >
              {sortedAllFields.map(([fieldId, calculation]) => {
                const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
                const isImportant = fieldInfo?.report_important === true;
                const displayName = fieldInfo?.display_name || calculation.display_name || formatFieldName(fieldId);
                const operator = calculation.operator || "sum";
                const columnName = fieldInfo?.column_name || fieldId;

                const hierarchicalBreakdown = getHierarchicalBreakdown(fieldId, calculation, activeFilters);
                const breakdownLevel = hierarchicalBreakdown.length > 0 ? hierarchicalBreakdown[0].breakdownLevel : "division";

                const getBreakdownLabel = (level) => {
                  const labels = { division: "Divisions", district: "Districts", ulb: "ULBs" };
                  return labels[level] || "Breakdown";
                };
                const breakdownLabel = getBreakdownLabel(breakdownLevel);

                const graphKey = fieldId;
                const graphData = fieldGraphsData[graphKey];
                const isLoadingGraph = loadingGraphs[graphKey];

                // Auto-load graph if not loaded - pass current filters
                if (!graphData && !isLoadingGraph) {
                  setTimeout(() => {
                    generateColumnGraph(columnName, displayName, fieldId, "pie");
                  }, 100);
                }

                return (
                  <div
                    key={fieldId}
                    style={{
                      padding: "20px",
                      border: "1px solid #e9ecef",
                      borderRadius: "12px",
                      background: "#ffffff",
                      transition: "all 0.2s ease",
                      position: "relative",
                      height: "fit-content",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                      e.currentTarget.style.borderColor = getOperatorColor(operator);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
                      e.currentTarget.style.borderColor = "#e9ecef";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: getOperatorColor(operator),
                        borderRadius: "3px 3px 0 0",
                      }}
                    />

                    {/* ========== 1-CARD VIEW LAYOUT WITH 50/50 SPLIT ========== */}
                    {viewMode === "single" ? (
                      <div style={{ display: "flex", minHeight: "280px" }}>
                        {/* LEFT SIDE - 50%: Metrics Section */}
                        <div style={{ flex: 1, paddingRight: "8px", display: "flex", flexDirection: "column", overflow: "auto" }}>
                          {/* Title */}
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "16px",
                              color: "#1a1a1a",
                              marginBottom: "12px",
                            }}
                          >
                            {displayName}
                          </div>

                          {/* State Total Box - Shows context for quick filter */}
                          <div
                            style={{
                              textAlign: "start",
                              padding: "8px 12px",
                              background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5",
                              borderRadius: "8px",
                              display: "inline-block",
                              width: "fit-content",
                              marginBottom: "16px",
                              borderLeft: quickFilter.isActive ? "3px solid #1890ff" : "none",
                            }}
                          >
                            <div style={{ fontSize: "12px", color: quickFilter.isActive ? "#1890ff" : "black" }}>
                              {quickFilter.isActive ? `Filtered Total (${quickFilter.fieldDisplayName} = ${quickFilter.value})` : "State Total"}
                            </div>
                            <div
                              style={{
                                fontSize: "18px",
                                fontWeight: 700,
                                color: "#1890ff",
                                lineHeight: 1.2,
                              }}
                            >
                              {calculateStateTotal(fieldId, calculation)}
                            </div>
                            {quickFilter.isActive && (
                              <div style={{ fontSize: "10px", color: "#8c8c8c", marginTop: "4px" }}>
                                Based on {quickFilter.filteredData.length} matching records
                              </div>
                            )}
                          </div>

                          {/* Active Filters Section */}
                          {activeFilters.length > 0 && (
                            <div
                              style={{
                                marginBottom: "16px",
                                paddingTop: "8px",
                                borderTop: "1px solid #f0f0f0",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  color: "#595959",
                                  marginBottom: "8px",
                                  textTransform: "uppercase",
                                }}
                              >
                                Active Filters
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {activeFilters.map((filter, idx) => {
                                  const filterValue = getFilterSpecificValue(
                                    fieldId,
                                    calculation,
                                    filter.type,
                                    filter.filterValue,
                                    activeFilters,
                                  );
                                  if (filterValue === null) return null;

                                  return (
                                    <div
                                      key={`${filter.type}-${idx}`}
                                      style={{
                                        flex: "1",
                                        minWidth: "140px",
                                        textAlign: "start",
                                        padding: "8px 12px",
                                        background: "#f5f5f5",
                                        borderRadius: "8px",
                                        display: "inline-block",
                                        width: "fit-content",
                                      }}
                                    >
                                      <div style={{ fontSize: "11px", color: "black", marginBottom: "2px" }}>
                                        {filter.label}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "15px",
                                          fontWeight: 700,
                                          color: "#fa8c16",
                                          lineHeight: 1.2,
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {filter.filterValue}
                                      </div>
                                      <div style={{ fontSize: "11px", color: "black", marginTop: "4px" }}>
                                        {formatValue(filterValue, calculation)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Metrics Cards - Shows Districts or ULBs based on drill level */}
                          {hierarchicalBreakdown.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: breakdownLevel === "ulb" ? "#fa8c16" : (breakdownLevel === "district" ? "#52c41a" : "#1890ff"),
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {breakdownLabel} ({hierarchicalBreakdown.length})
                                </div>
                                <div style={{ flex: 1, height: "1px", background: "#e9ecef" }} />
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(3, 1fr)",
                                  gap: "10px",
                                }}
                              >
                                {hierarchicalBreakdown.slice(0, 20).map((item, idx) => {
                                  const colorFamilies = [
                                    { start: "#096dd9", end: "#69c0ff" },
                                    { start: "#389e0d", end: "#95de64" },
                                    { start: "#d46b08", end: "#ffc069" },
                                    { start: "#cf1322", end: "#ff7875" },
                                    { start: "#531dab", end: "#b37feb" },
                                    { start: "#08979c", end: "#5cdbd3" },
                                    { start: "#c41d7f", end: "#ff85c0" },
                                    { start: "#d48806", end: "#ffd666" },
                                    { start: "#7cb305", end: "#bae637" },
                                    { start: "#1d39c4", end: "#597ef7" },
                                    { start: "#ee5a5a", end: "#ff9c9c" },
                                    { start: "#3dbdb5", end: "#87e8de" },
                                    { start: "#35a7c1", end: "#8fd4e8" },
                                    { start: "#86bea4", end: "#cce8d4" },
                                    { start: "#eeda97", end: "#fff5cc" },
                                  ];

                                  const colors = colorFamilies[idx % colorFamilies.length];

                                  // Determine what happens on click based on current level
                                  let clickMessage = "";
                                  if (breakdownLevel === "division") {
                                    clickMessage = "Click to view districts";
                                  } else if (breakdownLevel === "district") {
                                    clickMessage = "Click to view ULBs";
                                  } else if (breakdownLevel === "ulb") {
                                    clickMessage = "Click to filter ULB data";
                                  }

                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        background: `linear-gradient(135deg, ${colors.start} 0%, ${colors.end} 100%)`,
                                        borderRadius: "10px",
                                        padding: "12px 8px",
                                        border: "1px solid rgba(255,255,255,0.3)",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                      }}
                                      onClick={() => {
                                        if (breakdownLevel === "division") {
                                          console.log("📊 Drilling down to districts for division:", item.name);
                                          handleApplyFilter("division", item.name, item.value, fieldInfo, calculation);
                                        } else if (breakdownLevel === "district") {
                                          console.log("📊 Drilling down to ULBs for district:", item.name);
                                          handleApplyFilter("district", item.name, item.value, fieldInfo, calculation);
                                        } else if (breakdownLevel === "ulb") {
                                          console.log("📊 Applying ULB filter:", item.name);
                                          handleApplyFilter("ulb", item.name, item.value, fieldInfo, calculation);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
                                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.3)";
                                        e.currentTarget.style.border = "1px solid rgba(255,255,255,0.5)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                                        e.currentTarget.style.border = "1px solid rgba(255,255,255,0.3)";
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color: "#ffffff",
                                          marginBottom: "6px",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                        }}
                                      >
                                        {item.name}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "15px",
                                          fontWeight: 800,
                                          color: "#ffffff",
                                          textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                        }}
                                      >
                                        {formatValue(item.value, calculation)}
                                      </div>
                                      {clickMessage && (
                                        <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.7)", marginTop: "4px" }}>
                                          {clickMessage}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {hierarchicalBreakdown.length > 20 && (
                                <div style={{ textAlign: "center", marginTop: "8px", fontSize: "10px", color: "#8c8c8c" }}>
                                  +{hierarchicalBreakdown.length - 20} more {breakdownLabel.toLowerCase()}
                                </div>
                              )}
                            </div>
                          )}
                          {hierarchicalBreakdown.length === 0 && (
                            <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                              No {breakdownLabel.toLowerCase()} available
                            </div>
                          )}
                        </div>

                        {/* RIGHT SIDE - 50%: Interactive Graph */}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                          {isLoadingGraph ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "300px", flexDirection: "column", gap: "12px" }}>
                              <Spin size="large" />
                              <div style={{ fontSize: "11px", color: "#8c8c8c" }}>Loading graph data...</div>
                            </div>
                          ) : graphData && graphData.isGraphReady && graphData.data && graphData.data.length > 0 ? (
                            <div
                              style={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {/* Graph Type Toggle */}
                              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px", gap: "4px" }}>
                                <Tooltip title="Bar Chart">
                                  <Button
                                    size="small"
                                    type={graphData.chartType !== "pie" ? "primary" : "default"}
                                    icon={<BarChartOutlined />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleChartType(graphKey);
                                    }}
                                    style={{ padding: "0 6px", height: "24px", fontSize: "11px" }}
                                  />
                                </Tooltip>
                                <Tooltip title="Pie Chart">
                                  <Button
                                    size="small"
                                    type={graphData.chartType === "pie" ? "primary" : "default"}
                                    icon={<PieChartOutlined />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleChartType(graphKey);
                                    }}
                                    style={{ padding: "0 6px", height: "24px", fontSize: "11px" }}
                                  />
                                </Tooltip>
                                <Tooltip title="Full Screen">
                                  <Button
                                    size="small"
                                    icon={<FullscreenOutlined />}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setGraphModalData(graphData);
                                      setGraphModalVisible(true);
                                    }}
                                    style={{ padding: "0 6px", height: "24px", fontSize: "11px" }}
                                  />
                                </Tooltip>
                              </div>

                              <div
                                style={{
                                  flex: 1,
                                  minHeight: 0,
                                  transition: "transform 0.2s ease",
                                  background: "#FFFFFF",
                                  borderRadius: "8px",
                                  padding: "8px",
                                }}
                              >
                                {graphData.chartType === "pie" ? (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={graphData.data.slice(0, 12).map((item) => ({
                                          name: item.name,
                                          shortName: item.name.length > 15 ? item.name.substring(0, 12) + ".." : item.name,
                                          fullName: item.name,
                                          value: item.value,
                                          originalName: item.originalName || item.name,
                                        }))}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={35}
                                        outerRadius={75}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ shortName, name, percent }) => {
                                          const percentage = (percent * 100).toFixed(1);
                                          const displayName = shortName || name;
                                          return `${displayName}: ${percentage}%`;
                                        }}
                                        labelLine={true}
                                        isAnimationActive={false}
                                        stroke="none"
                                      >
                                        {graphData.data.slice(0, 12).map((entry, idx) => (
                                          <Cell
                                            key={`cell-${idx}`}
                                            fill={PIE_COLORS[idx % PIE_COLORS.length]}
                                            stroke="#fff"
                                            strokeWidth={2}
                                            style={{ cursor: "pointer" }}
                                          />
                                        ))}
                                      </Pie>
                                      <RechartsTooltip
                                        content={({ active, payload }) => {
                                          if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            const totalValue = graphData.data.slice(0, 12).reduce((sum, item) => sum + item.value, 0);
                                            const percentage = ((data.value / totalValue) * 100).toFixed(1);
                                            const currentLevel = graphData.aggregationLevel || breakdownLevel;

                                            let drillHint = "";
                                            if (currentLevel === "division") drillHint = "Click to view districts";
                                            else if (currentLevel === "district") drillHint = "Click to view ULBs";
                                            else if (currentLevel === "ulb") drillHint = "Click to filter ULB data";

                                            return (
                                              <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", minWidth: "200px" }}>
                                                <div><strong>{data.fullName || data.name}</strong></div>
                                                <div>Value: {data.value.toLocaleString()}</div>
                                                <div>Percentage: {percentage}%</div>
                                                {data.count && <div>Records: {data.count}</div>}
                                                {drillHint && (
                                                  <div style={{ marginTop: "6px", fontSize: "10px", color: "#1890ff", borderTop: "1px solid #f0f0f0", paddingTop: "4px" }}>
                                                    💡 {drillHint}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                      data={graphData.data}
                                      margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                                      <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                        interval={0}
                                        tick={{ fontSize: 9 }}
                                      />
                                      <YAxis
                                        tick={{ fontSize: 9 }}
                                        tickFormatter={(value) => {
                                          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                                          return value.toLocaleString();
                                        }}
                                      />
                                      <RechartsTooltip
                                        content={({ active, payload }) => {
                                          if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            const currentLevel = graphData.aggregationLevel || breakdownLevel;

                                            let drillHint = "";
                                            if (currentLevel === "division") drillHint = "Click to view districts";
                                            else if (currentLevel === "district") drillHint = "Click to view ULBs";
                                            else if (currentLevel === "ulb") drillHint = "Click to filter ULB data";

                                            return (
                                              <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                                                <div><strong>{data.originalName || data.name}</strong></div>
                                                <div>Value: {data.value.toLocaleString()}</div>
                                                {data.count && <div>Records: {data.count}</div>}
                                                {drillHint && (
                                                  <div style={{ marginTop: "6px", fontSize: "10px", color: "#1890ff", borderTop: "1px solid #f0f0f0", paddingTop: "4px" }}>
                                                    💡 {drillHint}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }}
                                      />
                                      <Bar
                                        dataKey="value"
                                        fill="#1890ff"
                                        radius={[4, 4, 0, 0]}
                                        cursor="pointer"
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}
                              </div>

                              {/* Graph Summary */}
                              {graphData.data.length > 0 && (
                                <div style={{
                                  marginTop: "8px",
                                  fontSize: "10px",
                                  color: "#8c8c8c",
                                  textAlign: "center",
                                  borderTop: "1px solid #f0f0f0",
                                  paddingTop: "6px"
                                }}>
                                  Total: {graphData.total?.toLocaleString() || graphData.data.reduce((sum, item) => sum + item.value, 0).toLocaleString()} •
                                  {graphData.data.length} {graphData.groupLabel || "items"} •
                                  {graphData.aggregationLevel === "district" ? "📊 District Level" :
                                    graphData.aggregationLevel === "division" ? "🏢 Division Level" :
                                      graphData.aggregationLevel === "ulb" ? "🏛️ ULB Level" : "Record Level"}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                minHeight: "300px",
                                background: "#F5F5F5",
                                borderRadius: "8px",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              <Spin size="large" />
                              <div style={{ fontSize: "11px", color: "#8c8c8c" }}>Loading graph data...</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // ========== 2-CARD VIEW LAYOUT ==========
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "16px",
                            flexWrap: "wrap",
                            gap: "12px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1a1a1a",
                              }}
                            >
                              {displayName}
                            </div>
                            <div
                              style={{
                                textAlign: "start",
                                padding: "4px 10px",
                                background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5",
                                borderRadius: "6px",
                                display: "inline-block",
                                marginTop: "6px",
                                borderLeft: quickFilter.isActive ? "2px solid #1890ff" : "none",
                              }}
                            >
                              <div style={{ fontSize: "12px", color: quickFilter.isActive ? "#1890ff" : "black" }}>
                                {quickFilter.isActive ? "Filtered Total" : "State Total"}
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 700,
                                  color: "#1890ff",
                                  lineHeight: 1.2,
                                }}
                              >
                                {calculateStateTotal(fieldId, calculation)}
                              </div>
                            </div>
                          </div>

                          <Button
                            type="primary"
                            icon={<BarChartOutlined />}
                            onClick={async (e) => {
                              e.stopPropagation();

                              if (graphData && graphData.isGraphReady) {
                                setGraphModalData(graphData);
                                setGraphModalVisible(true);
                              } else {
                                setGraphModalLoading(true);
                                setGraphModalVisible(true);

                                const generatedData = await generateColumnGraph(
                                  columnName,
                                  displayName,
                                  fieldId,
                                  "pie"
                                );

                                if (generatedData) {
                                  setGraphModalData(generatedData);
                                }
                                setGraphModalLoading(false);
                              }
                            }}
                            loading={isLoadingGraph}
                            size="small"
                            style={{
                              borderRadius: "8px",
                              fontWeight: 500,
                              fontSize: "12px",
                            }}
                          >
                            Graphical View
                          </Button>
                        </div>

                        {/* Active Filters Section */}
                        {activeFilters.length > 0 && (
                          <div
                            style={{
                              marginBottom: "16px",
                              paddingTop: "8px",
                              borderTop: "1px solid #f0f0f0",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#595959",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                              }}
                            >
                              Active Filters
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              {activeFilters.map((filter, idx) => {
                                const filterValue = getFilterSpecificValue(
                                  fieldId,
                                  calculation,
                                  filter.type,
                                  filter.filterValue,
                                  activeFilters,
                                );
                                if (filterValue === null) return null;

                                return (
                                  <div
                                    key={`${filter.type}-${idx}`}
                                    style={{
                                      flex: "1",
                                      minWidth: "140px",
                                      textAlign: "start",
                                      padding: "8px 12px",
                                      background: "#f5f5f5",
                                      borderRadius: "8px",
                                      display: "inline-block",
                                      width: "fit-content",
                                    }}
                                  >
                                    <div style={{ fontSize: "11px", color: "#8c8c8c", marginBottom: "2px" }}>
                                      {filter.label}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "15px",
                                        fontWeight: 700,
                                        color: "#fa8c16",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {filter.filterValue}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "4px" }}>
                                      {formatValue(filterValue, calculation)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* ALL Metrics in grid */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "8px",
                            marginTop: "8px",
                          }}
                        >
                          {hierarchicalBreakdown.slice(0, 15).map((item, idx) => (
                            <div
                              key={idx}
                              style={{
                                background: "#fafafa",
                                borderRadius: "6px",
                                padding: "8px 6px",
                                border: "1px solid #f0f0f0",
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              onClick={() => {
                                if (breakdownLevel === "division") {
                                  handleApplyFilter("division", item.name, item.value, fieldInfo, calculation);
                                } else if (breakdownLevel === "district") {
                                  handleApplyFilter("district", item.name, item.value, fieldInfo, calculation);
                                } else if (breakdownLevel === "ulb") {
                                  handleApplyFilter("ulb", item.name, item.value, fieldInfo, calculation);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f0f0f0";
                                e.currentTarget.style.transform = "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#fafafa";
                                e.currentTarget.style.transform = "translateY(0)";
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: "#333",
                                  marginBottom: "4px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={item.name}
                              >
                                {item.name}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: getOperatorColor(operator),
                                }}
                              >
                                {formatValue(item.value, calculation)}
                              </div>
                              {item.count && (
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#bfbfbf",
                                    marginTop: "2px",
                                  }}
                                >
                                  {item.count} records
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {hierarchicalBreakdown.length === 0 && (
                          <div style={{ textAlign: "center", padding: "20px", color: "#8c8c8c" }}>
                            No metrics available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderFilterSection = () => {
    if (!selectedForm) return null;
 if (!dashboardConfig.showAnalysisSection) return null;
    // ========== CHECK IF USER IS ULB ADMIN - HIDE ENTIRE FILTER BOX ==========
    const isUlbAdmin = !hasFullAccess() && (
      userRole === "ULB_ADMIN" ||
      userRole === "ulb_admin" ||
      userRole === "ULB_OFFICER" ||
      userRole === "ULB_CHIEF_OFFICIER"
    );

    // If ULB admin, return null to hide the entire filter box
    if (isUlbAdmin) {
      return null;
    }
    // ========== END ULB ADMIN CHECK ==========

    // Get the stored color index for the selected form
    const gradientColorIndex = selectedFormColorIndex !== null
      ? selectedFormColorIndex % CARD_START_COLORS.length
      : 0;

    const gradientStart = CARD_START_COLORS[gradientColorIndex];
    const gradientEnd = CARD_END_COLORS[gradientColorIndex];

    const divisionField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("division") ||
          f.toLowerCase().includes("विभाग")),
    );
    const districtField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("district") ||
          f.toLowerCase().includes("जिल्हा")),
    );
    const ulbField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("ulb") ||
          f.toLowerCase().includes("मनपा") ||
          f.toLowerCase().includes("municipality")),
    );

    const divisionValues =
      availableFilters.divisions.length > 0
        ? availableFilters.divisions
        : divisionField
          ? getUniqueFilterValues(divisionField)
          : [];

    let baseDistrictValues = availableFilters.districts.length > 0
      ? availableFilters.districts
      : districtField
        ? getUniqueFilterValues(districtField)
        : [];

    let baseUlbValues = availableFilters.ulbs.length > 0
      ? availableFilters.ulbs
      : ulbField
        ? getUniqueFilterValues(ulbField)
        : [];

    const districtHasMatchingUlbs = (district, typeFilter, classFilter) => {
      if (!formData || formData.length === 0) return true;

      return formData.some((row) => {
        const rowDistrict = row.district_name || row.district;
        if (rowDistrict !== district) return false;

        let matchesType = true;
        let matchesClass = true;

        if (typeFilter) {
          const rowUlbType = row.ulb_type;
          matchesType = rowUlbType === typeFilter;
        }

        if (classFilter) {
          const rowClass = row.class;
          matchesClass = rowClass === classFilter;
        }

        return matchesType && matchesClass;
      });
    };

    let filteredDistricts = [...baseDistrictValues];

    if (filters.ulb_type && formData && formData.length > 0) {
      filteredDistricts = filteredDistricts.filter((district) => {
        return districtHasMatchingUlbs(district, filters.ulb_type, null);
      });
    }

    if (filters.ulb_class && formData && formData.length > 0) {
      filteredDistricts = filteredDistricts.filter((district) => {
        return districtHasMatchingUlbs(district, null, filters.ulb_class);
      });
    }

    if (filters.division && formData && formData.length > 0) {
      filteredDistricts = filteredDistricts.filter((district) => {
        return formData.some(
          (row) =>
            (row.division_name === filters.division ||
              row.division === filters.division) &&
            (row.district_name === district || row.district === district),
        );
      });
    }

    let filteredUlbs = [...baseUlbValues];

    if (filters.ulb_type && formData && formData.length > 0) {
      filteredUlbs = filteredUlbs.filter((ulb) => {
        return formData.some((row) => {
          const rowUlbType = row.ulb_type;
          const rowUlb = row.ulb_name || row.ulb;
          return rowUlbType === filters.ulb_type && rowUlb === ulb;
        });
      });
    }

    if (filters.ulb_class && formData && formData.length > 0) {
      filteredUlbs = filteredUlbs.filter((ulb) => {
        return formData.some((row) => {
          const rowClass = row.class;
          const rowUlb = row.ulb_name || row.ulb;
          return rowClass === filters.ulb_class && rowUlb === ulb;
        });
      });
    }

    if (filters.division && formData && formData.length > 0) {
      filteredUlbs = filteredUlbs.filter((ulb) => {
        return formData.some(
          (row) =>
            (row.division_name === filters.division ||
              row.division === filters.division) &&
            (row.ulb_name === ulb || row.ulb === ulb),
        );
      });
    }

    if (filters.district && formData && formData.length > 0) {
      filteredUlbs = filteredUlbs.filter((ulb) => {
        return formData.some(
          (row) =>
            (row.district_name === filters.district ||
              row.district === filters.district) &&
            (row.ulb_name === ulb || row.ulb === ulb),
        );
      });
    }

    const hasAnyFilters = divisionField || districtField || ulbField;

    if (
      !hasAnyFilters &&
      (!ulbTypes || ulbTypes.length === 0) &&
      (!ulbClasses || ulbClasses.length === 0)
    ) return null;

    const isDistrictAdmin =
      userRole === "DISTRICT_ADMIN" || userRole === "district_admin";
    const isDivisionAdmin =
      userRole === "DIVISION_ADMIN" ||
      userRole === "division_admin" ||
      userRole === "DIVISIONAL_OFFICER" ||
      userRole === "divisional_officer";
    const isUlbAdminCheck =
      userRole === "ULB_ADMIN" ||
      userRole === "ulb_admin" ||
      userRole === "ULB_OFFICER" ||
      userRole === "ULB_CHIEF_OFFICIER";

    const isDivisionDisabled =
      !hasFullAccess() && (isDivisionAdmin || isDistrictAdmin || isUlbAdminCheck);

    const isDistrictDisabled =
      (!hasFullAccess() && isDistrictAdmin) ||
      (divisionField && !filters.division && !isDivisionAdmin);

    const isUlbDisabled =
      (!hasFullAccess() && isUlbAdminCheck) ||
      (districtField && !filters.district && !isDistrictAdmin) ||
      (divisionField && !filters.division && !isDivisionAdmin);

    const getDistrictPlaceholder = () => {
      if (isDistrictDisabled) return "District";
      if (divisionField && !filters.division) return "Select Division first";

      if (filters.ulb_type && filters.ulb_class) {
        return `District (Type: ${filters.ulb_type}, Class: ${filters.ulb_class})`;
      }
      if (filters.ulb_type) {
        return `District (Type: ${filters.ulb_type})`;
      }
      if (filters.ulb_class) {
        return `District (Class: ${filters.ulb_class})`;
      }

      return "District";
    };

    const getUlbPlaceholder = () => {
      if (isUlbDisabled) return "ULB";
      if (districtField && !filters.district) return "Select District first";
      if (divisionField && !filters.division && !filters.district)
        return "Select Division first";

      if (filters.ulb_type && filters.ulb_class) {
        return `ULB (Type: ${filters.ulb_type}, Class: ${filters.ulb_class})`;
      }
      if (filters.ulb_type) {
        return `ULB (Type: ${filters.ulb_type})`;
      }
      if (filters.ulb_class) {
        return `ULB (Class: ${filters.ulb_class})`;
      }

      return "ULB";
    };

    const getAllDistrictsDisplayText = () => {
      let text = "All Districts";
      if (filters.division) {
        text += ` in ${filters.division}`;
      }
      if (filters.ulb_type || filters.ulb_class) {
        text += ` (${filteredDistricts.length} districts match filters)`;
      }
      return text;
    };

    const getDisplayDistricts = () => {
      let districts = [...filteredDistricts];

      if (filters.division && formData && formData.length > 0) {
        districts = districts.filter((district) => {
          return formData.some(
            (row) =>
              (row.division_name === filters.division ||
                row.division === filters.division) &&
              (row.district_name === district || row.district === district),
          );
        });
      }

      return districts;
    };

    // ========== GET ALL FILTER OPERATIONS FOR ADVANCED ANALYSIS ==========
    // FIX: Check for ALL operations that can be used for filtering
    const getFilterOperations = () => {
      // Get all LTE/GTE operations
      const lteGteOps = calculationResults.filter(result =>
        result.operation === "lte" || result.operation === "gte"
      );

      // Get percentage operations
      const percentageOps = calculationResults.filter(result =>
        result.operation === "percentage"
      );

      // Get min/max operations
      const minMaxOps = calculationResults.filter(result =>
        result.operation === "min" || result.operation === "max"
      );

      // Get ranking operations - treat as filter when rankingType is "top" or "bottom"
      const rankingOps = calculationResults.filter(result =>
        result.operation === "ranking" && (result.rankingType === "top" || result.rankingType === "bottom")
      );

      return [...lteGteOps, ...percentageOps, ...minMaxOps, ...rankingOps];
    };
    const filterOperations = getFilterOperations();
    const hasMultipleFilterOps = filterOperations.length >= 2;

    // Helper function to get condition text for an operation
    const getOperationConditionText = (op) => {
      switch (op.operation) {
        case 'lte':
          // Check if this threshold applies to a percentage operation
          const relatedPercentageOp = calculationResults.find(p =>
            p.operation === "percentage" && p.fieldName === op.fieldName
          );
          if (relatedPercentageOp) {
            return `% ≤ ${parseFloat(op.threshold).toLocaleString()}%`;
          }
          return `≤ ${parseFloat(op.threshold).toLocaleString()}`;
        case 'gte':
          const relatedPercentageOpGte = calculationResults.find(p =>
            p.operation === "percentage" && p.fieldName === op.fieldName
          );
          if (relatedPercentageOpGte) {
            return `% ≥ ${parseFloat(op.threshold).toLocaleString()}%`;
          }
          return `≥ ${parseFloat(op.threshold).toLocaleString()}`;
        case 'min': return `= Minimum`;
        case 'max': return `= Maximum`;
        case 'percentage': return op.percentageSecondFieldName ? `% of ${op.percentageSecondFieldName}` : '%';
        default: return '';
      }
    };

    // Calculate matching ULBs count for all operations
    // REPLACE the calculateMatchingUlbsCount function with this version that handles percentage + threshold
    const calculateMatchingUlbsCount = () => {
      if (!rawDataForAnalysis || rawDataForAnalysis.length === 0) return 0;

      // Get all operations
      const allOperations = [...calculationResults];
      if (allOperations.length === 0) return 0;

      // Group data by ULB and calculate aggregates
      const ulbAggregates = new Map();

      rawDataForAnalysis.forEach((row) => {
        const ulbName = row.ulb_name || row.ulb;
        if (!ulbName) return;

        if (!ulbAggregates.has(ulbName)) {
          ulbAggregates.set(ulbName, {
            ulb: ulbName,
            district: row.district_name || row.district,
            division: row.division_name || row.division,
            values: {},      // For regular numeric fields
            percentages: {}   // For percentage calculations
          });
        }

        const ulbData = ulbAggregates.get(ulbName);

        // Collect values for regular operations
        allOperations.forEach(op => {
          if (op.operation !== "percentage") {
            const value = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
            if (!isNaN(value) && isFinite(value)) {
              if (!ulbData.values[op.fieldId]) {
                ulbData.values[op.fieldId] = 0;
              }
              ulbData.values[op.fieldId] += value;
            }
          }
        });

        // Collect values for percentage operations
        allOperations.forEach(op => {
          if (op.operation === "percentage" && op.percentageSecondColumnName) {
            const numerator = parseFloat(String(row[op.columnName] || "").replace(/,/g, ""));
            const denominator = parseFloat(String(row[op.percentageSecondColumnName] || "").replace(/,/g, ""));

            if (!isNaN(numerator) && isFinite(numerator) && !isNaN(denominator) && isFinite(denominator) && denominator !== 0) {
              if (!ulbData.percentages[op.fieldId]) {
                ulbData.percentages[op.fieldId] = { numerator: 0, denominator: 0 };
              }
              ulbData.percentages[op.fieldId].numerator += numerator;
              ulbData.percentages[op.fieldId].denominator += denominator;
            }
          }
        });
      });

      // Calculate percentage values for each ULB
      const ulbPercentages = new Map();
      for (const [ulbName, ulbData] of ulbAggregates.entries()) {
        // Find percentage operation
        const percentageOp = allOperations.find(op => op.operation === "percentage");
        if (percentageOp && ulbData.percentages[percentageOp.fieldId]) {
          const percentData = ulbData.percentages[percentageOp.fieldId];
          if (percentData.denominator > 0) {
            const percentage = (percentData.numerator / percentData.denominator) * 100;
            ulbPercentages.set(ulbName, {
              ulb: ulbName,
              district: ulbData.district,
              division: ulbData.division,
              percentage: percentage,
              numerator: percentData.numerator,
              denominator: percentData.denominator
            });
          }
        }
      }

      // Find ranking operation and get top N ULBs by percentage
      const rankingOp = allOperations.find(op => op.operation === "ranking" && (op.rankingType === "top" || op.rankingType === "bottom"));
      let topUlbs = new Set();

      if (rankingOp && ulbPercentages.size > 0) {
        // Sort ULBs by percentage
        const sortedUlbs = Array.from(ulbPercentages.values()).sort((a, b) => {
          if (rankingOp.rankingType === "top") {
            return b.percentage - a.percentage;
          } else {
            return a.percentage - b.percentage;
          }
        });

        const rankingCount = rankingOp.rankingCount || 10;
        const topList = sortedUlbs.slice(0, rankingCount);
        topList.forEach(item => topUlbs.add(item.ulb));
      }

      // Check each ULB against ALL conditions
      let matchingCount = 0;

      for (const [ulbName, ulbData] of ulbAggregates.entries()) {
        let meetsAll = true;

        for (const op of allOperations) {
          if (op.operation === "ranking") {
            // Check if this ULB is in the top N
            if (!topUlbs.has(ulbName)) {
              meetsAll = false;
              break;
            }
            continue;
          }

          // Skip ranking operations already handled
          if (op.operation === "ranking") continue;

          let conditionValue = null;
          let shouldCheck = true;

          // For percentage operation
          if (op.operation === "percentage") {
            const percentData = ulbData.percentages[op.fieldId];
            if (percentData && percentData.denominator > 0) {
              conditionValue = (percentData.numerator / percentData.denominator) * 100;
            } else {
              shouldCheck = false;
            }
          }
          // For LTE/GTE operation
          else if (op.operation === "lte" || op.operation === "gte") {
            // Check if this threshold should apply to a percentage
            const relatedPercentageOp = allOperations.find(p =>
              p.operation === "percentage" && p.fieldName === op.fieldName
            );

            if (relatedPercentageOp) {
              // Apply threshold to percentage value
              const percentData = ulbData.percentages[relatedPercentageOp.fieldId];
              if (percentData && percentData.denominator > 0) {
                conditionValue = (percentData.numerator / percentData.denominator) * 100;
              } else {
                shouldCheck = false;
              }
            } else {
              // Apply threshold to regular numeric value
              conditionValue = ulbData.values[op.fieldId] || 0;
              if (conditionValue === 0) shouldCheck = false;
            }
          }
          // For min/max operations
          else if (op.operation === "min" || op.operation === "max") {
            conditionValue = ulbData.values[op.fieldId] || 0;
            if (conditionValue === 0) shouldCheck = false;
          }

          if (!shouldCheck) {
            meetsAll = false;
            break;
          }

          // Check the condition
          if (op.operation === "lte") {
            const threshold = parseFloat(op.threshold);
            if (!(conditionValue <= threshold)) meetsAll = false;
          } else if (op.operation === "gte") {
            const threshold = parseFloat(op.threshold);
            if (!(conditionValue >= threshold)) meetsAll = false;
          } else if (op.operation === "min") {
            // Find global minimum for this field
            let minValue = Infinity;
            for (const [otherUlb, otherData] of ulbAggregates.entries()) {
              const otherValue = otherData.values[op.fieldId] || 0;
              if (otherValue > 0 && otherValue < minValue) {
                minValue = otherValue;
              }
            }
            if (conditionValue !== minValue) meetsAll = false;
          } else if (op.operation === "max") {
            // Find global maximum for this field
            let maxValue = -Infinity;
            for (const [otherUlb, otherData] of ulbAggregates.entries()) {
              const otherValue = otherData.values[op.fieldId] || 0;
              if (otherValue > maxValue) {
                maxValue = otherValue;
              }
            }
            if (conditionValue !== maxValue) meetsAll = false;
          } else if (op.operation === "percentage") {
            // For percentage operation without a threshold, always true
          }

          if (!meetsAll) break;
        }

        if (meetsAll) {
          matchingCount++;
        }
      }

      return matchingCount;
    };
    //18425
    return (
      <Card
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #0F766E 0%, #0F172A 60%, #020617 100%)",
          border: "none",
          borderRadius: "8px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          marginTop: showAnalysisResults ? -90 : 0,
          marginBottom: 0,
        }}
        bodyStyle={{
          padding: "8px 9px",
          background: "transparent",
        }}
        size="small"
      >


        {/* ROW 1: Location Filters */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "flex-end",
            marginBottom: "20px",
          }}
        >
          {/* ULB Type Filter */}
          {ulbTypes && ulbTypes.length > 0 && (
            <div style={{ flex: "1 1 0", minWidth: "160px" }}>
              <Select
                placeholder="ULB Type"
                value={filters.ulb_type}
                onChange={handleUlbTypeChange}
                allowClear={!isUlbDisabled}
                style={{ width: "100%" }}
                loading={loading}
                showSearch
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff", width: "260px" }}
                popupMatchSelectWidth={false}
              >
                <Option value="">All Types</Option>
                {ulbTypes.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* ULB Class Filter */}
          {ulbClasses && ulbClasses.length > 0 && (
            <div style={{ flex: "1 1 0", minWidth: "160px" }}>
              <Select
                placeholder="ULB Class"
                value={filters.ulb_class}
                onChange={handleUlbClassChange}
                allowClear={true}
                style={{ width: "100%" }}
                loading={loading}
                showSearch
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff", width: "260px" }}
                popupMatchSelectWidth={false}
              >
                <Option value="">All Classes</Option>
                {ulbClasses.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Division Filter */}
          {divisionField && divisionValues.length > 0 && (
            <div style={{ flex: "1 1 0", minWidth: "200px" }}>
              <Select
                placeholder="Division"
                value={filters.division}
                onChange={handleDivisionChange}
                allowClear={!isDivisionDisabled}
                style={{ width: "100%" }}
                loading={loading}
                showSearch
                disabled={isDivisionDisabled}
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff", width: "320px" }}
                popupMatchSelectWidth={false}
              >
                <Option value="">All Divisions</Option>
                {divisionValues.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* District Filter */}
          {districtField &&
            (baseDistrictValues.length > 0 || filteredDistricts.length > 0) && (
              <div style={{ flex: "1 1 0", minWidth: "220px" }}>
                <Select
                  placeholder={getDistrictPlaceholder()}
                  value={filters.district}
                  onChange={handleDistrictChange}
                  allowClear={!isDistrictDisabled}
                  style={{
                    width: "100%",
                    backgroundColor: "#ffffff"
                  }}
                  loading={loading}
                  showSearch
                  disabled={
                    isDistrictDisabled ||
                    (divisionField && !filters.division && !isDivisionAdmin)
                  }
                  size="middle"
                  dropdownStyle={{ backgroundColor: "#ffffff", width: "340px" }}
                  popupMatchSelectWidth={false}
                >
                  <Option value="">
                    {getAllDistrictsDisplayText()}
                  </Option>
                  {getDisplayDistricts().map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

          {/* ULB Filter */}
          {ulbField && (baseUlbValues.length > 0 || filteredUlbs.length > 0) && (
            <div style={{ flex: "1.2 1 0", minWidth: "240px" }}>
              <Select
                placeholder={getUlbPlaceholder()}
                value={filters.ulb_name}
                onChange={handleUlbChange}
                allowClear={!isUlbDisabled}
                style={{
                  width: "100%",
                  backgroundColor: "#ffffff"
                }}
                loading={loading}
                showSearch
                disabled={
                  isUlbDisabled ||
                  (districtField && !filters.district && !isDistrictAdmin) ||
                  (divisionField && !filters.division && !isDivisionAdmin)
                }
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff", width: "380px" }}
                popupMatchSelectWidth={false}
              >
                <Option value="">
                  All ULBs
                  {filters.district
                    ? ` in ${filters.district}`
                    : filters.division
                      ? ` in ${filters.division}`
                      : filters.ulb_type || filters.ulb_class
                        ? " matching selected filters"
                        : ""}
                </Option>
                {filteredUlbs.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={applyFilters}
              loading={loadingFormData}
              size="middle"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#ffffff",
                color: gradientStart,
                borderRadius: "8px",
                fontWeight: 500,
              }}
            >
              Apply
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={resetFilters}
              size="middle"
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderColor: "rgba(255,255,255,0.3)",
                color: "#ffffff",
                borderRadius: "8px",
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* ROW 2: Data Analysis Inputs */}
        <div>
          {(selectedOperations.length > 0 || isVisitor()) && (
            <div
              style={{
                marginBottom: "4px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              {isVisitor() && (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "14px",
                    padding: "2px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <WarningOutlined style={{ color: "#fa8c16", fontSize: "12px" }} />
                  <span style={{ color: "#fa8c16", fontSize: "12px", fontWeight: 600 }}>
                    Read Only Mode
                  </span>
                </div>
              )}
            </div>
          )}

          <div
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              whiteSpace: "nowrap",
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch",
              paddingBottom: "4px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                gap: "12px",
                alignItems: "flex-end",
                flexWrap: "nowrap",
                minWidth: "100%",
              }}
            >
              {/* Data Points Dropdown */}
              <div style={{ flex: "0 0 auto", width: "200px" }}>
                <Select
                  placeholder="Choose data point to analyze"
                  value={selectedField?.field_id}
                  onChange={async (fieldId) => {
                    if (isVisitor()) {
                      notification.warning({
                        message: "Read Only Access",
                        description: "As a Visitor, you can view data but cannot perform analysis.",
                        duration: 3,
                      });
                      return;
                    }

                    if (!fieldId) {
                      setSelectedField(null);
                      setSelectedOperations([]);
                      setCalculationResults([]);
                      setShowAnalysisResults(false);
                      setPercentageSecondField(null);
                      setShowPercentageSecondField(false);
                      return;
                    }

                    const field = reportEnabledFields.find(f => f.field_id === fieldId);
                    if (field) {
                      setSelectedField(field);
                      setSelectedOperation("sum");
                      setShowThresholdInput(false);
                      setShowPercentageSecondField(false);
                      setPercentageSecondField(null);
                    }
                  }}
                  style={{ width: "100%" }}
                  loading={loadingReportFields}
                  showSearch
                  disabled={isVisitor()}
                  size="middle"
                  dropdownStyle={{ backgroundColor: "#ffffff", width: "340px" }}
                  popupMatchSelectWidth={false}
                  optionLabelProp="label"
                  notFoundContent={loadingReportFields ? <Spin size="small" /> : "No data points available"}
                >
                  {reportEnabledFields.map((field) => (
                    <Option
                      key={field.field_id}
                      value={field.field_id}
                      label={field.display_name || field.field_id}
                    >
                      <div style={{ padding: "8px 4px" }}>
                        <div style={{ fontWeight: 500, fontSize: "13px", color: "#333" }}>
                          {field.display_name || field.field_id}
                          {field.report_important && (
                            <StarOutlined style={{ color: "#faad14", marginLeft: "8px" }} />
                          )}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Operation Selection */}
              <Select
                placeholder="Select Operation"
                value={selectedOperation}
                onChange={(value) => {
                  setSelectedOperation(value);
                  setShowThresholdInput(value === "lte" || value === "gte");
                  setShowPercentageSecondField(value === "percentage");
                  setShowRankingOptions(value === "ranking");
                  if (value !== "percentage") {
                    setPercentageSecondField(null);
                  }
                  if (value !== "ranking") {
                    setRankingType("top");
                    setRankingOrder("desc");
                    setRankingCount(10);
                    setShowRankingCountInput(false);
                  } else {
                    setShowRankingCountInput(rankingType === "top" || rankingType === "bottom");
                  }
                }}
                style={{ width: "200px" }}
                size="middle"
                dropdownStyle={{ backgroundColor: "#ffffff" }}
              >
                {operations.map((op) => (
                  <Option key={op.value} value={op.value}>
                    <Space>
                      <span>{op.value === "lte" ? "less than equal to" : op.value === "gte" ? "greater than equal to" : op.label}</span>
                    </Space>
                  </Option>
                ))}
              </Select>

              {/* Ranking Options with Number Input */}
              {showRankingOptions && selectedField && (
                <>
                  {/* Ranking Type Select */}
                  <div style={{ flex: "0 0 auto", minWidth: "150px", maxWidth: "180px" }}>
                    <Select
                      placeholder="Ranking Type"
                      value={rankingType}
                      onChange={(value) => {
                        setRankingType(value);
                        setShowRankingCountInput(value === "top" || value === "bottom");
                      }}
                      style={{ width: "100%" }}
                      size="middle"
                      dropdownStyle={{ backgroundColor: "#ffffff" }}
                    >
                      <Option value="top">Top</Option>
                      <Option value="bottom">Bottom</Option>
                      <Option value="all">All ULBs</Option>
                    </Select>
                  </div>

                  {/* Ranking Count Input - Shows for Top/Bottom */}
                  {showRankingCountInput && rankingType !== "all" && (
                    <div style={{ flex: "0 0 auto", minWidth: "120px", maxWidth: "150px" }}>
                      <Input
                        type="number"
                        min={1}
                        max={500}
                        placeholder="Number"
                        value={rankingCount}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setRankingCount(isNaN(value) ? 10 : Math.max(1, Math.min(500, value)));
                        }}
                        size="middle"
                        style={{ width: "100%" }}
                        addonAfter="ULBs"
                      />
                    </div>
                  )}

                  {/* Sort Order Dropdown - Shows for ALL ranking types */}
                  <div style={{ flex: "0 0 auto", minWidth: "150px", maxWidth: "180px" }}>
                    <Select
                      placeholder="Sort Order"
                      value={rankingOrder}
                      onChange={setRankingOrder}
                      style={{ width: "100%" }}
                      size="middle"
                      dropdownStyle={{ backgroundColor: "#ffffff" }}
                    >
                      <Option value="desc">
                        {rankingType === "top" ? "Highest to Lowest ⬇️" :
                          rankingType === "bottom" ? "Lowest to Highest ⬆️" :
                            "Highest to Lowest (Descending)"}
                      </Option>
                      <Option value="asc">
                        {rankingType === "top" ? "Lowest to Highest ⬆️" :
                          rankingType === "bottom" ? "Highest to Lowest ⬇️" :
                            "Lowest to Highest (Ascending)"}
                      </Option>
                    </Select>
                  </div>
                </>
              )}

              {/* Second Field Selection for Percentage */}
              {showPercentageSecondField && selectedField && (
                <div style={{ flex: "0 0 auto", width: "200px" }}>
                  <Select
                    placeholder="Select second data point (denominator)"
                    value={percentageSecondField?.field_id}
                    onChange={(fieldId) => {
                      const field = reportEnabledFields.find(f => f.field_id === fieldId);
                      if (field) {
                        setPercentageSecondField(field);
                      }
                    }}
                    style={{ width: "100%" }}
                    size="middle"
                    dropdownStyle={{ backgroundColor: "#ffffff", width: "340px" }}
                    popupMatchSelectWidth={false}
                    showSearch
                  >
                    {reportEnabledFields
                      .filter(f => f.field_id !== selectedField?.field_id)
                      .map((field) => (
                        <Option key={field.field_id} value={field.field_id}>
                          <div style={{ padding: "4px 0" }}>
                            <div style={{ fontWeight: 500, fontSize: "13px" }}>
                              {field.display_name || field.field_id}
                            </div>
                          </div>
                        </Option>
                      ))}
                  </Select>
                </div>
              )}

              {/* Threshold Input */}
              {selectedField && (selectedOperation === "lte" || selectedOperation === "gte") && (
                <div style={{ flex: "0 0 auto", width: "160px" }}>
                  <Input
                    type="number"
                    placeholder={`Threshold ${selectedOperation === "lte" ? "≤" : "≥"}`}
                    value={operationThresholds[selectedField.field_id] || ""}
                    onChange={(e) => {
                      setOperationThresholds(prev => ({
                        ...prev,
                        [selectedField.field_id]: e.target.value
                      }));
                    }}
                    size="middle"
                    style={{ width: "100%" }}
                  />
                </div>
              )}

              {/* Add Button */}
              {selectedField && (
                <div style={{ flex: "0 0 auto" }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addOperation}
                    size="middle"
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                    disabled={isVisitor()}
                  >
                    Add
                  </Button>
                </div>
              )}

              {/* Clear All Button */}
              {selectedOperations.length > 0 && (
                <div style={{ flex: "0 0 auto" }}>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={clearAllOperations}
                    size="middle"
                    style={{ borderRadius: "8px" }}
                    disabled={isVisitor()}
                  >
                    Clear All ({selectedOperations.length})
                  </Button>
                </div>
              )}

              {/* Apply Analysis Button */}
              {selectedOperations.length > 0 && (
                <div style={{ flex: "0 0 auto" }}>
                  <Button
                    type="primary"
                    icon={<CalculatorOutlined />}
                    onClick={() => {
                      const hasInvalidPercentage = selectedOperations.some(op => {
                        if (op.operation === "percentage" && !op.percentageSecondFieldId) {
                          notification.warning({
                            message: "Missing Second Data Point",
                            description: `Please select a second data point for ${op.fieldName} percentage calculation`,
                            duration: 4,
                          });
                          return true;
                        }
                        return false;
                      });

                      if (hasInvalidPercentage) return;

                      setSelectedFilterForm(selectedForm);
                      performCalculations();
                    }}
                    loading={loadingCalculation}
                    size="middle"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: "#ffffff",
                      color: gradientStart,
                      borderRadius: "8px",
                      fontWeight: 600,
                    }}
                    disabled={isVisitor()}
                  >
                    Apply
                  </Button>
                </div>
              )}

              {/* BACK TO FORMS BUTTON - Next to Apply Button */}
              {showAnalysisResults && (
                <div style={{ flex: "0 0 auto" }}>
                  <Button
                    size="middle"
                    icon={<LeftOutlined />}
                    onClick={() => {
                      setShowAnalysisResults(false);
                      setSelectedOperations([]);
                      setCalculationResults([]);
                      setRawDataForAnalysis([]);
                      setAnalysisDataColumns([]);
                      setHierarchicalStatistics({});
                      setSelectedStatsField(null);
                      setSelectedFormColorIndex(null);
                      setFrontPageFilters({
                        division: null,
                        district: null,
                        ulb_type: null,
                        ulb_class: null,
                        ulb_name: null,
                      });
                      setIsFilterApplied(false);
                      loadForms({});
                      setXAxisSelectedFields(['auto']);
                      setYAxisSelectedFields([]);
                    }}
                    style={{
                      backgroundColor: "rgba(0,0,0,0.06)",
                      borderColor: "#d9d9d9",
                      color: "#ffffff",
                      borderRadius: "8px",
                    }}
                  >
                    Back to Form
                  </Button>
                </div>
              )}

              {/* Advanced Analysis Toggle */}
              {hasMultipleFilterOps && (
                <div style={{
                  marginTop: "0px",
                  marginBottom: "0px",
                  background: "#08519C",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "6px",
                }}>
                  <Space>
                    <FilterOutlined style={{ color: showOnlyMatchingUlbs ? "#52c41a" : "#1890ff" }} />
                    <span style={{ fontWeight: 500, color: "white" }}>Advanced Analysis</span>
                  </Space>
                  <Space>
                    <Switch
                      checked={showOnlyMatchingUlbs}
                      onChange={setShowOnlyMatchingUlbs}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                  </Space>
                </div>
              )}
            </div>
          </div>

          {/* Selected Operations Tags */}
          {selectedOperations.length > 0 && (
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                padding: "4px 8px",
                background: "rgba(255,255,255,0.15)",
                borderRadius: "6px",
              }}
            >
              {selectedOperations.map((op) => (
                <Tag
                  key={op.id}
                  closable
                  onClose={() => removeOperation(op.id)}
                  color={getOperationColor(op.operation)}
                  style={{
                    padding: "2px 6px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                >
                  {getOperationIcon(op.operation)} {op.fieldName}
                  {(op.operation === "lte" || op.operation === "gte") && (
                    <span style={{ marginLeft: "4px" }}>
                      {op.operation === "lte" ? "≤" : "≥"} {op.threshold}
                    </span>
                  )}
                  {op.operation === "percentage" && op.percentageSecondFieldName && (
                    <span style={{ marginLeft: "4px" }}>
                      / {op.percentageSecondFieldName}
                    </span>
                  )}
                  {op.operation === "ranking" && (
                    <span style={{ marginLeft: "4px" }}>
                      {op.rankingType === "top" ? `Top ${op.rankingCount}` :
                        op.rankingType === "bottom" ? `Bottom ${op.rankingCount}` :
                          `All (${op.rankingOrder === "desc" ? "↓" : "↑"})`}
                    </span>
                  )}
                </Tag>
              ))}
            </div>
          )}
        </div>

      </Card>
    );
  };

  // ==================== RENDER COLUMN GRAPH ====================
  const renderColumnGraph = () => {
    if (!columnGraphData) return null;

    const { column, displayName, data, stats, aggregationLevel, groupLabel } =
      columnGraphData;

    return (
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>
              {aggregationLevel === "record"
                ? `Distribution of ${displayName}`
                : `${displayName} by ${groupLabel} (${showAggregated ? "Aggregated" : "Individual"})`}
            </span>
            <Tag color="blue">
              {data.length}{" "}
              {aggregationLevel === "record" ? "values" : groupLabel + "s"}
            </Tag>
            {aggregationLevel !== "record" && (
              <Tag color="purple">
                <ApartmentOutlined /> Aggregated from {stats.recordCount}{" "}
                records
              </Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <Button
              size="small"
              onClick={toggleAggregated}
              type={showAggregated ? "primary" : "default"}
            >
              {showAggregated ? "Aggregated" : "Individual"}
            </Button>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => generateColumnGraph(column)}
              loading={loadingColumnGraph}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            {aggregationLevel !== "record" && (
              <Alert
                message={`Viewing ${showAggregated ? "aggregated" : "individual"} data at ${groupLabel} level`}
                description={
                  <Space direction="vertical" size={2}>
                    {showAggregated ? (
                      <div>
                        Each bar represents the sum of values for all records in
                        that {groupLabel.toLowerCase()}.
                      </div>
                    ) : (
                      <div>
                        Each point represents an individual record with location
                        hierarchy.
                      </div>
                    )}
                    <div>
                      Total records: {stats.recordCount} •{" "}
                      {showAggregated
                        ? `Aggregated into: ${data.length} ${groupLabel}s`
                        : `Showing: ${data.length} records`}
                    </div>
                  </Space>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 60, bottom: 150 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={150}
                  interval={0}
                  tick={{ fontSize: 11 }}
                  label={{
                    value:
                      aggregationLevel === "record" ? "Records" : groupLabel,
                    position: "insideBottom",
                    offset: -120,
                    fontSize: 12,
                  }}
                />
                <YAxis
                  label={{
                    value: displayName,
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 12,
                  }}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            backgroundColor: "#fff",
                            padding: "12px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            maxWidth: "350px",
                          }}
                        >
                          <p>
                            <strong>{data.originalName || data.name}</strong>
                          </p>
                          {aggregationLevel !== "record" && showAggregated ? (
                            <>
                              <p>
                                <strong>Total {displayName}:</strong>{" "}
                                {data.value.toLocaleString()}
                              </p>
                              <p>
                                <strong>Average:</strong>{" "}
                                {data.avgValue.toLocaleString()}
                              </p>
                              <p>
                                <strong>Records in group:</strong> {data.count}
                              </p>
                            </>
                          ) : (
                            <>
                              <p>
                                <strong>Value:</strong> {data.raw}
                              </p>
                              <p>
                                <strong>Record ID:</strong> {data.recordId}
                              </p>
                              {data.division && (
                                <p>
                                  <strong>Division:</strong> {data.division}
                                </p>
                              )}
                              {data.district && (
                                <p>
                                  <strong>District:</strong> {data.district}
                                </p>
                              )}
                              {data.ulb && (
                                <p>
                                  <strong>ULB:</strong> {data.ulb}
                                </p>
                              )}
                              {data.ulb_type && (
                                <p>
                                  <strong>state:</strong> {data.ulb_type}
                                </p>
                              )}
                              {data.class && (
                                <p>
                                  <strong>Class:</strong> {data.class}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" fill="#1890ff" name={displayName} />
              </BarChart>
            </ResponsiveContainer>
          </Col>
          <Col span={24}>
            <Alert
              message="Statistics"
              description={
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Minimum"
                      value={stats.min}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Maximum"
                      value={stats.max}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Average"
                      value={stats.avg.toFixed(2)}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Total Sum"
                      value={stats.sum.toLocaleString()}
                    />
                  </Col>
                </Row>
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>
      </Card>
    );
  };

  // ==================== RENDER CHART ====================
  const renderChart = (type, data, config, onClick) => {
    if (!data || data.length === 0)
      return <Empty description="No data for chart" />;
    return (
      <div onClick={onClick} style={{ cursor: "pointer" }}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              tick={{ fontSize: 8 }}
            />
            <YAxis tick={{ fontSize: 8 }} width={30} />
            <RechartsTooltip />
            <Bar
              dataKey="value"
              fill="#1890ff"
              name={config?.yAxisLabel || "Value"}
            />
          </BarChart>
        </ResponsiveContainer>
        <div
          style={{
            textAlign: "center",
            marginTop: 4,
            fontSize: 10,
            color: "#1890ff",
          }}
        >
          Click to expand
        </div>
      </div>
    );
  };

  // ==================== RENDER DATA MODAL ====================
  const renderDataModal = () => {
    if (!selectedForm || !formData.length) return null;

    const displayData = formData;
    const getFilteredData = () => {
      let filtered = [...displayData];
      const divisionField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("division") ||
            f.toLowerCase().includes("विभाग")),
      );
      const districtField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("district") ||
            f.toLowerCase().includes("जिल्हा")),
      );
      const ulbField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb") ||
            f.toLowerCase().includes("मनपा") ||
            f.toLowerCase().includes("municipality")),
      );
      const ulbTypeField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("ulb_type") ||
            f.toLowerCase().includes("type")),
      );
      const classField = formFields.find(
        (f) =>
          f &&
          (f.toLowerCase().includes("class") ||
            f.toLowerCase().includes("वर्ग")),
      );

      if (modalFilters.division && divisionField)
        filtered = filtered.filter(
          (row) =>
            row[divisionField] === modalFilters.division ||
            row[`${divisionField}_id`] === modalFilters.division,
        );
      if (modalFilters.district && districtField)
        filtered = filtered.filter(
          (row) =>
            row[districtField] === modalFilters.district ||
            row[`${districtField}_id`] === modalFilters.district,
        );
      if (modalFilters.ulb && ulbField)
        filtered = filtered.filter(
          (row) =>
            row[ulbField] === modalFilters.ulb ||
            row[`${ulbField}_id`] === modalFilters.ulb,
        );
      if (modalFilters.ulb_type && ulbTypeField)
        filtered = filtered.filter(
          (row) => row[ulbTypeField] === modalFilters.ulb_type,
        );
      if (modalFilters.ulb_class && classField)
        filtered = filtered.filter(
          (row) => row[classField] === modalFilters.ulb_class,
        );
      if (modalFilters.searchText) {
        const searchLower = modalFilters.searchText.toLowerCase();
        filtered = filtered.filter((row) =>
          Object.values(row).some(
            (val) => val && String(val).toLowerCase().includes(searchLower),
          ),
        );
      }
      return filtered;
    };

    const filteredData = getFilteredData();
    const paginatedData = filteredData.slice(
      (dataModalPagination.current - 1) * dataModalPagination.pageSize,
      dataModalPagination.current * dataModalPagination.pageSize,
    );

    const divisionField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("division") ||
          f.toLowerCase().includes("विभाग")),
    );
    const districtField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("district") ||
          f.toLowerCase().includes("जिल्हा")),
    );
    const ulbField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("ulb") ||
          f.toLowerCase().includes("मनपा") ||
          f.toLowerCase().includes("municipality")),
    );
    const ulbTypeField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("ulb_type") ||
          f.toLowerCase().includes("type")),
    );
    const classField = formFields.find(
      (f) =>
        f &&
        (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग")),
    );

    const divisionValues = divisionField
      ? [
        ...new Set(
          displayData.map((row) => row[divisionField]).filter(Boolean),
        ),
      ].sort()
      : [];
    const districtValues = districtField
      ? [
        ...new Set(
          displayData.map((row) => row[districtField]).filter(Boolean),
        ),
      ].sort()
      : [];
    const ulbValues = ulbField
      ? [
        ...new Set(displayData.map((row) => row[ulbField]).filter(Boolean)),
      ].sort()
      : [];
    const ulbTypeValues = ulbTypeField
      ? [
        ...new Set(
          displayData.map((row) => row[ulbTypeField]).filter(Boolean),
        ),
      ].sort()
      : [];
    const classValues = classField
      ? [
        ...new Set(displayData.map((row) => row[classField]).filter(Boolean)),
      ].sort()
      : [];

    return (
      <Modal
        title={
          <Space>
            <DatabaseOutlined />
            <Text strong>
              {selectedForm.displayName || `Form ${selectedForm.id}`}
            </Text>
          </Space>
        }
        open={dataModalVisible}
        onCancel={() => {
          setDataModalVisible(false);
          resetModalFilters();

          // ========== UPDATED: For ULB Admin, District Admin, and Division Admin, go back to forms list ==========
          const isUlbAdmin = !hasFullAccess() && (
            userRole === "ULB_ADMIN" ||
            userRole === "ulb_admin" ||
            userRole === "ULB_OFFICER" ||
            userRole === "ULB_CHIEF_OFFICIER"
          );

          const isDistrictAdmin = !hasFullAccess() && (
            userRole === "DISTRICT_ADMIN" ||
            userRole === "district_admin"
          );

          const isDivisionAdmin = !hasFullAccess() && (
            userRole === "DIVISION_ADMIN" ||
            userRole === "division_admin" ||
            userRole === "DIVISIONAL_OFFICER" ||
            userRole === "divisional_officer" ||
            userRole === "Divisionalcommissioner"
          );

          // If ULB admin, District Admin, or Division Admin, go back to forms list when closing modal
          if (isUlbAdmin || isDistrictAdmin || isDivisionAdmin) {
            // Clear all form-related state
            setSelectedForm(null);
            setSelectedFormColorIndex(null);
            setColumnGraphData(null);
            setHierarchicalGraphs([]);
            setReportCalculations({});
            setFieldGraphsData({});
            setFormGraphData({});
            setLoadedGraphs({});
            setStableGraphData({});
            setLoadingFormGraphs({});

            setShowAnalysisResults(false);
            setSelectedOperations([]);
            setCalculationResults([]);
            setRawDataForAnalysis([]);
            setAnalysisDataColumns([]);
            setHierarchicalStatistics({});
            setSelectedStatsField(null);
            setBreakdownData(null);
            setSelectedBreakdownField(null);
            setSelectedDataPointId(null);

            setXAxisSelectedFields(['auto']);
            setYAxisSelectedFields([]);

            // Reset graph loading flags
            graphLoadingStarted.current = false;
            graphLoadQueueRef.current = [];
            isProcessingQueue.current = false;
            initialGraphsLoadedRef.current = false;

            // Clear any pending timeouts
            Object.values(graphLoadTimeouts).forEach(timeoutId => {
              if (timeoutId) clearTimeout(timeoutId);
            });
            setGraphLoadTimeouts({});

            setFrontPageFilters({
              division: null,
              district: null,
              ulb_type: null,
              ulb_class: null,
              ulb_name: null,
            });

            setIsFilterApplied(false);

            // Clear graph modal
            setGraphModalData(null);
            setGraphModalVisible(false);

            // Load forms with no filters to show the front page
            loadForms({});
          }
        }}
        width="98%"
        style={{
          top: 10,
          maxWidth: "100vw",
          padding: 0,
        }}
        bodyStyle={{
          padding: "16px",
          height: "calc(100vh - 120px)",
          overflow: "auto"
        }}
        maskClosable={false}
        keyboard={true}
        destroyOnClose
      >
        <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={6}>
              <Space>
                <FilterOutlined />
                <Text strong>Filter Data</Text>
                {Object.values(modalFilters).some((f) => f) && (
                  <Badge
                    count={Object.values(modalFilters).filter((f) => f).length}
                  />
                )}
              </Space>
            </Col>
            {divisionField && divisionValues.length > 0 && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by Division"
                  value={modalFilters.division}
                  onChange={(value) => {
                    setModalFilters({
                      ...modalFilters,
                      division: value,
                      district: null,
                      ulb: null,
                    });
                    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  size="small"
                >
                  {divisionValues.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {districtField && districtValues.length > 0 && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by District"
                  value={modalFilters.district}
                  onChange={(value) => {
                    setModalFilters({
                      ...modalFilters,
                      district: value,
                      ulb: null,
                    });
                    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  size="small"
                  disabled={divisionField && !modalFilters.division}
                >
                  {districtValues
                    .filter(
                      (value) =>
                        !modalFilters.division ||
                        displayData.some(
                          (row) =>
                            row[divisionField] === modalFilters.division &&
                            row[districtField] === value,
                        ),
                    )
                    .map((value) => (
                      <Option key={value} value={value}>
                        {value}
                      </Option>
                    ))}
                </Select>
              </Col>
            )}
            {ulbTypeValues.length > 0 && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by ULB Type"
                  value={modalFilters.ulb_type}
                  onChange={(value) => {
                    setModalFilters({ ...modalFilters, ulb_type: value });
                    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  size="small"
                >
                  {ulbTypeValues.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {classValues.length > 0 && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by ULB Class"
                  value={modalFilters.ulb_class}
                  onChange={(value) => {
                    setModalFilters({ ...modalFilters, ulb_class: value });
                    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  size="small"
                >
                  {classValues.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {ulbField && ulbValues.length > 0 && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by ULB"
                  value={modalFilters.ulb}
                  onChange={(value) => {
                    setModalFilters({ ...modalFilters, ulb: value });
                    setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  size="small"
                  disabled={
                    (districtField && !modalFilters.district) ||
                    (divisionField && !modalFilters.division)
                  }
                >
                  {ulbValues
                    .filter((value) => {
                      if (modalFilters.district && districtField)
                        return displayData.some(
                          (row) =>
                            row[districtField] === modalFilters.district &&
                            row[ulbField] === value,
                        );
                      if (
                        modalFilters.division &&
                        divisionField &&
                        !modalFilters.district
                      )
                        return displayData.some(
                          (row) =>
                            row[divisionField] === modalFilters.division &&
                            row[ulbField] === value,
                        );
                      if (modalFilters.ulb_type && ulbTypeField)
                        return displayData.some(
                          (row) =>
                            row[ulbTypeField] === modalFilters.ulb_type &&
                            row[ulbField] === value,
                        );
                      if (modalFilters.ulb_class && classField)
                        return displayData.some(
                          (row) =>
                            row[classField] === modalFilters.ulb_class &&
                            row[ulbField] === value,
                        );
                      return true;
                    })
                    .map((value) => (
                      <Option key={value} value={value}>
                        {value}
                      </Option>
                    ))}
                </Select>
              </Col>
            )}
            <Col xs={24} md={6}>
              <Input
                placeholder="Search in all fields..."
                prefix={<SearchOutlined />}
                value={modalFilters.searchText}
                onChange={(e) => {
                  setModalFilters({
                    ...modalFilters,
                    searchText: e.target.value,
                  });
                  setDataModalPagination((prev) => ({ ...prev, current: 1 }));
                }}
                allowClear
                size="small"
              />
            </Col>
            <Col xs={24} md={2}>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={resetModalFilters}
                block
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card>

        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Text type="secondary">Rows per page:</Text>
            <Select
              value={dataModalPagination.pageSize}
              onChange={(value) =>
                setDataModalPagination((prev) => ({
                  ...prev,
                  pageSize: value,
                  current: 1,
                }))
              }
              style={{ width: 80 }}
              size="small"
              options={[
                { value: 10, label: "10" },
                { value: 500, label: "500" },
                { value: 20, label: "20" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
              ]}
            />
          </Space>
          <Text type="secondary">
            Showing{" "}
            {Math.min(filteredData.length, dataModalPagination.pageSize)} of{" "}
            {filteredData.length} records
            {filteredData.length !== displayData.length &&
              ` (filtered from ${displayData.length} total)`}
          </Text>
        </div>

        <Table
          dataSource={paginatedData}
          columns={renderHierarchicalColumns()}
          pagination={false}
          rowKey={(record, index) =>
            `row_${(dataModalPagination.current - 1) * dataModalPagination.pageSize + index}`
          }
          scroll={{ x: "max-content", y: 500 }}
          size="small"
          bordered
        />

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Pagination
            current={dataModalPagination.current}
            pageSize={dataModalPagination.pageSize}
            total={filteredData.length}
            showSizeChanger
            pageSizeOptions={["10", "500", "20", "50", "100"]}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} records`
            }
            onChange={(page, pageSize) =>
              setDataModalPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
              }))
            }
            onShowSizeChange={(current, size) =>
              setDataModalPagination((prev) => ({
                ...prev,
                pageSize: size,
                current: 1,
              }))
            }
          />
        </div>
      </Modal>
    );
  };



  const renderKPICards = () => {
  // ========== IMPORTANT: IF ANALYSIS RESULTS ARE SHOWING, DON'T RENDER ANYTHING ==========
  if (showAnalysisResults) {
    return null;
  }

  if (!dashboardConfig.showNormalCardView) return null;

  // ========== CHECK USER ROLES ==========
  const isUlbAdmin = !hasFullAccess() && (
    userRole === "ULB_ADMIN" ||
    userRole === "ulb_admin" ||
    userRole === "ULB_OFFICER" ||
    userRole === "ULB_CHIEF_OFFICIER"
  );

  const isDivisionAdmin = !hasFullAccess() && (
    userRole === "DIVISION_ADMIN" ||
    userRole === "division_admin" ||
    userRole === "DIVISIONAL_OFFICER" ||
    userRole === "divisional_officer" ||
    userRole === "Divisionalcommissioner"
  );

  const isDistrictAdmin = !hasFullAccess() && (
    userRole === "DISTRICT_ADMIN" ||
    userRole === "district_admin"
  );

  // Hide normal cards for ULB Admin, Division Admin, and District Admin
  // Only show compact cards with images
  const shouldHideNormalCards = isUlbAdmin || isDivisionAdmin || isDistrictAdmin;

  // ========== BACKGROUND IMAGES FUNCTION ==========
  const renderBackground = () => {
    if (dashboardBgImages.length === 0) return null;

    const currentImage = dashboardBgImages[currentBgImageIndex];

    if (!currentImage) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'opacity 1s ease-in-out',
        }} />
        {/* Light overlay for text readability in form view */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          zIndex: 1
        }} />
      </div>
    );
  };

  // DYNAMIC ICON MAPPING
  const displayedForms = filteredForms.map(form => {
    if (form.form_icon && form.form_icon.trim() !== '') {
      let iconUrl = form.form_icon;
      if (iconUrl.startsWith('/') && !iconUrl.startsWith('//')) {
        iconUrl = `${API_BASE_URL}${iconUrl}`;
      }
      return {
        ...form,
        form_icon: iconUrl,
        form_icon_type: 'image'
      };
    }
    if (form.form_icon_emoji && form.form_icon_emoji.trim() !== '') {
      return {
        ...form,
        form_icon_type: 'emoji',
        form_icon_emoji: form.form_icon_emoji
      };
    }
    return {
      ...form,
      form_icon_type: 'none'
    };
  });

  const formatValueLocal = (value, calculation) => {
    if (value === undefined || value === null) return "";
    if (calculation.operator?.toLowerCase() === "percentage")
      return `${value.toFixed(2)}%`;
    return value.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      useGrouping: false
    });
  };

  const getOperatorIcon = (op) => {
    return null;
  };

  const activeFilters = [];
  if (frontPageFilters.division)
    activeFilters.push(`Division: ${frontPageFilters.division}`);
  if (frontPageFilters.district)
    activeFilters.push(`District: ${frontPageFilters.district}`);
  if (frontPageFilters.ulb_type)
    activeFilters.push(`ULB Type: ${frontPageFilters.ulb_type}`);
  if (frontPageFilters.ulb_class)
    activeFilters.push(`ULB Class: ${frontPageFilters.ulb_class}`);
  if (frontPageFilters.ulb_name)
    activeFilters.push(`ULB: ${frontPageFilters.ulb_name}`);

  const shouldShowFilterInfo = isFilterApplied && activeFilters.length > 0;

  // ==================== COMPACT CARD RENDERER ====================
  const renderCompactCard = (form, index) => {
    const hasImageIcon = form.form_icon_type === 'image' && form.form_icon;
    const hasEmojiIcon = form.form_icon_type === 'emoji' && form.form_icon_emoji;
    const hasNoIcon = form.form_icon_type === 'none';

    const startColor = startColors[index % startColors.length];
    const endColor = endColors[index % endColors.length];
    const hoverColor = hoverColors[index % hoverColors.length];

    const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
    const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

    return (
      <div
        onClick={() => handleFormSelect(form)}
        className={`radiant-card card-cross-fade`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 12px",
          marginBottom: "10px",
          borderRadius: "12px",
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: gradient,
          border: "1px solid rgba(255,255,255,0.35)",
          boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = hoverGradient;
          e.currentTarget.style.transform = "translateX(8px) scale(1.02)";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = gradient;
          e.currentTarget.style.transform = "translateX(0) scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.15)";
        }}
      >
        <div style={{ flexShrink: 0, width: "36px", textAlign: "center" }}>
          {hasImageIcon && (
            <img
              src={form.form_icon}
              alt=""
              style={{
                width: "28px",
                height: "28px",
                objectFit: "cover",
                borderRadius: "8px",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                background: "rgba(255,255,255,0.2)",
                padding: "2px",
              }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          {hasEmojiIcon && (
            <span style={{ fontSize: "24px", filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.15))" }}>
              {form.form_icon_emoji}
            </span>
          )}
          {hasNoIcon && (
            <DatabaseOutlined style={{ fontSize: "20px", color: "#ffffff", filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.15))" }} />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
              lineHeight: 1.3,
              letterSpacing: "0.3px",
            }}
          >
            {form.displayName || form.tableName}
          </div>
        </div>
      </div>
    );
  };

  // ==================== NORMAL CARD RENDERER ====================
  const renderNormalCard = (form, index) => {
    const reportFields = form.reportFields || [];

    const hasImageIcon = form.form_icon_type === 'image' && form.form_icon;
    const hasEmojiIcon = form.form_icon_type === 'emoji' && form.form_icon_emoji;
    const hasNoIcon = form.form_icon_type === 'none';

    const startColor = startColors[index % startColors.length];
    const endColor = endColors[index % endColors.length];
    const hoverColor = hoverColors[index % hoverColors.length];

    const gradient = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
    const hoverGradient = `linear-gradient(135deg, ${hoverColor} 0%, ${endColor} 100%)`;

    return (
      <Card
        hoverable
        className={`radiant-card card-cross-fade`}
        bodyStyle={{
          padding: "0 !important",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        style={{
          borderRadius: "14px",
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          background: gradient,
          border: "1px solid rgba(255,255,255,0.4)",
          overflow: "hidden",
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          height: "300px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleFormSelect(form, index)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = hoverGradient;
          e.currentTarget.style.transform = "translateY(-5px)";
          e.currentTarget.style.boxShadow = "0 15px 35px rgba(0,0,0,0.25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = gradient;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
        }}
      >
        <div style={{ padding: "12px 12px 0 12px", flexShrink: 0 }}>
          <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: hasNoIcon ? "0px" : "12px" }}>
            {hasImageIcon && (
              <img
                src={form.form_icon}
                alt={form.displayName || form.tableName}
                style={{
                  width: "38px",
                  height: "38px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.25)",
                  padding: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            {hasEmojiIcon && (
              <span style={{ fontSize: "36px", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }}>
                {form.form_icon_emoji}
              </span>
            )}
            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#ffffff",
                textShadow: "0 2px 4px rgba(0,0,0,0.25)",
                lineHeight: 1.2,
                transition: "all 0.3s ease",
                wordBreak: "break-word",
                flex: 1,
              }}
              onMouseEnter={(e) => (e.target.style.transform = "translateX(3px)")}
              onMouseLeave={(e) => (e.target.style.transform = "translateX(0)")}
            >
              {form.displayName || form.tableName}
            </div>
          </div>
        </div>

        <div
          style={{
            flex: "1 1 auto",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 12px",
            scrollbarWidth: "thin",
          }}
        >
          {reportFields.length > 0 && (
            <div style={{ marginBottom: "0", paddingBottom: "0" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {reportFields.slice(0, 6).map((field, idx) => {
                  const isLastItem = idx === reportFields.slice(0, 6).length - 1;
                  const totalItems = reportFields.slice(0, 6).length;
                  const isOddTotal = totalItems % 2 !== 0;
                  const shouldTakeFullWidth = isLastItem && isOddTotal;

                  return (
                    <div
                      key={idx}
                      style={{
                        flex: shouldTakeFullWidth ? "1 1 100%" : "1 1 calc(50% - 4px)",
                        padding: "10px 8px",
                        background: "rgba(255,255,255,0.95)",
                        borderRadius: "10px",
                        borderLeft: `3px solid rgba(255,255,255,0.8)`,
                        transition: "all 0.3s ease",
                        minHeight: "auto",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        backdropFilter: "blur(4px)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                        e.currentTarget.style.background = "#ffffff";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                        e.currentTarget.style.background = "rgba(255,255,255,0.95)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#333",
                          marginBottom: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Tooltip title={`Operator: ${field.operator}`}>
                          <span style={{ fontSize: "11px", color: "#666" }}>
                            {getOperatorIcon(field.operator)}
                          </span>
                        </Tooltip>
                        {field.display_name}
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: "#222",
                          textShadow: "none",
                          lineHeight: 1.2,
                        }}
                      >
                        {formatValueLocal(field.value, field.operator)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "10px 12px",
            borderTop: "1px solid rgba(255,255,255,0.3)",
            background: "transparent",
            flexShrink: 0,
            marginBottom: 0,
          }}
        >
          <Button
            block
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleFormSelect(form);
            }}
            style={{
              fontSize: "12px",
              height: "34px",
              borderRadius: "10px",
              fontWeight: 600,
              marginBottom: 0,
              backgroundColor: "#ffffff",
              borderColor: "#ffffff",
              color: startColor,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.15)";
            }}
          >
            Graphical View
          </Button>
        </div>
      </Card>
    );
  };

  // ==================== SEARCH COMPONENT FOR COMPACT VIEW ====================
  const renderCompactSearch = () => {
    return (
      <div
        style={{
          marginBottom: "18px",
          padding: "8px 12px",
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "10px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Search
          placeholder="Search by data point name or column..."
          allowClear
          size="middle"
          onChange={(e) => {
            const value = e.target.value;
            setColumnSearchTerm(value);
            handleColumnSearch(value);
          }}
          loading={searchingByColumn}
          style={{ width: "100%" }}
          value={columnSearchTerm}
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
              }}
            />
          }
        />
      </div>
    );
  };

  // ==================== FINAL RETURN WITH BACKGROUND ====================
  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Background Images */}
      {renderBackground()}

      {/* Main Content with higher z-index */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ width: "100%" }}>
          {/* HERO SECTION - Using DB images */}
          {dashboardBgImages.length > 0 && (
            <div
              style={{
                position: "relative",
                width: isMobile ? "100%" : "100vw",
                left: isMobile ? "0" : "50%",
                right: isMobile ? "0" : "50%",
                marginLeft: isMobile ? "0" : "-50vw",
                marginRight: isMobile ? "0" : "-50vw",
                minHeight: isMobile ? "400px" : "600px",
                marginBottom: isMobile ? "30px" : "50px",
                borderRadius: isMobile ? "12px" : "18px",
                overflow: "hidden",
              }}
            >
              {/* Carousel Images Container - Using DB images */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                }}
              >
                {dashboardBgImages.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      top: -50,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      opacity: currentBgImageIndex === index ? 1 : 0,
                      transition: "opacity 1s ease-in-out",
                    }}
                  />
                ))}
              </div>

              {/* Carousel Dots Indicator */}
              {dashboardBgImages.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "20px",
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                    zIndex: 3,
                  }}
                >
                  {dashboardBgImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBgImageIndex(index)}
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: currentBgImageIndex === index ? "#ffffff" : "rgba(255,255,255,0.5)",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        transition: "all 0.3s ease",
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Content Container */}
              <div
                style={{
                  position: "relative",
                  zIndex: 2,
                  minHeight: "620px",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  padding: "30px 3% 30px 40px",
                  maxWidth: "100%",
                  margin: "0 auto",
                }}
              >
                {/* Right side compact cards container */}
                {dashboardConfig.showCompactCardView && (
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "420px",
                      maxHeight: "calc(620px - 60px)",
                      overflowY: "auto",
                      paddingRight: "8px",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                    className="invisible-scroll"
                  >
                    {/* Search Box */}
                    {renderCompactSearch()}

                    {/* Compact Cards List */}
                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      {displayedForms.map((form, index) => (
                        <div key={`hero-compact-${form.id}`} style={{ width: "100%" }}>
                          {renderCompactCard(form, index)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FILTER AND CARDS SECTION */}
          <div
            style={{
              maxWidth: "1400px",
              margin: "0 auto",
              padding: "0 20px",
            }}
          >
            {/* Filter Section */}
            <div style={{ marginBottom: "30px" }}>
              {renderFrontPageFilterSection()}
            </div>

            {/* Normal Cards Grid - HIDDEN for ULB Admin, Division Admin, District Admin */}
            {!shouldHideNormalCards && dashboardConfig.showNormalCardView && (
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : (window.innerWidth <= 1024 ? "repeat(2, 1fr)" : "repeat(3, 1fr)"),
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  {displayedForms.map((form, index) => (
                    <div key={`normal-${form.id}`} style={{ width: "100%" }}>
                      {renderNormalCard(form, index)}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    textAlign: "center",
                    color: "#8C8C8C",
                    fontSize: "13px",
                  }}
                >
                  {/* Footer text if needed */}
                </div>
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
};
  // ==================== RENDER SELECTED FORM VIEW ====================
const renderSelectedFormView = () => {
  if (!selectedForm) return null;

  // ========== CHECK USER ROLE ==========
  // DMA, Super Admin, and DMA Desk Officer - show full form view
  // All other users - show loading state while modal opens

  // Check if user has full access (DMA, Super Admin, DMA Desk Officer, DMA User)
  const isFullAccessUser = hasFullAccess();

  // For non-full-access users, show loading state (modal will open automatically)
  if (!isFullAccessUser) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(100vh - 200px)",
        flexDirection: "column",
        background: "transparent"
      }}>
        <Spin size="large" tip="Loading form data..." />
        <div style={{ marginTop: 20, color: "#666", fontSize: "16px" }}>
          <DatabaseOutlined style={{ marginRight: 8 }} />
          {selectedForm.displayName || selectedForm.tableName}
        </div>
        <div style={{ marginTop: 12, color: "#999", fontSize: "14px" }}>
          Opening data view...
        </div>
      </div>
    );
  }

  // ========== FULL FORM VIEW FOR DMA, SUPER ADMIN, AND DMA DESK OFFICER ==========
  // Define 6 pastel colors
  const pastelColors = [
    "#FFCCCC", // pastel pink
    "#CCE5FF", // pastel blue
    "#CCFFCC", // pastel mint
    "#FFE5B3", // pastel yellow
    "#E0CCFF", // pastel lavender
    "#FFCCB3", // pastel peach
  ];

  const getPastelColor = (index) => pastelColors[index % pastelColors.length];

  // Calculate consistent height
  const containerHeight = "calc(100vh - 280px)";

  // ========== BACKGROUND IMAGES FUNCTION ==========
  const renderBackground = () => {
    if (dashboardBgImages.length === 0) return null;

    const currentImage = dashboardBgImages[currentBgImageIndex];

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'opacity 1s ease-in-out',
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          zIndex: 1
        }} />
      </div>
    );
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
    }}>
      {/* Background Images */}
      {renderBackground()}

      {/* Main Content with higher z-index */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{
          marginTop: -15,
          height: "550px",
          display: "flex",
          flexDirection: "column",
          background: "transparent",
          borderRadius: "0px",
          padding: "10px",
          width: "100%",
        }}>
          {/* Form Field Filter Component */}
          <FormFieldFilter
            selectedForm={selectedForm}
            formData={formData}
            formFields={formFields}
            fieldLabels={fieldLabels}
            referenceCache={referenceCache}
            onFilterChange={(field, value, filteredData, fieldDisplayName) => {
              console.log("📢 Received filter change:", { field, value, filteredDataLength: filteredData?.length, fieldDisplayName });

              setQuickFilter({
                field: field,
                value: value,
                filteredData: filteredData || [],
                isActive: field && value,
                fieldDisplayName: fieldDisplayName || field
              });

              if (field && value && filteredData && filteredData.length > 0) {
                recalculateWithQuickFilter(filteredData);
              } else {
                if (originalFormData.length > 0) {
                  recalculateWithQuickFilter(originalFormData);
                }
              }
            }}
          />

          {/* Horizontal Filter Section */}
          <div style={{ marginBottom: 10, flexShrink: 0 }}>
            {renderFilterSection()}
          </div>

          {/* Split Layout: Data Points List (Left) + Report Cards (Right) */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", gap: "16px" }}>

            {/* Left Column - Data Points List */}
            <div style={{
              width: "320px",
              flexShrink: 0,
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              height: containerHeight,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #f0f0f0",
              background: "rgba(255, 255, 255, 0.95)",
              overflow: "hidden",
            }}>
              {/* Header with gradient matching selected form */}
              <div
                style={{
                  background: "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)",
                  padding: "9px 11px",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "14px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Space>
                  <span>Data Points</span>
                </Space>
              </div>

              {/* Scrollable List Container */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  padding: "8px",
                }}
              >
                {reportEnabledFields.length === 0 ? (
                  <Empty
                    description="No data points available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  reportEnabledFields.map((record, idx) => {
                    const isSelected = selectedDataPointId === record.field_id;
                    const gradientData = DATAPOINT_GRADIENT_SHADES[idx % DATAPOINT_GRADIENT_SHADES.length];

                    return (
                      <div
                        key={record.field_id}
                        onClick={() => {
                          if (selectedDataPointId === record.field_id) {
                            setSelectedDataPointId(null);
                          } else {
                            setSelectedDataPointId(record.field_id);
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "12px 16px",
                          marginBottom: "8px",
                          background: `linear-gradient(135deg, ${gradientData.start}, ${gradientData.end})`,
                          borderRadius: "8px",
                          borderLeft: isSelected ? `4px solid #ffd700` : "3px solid transparent",
                          borderRight: isSelected ? "1px solid rgba(255,215,0,0.3)" : "none",
                          transition: "all 0.2s ease",
                          boxShadow: isSelected ? "0 4px 12px rgba(0,0,0,0.25)" : "0 1px 3px rgba(0,0,0,0.1)",
                          opacity: 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.transform = "translateX(4px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                            e.currentTarget.style.borderLeft = "3px solid rgba(255,255,255,0.5)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.transform = "translateX(0)";
                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                            e.currentTarget.style.borderLeft = "3px solid transparent";
                          }
                        }}
                      >
                        <div style={{
                          fontWeight: isSelected ? 700 : 600,
                          fontSize: "13px",
                          color: "#ffffff",
                          wordBreak: "break-word",
                          textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}>
                          <span>{record.display_name || record.field_id}</span>
                          <span>
                            {record.report_important && (
                              <StarOutlined style={{ color: "#ffd700", marginLeft: "8px", fontSize: "11px" }} />
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column - Report Cards */}
            <div style={{
              flex: 1,
              minWidth: 0,
              height: containerHeight,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
              {/* Show message when a data point is selected */}
              {selectedDataPointId && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    background: "#e6f7ff",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <span>
                    <EyeOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                    Showing only: <strong>
                      {reportEnabledFields.find(f => f.field_id === selectedDataPointId)?.display_name || selectedDataPointId}
                    </strong>
                  </span>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setSelectedDataPointId(null)}
                  >
                    Show All
                  </Button>
                </div>
              )}

              {/* Report Fields Cards */}
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {(() => {
                  const fieldsToShow = selectedDataPointId
                    ? reportEnabledFields.filter(field => field.field_id === selectedDataPointId)
                    : reportEnabledFields;

                  return fieldsToShow.length === 0 ? (
                    <Empty
                      description="No data points available"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : (
                    renderReportEnabledCardsWithFilter(fieldsToShow)
                  );
                })()}
              </div>
            </div>
          </div>

          {renderColumnGraph()}
        </div>
      </div>
    </div>
  );
};

  // ==================== GRAPH MODAL COMPONENT ====================
  const GraphModal = () => {
    const [localChartType, setLocalChartType] = useState("pie");

    React.useEffect(() => {
      if (graphModalData) {
        setLocalChartType(graphModalData.chartType || "pie");
      }
    }, [graphModalData]);

    if (!graphModalData) return null;

    const { displayName, data, stats, aggregationLevel, groupLabel } = graphModalData;

    const pieData = data?.slice(0, 12).map((item) => ({
      name: item.name.length > 25 ? item.name.substring(0, 22) + "..." : item.name,
      fullName: item.name,
      value: item.value,
      originalName: item.originalName,
      count: item.count,
      avgValue: item.avgValue,
    }));

    const totalValue = pieData?.reduce((sum, item) => sum + item.value, 0) || 0;

    return (
      <Modal
        title={
          <Space>
            <BarChartOutlined style={{ color: "#1890ff" }} />
            <span>{displayName}</span>
            <Tag color="blue">
              {aggregationLevel === "record" ? "Record Level" : `${groupLabel} Level`}
            </Tag>
            {stats && (
              <Tag color="green">
                Total: {stats.sum?.toLocaleString() || 0}
              </Tag>
            )}
          </Space>
        }
        open={graphModalVisible}
        onCancel={() => {
          setGraphModalVisible(false);
          setGraphModalData(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setGraphModalVisible(false);
            setGraphModalData(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ padding: "20px" }}
      >
        {graphModalLoading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#8c8c8c" }}>Loading graph...</div>
          </div>
        ) : !data || data.length === 0 ? (
          <Empty description="No data available for this graph" />
        ) : (
          <>
            <div style={{ marginBottom: 16, textAlign: "right" }}>
              <Space>
                <Button
                  size="small"
                  type={localChartType !== "pie" ? "primary" : "default"}
                  icon={<BarChartOutlined />}
                  onClick={() => setLocalChartType("bar")}
                >
                  Bar Chart
                </Button>
                <Button
                  size="small"
                  type={localChartType === "pie" ? "primary" : "default"}
                  icon={<PieChartOutlined />}
                  onClick={() => setLocalChartType("pie")}
                >
                  Pie Chart
                </Button>
              </Space>
            </div>

            <ResponsiveContainer width="100%" height={400}>
              {localChartType === "pie" ? (
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => {
                      const percentage = (percent * 100).toFixed(1);
                      return `${name} (${percentage}%)`;
                    }}
                    outerRadius={130}
                    innerRadius={60}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {pieData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const dataPoint = payload[0].payload;
                        const percentage = ((dataPoint.value / totalValue) * 100).toFixed(1);
                        return (
                          <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                            <div><strong>{dataPoint.fullName || dataPoint.name}</strong></div>
                            <div>Value: {dataPoint.value.toLocaleString()}</div>
                            <div>Percentage: {percentage}%</div>
                            {dataPoint.count && <div>Records: {dataPoint.count}</div>}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              ) : (
                <BarChart data={data} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 11 }}
                    label={{ value: groupLabel || "Category", position: "insideBottom", offset: -60 }}
                  />
                  <YAxis
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                      return value.toLocaleString();
                    }}
                    label={{ value: displayName, angle: -90, position: "insideLeft" }}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const dataPoint = payload[0].payload;
                        return (
                          <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                            <div><strong>{dataPoint.originalName || dataPoint.name}</strong></div>
                            <div>Value: {dataPoint.value.toLocaleString()}</div>
                            {dataPoint.avgValue && <div>Average: {dataPoint.avgValue.toLocaleString()}</div>}
                            {dataPoint.count && <div>Records: {dataPoint.count}</div>}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>

            {stats && localChartType !== "pie" && (
              <div style={{ marginTop: 16, padding: "12px", background: "#f8f9fa", borderRadius: "8px" }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic title="Minimum" value={stats.min} valueStyle={{ color: "#fa8c16" }} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Maximum" value={stats.max} valueStyle={{ color: "#f5222d" }} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Average" value={stats.avg?.toFixed(2)} valueStyle={{ color: "#52c41a" }} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Total Sum" value={stats.sum?.toLocaleString()} valueStyle={{ color: "#1890ff" }} />
                  </Col>
                </Row>
              </div>
            )}
          </>
        )}
      </Modal>
    );
  };
  // Location Modal Component
  const LocationModal = () => {
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [modalTitle, setModalTitle] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [threshold, setThreshold] = useState(null);
    const [operator, setOperator] = useState('');

    // Fetch locations when modal opens
    React.useEffect(() => {
      if (locationModalVisible && locationModalData.locations) {
        setLocations(locationModalData.locations);
        setModalTitle(locationModalData.title);
        setFieldName(locationModalData.fieldName);
        setThreshold(locationModalData.threshold);
        setOperator(locationModalData.operator);
        setLoading(false);
      }
    }, [locationModalVisible, locationModalData]);

    const columns = [
      {
        title: 'ULB Name',
        dataIndex: 'ulb',
        key: 'ulb',
        sorter: (a, b) => a.ulb.localeCompare(b.ulb),
      },
      {
        title: 'District',
        dataIndex: 'district',
        key: 'district',
        sorter: (a, b) => a.district.localeCompare(b.district),
      },
      {
        title: 'Division',
        dataIndex: 'division',
        key: 'division',
        sorter: (a, b) => a.division.localeCompare(b.division),
      },
      {
        title: `Total ${fieldName}`,
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        sorter: (a, b) => a.total - b.total,
        render: (value) => value.toLocaleString(),
      },

    ];

    const operatorSymbol = operator === 'lte' ? '≤' : '≥';

    return (
      <Modal
        title={
          <Space>
            <TrophyOutlined style={{ color: "#faad14" }} />
            <span>ULBs with {fieldName} {operatorSymbol} {threshold?.toLocaleString()}</span>
            <Tag color="blue">{locations.length} ULBs</Tag>
          </Space>
        }
        open={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false);
          setLocationModalData({ title: '', locations: [], fieldName: '', threshold: null, operator: '' });
        }}
        footer={[
          <Button key="close" onClick={() => {
            setLocationModalVisible(false);
            setLocationModalData({ title: '', locations: [], fieldName: '', threshold: null, operator: '' });
          }}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#8c8c8c" }}>Loading locations...</div>
          </div>
        ) : locations.length === 0 ? (
          <Empty description="No ULBs found matching the condition" />
        ) : (
          <>
            <div style={{ marginBottom: 16, padding: "12px", background: "#f0f7ff", borderRadius: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Condition:</span>
                  <span style={{ marginLeft: 8, color: "#1890ff" }}>
                    {fieldName} {operatorSymbol} {threshold?.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Matching ULBs:</span>
                  <span style={{ marginLeft: 8, color: "#52c41a", fontSize: "18px", fontWeight: 700 }}>
                    {locations.length}
                  </span>
                </div>
              </div>
            </div>

            <Table
              dataSource={locations}
              columns={columns}
              rowKey={(record, index) => `${record.ulb}_${index}`}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ULBs`,
              }}
              size="small"
              scroll={{ y: 400 }}
            />
          </>
        )}
      </Modal>
    );
  };
  // Ranking Modal Component
  // Ranking Modal Component - Updated to show sum totals clearly
  // Ranking Modal Component - Updated to show applied filters
  const RankingModal = () => {
    if (!rankingResults) return null;

    const { data, fieldName, rankingTitle, totalULBs, operation, appliedFilters, totalRecords } = rankingResults;

    const columns = [
      {
        title: "Rank",
        key: "rank",
        width: 70,
        render: (_, __, index) => index + 1,
      },
      {
        title: "ULB Name",
        dataIndex: "ulb",
        key: "ulb",
        sorter: (a, b) => a.ulb.localeCompare(b.ulb),
        fixed: "left",
      },
      {
        title: "District",
        dataIndex: "district",
        key: "district",
        sorter: (a, b) => a.district.localeCompare(b.district),
      },
      {
        title: "Division",
        dataIndex: "division",
        key: "division",
        sorter: (a, b) => a.division.localeCompare(b.division),
      },
      {
        title: `${fieldName} (Total)`,
        dataIndex: "value",
        key: "value",
        align: "right",
        sorter: (a, b) => a.value - b.value,
        render: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 2 }),
      },
      {
        title: "Records",
        dataIndex: "count",
        key: "count",
        align: "center",
        width: 80,
        render: (count) => count,
      },
      {
        title: "Average",
        dataIndex: "average",
        key: "average",
        align: "right",
        width: 100,
        render: (avg) => avg?.toFixed(2),
      },
    ];

    // Calculate total sum of all values
    const totalSum = data.reduce((sum, item) => sum + item.value, 0);

    return (
      <Modal

        open={showRankingModal}
        onCancel={() => {
          setShowRankingModal(false);
          setRankingResults(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowRankingModal(false);
            setRankingResults(null);
          }}>
            Close
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {/* Show applied filters banner */}
        {appliedFilters && appliedFilters.length > 0 && (
          <div style={{
            marginBottom: 16,
            padding: "8px 12px",
            background: "#e6f7ff",
            borderRadius: "8px",
            borderLeft: "3px solid #1890ff"
          }}>
            <Space wrap>
              <FilterOutlined style={{ color: "#1890ff" }} />
              <span style={{ fontWeight: 500 }}>Applied Filters:</span>
              {appliedFilters.map((filter, idx) => (
                <Tag key={idx} color="blue">{filter}</Tag>
              ))}
              {totalRecords && (
                <Tag color="cyan">Total Records: {totalRecords}</Tag>
              )}
            </Space>
          </div>
        )}

        <Table
          dataSource={data}
          columns={columns}
          rowKey={(record, index) => `${record.ulb}_${index}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ULBs`,
          }}
          size="small"
          scroll={{ x: 800, y: 500 }}
        />
      </Modal>
    );
  };

  const contentMarginLeft = 0;

  return (
    <>
      <Navbar onMenuClick={() => setSbCollapsed(false)} />
      {/* <Sidebar collapsed={sbCollapsed} setCollapsed={setSbCollapsed} /> */}

      {/* Backdrop — clicking it collapses the sidebar */}
      {!sbCollapsed && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: baseMargin,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            zIndex: 998,
            transition: "all 0.3s",
            cursor: "pointer",
          }}
          onClick={() => setSbCollapsed(true)}
        />
      )}

      <div
        style={{
          marginLeft: contentMarginLeft,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#f8fafc",
          transition: "margin-left 0.25s ease",
          width: isMobile ? "100%" : `calc(100% - ${contentMarginLeft}px)`,
          position: "relative",
          zIndex: 1,
          overflow: "hidden",
        }}
      >

        {/*form name and buttons*/}
        {selectedForm && (
          <div
            style={{
              flexShrink: 0,
              margin: "65px 8px 0 10px",
              padding: "12px 10px 0 12px",
              borderRadius: "8px 8px 0 0",
              borderBottom: "1px solid #e8e8e8",
              zIndex: 10,
              backgroundColor: "#ffffff",
            }}
          >
            <div
              style={{
                marginBottom: 8,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "14px",
              }}
            >
              <Title level={2} style={{ margin: 0 }}>
                {selectedForm.displayName || `Form ${selectedForm.id}`}
              </Title>

              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <Button
                  icon={<LeftOutlined />}
                  onClick={() => {
                    // Clear ALL state when going back to all forms
                    setSelectedForm(null);
                    setSelectedFormColorIndex(null);
                    setColumnGraphData(null);
                    setHierarchicalGraphs([]);
                    setReportCalculations({});
                    setFieldGraphsData({});
                    setFormGraphData({});
                    setLoadedGraphs({});
                    setStableGraphData({});
                    setLoadingFormGraphs({});

                    setShowAnalysisResults(false);
                    setSelectedOperations([]);
                    setCalculationResults([]);
                    setRawDataForAnalysis([]);
                    setAnalysisDataColumns([]);
                    setHierarchicalStatistics({});
                    setSelectedStatsField(null);
                    setBreakdownData(null);
                    setSelectedBreakdownField(null);
                    setSelectedDataPointId(null);

                    setXAxisSelectedFields(['auto']);
                    setYAxisSelectedFields([]);

                    // Reset graph loading flags
                    graphLoadingStarted.current = false;
                    graphLoadQueueRef.current = [];
                    isProcessingQueue.current = false;
                    initialGraphsLoadedRef.current = false;

                    // Clear any pending timeouts
                    Object.values(graphLoadTimeouts).forEach(timeoutId => {
                      if (timeoutId) clearTimeout(timeoutId);
                    });
                    setGraphLoadTimeouts({});

                    setFrontPageFilters({
                      division: null,
                      district: null,
                      ulb_type: null,
                      ulb_class: null,
                      ulb_name: null,
                    });

                    setIsFilterApplied(false);

                    // Clear graph modal
                    setGraphModalData(null);
                    setGraphModalVisible(false);

                    loadForms({});
                  }}
                  size="medium"
                >
                  Back to All Forms
                </Button>

                <Button
  type="primary"
  icon={<EyeOutlined />}
  onClick={() => {
    setDataModalVisible(true);
    resetModalFilters();
  }}
  size="medium"
>
  View All Data
</Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadFormData(selectedForm.id, filters)}
                  loading={loadingFormData}
                  size="medium"
                >
                  Refresh
                </Button>
              </div>
            </div>

            <div
              style={{
                height: "1px",
                backgroundColor: "#e8e8e8",
                width: "100%",
              }}
            />
          </div>
        )}

        {/* Scrollable Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "16px",
            margin: selectedForm ? "0 16px 16px 16px" : "0 16px 16px 16px",
            background: cardSizeMode === "compact" && !selectedForm && !showAnalysisResults ? "transparent" : "#fff",
            borderRadius: selectedForm ? "0 0 8px 8px" : "8px",
          }}
        >
          {showAnalysisResults ? (
            // Show analysis results with filters (overrides both front page and form view)
            <div style={{ minHeight: "100%", paddingTop: "90px", }}>

              {selectedForm ? renderFilterSection() : renderFrontPageFilterSection()}      {renderAnalysisResults()}
            </div>
          ) : !selectedForm ? (
            // Front page view (no analysis, no selected form)
            <div style={{
              minHeight: "100%",
              position: "relative"
            }}>
              {/* Background overlay for compact view */}
              {cardSizeMode === "compact" && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: -16,
                      left: -16,
                      right: -16,
                      bottom: -16,
                      borderRadius: "3px",
                      zIndex: 0,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: -16,
                      left: -16,
                      right: -16,
                      bottom: -16,
                      borderRadius: "8px",
                      zIndex: 0,
                    }}
                  />
                </>
              )}

              <div style={{ position: "relative", zIndex: 1 }}>
                {/* Search Bar Section - Always visible on dashboard */}
                <div style={{ marginBottom: 90, width: "100%" }}>
                  {/* Search Box for Compact View */}
                  {/* <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e8e8e8",
            }}
          >
            <Search
              placeholder="Search by form name, data point or column..."
              allowClear
              size="large"
              onChange={(e) => {
                const value = e.target.value;
                setColumnSearchTerm(value);
                handleColumnSearch(value);
              }}
              loading={searchingByColumn}
              style={{ width: "100%" }}
              value={columnSearchTerm}
              enterButton={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  style={{
                    backgroundColor: "#1890ff",
                    borderColor: "#1890ff",
                    height: "20px",
                  }}
                >
                  Search
                </Button>
              }
            />
            <div style={{ marginTop: 5, fontSize: "5px", color: "#8c8c8c" }}>

            </div>
          </div> */}
                </div>

                {/* Horizontal Filter Section - Only show in normal view */}
                {cardSizeMode !== "compact" && (
                  <div style={{ marginBottom: 24 }}>
                    {renderFrontPageFilterSection()}
                  </div>
                )}

                {/* Cards Section - Full width below filters */}
                <div style={{ width: "100%" }}>
                  {loadingReportFields && (
                    <div style={{ textAlign: "center", padding: 20 }}>
                      <Spin tip="Loading report-enabled fields..." />
                    </div>
                  )}
                  {renderKPICards()}
                </div>
              </div>
            </div>
          ) : (
            // Form view (no analysis, has selected form)
            renderSelectedFormView()
          )}

          {(loading || loadingFormData) && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          )}
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </div>

      {renderDataModal()}
      <GraphModal />
      <LocationModal />
      <RankingModal />
    </>
  );
};

export default FormKPIDashboard;